import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Heart, Volume2, VolumeX, Loader2, BookOpen, MessagesSquare, Sparkles, Send, RefreshCw, AlertCircle, CheckCircle2, MessageCircleQuestion, Mic, MicOff, ImageIcon, Home, Headphones, Pencil, Languages, Star, Award, X, ArrowLeft, Lock, Unlock, Crown, Store, Clock, Music, Mail, Eye, EyeOff } from 'lucide-react';
import { Live2DViewer } from './Live2DViewer';
import musicTracks from './music.json';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { auth, googleProvider, db } from './firebase';
import { signInWithPopup, signOut, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, collection, addDoc, serverTimestamp, increment } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { backgrounds } from './backgrounds';
import classroomBg from './assets/images/anime_classroom_1780237942022.png';
import libraryBg from './assets/images/anime_library_1781020348724.png';
import studyDeskBg from './assets/images/anime_study_desk_1781020364392.png';
import bedroomBg from './assets/images/anime_bedroom_1780196695443.png';
import parkBg from './assets/images/anime_sakura_park_1780196723462.png';

const englishBackgrounds = [
  { id: 'classroom', url: classroomBg, label: 'Classroom', price: 0 },
  { id: 'library', url: libraryBg, label: 'Library', price: 30 },
  { id: 'desk', url: studyDeskBg, label: 'Study Desk', price: 30 },
  { id: 'room', url: bedroomBg, label: 'Room', price: 30 },
  { id: 'park', url: parkBg, label: 'Sakura Park', price: 30 },
];

const chatBackgrounds = [
  { id: 'bedroom', url: backgrounds[0], label: 'Bedroom', price: 0 },
  { id: 'japan_street', url: backgrounds[1], label: 'Japan Street', price: 30 },
  { id: 'sakura_park', url: backgrounds[2], label: 'Sakura Park', price: 30 },
  { id: 'night_street', url: backgrounds[3], label: 'Night Street', price: 30 },
];

function AnimatedStarScore({ score, maxScore = 5, size = 'sm', showMax = true }: { score: number, maxScore?: number, size?: 'sm' | 'md' | 'lg' | 'xl', showMax?: boolean }) {
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    let current = displayScore;
    if (current === score) return;
    const isCountingUp = score > displayScore;
    
    const interval = setInterval(() => {
       if (isCountingUp) {
          current += 0.5;
          if (current >= score) {
             current = score;
             clearInterval(interval);
          }
       } else {
          current -= 0.5;
          if (current <= score) {
             current = score;
             clearInterval(interval);
          }
       }
       setDisplayScore(current);
    }, 80);
    
    return () => clearInterval(interval);
  }, [score, displayScore]);

  const textSize = size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : size === 'lg' ? 'text-xl' : 'text-3xl';
  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : size === 'lg' ? 'w-7 h-7' : 'w-10 h-10';

  return (
    <div className={`flex items-center gap-1.5 font-mono drop-shadow-sm font-black ${score === maxScore ? 'text-amber-400' : 'text-amber-500'}`}>
      <motion.div 
        animate={score > 0 ? { scale: [1, 1.25, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        <Star className={`${iconSize} text-amber-400 fill-amber-400 drop-shadow-[0_1px_4px_rgba(251,191,36,0.6)] ${score === maxScore ? 'animate-pulse' : ''}`} />
      </motion.div>
      <span className={`${textSize} select-none`}>
        {Number.isInteger(displayScore) ? displayScore : displayScore.toFixed(1)} 
        {showMax && <span className="text-white/40 text-[0.8em]"> / {maxScore}</span>}
      </span>
    </div>
  );
}

interface SetupStatus {
  status: 'idle' | 'downloading' | 'ready' | 'error';
  progress: number;
  error: string;
  isReady: boolean;
  modelPath?: string;
}

const learningTopics = [
  { id: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
  { id: 'hobbies', label: 'Hobbies', icon: '🎨' },
  { id: 'travel', label: 'Travel', icon: '✈️' },
  { id: 'food', label: 'Food & Cooking', icon: '🍕' },
  { id: 'work', label: 'Work', icon: '💼' },
  { id: 'routine', label: 'Daily Routine', icon: '⏰' },
];

const practiceSkills = [
  { id: 'reading', label: 'Reading', icon: BookOpen, color: 'text-emerald-500 hover:text-white', bg: 'bg-white hover:bg-emerald-500 border-emerald-100 hover:border-emerald-500 shadow-emerald-100/50' },
  { id: 'listening', label: 'Listening', icon: Headphones, color: 'text-blue-500 hover:text-white', bg: 'bg-white hover:bg-blue-500 border-blue-100 hover:border-blue-500 shadow-blue-100/50' },
  { id: 'writing', label: 'Writing', icon: Pencil, color: 'text-indigo-500 hover:text-white', bg: 'bg-white hover:bg-indigo-500 border-indigo-100 hover:border-indigo-500 shadow-indigo-100/50' },
  { id: 'verbs', label: 'Conversation', icon: MessagesSquare, color: 'text-purple-500 hover:text-white', bg: 'bg-white hover:bg-purple-500 border-purple-100 hover:border-purple-500 shadow-purple-100/50' },
  { id: 'speaking', label: 'Speaking', icon: Mic, color: 'text-pink-500 hover:text-white', bg: 'bg-white hover:bg-pink-500 border-pink-100 hover:border-pink-500 shadow-pink-100/50' },
  { id: 'vocabulary', label: 'Vocabulary', icon: Languages, color: 'text-orange-500 hover:text-white', bg: 'bg-white hover:bg-orange-500 border-orange-100 hover:border-orange-500 shadow-orange-100/50' },
];

interface LevelInfo {
  level: number;
  label: string;
  code: string;
  cycles: number;
  minWords: number;
  desc: string;
  longDesc: string;
}

const levelsSpecs: LevelInfo[] = [
  { level: 1, label: "Starter", code: "Starter (A1-)", cycles: 0, minWords: 0, desc: "Free learning start", longDesc: "🌱 Free start (no exam). The user begins learning and practicing freely." },
  { level: 2, label: "Beginner A1", code: "A1", cycles: 3, minWords: 1, desc: "3 rounds · Min 1-2 words", longDesc: "👶 Extremely simple 1-2 word phrases using elementary vocabulary and basic present verbs." },
  { level: 3, label: "Strong Beginner A1+", code: "A1+", cycles: 4, minWords: 2, desc: "4 rounds · Min 2-3 words", longDesc: "🧱 Simple 2-3 word sentences with basic descriptions of people or objects." },
  { level: 4, label: "Elementary A2", code: "A2", cycles: 5, minWords: 3, desc: "5 rounds · Min 3-4 words", longDesc: "🚶 3-4 word phrases. Uses everyday phrases, activity vocabulary, and simple descriptions." },
  { level: 5, label: "Intermediate A2+", code: "A2+", cycles: 6, minWords: 4, desc: "6 rounds · Min 4-5 words", longDesc: "🏃 4-5 word phrases. Uses simple compound tenses and basic connectors." },
  { level: 6, label: "Intermediate B1", code: "B1", cycles: 7, minWords: 5, desc: "7 rounds · Min 5-7 words", longDesc: "🗣️ Fluent English usage with 5-7 words. Ability to talk about topics of personal interest." },
  { level: 7, label: "Strong Intermediate B1+", code: "B1+", cycles: 8, minWords: 6, desc: "8 rounds · Min 6-8 words", longDesc: "💬 Natural English usage with 6-8 word sentences and a wider lexical variety." },
  { level: 8, label: "Upper-Intermediate B2", code: "B2", cycles: 9, minWords: 8, desc: "9 rounds · Min 8-10 words", longDesc: "🎯 Fluent and spontaneous 8-10 word conversations with good lexical mastery and complex ideas." },
  { level: 9, label: "Advanced C1", code: "C1", cycles: 11, minWords: 10, desc: "11 rounds · Min 10-12 words", longDesc: "🏆 Rich and fluid 10-12 word responses. Uses idioms, complex structures, and nuances." },
  { level: 10, label: "Expert/Native C1+", code: "C1+", cycles: 12, minWords: 15, desc: "12 rounds · Min 15+ words", longDesc: "👑 The final exam. Complex 15+ word responses with native fluency and absolute assertiveness." }
];

function analyzeText(sentence: string) {
  // Regex that matches words and separates with punctuation/spacing
  const tokens = sentence.split(/(\b[a-zA-Z']+\b)/g);
  
  // Lists of common English verbs
  const verbs = new Set([
     'be', 'is', 'am', 'are', 'was', 'were', 'been', 'being', 'became', 'become', 'becomes', 'becoming',
     'have', 'has', 'had', 'having',
     'do', 'does', 'did', 'done', 'doing',
     'go', 'goes', 'went', 'gone', 'going',
     'say', 'says', 'said', 'saying',
     'make', 'makes', 'made', 'making',
     'get', 'gets', 'got', 'gotten', 'getting',
     'know', 'knows', 'knew', 'known', 'knowing',
     'think', 'thinks', 'thought', 'thinking',
     'take', 'takes', 'took', 'taken', 'taking',
     'see', 'sees', 'saw', 'seen', 'seeing',
     'come', 'comes', 'came', 'coming',
     'want', 'wants', 'wanted', 'wanting',
     'use', 'uses', 'used', 'using',
     'look', 'looks', 'looked', 'looking',
     'find', 'finds', 'found', 'finding',
     'give', 'gives', 'gave', 'given', 'giving',
     'tell', 'tells', 'told', 'telling',
     'work', 'works', 'worked', 'working',
     'call', 'calls', 'called', 'calling',
     'try', 'tries', 'tried', 'trying',
     'ask', 'asks', 'asked', 'asking',
     'need', 'needs', 'needed', 'needing',
     'feel', 'feels', 'felt', 'feeling',
     'keep', 'keeps', 'kept', 'keeping',
     'let', 'lets', 'letting',
     'begin', 'begins', 'began', 'begun', 'beginning',
     'seem', 'seems', 'seemed', 'seeming',
     'help', 'helps', 'helped', 'helping',
     'talk', 'talks', 'talked', 'talking',
     'play', 'plays', 'played', 'playing',
     'learn', 'learns', 'learned', 'learning',
     'start', 'starts', 'started', 'starting',
     'live', 'lives', 'lived', 'living',
     'run', 'runs', 'ran', 'running',
     'write', 'writes', 'wrote', 'written', 'writing',
     'speak', 'speaks', 'spoke', 'spoken', 'speaking',
     'cook', 'cooks', 'cooked', 'cooking',
     'prepare', 'prepares', 'prepared', 'preparing',
     'eat', 'eats', 'ate', 'eaten', 'eating',
     'drink', 'drinks', 'drank', 'drunk', 'drinking',
     'sleep', 'sleeps', 'slept', 'sleeping',
     'wake', 'wakes', 'woke', 'woken', 'waking',
     'buy', 'buys', 'bought', 'buying',
     'sell', 'sells', 'sold', 'selling',
     'swim', 'swims', 'swam', 'swimming',
     'sing', 'sings', 'sang', 'sung', 'singing',
     'dance', 'dances', 'danced', 'dancing',
     'enjoy', 'enjoys', 'enjoyed', 'enjoying',
     'love', 'loves', 'loved', 'loving',
     'like', 'likes', 'liked', 'liking',
     'prefer', 'prefers', 'preferred', 'preferring',
     'travel', 'travels', 'traveled', 'traveling',
     'visit', 'visits', 'visited', 'visiting',
     'drive', 'drives', 'drove', 'driven', 'driving',
     'ride', 'rides', 'rode', 'ridden', 'riding',
     'fly', 'flies', 'flew', 'flown', 'flying',
     'stay', 'stays', 'stayed', 'staying',
     'clean', 'cleans', 'cleaned', 'cleaning',
     'wash', 'washes', 'washed', 'washing',
     'brush', 'brushes', 'brushed', 'brushing',
     'wake', 'wakes', 'woke', 'waking',
     'exercise', 'exercises', 'exercised', 'exercising',
     'teach', 'teaches', 'taught', 'teaching',
     'practice', 'practices', 'practiced', 'practicing',
     'discuss', 'discusses', 'discussed', 'discussing',
     'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might', 'must'
  ]);

  // Lists of common English nouns
  const nouns = new Set([
     'cook', 'chef', 'kitchen', 'dinner', 'lunch', 'breakfast', 'meal', 'meals', 'apple', 'apples', 'pizza', 'pizzas', 'pasta', 'rice', 'bread', 'water', 'milk', 'coffee', 'tea', 'restaurant', 'restaurants', 'food', 'foods',
     'work', 'job', 'jobs', 'office', 'offices', 'company', 'companies', 'boss', 'colleague', 'colleagues', 'project', 'projects', 'meeting', 'meetings', 'desk', 'computer', 'computers', 'manager', 'team', 'teams', 'business',
     'day', 'days', 'routine', 'routines', 'morning', 'mornings', 'afternoon', 'afternoons', 'evening', 'evenings', 'night', 'nights', 'clock', 'clocks', 'time', 'times', 'bed', 'beds', 'sleep', 'shower', 'showers', 'teeth', 'school', 'schools', 'car', 'cars', 'bus', 'buses', 'train', 'trains', 'plane', 'planes', 'suitcase', 'ticket', 'tickets', 'hotel', 'hotels', 'beach', 'beaches', 'mountain', 'mountains', 'city', 'cities', 'country', 'countries', 'map', 'maps', 'suitcase', 'suitcases', 'passport',
     'hobby', 'hobbies', 'game', 'games', 'sport', 'sports', 'music', 'book', 'books', 'movie', 'movies', 'reading', 'writing', 'painting', 'drawing', 'guitar', 'guitars', 'piano', 'pianos', 'soccer', 'tennis', 'photography',
     'family', 'families', 'father', 'fathers', 'mother', 'mothers', 'parent', 'parents', 'brother', 'brothers', 'sister', 'sisters', 'sibling', 'siblings', 'son', 'sons', 'daughter', 'daughters', 'child', 'children', 'baby', 'babies', 'grandfather', 'grandmother', 'uncle', 'uncles', 'aunt', 'aunts', 'cousin', 'cousins',
     'dog', 'dogs', 'cat', 'cats', 'pet', 'pets', 'friend', 'friends', 'person', 'people', 'man', 'men', 'woman', 'women', 'boy', 'boys', 'girl', 'girls', 'teacher', 'teachers', 'student', 'students', 'doctor', 'doctors', 'animal', 'animals',
     'lesson', 'lessons', 'story', 'stories', 'word', 'words', 'sentence', 'sentences', 'english', 'tutor', 'room', 'rooms', 'street', 'streets', 'house', 'houses', 'home', 'homes', 'year', 'years', 'week', 'weeks', 'month', 'months', 'hour', 'hours', 'minute', 'minutes', 'park', 'parks', 'garden', 'gardens', 'store', 'stores', 'shop', 'shops'
  ]);

  return tokens.map((token, idx) => {
    if (!/^[a-zA-Z']+$/.test(token)) {
      return { text: token, type: 'other' };
    }

    const lower = token.toLowerCase();

    if (verbs.has(lower)) {
      return { text: token, type: 'verb' };
    }
    if (nouns.has(lower)) {
      return { text: token, type: 'noun' };
    }

    if (lower.endsWith('ing') && lower.length > 4 && !['thing', 'spring', 'ring', 'sing', 'wing', 'king', 'morning', 'evening', 'nothing', 'everything', 'something', 'sibling', 'clothing', 'ceiling'].includes(lower)) {
      return { text: token, type: 'verb' };
    }
    if (lower.endsWith('ed') && lower.length > 4 && !['hundred', 'bed', 'red', 'shed', 'fed'].includes(lower)) {
      return { text: token, type: 'verb' };
    }

    if (lower.endsWith('s') && lower.length > 2) {
      let singular = lower.slice(0, -1);
      if (nouns.has(singular)) {
        return { text: token, type: 'noun' };
      }
      if (lower.endsWith('es')) {
        let singular2 = lower.slice(0, -2);
        if (nouns.has(singular2)) {
          return { text: token, type: 'noun' };
        }
      }
    }

    return { text: token, type: 'other' };
  });
}

function getCleanWordsArray(text: string): string[] {
  if (!text) return [];
  return text.toLowerCase().match(/[a-zA-Z']+/g) || [];
}

function getStoryHash(text: string): string {
  if (!text) return 'empty';
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const chr = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return 'h' + Math.abs(hash).toString(36);
}

interface AlignedSpokenItem {
  word: string;
  isCorrect: boolean;
  matchedExpectedIdx: number | null;
}

interface AlignedExpectedItem {
  word: string;
  isSpoken: boolean;
  matchedSpokenIdx: number | null;
}

function alignSpeech(expected: string[], spoken: string[]) {
  const n = expected.length;
  const m = spoken.length;
  
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  
  for (let i = 0; i <= n; i++) dp[i][0] = i;
  for (let j = 0; j <= m; j++) dp[0][j] = j;
  
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (expected[i - 1] === spoken[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1]; // Exact match (cost 0)
      } else {
        // substitution threshold: slightly more than 1 to prefer match > insert+delete > substitute?
        // Wait, 1 sub (cost 1.2) is BETTER than 1 del (cost 1) + 1 ins (cost 1) = 2.0. So it prefers substitution.
        dp[i][j] = Math.min(
          dp[i - 1][j - 1] + 1.2, // Substitution
          dp[i - 1][j] + 1,       // Deletion (omitted)
          dp[i][j - 1] + 1        // Insertion (extra)
        );
      }
    }
  }

  const alignedSpoken: AlignedSpokenItem[] = [];
  for (let j = 0; j < m; j++) {
    alignedSpoken.push({ word: spoken[j], isCorrect: false, matchedExpectedIdx: null });
  }
  
  const alignedExpected: AlignedExpectedItem[] = [];
  for (let i = 0; i < n; i++) {
    alignedExpected.push({ word: expected[i], isSpoken: false, matchedSpokenIdx: null });
  }
  
  let i = n, j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && expected[i - 1] === spoken[j - 1]) {
      alignedSpoken[j - 1].isCorrect = true;
      alignedSpoken[j - 1].matchedExpectedIdx = i - 1;
      
      alignedExpected[i - 1].isSpoken = true;
      alignedExpected[i - 1].matchedSpokenIdx = j - 1;
      i--;
      j--;
    } else if (i > 0 && j > 0 && Math.abs(dp[i][j] - (dp[i - 1][j - 1] + 1.2)) < 0.0001) {
      alignedSpoken[j - 1].isCorrect = false;
      alignedSpoken[j - 1].matchedExpectedIdx = i - 1;
      
      alignedExpected[i - 1].isSpoken = false;
      alignedExpected[i - 1].matchedSpokenIdx = j - 1;
      i--;
      j--;
    } else if (i > 0 && Math.abs(dp[i][j] - (dp[i - 1][j] + 1)) < 0.0001) {
      i--;
    } else {
      j--;
    }
  }
  
  return { alignedSpoken, alignedExpected };
}

function playStarSound(starIndex: number) {
  try {
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    
    // Frequencies: 1: C4 (261.63 Hz), 2: E4 (329.63 Hz), 3: G4 (392.00 Hz), 4: C5 (523.25 Hz), 5: G5 (783.99 Hz)
    const frequencies = [261.63, 329.63, 392.00, 523.25, 783.99];
    const baseFreq = frequencies[starIndex - 1] || 261.63;
    
    // Layer standard triangle and sine oscillator for rich organic synth feel
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(baseFreq, ctx.currentTime);
    
    // Add a fifth harmonic to give it that crystal-chime clarity
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(baseFreq * 1.5, ctx.currentTime);
    
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.03);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    
    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.5);
    osc2.stop(ctx.currentTime + 0.5);
  } catch (e) {
    console.warn("Audio exception:", e);
  }
}

function playSuccessShinChime() {
  try {
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    
    // Sparkly upward major 7/9 arpeggio chord cascade
    const notes = [523.25, 659.25, 783.99, 987.77, 1174.66, 1567.98];
    
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const subOsc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.connect(gainNode);
      subOsc.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.06);
      
      subOsc.type = 'triangle';
      subOsc.frequency.setValueAtTime(freq + 4, ctx.currentTime + idx * 0.06); // Detuned chorus
      
      const startTime = ctx.currentTime + idx * 0.06;
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.10, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
      
      osc.start(startTime);
      subOsc.start(startTime);
      osc.stop(startTime + 0.7);
      subOsc.stop(startTime + 0.7);
    });
  } catch (e) {
    console.warn("Audio success exception:", e);
  }
}

function playSubscriptionFanfare() {
  try {
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    
    // Play an energetic fanfare (C major triad: C E G C)
    const fanfareNotes = [
      { freq: 523.25, duration: 0.15, startTime: 0 },         // C5
      { freq: 659.25, duration: 0.15, startTime: 0.15 },      // E5
      { freq: 783.99, duration: 0.15, startTime: 0.3 },       // G5
      { freq: 1046.50, duration: 0.6, startTime: 0.45 },      // C6
    ];

    fanfareNotes.forEach((note) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'square';
      osc.frequency.value = note.freq;

      gain.gain.setValueAtTime(0, ctx.currentTime + note.startTime);
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + note.startTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + note.startTime + note.duration);

      osc.start(ctx.currentTime + note.startTime);
      osc.stop(ctx.currentTime + note.startTime + note.duration);
    });

    // Add some noise (pseudo applause/cheer) alongside the chords
    const bufferSize = ctx.sampleRate * 2.0; // 2 seconds of noise
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 1000;
    
    const noiseGain = ctx.createGain();
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    
    // Envelope for the "applause" (builds up then fades out)
    noiseGain.gain.setValueAtTime(0, ctx.currentTime);
    noiseGain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.5);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2.0);
    
    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 2.0);

  } catch (e) {
    console.warn("Audio applause exception:", e);
  }
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export default function App() {
  const [text, setText] = useState('');
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAsking, setIsAsking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [history, setHistory] = useState<{ text: string; audioUrl: string }[]>([]);
  const [setup, setSetup] = useState<SetupStatus>({ status: 'idle', progress: 0, error: '', isReady: false });
  const [selectedAppMode, setSelectedAppMode] = useState<'chat' | 'english' | null>(null);
  const [isLive2DEngineLoaded, setIsLive2DEngineLoaded] = useState(false);
  const [micPermissionStatus, setMicPermissionStatus] = useState<'checking' | 'prompt' | 'granted' | 'denied' | 'requesting'>('checking');
  const [micRetryCountdown, setMicRetryCountdown] = useState(0);
  const [secondaryLoadingProgress, setSecondaryLoadingProgress] = useState(0);
  const [emotionState, setEmotionState] = useState<{name: string, level: number}>(() => {
    const saved = localStorage.getItem('kokoro_emotion');
    return saved ? JSON.parse(saved) : { name: 'alegría', level: 1 };
  });
  const [englishEmotionIndex, setEnglishEmotionIndex] = useState<number>(() => {
    return parseInt(localStorage.getItem('kokoro_english_emotion_index') || '0', 10);
  });
  const englishEmotionsList = ['alegría', 'tristeza', 'enojo', 'nerviosa'];
  const [englishEmotionSequence, setEnglishEmotionSequence] = useState<{emocion: string, nivel: number}[]>([]);
  const [englishLevel, setEnglishLevel] = useState<number>(() => {
    return parseInt(localStorage.getItem('kokoro_english_level') || '1', 10);
  });

  // PROGRESSION SYSTEM AND ORAL EXAMS STATES
  const [unlockedLevels, setUnlockedLevels] = useState<number[]>(() => {
    const saved = localStorage.getItem('kokoro_unlocked_levels');
    return saved ? JSON.parse(saved) : [1];
  });
  const [masteryTokens, setMasteryTokens] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_mastery_tokens');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [starsCount, setStarsCount] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_stars_count');
    return saved !== null ? parseInt(saved, 10) : 0;
  });

  useEffect(() => {
    localStorage.setItem('kokoro_stars_count', starsCount.toString());
  }, [starsCount]);

  const [listeningGrade, setListeningGrade] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_stat_listening');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [vocabularyGrade, setVocabularyGrade] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_stat_vocabulary');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [speakingGrade, setSpeakingGrade] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_stat_speaking');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [readingGrade, setReadingGrade] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_stat_reading');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [writingGrade, setWritingGrade] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_stat_writing');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [hasStartedStory, setHasStartedStory] = useState<boolean>(() => {
    return localStorage.getItem('kokoro_has_started_story') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('kokoro_has_started_story', hasStartedStory ? 'true' : 'false');
  }, [hasStartedStory]);

  const [dailyStoryAttempts, setDailyStoryAttempts] = useState<number>(() => {
    const today = new Date().toDateString();
    const savedDate = localStorage.getItem('kokoro_story_attempts_date');
    const savedAttempts = localStorage.getItem('kokoro_story_attempts_left');
    if (savedDate !== today) {
      localStorage.setItem('kokoro_story_attempts_date', today);
      localStorage.setItem('kokoro_story_attempts_left', '2');
      return 2;
    }
    return savedAttempts ? parseInt(savedAttempts, 10) : 2;
  });

  useEffect(() => {
    localStorage.setItem('kokoro_story_attempts_left', dailyStoryAttempts.toString());
    localStorage.setItem('kokoro_story_attempts_date', new Date().toDateString());
  }, [dailyStoryAttempts]);

  const [selectedWelcomePlan, setSelectedWelcomePlan] = useState<'gratis' | 'premium' | null>(null);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<'idle' | 'success'>('idle');

  const handleFeedbackSubmit = async () => {
    if (!feedbackMessage.trim()) return;
    const userEmail = auth.currentUser?.email;
    if (!userEmail) return;

    setIsSubmittingFeedback(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        email: userEmail,
        message: feedbackMessage.trim(),
        createdAt: serverTimestamp()
      });
      setFeedbackStatus('success');
      setFeedbackMessage('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const [showEnglishWelcomeModal, setShowEnglishWelcomeModal] = useState<boolean>(() => {
    return localStorage.getItem('kokoro_app_mode') === 'english';
  });

  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex(prev => (prev + 1) % 3);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const [studyMinutes, setStudyMinutes] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_study_minutes');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [dailyUsage, setDailyUsage] = useState<{chat: number, learn: number, date: string}>(() => {
    const today = new Date().toLocaleDateString('en-CA');
    const saved = localStorage.getItem('kokoro_daily_usage');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.date === today) {
          return parsed;
        }
      } catch(e) {}
    }
    return { chat: 0, learn: 0, date: today };
  });

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isPremiumUser, setIsPremiumUser] = useState<boolean>(() => {
    const saved = localStorage.getItem('kokoro_is_premium');
    return saved === 'true';
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState<boolean>(false);
  const [subscriptionAlert, setSubscriptionAlert] = useState<{type: 'expired' | 'failed' | 'success', message: string} | null>(null);

  const activatePremium = async () => {
    // No forzamos el estado Premium local o localKey de manera arbitraria.
    // El estado real se sincroniza a través de Firestore como la única fuente de verdad.
    setShowPaymentSuccess(true);
    playSubscriptionFanfare();
    if (auth.currentUser) {
      // Sincronizamos inmediatamente con nuestro endpoint del servidor para
      // confirmar y actualizar el estado de Stripe de forma segura desde la API oficial.
      setTimeout(() => {
        fetch('/api/sync-stripe-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: auth.currentUser?.uid })
        }).catch(() => { /* silent fallback */ });
      }, 1500);
    }
  };

  useEffect(() => {
    if (window.location.search.includes('payment=success')) {
      if (window.opener) {
        window.opener.postMessage('payment_success', '*');
        window.close();
      } else {
        activatePremium();
        // Clean up the URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => setShowPaymentSuccess(false), 5000);
      }
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'payment_success') {
        activatePremium();
        setShowEnglishWelcomeModal(false);
        setHasInteracted(true);
        setTimeout(() => setShowPaymentSuccess(false), 5000);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(true); // initially true while checking auth state
  const [authMode, setAuthMode] = useState<'google' | 'email_login' | 'email_signup' | 'email_verify_code'>('google');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authVerificationCode, setAuthVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState<any>(null);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [testModeCode, setTestModeCode] = useState('');
  const [showSignOutWarning, setShowSignOutWarning] = useState<boolean>(false);

  // States for dynamic Google User Profile selection
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userAvatar, setUserAvatar] = useState<string>('');

  const [unlockedBackgrounds, setUnlockedBackgrounds] = useState<number[]>(() => {
    const saved = localStorage.getItem('kokoro_unlocked_backgrounds');
    return saved ? JSON.parse(saved) : [0];
  });
  
  const [unlockedChatBackgrounds, setUnlockedChatBackgrounds] = useState<number[]>(() => {
    const saved = localStorage.getItem('kokoro_unlocked_chat_backgrounds');
    return saved ? JSON.parse(saved) : [0];
  });
  
  const [unlockedMusic, setUnlockedMusic] = useState<number[]>(() => {
    const saved = localStorage.getItem('kokoro_unlocked_music');
    return saved ? JSON.parse(saved) : [];
  });

  const [musicIndex, setMusicIndex] = useState<number>(-1);
  const [isMusicLoading, setIsMusicLoading] = useState(false);
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [musicVolume, setMusicVolume] = useState(() => {
    const saved = localStorage.getItem('kokoro_music_volume');
    return saved ? parseFloat(saved) : 0.5;
  });
  const [isMusicVolumeExpanded, setIsMusicVolumeExpanded] = useState(false);

  const [localTrackUrls, setLocalTrackUrls] = useState<Record<string, string>>({});
  const [musicDownloadProgress, setMusicDownloadProgress] = useState<Record<string, { status: 'idle' | 'downloading' | 'cached' | 'error'; percent?: number }>>(() => {
    const prog: Record<string, { status: 'cached'; percent: 100 }> = {};
    for (const track of musicTracks) {
      prog[track.id] = { status: 'cached', percent: 100 };
    }
    return prog;
  });
  const [isPreloadingMusic, setIsPreloadingMusic] = useState(false);

  useEffect(() => {
    const initializeAndPreloadMusic = async () => {
      if (!('caches' in window)) return;
      try {
        const MUSIC_CACHE_NAME = 'kokoro-music-store-v1';
        // Delete any old cached data of the over-engineered downloader to clear corrupted files
        await caches.delete(MUSIC_CACHE_NAME);
        console.log("Successfully cleared any corrupted cached music storage.");
      } catch (err) {
        console.error("Error clearing music cache:", err);
      }
    };

    initializeAndPreloadMusic();
  }, []);

  const reDownloadTrack = async (trackId: string) => {
    console.log(`Bypassing manual re-download for local static track: ${trackId}`);
    setMusicDownloadProgress(prev => ({
      ...prev,
      [trackId]: { status: 'cached', percent: 100 }
    }));
    return true;
  };

  useEffect(() => {
    localStorage.setItem('kokoro_music_index', musicIndex.toString());
  }, [musicIndex]);

  useEffect(() => {
    localStorage.setItem('kokoro_music_volume', musicVolume.toString());
  }, [musicVolume]);

  // Sync de suscripción en tiempo real con Firebase
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!isLoggedIn || !currentUser) return;
    
    let isInitialRender = true;
    const userPath = `users/${currentUser.uid}`;

    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Premium status
        const isNowPremium = data.isPremium === true;
        const currentPremiumState = localStorage.getItem('kokoro_is_premium') === 'true';
        const currentSubStatus = localStorage.getItem('kokoro_sub_status') || 'none';
        
        if (currentPremiumState !== isNowPremium || currentSubStatus !== data.stripeSubscriptionStatus) {
           // Notificamos si la suscripción cambió durante la sesión activa o desde la última vez
           if (!isNowPremium && currentPremiumState === true) {
             const failed = data.stripeLastPaymentFailed;
             const isRefunded = data.stripeSubscriptionRefunded === true || data.stripeSubscriptionStatus === 'refunded';
             const cancellationReason = data.stripeCancellationReason;
             
             let alertMsg = "Lamentamos verte partir. Tu suscripción Premium ha sido cancelada y tu cuenta ingresó al Plan Gratuito nuevamente.";
             let alertType: 'failed' | 'expired' = 'expired';
             
             if (failed) {
               alertType = 'failed';
               alertMsg = "El intento de renovación mensual de suscripción falló. Por favor, verifica el saldo de tu tarjeta. Tu cuenta ha regresado al Plan Gratuito.";
             } else if (isRefunded) {
               alertMsg = "Tu suscripción ha sido cancelada debido a un reembolso. Se ha efectuado la devolución de tu dinero y tu cuenta ha regresado al Plan Gratuito.";
             } else if (cancellationReason === 'payment_failed') {
               alertMsg = "Tu suscripción automática ha sido cancelada debido a problemas recurrentes con tu método de pago. Tu cuenta regresó al Plan Gratuito.";
             } else if (cancellationReason === 'payment_disputed') {
               alertType = 'failed';
               alertMsg = "Suscripción cancelada debido a una disputa de pago. Tu cuenta ha retornado al Plan Gratuito.";
             } else if (cancellationReason === 'cancellation_requested') {
               alertMsg = "Lamentamos verte partir. Tu suscripción ha sido cancelada por solicitud inmediata y tu cuenta ingresó al Plan Gratuito nuevamente.";
             } else if (data.stripeCancellationComment) {
               alertMsg = `Tu suscripción Premium ha sido cancelada de inmediato: "${data.stripeCancellationComment}". Tu cuenta ingresó al Plan Gratuito.`;
             } else {
               alertMsg = "Lamentamos verte partir. Tu suscripción Premium ha sido cancelada (debido a una solicitud inmediata o reembolso) y tu cuenta ingresó al Plan Gratuito nuevamente.";
             }
             
             setSubscriptionAlert({
               type: alertType,
               message: alertMsg
             });
           } else if (data.stripeSubscriptionStatus === 'canceled_at_period_end' && currentSubStatus !== 'canceled_at_period_end' && currentPremiumState === true) {
             setSubscriptionAlert({
               type: 'expired',
               message: "Tu suscripción ha sido cancelada, pero seguirás disfrutando de los beneficios Premium hasta el final de tu periodo de facturación actual."
             });
           } else if (currentPremiumState === false && isNowPremium) {
             if (!isInitialRender) {
               playSubscriptionFanfare();
             }
             setSubscriptionAlert({
               type: 'success',
               message: "👑 ¡Felicidades! ¡Verificación exitosa! Ahora disfrutas de los beneficios del Plan Premium sin límites."
             });
           }
        }
        
        setIsPremiumUser(isNowPremium);
        setSubscriptionStatus(data.stripeSubscriptionStatus || null);
        localStorage.setItem('kokoro_is_premium', String(isNowPremium));
        if (data.stripeSubscriptionStatus) {
            localStorage.setItem('kokoro_sub_status', data.stripeSubscriptionStatus);
        }
        isInitialRender = false;
      }
    }, (error) => {
      // Capturamos el error de permisos de Firebase de manera segura durante logout o transición de sesión
      try {
        handleFirestoreError(error, OperationType.GET, userPath);
      } catch (e) {
        console.warn("Gracefully logged and handled snapshot error during transition:", e);
      }
    });

    return () => unsubscribe();
  }, [isLoggedIn, auth.currentUser]);

  const syncProgressToCloud = async (
    levels: number[], 
    tokens: number, 
    currentLevel: number,
    optionalGrades?: { listening?: number; vocabulary?: number; speaking?: number; reading?: number; writing?: number },
    optionalStudyMinutes?: number,
    optionalStarsCount?: number,
    optionalUnlockedBackgrounds?: number[],
    optionalUnlockedChatBackgrounds?: number[],
    optionalUnlockedMusic?: number[]
  ) => {
    if (!auth.currentUser) return;
    try {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const lGrade = optionalGrades?.listening ?? listeningGrade;
      const vGrade = optionalGrades?.vocabulary ?? vocabularyGrade;
      const sGrade = optionalGrades?.speaking ?? speakingGrade;
      const rGrade = optionalGrades?.reading ?? readingGrade;
      const wGrade = optionalGrades?.writing ?? writingGrade;
      const mins = optionalStudyMinutes ?? studyMinutes;
      const stars = optionalStarsCount ?? starsCount;
      const bgToSync = optionalUnlockedBackgrounds ?? unlockedBackgrounds;
      const chatBgToSync = optionalUnlockedChatBackgrounds ?? unlockedChatBackgrounds;
      const musicToSync = optionalUnlockedMusic ?? unlockedMusic;

      await setDoc(userDocRef, {
        unlockedLevels: levels,
        unlockedBackgrounds: bgToSync,
        unlockedChatBackgrounds: chatBgToSync,
        unlockedMusic: musicToSync,
        masteryTokens: tokens,
        englishLevel: currentLevel,
        starsCount: stars,
        listeningGrade: lGrade,
        vocabularyGrade: vGrade,
        speakingGrade: sGrade,
        readingGrade: rGrade,
        writingGrade: wGrade,
        studyMinutes: mins,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.warn('Error syncing progress to Firestore (offline fallback active):', error);
      try {
        handleFirestoreError(error, OperationType.WRITE, `users/${auth.currentUser.uid}`);
      } catch (e) {
        // Safe catch-all to prevent throwing to the caller context
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const isEmailProvider = user.providerData.some(p => p.providerId === 'password');
        if (isEmailProvider && !user.emailVerified) {
          setIsLoggedIn(false);
          setIsLoggingIn(false);
          return;
        }

        setIsLoggedIn(true);
        setUserName(user.displayName || 'Estudiante');
        setUserEmail(user.email || '');
        setUserAvatar(user.photoURL || '');

        // Detect switched user and reset local storage/states to avoid carrying over stats
        const savedUID = localStorage.getItem('kokoro_last_auth_uid');
        if (savedUID && savedUID !== user.uid) {
          console.log('Switched Google account detected! Resetting local cache to prevent data overlap.');
          
          localStorage.removeItem('kokoro_unlocked_levels');
          localStorage.removeItem('kokoro_unlocked_backgrounds');
          localStorage.removeItem('kokoro_unlocked_chat_backgrounds');
          localStorage.removeItem('kokoro_unlocked_music');
          localStorage.removeItem('kokoro_bg_index');
          localStorage.removeItem('kokoro_english_bg');
          localStorage.removeItem('kokoro_mastery_tokens');
          localStorage.removeItem('kokoro_stars_count');
          localStorage.removeItem('kokoro_has_started_story');
          localStorage.removeItem('kokoro_english_level');
          localStorage.removeItem('kokoro_stat_listening');
          localStorage.removeItem('kokoro_stat_vocabulary');
          localStorage.removeItem('kokoro_stat_speaking');
          localStorage.removeItem('kokoro_stat_reading');
          localStorage.removeItem('kokoro_stat_writing');
          localStorage.removeItem('kokoro_study_minutes');
          localStorage.removeItem('kokoro_last_auth_uid');
          localStorage.removeItem('kokoro_is_premium');
          localStorage.removeItem('kokoro_sub_status');

          setUnlockedLevels([1]);
          setUnlockedBackgrounds([0]);
          setUnlockedChatBackgrounds([0]);
          setUnlockedMusic([]);
          setBgIndex(0);
          setEnglishBgIndex(0);
          setMusicIndex(-1);
          setMasteryTokens(0);
          setStarsCount(0);
          setHasStartedStory(false);
          setEnglishLevel(1);
          setIsPremiumUser(false);
          setListeningGrade(0);
          setVocabularyGrade(0);
          setSpeakingGrade(0);
          setReadingGrade(0);
          setWritingGrade(0);
          setStudyMinutes(0);
        }

        localStorage.setItem('kokoro_last_auth_uid', user.uid);

        // Load data from Firestore
        const userPath = `users/${user.uid}`;
        try {
          const userDocRef = doc(db, 'users', user.uid);
          let userSnap;
          try {
            userSnap = await getDoc(userDocRef);
          } catch (getErr) {
            console.warn('Firestore getDoc threw, falling back to local cached storage:", ', getErr);
            try {
              handleFirestoreError(getErr, OperationType.GET, userPath);
            } catch (e) {
              // Gracefully swallowed to allow using local progress stored on device
            }
          }

          if (userSnap && userSnap.exists()) {
            const cloudData = userSnap.data();
            const cloudLevels = cloudData.unlockedLevels || [1];
            const cloudPremium = cloudData.isPremium || false;
            const subStatus = cloudData.stripeSubscriptionStatus || null;
            
            setIsPremiumUser(cloudPremium);
            setSubscriptionStatus(subStatus);
            localStorage.setItem('kokoro_is_premium', cloudPremium.toString());

            // Sincronizamos estado de la suscripción de forma interactiva con la API de Stripe
            fetch('/api/sync-stripe-status', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: user.uid })
            }).catch(() => { /* silent fallback */ });

            const cloudBackgrounds = cloudData.unlockedBackgrounds || [0];
            const cloudChatBackgrounds = cloudData.unlockedChatBackgrounds || [0];
            const cloudMusic = cloudData.unlockedMusic || [];
            const cloudTokens = cloudData.masteryTokens ?? 0;
            const cloudStars = cloudData.starsCount ?? 0;
            const cloudHasStartedStory = cloudData.hasStartedStory ?? false;
            const cloudLevel = cloudData.englishLevel ?? 1;
            const cloudListening = cloudData.listeningGrade ?? 0;
            const cloudVocabulary = cloudData.vocabularyGrade ?? 0;
            const cloudSpeaking = cloudData.speakingGrade ?? 0;
            const cloudReading = cloudData.readingGrade ?? 0;
            const cloudWriting = cloudData.writingGrade ?? 0;
            const cloudStudyMinutes = cloudData.studyMinutes ?? 0;

            setUnlockedLevels(cloudLevels);
            setUnlockedBackgrounds(cloudBackgrounds);
            setUnlockedChatBackgrounds(cloudChatBackgrounds);
            setUnlockedMusic(cloudMusic);
            setMasteryTokens(cloudTokens);
            setStarsCount(cloudStars);
            setHasStartedStory(cloudHasStartedStory);
            setEnglishLevel(cloudLevel);
            setListeningGrade(cloudListening);
            setVocabularyGrade(cloudVocabulary);
            setSpeakingGrade(cloudSpeaking);
            setReadingGrade(cloudReading);
            setWritingGrade(cloudWriting);
            setStudyMinutes(cloudStudyMinutes);

            if (cloudData.selectedBgIndex !== undefined) {
              setBgIndex(cloudData.selectedBgIndex);
              localStorage.setItem('kokoro_bg_index', cloudData.selectedBgIndex.toString());
            }
            if (cloudData.selectedEnglishBgIndex !== undefined) {
              setEnglishBgIndex(cloudData.selectedEnglishBgIndex);
              localStorage.setItem('kokoro_english_bg', cloudData.selectedEnglishBgIndex.toString());
            }
            if (cloudData.selectedMusicIndex !== undefined) {
              setMusicIndex(cloudData.selectedMusicIndex);
            }

            localStorage.setItem('kokoro_unlocked_levels', JSON.stringify(cloudLevels));
            localStorage.setItem('kokoro_unlocked_backgrounds', JSON.stringify(cloudBackgrounds));
            localStorage.setItem('kokoro_unlocked_chat_backgrounds', JSON.stringify(cloudChatBackgrounds));
            localStorage.setItem('kokoro_unlocked_music', JSON.stringify(cloudMusic));
            localStorage.setItem('kokoro_mastery_tokens', cloudTokens.toString());
            localStorage.setItem('kokoro_stars_count', cloudStars.toString());
            localStorage.setItem('kokoro_has_started_story', cloudHasStartedStory ? 'true' : 'false');
            localStorage.setItem('kokoro_english_level', cloudLevel.toString());
            localStorage.setItem('kokoro_stat_listening', cloudListening.toString());
            localStorage.setItem('kokoro_stat_vocabulary', cloudVocabulary.toString());
            localStorage.setItem('kokoro_stat_speaking', cloudSpeaking.toString());
            localStorage.setItem('kokoro_stat_reading', cloudReading.toString());
            localStorage.setItem('kokoro_stat_writing', cloudWriting.toString());
            localStorage.setItem('kokoro_study_minutes', cloudStudyMinutes.toString());
          } else {
            // First time Google account is connected! Save current local progress to cloud.
            const currentUnlockedStr = localStorage.getItem('kokoro_unlocked_levels');
            const currentUnlocked = currentUnlockedStr ? JSON.parse(currentUnlockedStr) : [1];
            const currentBackgroundsStr = localStorage.getItem('kokoro_unlocked_backgrounds');
            const currentBackgrounds = currentBackgroundsStr ? JSON.parse(currentBackgroundsStr) : [0];
            const currentChatBackgroundsStr = localStorage.getItem('kokoro_unlocked_chat_backgrounds');
            const currentChatBackgrounds = currentChatBackgroundsStr ? JSON.parse(currentChatBackgroundsStr) : [0];
            const currentMusicStr = localStorage.getItem('kokoro_unlocked_music');
            const currentMusic = currentMusicStr ? JSON.parse(currentMusicStr) : [];
            const currentTokens = parseInt(localStorage.getItem('kokoro_mastery_tokens') || '0', 10);
            const currentStars = parseInt(localStorage.getItem('kokoro_stars_count') || '0', 10);
            const currentHasStartedStory = localStorage.getItem('kokoro_has_started_story') === 'true';
            const currentLevel = parseInt(localStorage.getItem('kokoro_english_level') || '1', 10);
            const currentListening = parseInt(localStorage.getItem('kokoro_stat_listening') || '0', 10);
            const currentVocabulary = parseInt(localStorage.getItem('kokoro_stat_vocabulary') || '0', 10);
            const currentSpeaking = parseInt(localStorage.getItem('kokoro_stat_speaking') || '0', 10);
            const currentReading = parseInt(localStorage.getItem('kokoro_stat_reading') || '0', 10);
            const currentWriting = parseInt(localStorage.getItem('kokoro_stat_writing') || '0', 10);
            const currentStudyMinutes = parseInt(localStorage.getItem('kokoro_study_minutes') || '0', 10);

            try {
              await setDoc(userDocRef, {
                unlockedLevels: currentUnlocked,
                unlockedBackgrounds: currentBackgrounds,
                unlockedChatBackgrounds: currentChatBackgrounds,
                unlockedMusic: currentMusic,
                masteryTokens: currentTokens,
                starsCount: currentStars,
                hasStartedStory: currentHasStartedStory,
                englishLevel: currentLevel,
                listeningGrade: currentListening,
                vocabularyGrade: currentVocabulary,
                speakingGrade: currentSpeaking,
                readingGrade: currentReading,
                writingGrade: currentWriting,
                studyMinutes: currentStudyMinutes,
                selectedBgIndex: bgIndex,
                selectedEnglishBgIndex: englishBgIndex,
                selectedMusicIndex: musicIndex,
                updatedAt: new Date()
              });
            } catch (setErr) {
              console.warn('Firestore setDoc initial sync failed, continuing offline safely:', setErr);
              try {
                handleFirestoreError(setErr, OperationType.WRITE, userPath);
              } catch (e) {
                // Ignore thrown exception
              }
            }
          }
        } catch (error) {
          console.error('Error pulling/setting user document in Firestore:', error);
        }
      } else {
        setIsLoggedIn(false);
        setUserName('');
        setUserEmail('');
        setUserAvatar('');
        localStorage.removeItem('kokoro_last_auth_uid');
      }
      setIsLoggingIn(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.hidden || !hasStartedStory) return;
      setStudyMinutes(prev => {
        const next = prev + 1;
        localStorage.setItem('kokoro_study_minutes', next.toString());
        if (auth.currentUser) {
          syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, next);
        }
        return next;
      });
    }, 60000);
    return () => clearInterval(timer);
  }, [unlockedLevels, masteryTokens, englishLevel, hasStartedStory]);

  const getFirebaseErrorMessage = (error: any) => {
    const code = error?.code;
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Correo electrónico o contraseña incorrectos.';
      case 'auth/email-already-in-use':
        return 'El correo electrónico ya está registrado. Por favor, inicia sesión.';
      case 'auth/weak-password':
        return 'La contraseña debe tener al menos 6 caracteres.';
      case 'auth/invalid-email':
        return 'El formato del correo electrónico es inválido.';
      case 'auth/operation-not-allowed':
        return 'El inicio de sesión con correo no está habilitado en Firebase.';
      default:
        return 'Ocurrió un error inesperado. Por favor, inténtalo de nuevo.';
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setTestModeCode('');
    setIsLoggingIn(true);
    try {
      // Send the code
      const res = await fetch("/api/auth/send-custom-verification-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail, isSignUp: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Error al enviar el código de verificación");
      }

      if (data.testMode && data.code) {
        setTestModeCode(data.code);
        setAuthSuccess('Modo de prueba activo. Hemos obtenido tu código de verificación automáticamente.');
      } else {
        setAuthSuccess('Te hemos enviado un código a tu correo.');
      }
      setAuthMode('email_verify_code');
      setIsLoggingIn(false);
    } catch (error: any) {
      console.log('Error signing up with email', error);
      setAuthError(error.message);
      setIsLoggingIn(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setIsLoggingIn(true);
    try {
      const res = await fetch("/api/auth/verify-custom-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail, code: authVerificationCode, password: authPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Código incorrecto");
      }

      // After successful verification, sign in immediately!
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      
      setAuthSuccess('Cuenta verificada correctamente.');
      setShowLoginModal({isOpen: false, message: ""});
      setAuthMode('google');
      setAuthEmail('');
      setAuthPassword('');
      setAuthVerificationCode('');
      setTestModeCode('');
      setUnverifiedUser(null);
    } catch (error: any) {
      console.log('Error verifying code', error);
      setAuthError(error.message);
      setIsLoggingIn(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setTestModeCode('');
    setIsLoggingIn(true);
    setUnverifiedUser(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, authEmail, authPassword);
      if (!userCredential.user.emailVerified) {
        setUnverifiedUser(userCredential.user);
        setAuthError('Tu cuenta no está verificada. Revisa tu correo o reenvía el código.');
        setIsLoggingIn(false);
        setAuthMode('email_verify_code');
        return;
      }
      setShowLoginModal({isOpen: false, message: ""});
      setAuthMode('google');
      setAuthEmail('');
      setAuthPassword('');
    } catch (error: any) {
      console.log('Error signing in with email', error);
      setAuthError(getFirebaseErrorMessage(error));
      setIsLoggingIn(false);
    }
  };

  const handleResendVerification = async () => {
    setAuthError('');
    setAuthSuccess('');
    setTestModeCode('');
    try {
      const res = await fetch("/api/auth/send-custom-verification-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Error al reenviar el código");
      }
      if (data.testMode && data.code) {
        setTestModeCode(data.code);
        setAuthSuccess('Modo de prueba activo. Código obtenido automáticamente.');
      } else {
        setAuthSuccess('Código reenviado a tu correo.');
      }
    } catch (error: any) {
      setAuthError(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoggingIn(true);
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        if (result.credential?.idToken) {
          const credential = GoogleAuthProvider.credential(result.credential.idToken);
          await signInWithCredential(auth, credential);
        }
      } else {
        await signInWithPopup(auth, googleProvider);
      }
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      setIsLoggingIn(false);
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        alert("La ventana emergente de inicio de sesión fue bloqueada o cerrada. Si estás en una APK, asegúrate de que el WebView permita ventanas emergentes.");
      } else {
        alert("Error al iniciar sesión con Google: " + error.message);
      }
    }
  };

  const handleGoogleSwitchAccount = async () => {
    try {
      setIsLoggingIn(true);
      if (auth.currentUser) {
        console.log("Saving progress to cloud before switching accounts...");
        try {
          await syncProgressToCloud(
            unlockedLevels,
            masteryTokens,
            englishLevel,
            {
              listening: listeningGrade,
              vocabulary: vocabularyGrade,
              speaking: speakingGrade,
              reading: readingGrade,
              writing: writingGrade
            },
            studyMinutes,
            starsCount,
            unlockedBackgrounds,
            unlockedChatBackgrounds,
            unlockedMusic
          );
        } catch (syncErr) {
          console.warn("Failed to sync progress before switching accounts:", syncErr);
        }
      }
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      });
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        if (result.credential?.idToken) {
          const credential = GoogleAuthProvider.credential(result.credential.idToken);
          await signInWithCredential(auth, credential);
        }
      } else {
        await signInWithPopup(auth, googleProvider);
      }
    } catch (error: any) {
      console.error('Error switching Google account:', error);
      setIsLoggingIn(false);
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        alert("La ventana emergente fue bloqueada o cerrada. Si estás en una APK, revisa los permisos del WebView.");
      } else {
        alert("Error al cambiar de cuenta de Google: " + error.message);
      }
    } finally {
      googleProvider.setCustomParameters({});
    }
  };

  const handleGoogleSignOut = () => {
    // Show validation popup notice first to prevent accidental reset/data leakage
    setShowSignOutWarning(true);
  };

  const confirmSignOut = async () => {
    try {
      if (auth.currentUser) {
        console.log("Saving progress to cloud before signing out...");
        try {
          await syncProgressToCloud(
            unlockedLevels,
            masteryTokens,
            englishLevel,
            {
              listening: listeningGrade,
              vocabulary: vocabularyGrade,
              speaking: speakingGrade,
              reading: readingGrade,
              writing: writingGrade
            },
            studyMinutes,
            starsCount,
            unlockedBackgrounds,
            unlockedChatBackgrounds,
            unlockedMusic
          );
        } catch (syncErr) {
          console.warn("Failed to sync progress before sign out:", syncErr);
        }
      }
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut();
      }
      await signOut(auth);
      // Clear localStorage of progression counters for privacy and clean state on subsequent session
      localStorage.removeItem('kokoro_unlocked_levels');
      localStorage.removeItem('kokoro_unlocked_backgrounds');
      localStorage.removeItem('kokoro_unlocked_chat_backgrounds');
      localStorage.removeItem('kokoro_unlocked_music');
      localStorage.removeItem('kokoro_mastery_tokens');
      localStorage.removeItem('kokoro_stars_count');
      localStorage.removeItem('kokoro_has_started_story');
      localStorage.removeItem('kokoro_english_level');
      localStorage.removeItem('kokoro_stat_listening');
      localStorage.removeItem('kokoro_stat_vocabulary');
      localStorage.removeItem('kokoro_stat_speaking');
      localStorage.removeItem('kokoro_stat_reading');
      localStorage.removeItem('kokoro_stat_writing');
      localStorage.removeItem('kokoro_study_minutes');
      localStorage.removeItem('kokoro_last_auth_uid');
      localStorage.removeItem('kokoro_is_premium');
      localStorage.removeItem('kokoro_sub_status');
      localStorage.removeItem('kokoro_bg_index');
      localStorage.removeItem('kokoro_english_bg');

      // Reset state variables to default values
      setUnlockedLevels([1]);
      setUnlockedBackgrounds([0]);
      setUnlockedChatBackgrounds([0]);
      setUnlockedMusic([]);
      setBgIndex(0);
      setEnglishBgIndex(0);
      setMusicIndex(-1);
      setMasteryTokens(0);
      setStarsCount(0);
      setHasStartedStory(false);
      setEnglishLevel(1);
      setIsPremiumUser(false);
      setListeningGrade(0);
      setVocabularyGrade(0);
      setSpeakingGrade(0);
      setReadingGrade(0);
      setWritingGrade(0);
      setStudyMinutes(0);
    } catch (error) {
      console.error('Error executing sign out:', error);
    } finally {
      setShowSignOutWarning(false);
    }
  };

  const [isProgressionMenuOpen, setIsProgressionMenuOpen] = useState(false);
  const [activeProgressionTab, setActiveProgressionTab] = useState<'account' | 'progress' | 'rewards'>('account');
  const [stripeLogs, setStripeLogs] = useState<any[]>([]);
  const [isLoadingStripeLogs, setIsLoadingStripeLogs] = useState<boolean>(false);
  const [showStripeLogsToggle, setShowStripeLogsToggle] = useState<boolean>(false);

  const loadStripeLogs = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setIsLoadingStripeLogs(true);
    try {
      const res = await fetch(`/api/stripe-logs?userId=${currentUser.uid}`);
      const data = await res.json();
      if (data && data.logs) {
        setStripeLogs(data.logs);
      }
    } catch (e) {
      console.error("Error al cargar los logs de Stripe:", e);
    } finally {
      setIsLoadingStripeLogs(false);
    }
  };

  useEffect(() => {
    if (activeProgressionTab === 'account' && isProgressionMenuOpen && isLoggedIn) {
      loadStripeLogs();
    }
  }, [activeProgressionTab, isProgressionMenuOpen, isLoggedIn]);
  const [isExamActive, setIsExamActive] = useState(false);
  const [showExamExitConfirm, setShowExamExitConfirm] = useState(false);
  const [isSpeakingExamRules, setIsSpeakingExamRules] = useState(false);
  const isSpeakingExamRulesRef = useRef(false);
  useEffect(() => {
    isSpeakingExamRulesRef.current = isSpeakingExamRules;
  }, [isSpeakingExamRules]);
  const examIntervalRef = useRef<any>(null);
  const [examTargetLevel, setExamTargetLevel] = useState<number | null>(null);
  const [examTopicsList, setExamTopicsList] = useState<string[]>([]);
  const [examCurrentRound, setExamCurrentRound] = useState<number>(1);
  const [examHistory, setExamHistory] = useState<{ role: 'kokoro' | 'user'; text: string }[]>([]);
  const [examScores, setExamScores] = useState<number[]>([]);
  const [examRoundWordCounts, setExamRoundWordCounts] = useState<number[]>([]);
  const [examRoundValidity, setExamRoundValidity] = useState<boolean[]>([]);
  const [isExamFinished, setIsExamFinished] = useState(false);
  const isExamFinishedRef = useRef(false);
  const [examCountdown, setExamCountdown] = useState<number | null>(null);
  const [examMicCountdown, setExamMicCountdown] = useState<number | null>(null);
  const setExamMicCountdownRef = useRef<any>(null);

  useEffect(() => {
    setExamMicCountdownRef.current = setExamMicCountdown;
  }, [setExamMicCountdown]);

  useEffect(() => {
    isExamFinishedRef.current = isExamFinished;
  }, [isExamFinished]);

  useEffect(() => {
    if (examMicCountdown === null) return;
    if (examMicCountdown > 0) {
      const timer = setTimeout(() => {
        setExamMicCountdown(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
      return () => clearTimeout(timer);
    } else if (examMicCountdown === 0) {
      // time's up, process what we have so far (if user was speaking, `speakingTranscript` could be used, or just empty)
      if (handleExamUserAnswerRef.current) {
        handleExamUserAnswerRef.current(""); // empty will be handled as silences
      }
      setExamMicCountdown(null);
    }
  }, [examMicCountdown]);

  const [examStatusMessage, setExamStatusMessage] = useState<string>('');
  const [showLevelLockedModal, setShowLevelLockedModal] = useState<number | null>(null);
  const [isExamLoading, setIsExamLoading] = useState(false);
  const [isMyTurnToSpeakExam, setIsMyTurnToSpeakExam] = useState(false);
  
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState<{isOpen: boolean, message: string}>({isOpen: false, message: ""});
  const [isCancelling, setIsCancelling] = useState(false);
  const [examFeedbackNotification, setExamFeedbackNotification] = useState<{
    score: number;
    passedWords: boolean;
    isCoherent: boolean;
    feedback: string;
  } | null>(null);
  const [examCompletedSuccessfully, setExamCompletedSuccessfully] = useState<boolean | null>(null);

  const [dailyContext, setDailyContext] = useState<string>(() => {
    const savedContext = localStorage.getItem('kokoro_context');
    const lastUpdate = localStorage.getItem('kokoro_last_update');
    
    // Si han pasado más de 4 horas, reiniciamos el tema (Sesión nueva)
    if (lastUpdate && Date.now() - parseInt(lastUpdate) > 14400000) {
      return 'Ninguno. Sesión nueva.';
    }
    return savedContext || 'Ninguno aún. Estamos empezando a conocernos.';
  });
  const [longTermMemory, setLongTermMemory] = useState<string>(() => {
    return localStorage.getItem('kokoro_long_term') || 'Aún no sé mucho sobre ti. ¿Cómo te llamas?';
  });
  const [showSponsor, setShowSponsor] = useState(false);
  const [hasShownSponsor, setHasShownSponsor] = useState(false);
  const [showSponsor5Min, setShowSponsor5Min] = useState(false);
  const [hasShownSponsor5Min, setHasShownSponsor5Min] = useState(false);
  const [activeAdUrl, setActiveAdUrl] = useState<string | null>(null);
  const [hasIntroducedChat, setHasIntroducedChat] = useState(() => {
    return localStorage.getItem('kokoro_has_introduced_chat') === 'true';
  });
  const [hasIntroducedEnglish, setHasIntroducedEnglish] = useState(() => {
    return localStorage.getItem('kokoro_has_introduced_english') === 'true';
  });
  const [hideMicForEnglishIntro, setHideMicForEnglishIntro] = useState(() => {
    return (localStorage.getItem('kokoro_app_mode') as 'chat' | 'english' || 'chat') === 'english';
  });
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasGreetedThisSession, setHasGreetedThisSession] = useState(false);

  const [storyCooldown, setStoryCooldown] = useState(0);

  useEffect(() => {
    if (storyCooldown > 0) {
      const timer = setInterval(() => {
        setStoryCooldown(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [storyCooldown]);
  const [hasChosenTopic, setHasChosenTopic] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<{id: string, label: string, icon: string} | null>(null);
  const [hasEverSelectedTopic, setHasEverSelectedTopic] = useState(false);
  const [isListeningForOtherTopic, setIsListeningForOtherTopic] = useState(false);
  const isListeningForOtherTopicRef = useRef(false);
  const hasAutoOpenedPracticeRef = useRef(false);
  const pendingTopicPromptOnAudioEndRef = useRef(false);
  const [unlockedSuccessAnim, setUnlockedSuccessAnim] = useState<{ level: number; method: 'xp' | 'stars' | 'exam' } | null>(null);
  const [unlockedBgAnim, setUnlockedBgAnim] = useState<{ bgId: number; price: number } | null>(null);
  const [isLockAnimOpen, setIsLockAnimOpen] = useState(false);
  useEffect(() => {
    if (unlockedSuccessAnim || unlockedBgAnim) {
      setIsLockAnimOpen(false);
      playSuccessShinChime();
      const timer = setTimeout(() => {
        setIsLockAnimOpen(true);
        try {
          // Play a delightful lock sound effect
          const chime = new Audio('https://assets.mixkit.co/active_storage/sfx/2012/2012-84.wav');
          chime.volume = 0.35;
          chime.play().catch(() => {});
        } catch(e) {}
      }, 750);
      return () => clearTimeout(timer);
    }
  }, [unlockedSuccessAnim, unlockedBgAnim]);
  
  const [isTopicsMenuOpen, setIsTopicsMenuOpen] = useState(true);
  const [isLevelsMenuOpen, setIsLevelsMenuOpen] = useState(false);
  const [hasChosenLevel, setHasChosenLevel] = useState<boolean>(!!localStorage.getItem('kokoro_english_level'));
  const [isPracticeMenuOpen, setIsPracticeMenuOpen] = useState(false);
  const [isListeningPracticeActive, setIsListeningPracticeActive] = useState(false);
  const [listeningCountdown, setListeningCountdown] = useState<number | null>(null);
  const [isListeningPlaybackFinished, setIsListeningPlaybackFinished] = useState(false);
  const [conversationCountdown, setConversationCountdown] = useState<number | null>(null);
  const [spokenStory, setSpokenStory] = useState('');
  const [storyAudioUrl, setStoryAudioUrl] = useState<string | null>(null);

  // Skill practice usage counter states (for Report Card proportional split)
  const [listeningCount, setListeningCount] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_count_listening');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [vocabularyCount, setVocabularyCount] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_count_vocabulary');
    return saved ? parseInt(saved, 15) || parseInt(saved, 10) : 0;
  });
  const [speakingCount, setSpeakingCount] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_count_speaking');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [readingCount, setReadingCount] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_count_reading');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [writingCount, setWritingCount] = useState<number>(() => {
    const saved = localStorage.getItem('kokoro_count_writing');
    return saved ? parseInt(saved, 10) : 0;
  });

  const incrementListeningCount = () => {
    setListeningCount(prev => {
      const next = prev + 1;
      localStorage.setItem('kokoro_count_listening', next.toString());
      return next;
    });
  };
  const incrementVocabularyCount = () => {
    setVocabularyCount(prev => {
      const next = prev + 1;
      localStorage.setItem('kokoro_count_vocabulary', next.toString());
      return next;
    });
  };
  const incrementSpeakingCount = () => {
    setSpeakingCount(prev => {
      const next = prev + 1;
      localStorage.setItem('kokoro_count_speaking', next.toString());
      return next;
    });
  };
  const incrementReadingCount = () => {
    setReadingCount(prev => {
      const next = prev + 1;
      localStorage.setItem('kokoro_count_reading', next.toString());
      return next;
    });
  };
  const incrementWritingCount = () => {
    setWritingCount(prev => {
      const next = prev + 1;
      localStorage.setItem('kokoro_count_writing', next.toString());
      return next;
    });
  };
  
  // Conversation Practice States
  const [conversationRound, setConversationRound] = useState<number>(0);
  const [conversationHistory, setConversationHistory] = useState<{ role: 'kokoro' | 'user', text: string }[]>([]);
  const [conversationScores, setConversationScores] = useState<number[]>([]);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [isMyTurnToSpeak, setIsMyTurnToSpeak] = useState(false);
  const [conversationStatusMessage, setConversationStatusMessage] = useState<string>('');
  const [conversationFeedbackNotification, setConversationFeedbackNotification] = useState<{ score: number; feedback: string } | null>(null);
  const activePracticePanelRef = useRef<string | null>(null);

  const [activePracticePanel, setActivePracticePanel] = useState<'reading' | 'speaking' | 'writing' | 'vocabulary' | 'conversation' | null>(null);
  const [readingStarEarned, setReadingStarEarned] = useState(false);
  const [vocabularyStarEarned, setVocabularyStarEarned] = useState(false);
  const [listeningCompletedCount, setListeningCompletedCount] = useState(0);
  const [listeningStarEarned, setListeningStarEarned] = useState(false);
  const [speakingCompletedCount, setSpeakingCompletedCount] = useState<number>(0);
  const [speakingStarEarned, setSpeakingStarEarned] = useState(false);
  const [listeningXpAwarded, setListeningXpAwarded] = useState(false);
  const [speakingXpAwarded, setSpeakingXpAwarded] = useState(false);

  const [activePracticeSession, setActivePracticeSession] = useState<'reading' | 'speaking' | 'writing' | 'vocabulary' | 'listening' | 'conversation' | null>(null);
  const [sessionSeconds, setSessionSeconds] = useState<number>(0);
  const [sessionXpAwarded, setSessionXpAwarded] = useState<boolean>(false);

  // Focus Star Award for Listening (at 3 repetitions)
  useEffect(() => {
    const hash = getStoryHash(spokenStory);
    const starKey = `kokoro_star_${englishLevel}_listening_${hash}`;
    if (listeningCompletedCount >= 3 && !listeningStarEarned) {
      setListeningStarEarned(true);
      if (!localStorage.getItem(starKey)) {
        localStorage.setItem(starKey, 'true');
        setStarsCount(prev => {
          const nextStars = prev + 1;
          localStorage.setItem('kokoro_stars_count', nextStars.toString());
          syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
          return nextStars;
        });
        setListeningGrade(prev => {
          const next = Math.min(prev + 5, 100);
          localStorage.setItem('kokoro_stat_listening', next.toString());
          syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { listening: next });
          return next;
        });
        playSuccessShinChime();
      }
    }
  }, [listeningCompletedCount, listeningStarEarned, unlockedLevels, masteryTokens, englishLevel, spokenStory]);

  // XP Award for Listening (at 5 repetitions)
  useEffect(() => {
    const hash = getStoryHash(spokenStory);
    const xpKey = `kokoro_xp_${englishLevel}_listening_${hash}`;
    if (listeningCompletedCount >= 5 && !listeningXpAwarded) {
      setListeningXpAwarded(true);
      if (!localStorage.getItem(xpKey)) {
        localStorage.setItem(xpKey, 'true');
        setMasteryTokens(prevTokens => {
          const baseTokens = 3;
          const awardedTokens = isPremiumUser ? Math.ceil(baseTokens * 1.2) : baseTokens;
          const nextTokens = prevTokens + awardedTokens; // Listening awards 3 XP (or more if premium)
          localStorage.setItem('kokoro_mastery_tokens', nextTokens.toString());
          syncProgressToCloud(unlockedLevels, nextTokens, englishLevel);
          return nextTokens;
        });
        playSuccessShinChime();
      }
    }
  }, [listeningCompletedCount, listeningXpAwarded, unlockedLevels, englishLevel, spokenStory, isPremiumUser]);

  // Focus Star Award for Speaking (at 3 repetitions)
  useEffect(() => {
    const hash = getStoryHash(spokenStory);
    const starKey = `kokoro_star_${englishLevel}_speaking_${hash}`;
    if (speakingCompletedCount >= 3 && !speakingStarEarned) {
      setSpeakingStarEarned(true);
      if (!localStorage.getItem(starKey)) {
        localStorage.setItem(starKey, 'true');
        setStarsCount(prev => {
          const nextStars = prev + 1;
          localStorage.setItem('kokoro_stars_count', nextStars.toString());
          syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
          return nextStars;
        });
        setSpeakingGrade(prev => {
          const next = Math.min(prev + 5, 100);
          localStorage.setItem('kokoro_stat_speaking', next.toString());
          syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { speaking: next });
          return next;
        });
        playSuccessShinChime();
      }
    }
  }, [speakingCompletedCount, speakingStarEarned, unlockedLevels, masteryTokens, englishLevel, spokenStory]);

  // XP Award for Speaking (at 5 repetitions)
  useEffect(() => {
    const hash = getStoryHash(spokenStory);
    const xpKey = `kokoro_xp_${englishLevel}_speaking_${hash}`;
    if (speakingCompletedCount >= 5 && !speakingXpAwarded) {
      setSpeakingXpAwarded(true);
      if (!localStorage.getItem(xpKey)) {
        localStorage.setItem(xpKey, 'true');
        setMasteryTokens(prevTokens => {
          const baseTokens = 5;
          const awardedTokens = isPremiumUser ? Math.ceil(baseTokens * 1.2) : baseTokens;
          const nextTokens = prevTokens + awardedTokens; // Speaking awards 5 XP (or more if premium)
          localStorage.setItem('kokoro_mastery_tokens', nextTokens.toString());
          syncProgressToCloud(unlockedLevels, nextTokens, englishLevel);
          return nextTokens;
        });
        playSuccessShinChime();
      }
    }
  }, [speakingCompletedCount, speakingXpAwarded, unlockedLevels, englishLevel, spokenStory, isPremiumUser]);

  // Reset session counters and awards when entering/exiting practice panels
  useEffect(() => {
    if (activePracticePanel === 'speaking') {
      const hash = getStoryHash(spokenStory);
      setSpeakingCompletedCount(0);
      setSpeakingStarEarned(!!localStorage.getItem(`kokoro_star_${englishLevel}_speaking_${hash}`));
      setSpeakingXpAwarded(!!localStorage.getItem(`kokoro_xp_${englishLevel}_speaking_${hash}`));
    }
    if (activePracticePanel === 'reading') {
      const hash = getStoryHash(spokenStory);
      setReadingStarEarned(!!localStorage.getItem(`kokoro_star_${englishLevel}_reading_${hash}`));
    }
    if (activePracticePanel === 'vocabulary') {
      const hash = getStoryHash(spokenStory);
      setVocabularyStarEarned(!!localStorage.getItem(`kokoro_star_${englishLevel}_vocabulary_${hash}`));
    }
  }, [activePracticePanel, englishLevel, spokenStory]);

  useEffect(() => {
    if (isListeningPracticeActive) {
      const hash = getStoryHash(spokenStory);
      setListeningCompletedCount(0);
      setListeningStarEarned(!!localStorage.getItem(`kokoro_star_${englishLevel}_listening_${hash}`));
      setListeningXpAwarded(!!localStorage.getItem(`kokoro_xp_${englishLevel}_listening_${hash}`));
    }
  }, [isListeningPracticeActive, englishLevel, spokenStory]);

  // Synchronize activePracticeSession with panels and sub-sessions
  useEffect(() => {
    if (isListeningPracticeActive) {
      setActivePracticeSession('listening');
    } else if (activePracticePanel) {
      setActivePracticeSession(activePracticePanel);
    } else {
      setActivePracticeSession(null);
    }
  }, [activePracticePanel, isListeningPracticeActive]);

  // Track session timer and award XP + Star (where applicable) after 1 or 3 minutes
  useEffect(() => {
    if (!activePracticeSession) {
      setSessionSeconds(0);
      setSessionXpAwarded(false);
      return;
    }

    if (activePracticeSession === 'listening' || activePracticeSession === 'speaking') {
      // Repetition based practices, timer not used for rewards
      const hash = getStoryHash(spokenStory);
      setSessionSeconds(0);
      setSessionXpAwarded(!!localStorage.getItem(`kokoro_xp_${englishLevel}_${activePracticeSession}_${hash}`));
      return;
    }

    const hash = getStoryHash(spokenStory);
    const usesHash = true; // All practices are now tied to the specific story hash


    setSessionSeconds(0);
    const initXpKey = usesHash 
      ? `kokoro_xp_${englishLevel}_${activePracticeSession}_${hash}`
      : `kokoro_xp_${englishLevel}_${activePracticeSession}`;
    setSessionXpAwarded(!!localStorage.getItem(initXpKey));

    const interval = setInterval(() => {
      if (document.hidden) return;

      setSessionSeconds(prevSeconds => {
        if (prevSeconds >= 180) {
          return 180; // Timer stops at 180 seconds (3 minutes) since all timed rewards are max 3m
        }

        const nextSeconds = prevSeconds + 1;

        // --- EXPLICIT STAR AWARDS AT 60 SECONDS ---
        if (nextSeconds === 60) {
          if (activePracticeSession === 'vocabulary') {
            const starKey = `kokoro_star_${englishLevel}_vocabulary_${hash}`;
            setVocabularyStarEarned(isEarned => {
              if (!isEarned && !localStorage.getItem(starKey)) {
                localStorage.setItem(starKey, 'true');
                setStarsCount(prevStars => {
                  const nextStars = prevStars + 1;
                  localStorage.setItem('kokoro_stars_count', nextStars.toString());
                  syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
                  return nextStars;
                });
                setVocabularyGrade(prevGrade => {
                  const nextGrade = Math.min(prevGrade + 5, 100);
                  localStorage.setItem('kokoro_stat_vocabulary', nextGrade.toString());
                  syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { vocabulary: nextGrade });
                  return nextGrade;
                });
                playSuccessShinChime();
              }
              return true;
            });
          } else if (activePracticeSession === 'reading') {
            const starKey = `kokoro_star_${englishLevel}_reading_${hash}`;
            setReadingStarEarned(isEarned => {
              if (!isEarned && !localStorage.getItem(starKey)) {
                localStorage.setItem(starKey, 'true');
                setStarsCount(prevStars => {
                  const nextStars = prevStars + 1;
                  localStorage.setItem('kokoro_stars_count', nextStars.toString());
                  syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
                  return nextStars;
                });
                setReadingGrade(prevGrade => {
                  const nextGrade = Math.min(prevGrade + 5, 100);
                  localStorage.setItem('kokoro_stat_reading', nextGrade.toString());
                  syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { reading: nextGrade });
                  return nextGrade;
                });
                playSuccessShinChime();
              }
              return true;
            });
          }
        }

        // --- EXPLICIT XP AWARDS AT 180 SECONDS (3 MINUTES) --- //
        if (nextSeconds === 180) {
          const xpKey = usesHash
            ? `kokoro_xp_${englishLevel}_${activePracticeSession}_${hash}`
            : `kokoro_xp_${englishLevel}_${activePracticeSession}`;
          setSessionXpAwarded(wasAwarded => {
            if (!wasAwarded && !localStorage.getItem(xpKey)) {
              localStorage.setItem(xpKey, 'true');
              let xpToAward = 0;
              if (activePracticeSession === 'vocabulary' || activePracticeSession === 'reading' || activePracticeSession === 'writing') {
                xpToAward = 3;
              } else if (activePracticeSession === 'conversation') { // speaking is handled by reps now
                xpToAward = 5;
              }

              // 1. Award XP (Mastery Tokens)
              if (xpToAward > 0) {
                setMasteryTokens(prevTokens => {
                  const awardedTokens = isPremiumUser ? Math.ceil(xpToAward * 1.2) : xpToAward;
                  const nextTokens = prevTokens + awardedTokens;
                  localStorage.setItem('kokoro_mastery_tokens', nextTokens.toString());
                  syncProgressToCloud(unlockedLevels, nextTokens, englishLevel);
                  return nextTokens;
                });
              }

              // 2. Play success chime sound precisely ONCE
              playSuccessShinChime();
            }
            return true;
          });
        }

        return nextSeconds;
      } );
    }, 1000);

    return () => clearInterval(interval);
  }, [activePracticeSession, unlockedLevels, masteryTokens, englishLevel, spokenStory]);

  useEffect(() => {
    activePracticePanelRef.current = activePracticePanel;
  }, [activePracticePanel]);

  const renderXpTimer = (customClass?: string) => {
    if (!activePracticeSession) return null;
    
    // Adapt styling based on whether the session background is dark
    const isDark = activePracticeSession === 'listening' || activePracticeSession === 'conversation';

    const textPrimary = isDark ? 'text-slate-100' : 'text-slate-700';
    const textSecondary = isDark ? 'text-indigo-300' : 'text-indigo-650';
    const trackColor = isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200/60';

    if (activePracticeSession === 'listening' || activePracticeSession === 'speaking') {
      const hasAwarded = activePracticeSession === 'listening' ? listeningXpAwarded : speakingXpAwarded;
      let count = activePracticeSession === 'listening' ? listeningCompletedCount : speakingCompletedCount;
      const maxCount = 5;
      
      const isCompleted = hasAwarded || count >= maxCount;
      if (isCompleted) {
        count = maxCount;
      }
      
      const progressPercent = Math.min(100, (count / maxCount) * 100);
      const xpValue = activePracticeSession === 'speaking' ? 5 : 3;

      return (
        <div className={`flex flex-col gap-1 w-[100px] sm:w-[130px] text-left shrink-0 select-none ${customClass || ''}`}>
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-0.5 sm:gap-1">
              {isCompleted ? (
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-500 fill-emerald-400 animate-spin" style={{ animationDuration: '3s' }} />
              ) : (
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-indigo-500 animate-pulse" />
              )}
              <span className={`text-[8px] sm:text-[10px] font-black tracking-tight ${textPrimary}`}>
                {isCompleted ? 'XP Ready' : 'XP'}
              </span>
            </div>
            <span className={`text-[8px] sm:text-[9px] font-mono font-black px-1 py-0.5 rounded-md ${isCompleted ? 'bg-emerald-500/25 text-emerald-300 border border-emerald-500/30' : 'bg-indigo-500/15 text-indigo-600 border border-indigo-500/30'}`}>
              +{xpValue}
            </span>
          </div>

          <div className={`w-full h-1 sm:h-1.5 rounded-full overflow-hidden border relative ${trackColor}`}>
            <motion.div 
              className={`h-full rounded-full ${isCompleted ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500'}`}
              style={{ width: `${progressPercent}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ type: "spring", stiffness: 80, damping: 15 }}
            />
          </div>
          <p className={`text-[8px] sm:text-[9px] font-extrabold leading-none select-none ${textSecondary} hidden sm:block truncate`}>
            {isCompleted ? '¡Ronda OK! 🎉' : `${count}/${maxCount} ${activePracticeSession === 'listening' ? 'escuchas' : 'repeticiones'} para +${xpValue} XP`}
          </p>
        </div>
      );
    }

    const targetSeconds = 180;
    const remaining = Math.max(0, targetSeconds - sessionSeconds);
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    const formattedTime = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    const progressPercent = Math.min(100, (sessionSeconds / targetSeconds) * 100);
    
    const xpValue = (activePracticeSession === 'conversation') ? 5 : 3;
    const isCompleted = sessionXpAwarded || remaining === 0;

    return (
      <div className={`flex flex-col gap-1 w-[100px] sm:w-[130px] text-left shrink-0 select-none ${customClass || ''}`}>
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-0.5 sm:gap-1">
            {isCompleted ? (
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-500 fill-emerald-400 animate-spin" style={{ animationDuration: '3s' }} />
            ) : (
              <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-indigo-500 animate-pulse" />
            )}
            <span className={`text-[8px] sm:text-[10px] font-black tracking-tight ${textPrimary}`}>
              {isCompleted ? 'XP Ready' : 'XP Timer'}
            </span>
          </div>
          <span className={`text-[8px] sm:text-[9px] font-mono font-black px-1 py-0.5 rounded-md ${isCompleted ? 'bg-emerald-500/25 text-emerald-300 border border-emerald-500/30' : 'bg-indigo-500/15 text-indigo-600 border border-indigo-500/30'}`}>
            +{xpValue}
          </span>
        </div>
        
        {/* Tiny visual progress bar */}
        <div className={`w-full h-1 sm:h-1.5 rounded-full overflow-hidden border relative ${trackColor}`}>
          <motion.div 
            className={`h-full rounded-full ${isCompleted ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500'}`}
            style={{ width: `${progressPercent}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ type: "spring", stiffness: 80, damping: 15 }}
          />
        </div>
        <p className={`text-[8px] sm:text-[9px] font-extrabold leading-none select-none ${textSecondary} hidden sm:block truncate`}>
          {isCompleted ? '¡Ronda OK! 🎉' : `${formattedTime} para +${xpValue}`}
        </p>
      </div>
    );
  };

  const renderStarsIndicator = (score: number, maxScore: number, customClass?: string, description?: string) => {
    if (!activePracticeSession) return null;
    
    // Adapt styling based on whether the session background is dark
    const isDark = activePracticeSession === 'listening' || activePracticeSession === 'conversation';

    const textPrimary = isDark ? 'text-slate-100' : 'text-slate-700';
    const textSecondary = isDark ? 'text-amber-400' : 'text-amber-600';
    const trackColor = isDark ? 'bg-slate-850 border-slate-755' : 'bg-amber-50 border-amber-200/60';
    
    const isTimerBased = activePracticeSession === 'reading' || activePracticeSession === 'vocabulary';
    const focusTarget = 60; // 1 minute focus

    let isCompleted = score === maxScore;
    let progressPercent = 0;
    let formattedCountdown = '';

    if (isTimerBased) {
      isCompleted = score >= 1; // already earned
      if (isCompleted) {
        progressPercent = 100;
        formattedCountdown = "Recibido! 🏆";
      } else {
        const elapsed = Math.min(focusTarget, sessionSeconds);
        progressPercent = (elapsed / focusTarget) * 100;
        const remaining = Math.max(0, focusTarget - elapsed);
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        formattedCountdown = `${mins}:${secs < 10 ? '0' : ''}${secs} focus`;
      }
    } else {
      isCompleted = score === maxScore;
      progressPercent = maxScore > 0 ? (score / maxScore) * 100 : 0;
    }

    return (
      <div className={`flex flex-col gap-1 w-[100px] sm:w-[130px] text-left shrink-0 select-none ${customClass || ''}`}>
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-0.5 sm:gap-1">
            {isCompleted ? (
              <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400 fill-amber-400 animate-spin" style={{ animationDuration: '3s' }} strokeWidth={1.2} />
            ) : (
              <Star 
                className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all ${
                  score > 0 || progressPercent > 0 
                    ? 'text-amber-400 fill-amber-400 animate-pulse' 
                    : 'text-amber-400/35'
                }`}
                strokeWidth={1.2} 
              />
            )}
            <span className={`text-[8px] sm:text-[10px] font-black tracking-tight ${textPrimary}`}>
              {maxScore === 1 ? 'Star Timer' : 'Stars'}
            </span>
          </div>
          <span className={`text-[8px] sm:text-[9px] font-mono font-black px-1 py-0.5 rounded-md ${isCompleted ? 'bg-amber-550/20 text-amber-300 border border-amber-500/30' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'}`}>
            {isTimerBased ? (isCompleted ? '1/1' : '0/1') : `${score}/${maxScore}`}
          </span>
        </div>
        
        {/* Tiny visual progress bar */}
        <div className={`w-full h-1 sm:h-1.5 rounded-full overflow-hidden border relative ${trackColor}`}>
          <motion.div 
            className="h-full rounded-full bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-500"
            style={{ width: `${progressPercent}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ type: "spring", stiffness: 80, damping: 15 }}
          />
        </div>
        <p className={`text-[8px] sm:text-[9px] font-extrabold leading-none select-none ${textSecondary} hidden sm:block truncate`}>
          {isTimerBased 
            ? (isCompleted ? "¡Foco estrella! 🏆" : `${formattedCountdown}`) 
            : (description || (isCompleted ? "¡Todo hecho! 🏆" : "Sigue practicando"))}
        </p>
      </div>
    );
  };

  // Reset emotion state on exiting practice panels or exams
  useEffect(() => {
    if (activePracticePanel === null && !isExamActive) {
      setEmotionState({ name: 'alegría', level: 1 });
      if (audioRef.current) {
        try {
          if (audioRef.current.src && !audioRef.current.src.startsWith('data:audio/wav;base64')) {
            audioRef.current.pause();
            audioRef.current.removeAttribute('src');
            audioRef.current.load();
          }
        } catch (e) {}
      }
      setIsPlaying(false);
    }
  }, [activePracticePanel, isExamActive]);
  const [selectedVocabWord, setSelectedVocabWord] = useState<string | null>(null);
  const [vocabTranslation, setVocabTranslation] = useState<string>('');
  const [vocabDetails, setVocabDetails] = useState<{
    phonetic?: string;
    partOfSpeech?: string;
    definition?: string;
    example?: string;
  } | null>(null);
  const [isVocabLoading, setIsVocabLoading] = useState(false);
  const [isVocabSpeaking, setIsVocabSpeaking] = useState(false);

  const handleSpeakWord = async (word: string) => {
    if (!word || isVocabSpeaking) return;
    setIsVocabSpeaking(true);
    try {
      const response = await fetch('/api/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: word, mode: 'english', level: englishLevel })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.volume = volume;
        audio.preservesPitch = false;
        (audio as any).mozPreservesPitch = false;
        (audio as any).webkitPreservesPitch = false;
        audio.playbackRate = 1.20;
        audio.play().catch(() => {});
      }
    } catch (e) {
      console.error("Failed to speak vocabulary word:", e);
    } finally {
      setIsVocabSpeaking(false);
    }
  };

  const handleVocabWordClick = async (cleanWord: string) => {
    if (!cleanWord) return;
    const normalizedWord = cleanWord.toLowerCase().trim().replace(/[^a-z']/g, '');
    if (!normalizedWord) return;

    setSelectedVocabWord(normalizedWord);
    setIsVocabLoading(true);
    setVocabTranslation('');
    setVocabDetails(null);

    try {
      // 1. Fetch translation via google translate wrapper
      let translation = wordTranslationCache[normalizedWord];
      if (!translation) {
        try {
          const response = await fetch('/api/translate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: normalizedWord })
          });
          if (response.ok) {
            const data = await response.json();
            translation = data.translation;
            if (translation) {
              setWordTranslationCache(prev => ({ ...prev, [normalizedWord]: translation }));
            }
          }
        } catch (e) {
          console.warn("Translation API fetch failed", e);
        }
      }
      setVocabTranslation(translation || normalizedWord);

      // 2. Fetch dictionary definition (for phonetic and partOfSpeech) and Gemini (for def/example)
      let phonetic = '';
      let partOfSpeech = '';

      try {
        const dictResponse = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${normalizedWord}`);
        if (dictResponse.ok) {
          const dictData = await dictResponse.json();
          if (Array.isArray(dictData) && dictData[0]) {
            const firstEntry = dictData[0];
            phonetic = firstEntry.phonetic || (firstEntry.phonetics && firstEntry.phonetics.find((p: any) => p.text)?.text) || '';
            if (firstEntry.meanings && firstEntry.meanings.length > 0) {
              partOfSpeech = firstEntry.meanings[0].partOfSpeech || '';
            }
          }
        }
      } catch (e) {
        console.warn("Dictionary API fetch failed for phonetics", e);
      }

      // 3. Fetch Spanish definition and example via AI
      let trDefinition = 'Una palabra de vocabulario de la narrativa.';
      let trExample = 'Se encuentra en la historia de práctica actual.';

      try {
        const defineRes = await fetch('/api/define-vocab', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ word: normalizedWord })
        });
        if (defineRes.ok) {
          const defineData = await defineRes.json();
          if (defineData.definition) trDefinition = defineData.definition;
          if (defineData.example) trExample = defineData.example;
        }
      } catch (e) {
        console.warn("Define Vocab API failed", e);
      }

      setVocabDetails({ 
        phonetic, 
        partOfSpeech: partOfSpeech || 'word', 
        definition: trDefinition, 
        example: trExample 
      });
    } catch (error) {
      console.error("Error fetching vocab dictionary:", error);
    } finally {
      setIsVocabLoading(false);
    }
  };

  const [writingExercise, setWritingExercise] = useState<{
    originalText: string;
    tokens: {
      id: number;
      text: string;
      isWord: boolean;
      blankIndex: number | null;
      correctWord: string;
    }[];
    userAnswers: { [key: number]: string };
    checked: boolean;
    score: number;
  } | null>(null);
  const [speakingTranscript, setSpeakingTranscript] = useState('');
  const [isSpeakingListening, setIsSpeakingListening] = useState(false);
  const [maxStarsAchieved, setMaxStarsAchieved] = useState(0);
  const speakingRecognitionRef = useRef<any>(null);
  const speakingTimeoutRef = useRef<any>(null);

  useEffect(() => {
    if (activePracticePanel !== 'speaking') {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
        speakingTimeoutRef.current = null;
      }
      if (speakingRecognitionRef.current) {
        try {
          speakingRecognitionRef.current.stop();
        } catch (e) {}
      }
      setIsSpeakingListening(false);
    } else {
      setMaxStarsAchieved(0);
    }

    if (activePracticePanel !== 'reading') {
      setShowSpanishTranslation(false);
      setSpanishTranslation('');
    }
  }, [activePracticePanel]);
  const [spanishTranslation, setSpanishTranslation] = useState('');
  const [showSpanishTranslation, setShowSpanishTranslation] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeWordTranslation, setActiveWordTranslation] = useState<{ 
    index: number; 
    word: string; 
    translation: string; 
    loading: boolean;
    x?: number;
    y?: number;
  } | null>(null);
  const [wordTranslationCache, setWordTranslationCache] = useState<Record<string, string>>({});

  useEffect(() => {
    setSpanishTranslation('');
    setShowSpanishTranslation(false);
  }, [spokenStory]);

  // Trigger sound effects for unlocked stars ONLY at the end of speaking (when isSpeakingListening becomes false)
  useEffect(() => {
    if (!spokenStory) {
      if (maxStarsAchieved !== 0) {
        setMaxStarsAchieved(0);
      }
      return;
    }

    // Delay evaluation and sound playback until speaking state is completed/stopped
    if (isSpeakingListening) {
      return;
    }

    const expectedWords = getCleanWordsArray(spokenStory);
    const spokenWords = getCleanWordsArray(speakingTranscript);
    if (expectedWords.length === 0 || spokenWords.length === 0) {
      if (maxStarsAchieved !== 0) {
        setMaxStarsAchieved(0);
      }
      return;
    }

    const { alignedSpoken } = alignSpeech(expectedWords, spokenWords);
    const correctWordsCount = alignedSpoken.filter(w => w.isCorrect).length;
    const extraWordsCount = alignedSpoken.filter(w => !w.isCorrect).length;

    let currentStars = 0;
    if (expectedWords.length > 0 && correctWordsCount > 0) {
      if (correctWordsCount === expectedWords.length && extraWordsCount === 0) {
        currentStars = 5;
      } else {
        const netScore = Math.max(0, correctWordsCount - extraWordsCount);
        const ratio = netScore / expectedWords.length;
        if (ratio >= 0.85) {
          currentStars = 4;
        } else if (ratio >= 0.65) {
          currentStars = 3;
        } else if (ratio >= 0.45) {
          currentStars = 2;
        } else if (ratio >= 0.20) {
          currentStars = 1;
        } else {
          currentStars = 0;
        }
      }
    }

    if (currentStars > maxStarsAchieved) {
      const correctnessPercent = Math.round((correctWordsCount / expectedWords.length) * 100);
      setSpeakingGrade(prev => {
        const next = Math.max(prev, correctnessPercent);
        localStorage.setItem('kokoro_stat_speaking', next.toString());
        syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { speaking: next });
        return next;
      });

      // Play star sound in cascade for newly unlocked stars
      for (let s = maxStarsAchieved + 1; s <= currentStars; s++) {
        setTimeout(() => {
          playStarSound(s);
        }, (s - (maxStarsAchieved + 1)) * 150);
      }
      setTimeout(() => {
        playSuccessShinChime();
      }, (currentStars - maxStarsAchieved) * 150);
      setMaxStarsAchieved(currentStars);
    } else if (currentStars < maxStarsAchieved) {
      // If of some reason the current stars decreased, sync it down silently without audio
      setMaxStarsAchieved(currentStars);
    }

    // Award pronunciation stars incrementally based on previous historical best for this story
    const hash = getStoryHash(spokenStory);
    const bestStarsKey = `kokoro_speaking_best_stars_${englishLevel}_${hash}`;
    const previousBest = parseInt(localStorage.getItem(bestStarsKey) || '0', 10);
    if (currentStars > previousBest) {
      const delta = currentStars - previousBest;
      setStarsCount(prevStars => {
        const nextStars = prevStars + delta;
        localStorage.setItem('kokoro_stars_count', nextStars.toString());
        syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
        return nextStars;
      });
      localStorage.setItem(bestStarsKey, currentStars.toString());
    }
  }, [speakingTranscript, spokenStory, maxStarsAchieved, unlockedLevels, masteryTokens, englishLevel, isSpeakingListening]);

  const toggleSpeakingPracticeListening = () => {
    if (isSpeakingListening) {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
        speakingTimeoutRef.current = null;
      }
      if (speakingRecognitionRef.current) {
        try {
          speakingRecognitionRef.current.stop();
        } catch (e) {}
      }
      setIsSpeakingListening(false);
    } else {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setError("Your browser does not support offline speech recognition. Please use Google Chrome or Safari.");
        setTimeout(() => setError(null), 5000);
        return;
      }

      // Stop main-app listener/speech if running
      setIsListening(false);
      try {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      } catch (e) {}
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch(e) {}
        setIsPlaying(false);
      }

      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = 'en-US'; // English practice

      rec.onstart = () => {
        setIsSpeakingListening(true);
        setSpeakingTranscript('');
        setMaxStarsAchieved(0);
      };

      rec.onresult = (event: any) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; ++i) {
          const part = event.results[i][0].transcript.trim();
          if (!part) continue;
          
          if (transcript.length > 0 && part.toLowerCase().startsWith(transcript.toLowerCase() + ' ')) {
            // Android Chrome cumulative bug detected: this result already contains the past transcript
            transcript = part;
          } else if (transcript.length > 0 && part.toLowerCase() === transcript.toLowerCase()) {
            // Android Chrome bug: exactly identical cumulative string
            transcript = part;
          } else {
            // Desktop Chrome delta behavior
            transcript += (transcript.length > 0 ? ' ' : '') + part;
          }
        }
        
        transcript = transcript.replace(/\s+/g, ' ').toLowerCase().trim();
        
        // Android Chrome bug workaround: remove consecutive duplicate words
        const words = transcript.split(' ');
        const deduplicatedWords = words.filter((word, index) => {
          return index === 0 || word !== words[index - 1];
        });
        transcript = deduplicatedWords.join(' ');

        setSpeakingTranscript(transcript);

        // Cancel previous silence timer
        if (speakingTimeoutRef.current) {
          clearTimeout(speakingTimeoutRef.current);
        }

        // 1. Silent timeout: stops after 3.2 seconds of no speaking activity
        speakingTimeoutRef.current = setTimeout(() => {
          try {
            rec.stop();
          } catch (e) {}
        }, 3200);

        // 2. Story completion check: if user speaks at least as many words as the story
        const expectedWordsOnly = getCleanWordsArray(spokenStory);
        const spokenWordsOnly = getCleanWordsArray(transcript);
        if (expectedWordsOnly.length > 0 && spokenWordsOnly.length >= expectedWordsOnly.length) {
          if (speakingTimeoutRef.current) {
            clearTimeout(speakingTimeoutRef.current);
          }
          // slightly higher timeout so they can finish pronouncing the last word comfortably
          speakingTimeoutRef.current = setTimeout(() => {
            try {
              rec.stop();
            } catch (e) {}
          }, 1000);
        }
      };

      rec.onerror = (e: any) => {
        if (e.error !== 'aborted') {
          console.error("Speaking practice error:", e.error);
        }
        if (e.error === 'not-allowed') {
          setError("Microphone permission denied. Please allow microphone access.");
          setTimeout(() => setError(null), 5000);
        }
      };

      rec.onend = () => {
        setIsSpeakingListening(false);
        if (speakingTimeoutRef.current) {
          clearTimeout(speakingTimeoutRef.current);
          speakingTimeoutRef.current = null;
        }
        setSpeakingTranscript(curr => {
          if (curr && curr.trim().length > 0) {
            setSpeakingCompletedCount(prev => prev + 1);
          }
          return curr;
        });
      };

      speakingRecognitionRef.current = rec;
      try {
        rec.start();
      } catch (err) {
        console.error("Speech Recognition starting error:", err);
      }
    }
  };

  const handleWordPress = async (index: number, word: string, event: React.PointerEvent<HTMLSpanElement>) => {
    // Clean non-alphabetic characters
    const cleanWord = word.replace(/[^a-zA-Z']/g, '');
    if (!cleanWord) return;
    
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const modalElement = target.closest('.bg-white.rounded-3xl');
    let x = 0;
    let y = 0;
    
    if (modalElement) {
      const modalRect = modalElement.getBoundingClientRect();
      x = rect.left - modalRect.left + (rect.width / 2);
      y = rect.top - modalRect.top;
    }

    // Prevent default context menu or other behaviors
    setActiveWordTranslation({
      index,
      word: cleanWord,
      translation: wordTranslationCache[cleanWord.toLowerCase()] || '',
      loading: !wordTranslationCache[cleanWord.toLowerCase()],
      x,
      y
    });

    if (wordTranslationCache[cleanWord.toLowerCase()]) {
      return;
    }

    try {
      const response = await fetch('/api/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: cleanWord })
      });
      const data = await response.json();
      if (data.translation) {
        const trans = data.translation.trim().replace(/[".]/g, '');
        setWordTranslationCache(prev => ({
          ...prev,
          [cleanWord.toLowerCase()]: trans
        }));
        setActiveWordTranslation(prev => {
          if (prev && prev.index === index) {
            return { ...prev, translation: trans, loading: false };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error translating word:", error);
      setActiveWordTranslation(prev => {
        if (prev && prev.index === index) {
          return { ...prev, translation: '?', loading: false };
        }
        return prev;
      });
    }
  };

  const handleWordRelease = () => {
    setActiveWordTranslation(null);
  };

  const translateStoryToSpanish = async () => {
    if (!spokenStory || isTranslating) return;
    
    if (spanishTranslation) {
      setShowSpanishTranslation(!showSpanishTranslation);
      return;
    }
    
    setIsTranslating(true);
    try {
      const response = await fetch('/api/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: spokenStory })
      });
      const data = await response.json();
      if (data.translation) {
        setSpanishTranslation(data.translation);
        setShowSpanishTranslation(true);
      } else if (data.error) {
        console.error("Translation API error:", data.error);
        setError("Translation failed. Please try again.");
        setTimeout(() => setError(null), 4000);
      }
    } catch (e) {
      console.error("Error translating text:", e);
      setError("Network error of translation.");
      setTimeout(() => setError(null), 4000);
    } finally {
      setIsTranslating(false);
    }
  };

  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('kokoro_volume');
    return saved ? parseFloat(saved) : 1;
  });
  const [isVolumeExpanded, setIsVolumeExpanded] = useState(false);
  const [appMode, setAppMode] = useState<'chat' | 'english'>(() => {
    return (localStorage.getItem('kokoro_app_mode') as 'chat' | 'english') || 'chat';
  });
  const [chatLanguage, setChatLanguage] = useState<'es' | 'en'>(() => {
    return (localStorage.getItem('kokoro_chat_lang') as 'es' | 'en') || 'es';
  });
  const [showPremiumText, setShowPremiumText] = useState(true);
  const [bgIndex, setBgIndex] = useState(() => {
    const saved = localStorage.getItem('kokoro_bg_index');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [showBgSelector, setShowBgSelector] = useState(false);
  const [englishBgIndex, setEnglishBgIndex] = useState(() => {
    const saved = localStorage.getItem('kokoro_english_bg');
    return saved ? parseInt(saved, 10) : 0;
  });

  // Validate selected assets are unlocked (healing hook for switched accounts or resets)
  useEffect(() => {
    if (!unlockedChatBackgrounds.includes(bgIndex)) {
      setBgIndex(0);
      localStorage.setItem('kokoro_bg_index', '0');
    }
    if (!unlockedBackgrounds.includes(englishBgIndex)) {
      setEnglishBgIndex(0);
      localStorage.setItem('kokoro_english_bg', '0');
    }
    if (musicIndex >= 0 && musicTracks[musicIndex]) {
      const track = musicTracks[musicIndex];
      const isUnlocked = track.unlocked || track.free || unlockedMusic.includes(musicIndex);
      if (!isUnlocked) {
        setMusicIndex(-1);
      }
    }
  }, [unlockedChatBackgrounds, unlockedBackgrounds, unlockedMusic, bgIndex, englishBgIndex, musicIndex]);

  // Analytics: Invisible session timer
  useEffect(() => {
    if (!auth.currentUser || !isLoggedIn) return;

    let lastTick = Date.now();
    let accumulatedChat = 0;
    let accumulatedEnglish = 0;

    const interval = setInterval(async () => {
      const now = Date.now();
      const deltaSeconds = Math.floor((now - lastTick) / 1000);
      lastTick = now;

      // Skip tracking if page is hidden
      if (document.hidden) return;

      if (appMode === 'chat') {
        accumulatedChat += deltaSeconds;
      } else if (appMode === 'english') {
        accumulatedEnglish += deltaSeconds;
      }

      // Sync to Firebase every 60 seconds of accumulated time
      if (accumulatedChat >= 60 || accumulatedEnglish >= 60) {
        try {
          const userDocRef = doc(db, 'users', auth.currentUser!.uid);
          const updateData: any = {};
          
          if (accumulatedChat >= 60) {
            updateData.timeSpentChatSeconds = increment(accumulatedChat);
            accumulatedChat = 0;
          }
          if (accumulatedEnglish >= 60) {
            updateData.timeSpentEnglishSeconds = increment(accumulatedEnglish);
            accumulatedEnglish = 0;
          }
          
          if (Object.keys(updateData).length > 0) {
            await setDoc(userDocRef, updateData, { merge: true });
          }
        } catch (e) {
          console.error("Failed to sync usage time:", e);
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [auth.currentUser, isLoggedIn, appMode]);

  const showSkillsMenu = appMode === 'english' && hasChosenTopic && !isPlaying && !isLoading && !isAsking && history.length > 0;
  
  useEffect(() => {
    localStorage.setItem('kokoro_bg_index', bgIndex.toString());
  }, [bgIndex]);

  useEffect(() => {
    localStorage.setItem('kokoro_english_bg', englishBgIndex.toString());
  }, [englishBgIndex]);

  useEffect(() => {
    localStorage.setItem('kokoro_chat_lang', chatLanguage);
  }, [chatLanguage]);

  useEffect(() => {
    localStorage.setItem('kokoro_app_mode', appMode);
    
    setShowPremiumText(true);
    const timer = setTimeout(() => {
      setShowPremiumText(false);
    }, 3000);
    
    if (appMode === 'english') {
      setHideMicForEnglishIntro(true);
      setShowEnglishWelcomeModal(true);
    } else {
      setHideMicForEnglishIntro(false);
    }
    
    return () => clearTimeout(timer);
  }, [appMode]);

  const cycleBackground = () => {
    setBgIndex((prev) => (prev + 1) % backgrounds.length);
  };
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<any>(null);
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const bgAudio = bgMusicRef.current;
    if (!bgAudio) return;

    if (appMode === 'chat' && musicIndex >= 0 && musicTracks[musicIndex]) {
      const track = musicTracks[musicIndex];
      const trackUrl = track.url;
      const resolvedUrl = trackUrl.startsWith('http') || trackUrl.startsWith('blob:') ? trackUrl : window.location.origin + trackUrl;
      
      // Only set source and load if it has actually changed
      if (bgAudio.src !== resolvedUrl && !bgAudio.src.endsWith(track.url)) {
        bgAudio.src = trackUrl;
        bgAudio.loop = true;
        bgAudio.load();
      }

      if (bgAudio.paused) {
        bgAudio.play().catch(err => {
          console.warn("Background music play was interrupted or requires user interaction:", err);
        });
      }
    } else {
      if (!bgAudio.paused) {
        bgAudio.pause();
      }
      // Only clear src if we aren't just temporarily ducking due to mode switch, or we can just pause.
      // But clearing src when mode changes means it re-loads when switching back. 
      // It's probably better to simply pause it if we switch to English mode.
      if (musicIndex < 0) {
          bgAudio.removeAttribute('src');
          bgAudio.load();
      }
    }
  }, [musicIndex, appMode]);

  useEffect(() => {
    const bgAudio = bgMusicRef.current;
    if (!bgAudio || musicIndex < 0) return;

    const isDucked = isListening || isPlaying;
    // Ducked background music ratio: 20% of its set volume
    const targetVolume = isDucked ? Math.max(0, musicVolume * 0.2) : musicVolume;
    
    // Smoothly apply volume adjustment directly on the audio element to avoid clicks/stutter
    bgAudio.volume = targetVolume;
  }, [musicIndex, musicVolume, isListening, isPlaying]);

  const handleAskRef = useRef<any>(null);

  const autoResumeMicRef = useRef(false);
  const isBusyRef = useRef(false);

  useEffect(() => {
    isBusyRef.current = isAsking || isLoading || isPlaying;
  }, [isAsking, isLoading, isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
    localStorage.setItem('kokoro_volume', volume.toString());
  }, [volume]);

  const handleConversationUserAnswerRef = useRef<any>(null);
  useEffect(() => {
    handleConversationUserAnswerRef.current = handleConversationUserAnswer;
  }, [handleConversationUserAnswer]);

  const isExamActiveRef = useRef(false);
  useEffect(() => {
    isExamActiveRef.current = isExamActive;
  }, [isExamActive]);

  const examTopicsListRef = useRef<string[]>([]);

  const handleExamUserAnswerRef = useRef<any>(null);
  useEffect(() => {
    handleExamUserAnswerRef.current = handleExamUserAnswer;
  }, [handleExamUserAnswer]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (recognitionRef.current) {
        recognitionRef.current.lang = (appMode === 'english' || (appMode === 'chat' && chatLanguage === 'en')) ? 'en-US' : 'es-ES';
      }
    }
  }, [appMode, chatLanguage]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = (appMode === 'english' || (appMode === 'chat' && chatLanguage === 'en')) ? 'en-US' : 'es-ES';

        let silenceTimeout: NodeJS.Timeout | null = null;
        let finalAccumulatedTranscript = '';
        let lastProcessedTranscript = '';
        let currentLiveTranscript = '';

        recognitionRef.current.onstart = () => {
          finalAccumulatedTranscript = '';
          lastProcessedTranscript = '';
          currentLiveTranscript = '';
        };

        recognitionRef.current.onspeechstart = () => {
          if (isExamActiveRef.current && setExamMicCountdownRef.current) {
            setExamMicCountdownRef.current(null);
          }
        };
        recognitionRef.current.onsoundstart = () => {
          if (isExamActiveRef.current && setExamMicCountdownRef.current) {
            setExamMicCountdownRef.current(null);
          }
        };
        
        const processFinalTranscript = (transcriptToProcess: string) => {
          const transcript = transcriptToProcess.trim();
          if (!transcript || transcript === lastProcessedTranscript) return;
          lastProcessedTranscript = transcript;
          
          if (isListeningForOtherTopicRef.current) {
            isListeningForOtherTopicRef.current = false;
            setIsListeningForOtherTopic(false);
            const capitalized = transcript.charAt(0).toUpperCase() + transcript.slice(1);
            setSelectedTopic({
              id: 'custom_other',
              label: capitalized,
              icon: '🎙️'
            });
            setHasChosenTopic(true);
            setHasStartedStory(false);
            setHasEverSelectedTopic(true);
            setIsTopicsMenuOpen(false);
            setIsLevelsMenuOpen(true);
            try { recognitionRef.current.stop(); } catch (e) {}
            return;
          }

          if (isExamActiveRef.current) {
            if (handleExamUserAnswerRef.current) {
              handleExamUserAnswerRef.current(transcript);
            }
            try { recognitionRef.current.stop(); } catch (e) {}
            return;
          }

          if (activePracticePanelRef.current === 'conversation') {
            if (handleConversationUserAnswerRef.current) {
              handleConversationUserAnswerRef.current(transcript);
            }
            try { recognitionRef.current.stop(); } catch (e) {}
            return;
          }

          setQuestion(transcript);
          autoResumeMicRef.current = true;
          if (handleAskRef.current) {
            handleAskRef.current(transcript);
          }
          
          try { recognitionRef.current.stop(); } catch(e) {}
        };

        recognitionRef.current.onresult = (event: any) => {
          let interimTranscript = '';
          let finalPart = '';
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              finalPart += event.results[i][0].transcript + ' ';
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }
          finalAccumulatedTranscript += finalPart;
          currentLiveTranscript = (finalAccumulatedTranscript + interimTranscript).trim();
          console.log('Speech interim/final:', currentLiveTranscript);
          
          if (silenceTimeout) clearTimeout(silenceTimeout);
          
          if (currentLiveTranscript) {
            silenceTimeout = setTimeout(() => {
              processFinalTranscript(currentLiveTranscript);
            }, 4500); // 4.5 seconds of silence before auto-submitting
          }
        };

        let shouldAutoRestart = false;

        recognitionRef.current.onerror = (event: any) => {
          if (event.error === 'not-allowed') {
            autoResumeMicRef.current = false;
            setError('Permiso de micrófono denegado. Presiona el candado en la URL para dar permiso.');
            setTimeout(() => setError(null), 8000);
          } else if (event.error === 'no-speech') {
             shouldAutoRestart = true;
          } else if (event.error !== 'aborted') {
             console.error('Speech recognition error', event.error);
             setError(`Error de micrófono: ${event.error}`);
             setTimeout(() => setError(null), 4000);
          }
        };

        recognitionRef.current.onend = () => {
          if (silenceTimeout) clearTimeout(silenceTimeout);
          // Process if there's leftover transcript when mic stops (e.g., user clicked stop manually)
          if (currentLiveTranscript && currentLiveTranscript !== lastProcessedTranscript) {
             processFinalTranscript(currentLiveTranscript);
          }
          
          if (autoResumeMicRef.current && !isBusyRef.current && shouldAutoRestart) {
             shouldAutoRestart = false;
             try {
               recognitionRef.current.start();
               return; 
             } catch(e) {}
          }
          shouldAutoRestart = false;
          setIsListening(false);
        };
      }
    }
  }, []);

  // Poll setup status
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    const checkStatus = async () => {
      try {
        const res = await fetch(`/api/setup/status?t=${Date.now()}`);
        if (!res.ok) throw new Error("HTTP " + res.status);
        const data = await res.json();
        setSetup(data);
        if (data.isReady || data.status === 'error') {
          clearInterval(interval);
        }
      } catch (err) {
        // Do not clear the interval on network errors, the local dev server might just be restarting.
        // We will keep polling without logging an error.
      }
    };

    checkStatus();
    interval = setInterval(checkStatus, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleSelectMode = (mode: 'chat' | 'english') => {
    const t0 = performance.now();
    console.log(`[MicOpt] User clicked ${mode} at ${t0.toFixed(2)}ms`);

    // 1. Play silent audio to unlock AudioContext first
    if (audioRef.current) {
      audioRef.current.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      audioRef.current.play().catch((e) => console.warn('[MicOpt] Audio unlock failed:', e));
    }

    // 2. Initiate mic permission request IMMEDIATELY
    checkAndRequestMicrophone(t0);

    // 3. Reset Live2D and Set the mode after yielding the main thread.
    // This allows the browser to actually paint the native permission dialog
    // before React freezes the thread with heavy component mounting (Live2D).
    setTimeout(() => {
      setIsLive2DEngineLoaded(false);
      setSelectedAppMode(mode);
    }, 50);
  };

  const checkAndRequestMicrophone = async (startTime?: number) => {
    const start = startTime || performance.now();
    console.log(`[MicOpt] Checking mic permission at ${performance.now().toFixed(2)}ms (Delay from click: ${(performance.now() - start).toFixed(2)}ms)`);
    setMicPermissionStatus('requesting');
    
    try {
      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
        throw new Error("Microphone API not supported or disabled in this iframe container");
      }
      
      const tGUM = performance.now();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tStream = performance.now();
      console.log(`[MicOpt] getUserMedia returned at ${tStream.toFixed(2)}ms (Time to get stream: ${(tStream - tGUM).toFixed(2)}ms)`);
      
      if (stream) {
        const tracks = stream.getTracks();
        if (Array.isArray(tracks)) {
          tracks.forEach(track => {
            if (track && typeof track.stop === 'function') {
              track.stop();
            }
          });
        }
      }
      setMicPermissionStatus('granted');
    } catch (err: any) {
      console.warn(`[MicOpt] Microphone permission denied or errored at ${performance.now().toFixed(2)}ms`);
      console.warn("[MicOpt] Error Name:", err?.name);
      console.warn("[MicOpt] Error Message:", err?.message);
      console.warn("[MicOpt] Error Stack:", err?.stack);
      console.warn("[MicOpt] Full Error:", err);
      setMicPermissionStatus('denied');
      setMicRetryCountdown(5);
    }
  };

  useEffect(() => {
    if (micPermissionStatus === 'denied' && micRetryCountdown > 0) {
      const timer = setTimeout(() => {
        setMicRetryCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [micPermissionStatus, micRetryCountdown]);

  // Handle automatic 4-second safety fallback to 2D Mode if Live2D engine is slow or fails to initialize
  useEffect(() => {
    if (selectedAppMode && micPermissionStatus === 'granted' && !isLive2DEngineLoaded) {
      const timeout = setTimeout(() => {
        console.log("Live2D loading timed out or browser lacks WebGL, auto-falling back to 2D Mode");
        setIsLive2DEngineLoaded(true);
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [selectedAppMode, micPermissionStatus, isLive2DEngineLoaded]);

  useEffect(() => {
    if (!selectedAppMode) {
      setSecondaryLoadingProgress(0);
      return;
    }

    let targetProgress = 15;
    if (micPermissionStatus === 'granted') {
      targetProgress = 50;
    }
    if (micPermissionStatus === 'granted' && isLive2DEngineLoaded) {
      targetProgress = 100;
    }

    // Smoothly animate progress toward targetProgress
    const interval = setInterval(() => {
      setSecondaryLoadingProgress(prev => {
        if (prev < targetProgress) {
          const next = prev + Math.ceil((targetProgress - prev) * 0.1);
          return Math.min(next, targetProgress);
        }
        return prev;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [selectedAppMode, micPermissionStatus, isLive2DEngineLoaded]);

  useEffect(() => {
    if (secondaryLoadingProgress === 100 && selectedAppMode) {
      const timer = setTimeout(() => {
        setAppMode(selectedAppMode);
        setHasInteracted(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [secondaryLoadingProgress, selectedAppMode]);

  // Screen Wake Lock API to prevent screen from sleeping
  useEffect(() => {
    if (!hasInteracted) return;

    let wakeLock: any = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log("Screen Wake Lock acquired successfully.");
        }
      } catch (err: any) {
        console.warn(`Wake Lock error: ${err.name}, ${err.message}`);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        await requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock !== null) {
        wakeLock.release().catch(() => {});
      }
    };
  }, [hasInteracted]);

  useEffect(() => {
    localStorage.setItem('kokoro_emotion', JSON.stringify(emotionState));

    const nameLower = emotionState.name.toLowerCase().trim();
    if ((nameLower === 'alegría' || nameLower === 'alegria' || nameLower === 'feliz' || nameLower === 'happy' || nameLower === 'happiness') && emotionState.level === 2) {
      const timer = setTimeout(() => {
        setEmotionState(prev => {
          const pNameLower = prev.name.toLowerCase().trim();
          return (pNameLower === 'alegría' || pNameLower === 'alegria' || pNameLower === 'feliz' || pNameLower === 'happy' || pNameLower === 'happiness') && prev.level === 2 ? { name: prev.name, level: 1 } : prev;
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [emotionState]);

  // Set initial emotional state when starting an english speech
  useEffect(() => {
    if ((appMode === 'english' || appMode === 'chat') && isPlaying && englishEmotionSequence.length > 0) {
      setEmotionState({
        name: englishEmotionSequence[0].emocion,
        level: englishEmotionSequence[0].nivel
      });
    }
  }, [isPlaying, englishEmotionSequence, appMode]);

  useEffect(() => {
    localStorage.setItem('kokoro_context', dailyContext);
    localStorage.setItem('kokoro_last_update', Date.now().toString());
  }, [dailyContext]);

  useEffect(() => {
    localStorage.setItem('kokoro_long_term', longTermMemory);
  }, [longTermMemory]);

  const isPremiumChatLimitReached = isPremiumUser && appMode === 'chat' && dailyUsage.chat >= 60;
  const isPremiumLearnLimitReached = isPremiumUser && appMode === 'english' && dailyUsage.learn >= 60;
  const shouldShowAds = !isPremiumUser || isPremiumChatLimitReached || isPremiumLearnLimitReached;

  useEffect(() => {
    if (isPremiumUser) {
      setShowSponsor(false);
      setShowSponsor5Min(false);
    }
  }, [isPremiumUser]);

  useEffect(() => {
    if (shouldShowAds && setup.isReady && hasInteracted && !showEnglishWelcomeModal) {
      let adIntervalMs = 5 * 60 * 1000;
      if (!isPremiumUser) {
        adIntervalMs = hasShownSponsor5Min ? 4 * 60 * 1000 : 60 * 1000;
      }
      const timer = setInterval(() => {
        setShowSponsor5Min(true);
        setHasShownSponsor5Min(true);
        autoResumeMicRef.current = false;
        setIsListening(false);
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch(e) {}
        }
      }, adIntervalMs);
      return () => clearInterval(timer);
    }
  }, [shouldShowAds, setup.isReady, isPremiumUser, hasShownSponsor5Min, hasInteracted, showEnglishWelcomeModal]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.hidden || !setup.isReady) return;
      
      setDailyUsage(prev => {
        const today = new Date().toLocaleDateString('en-CA');
        const current = prev.date === today ? prev : { chat: 0, learn: 0, date: today };
        const next = { ...current };
        
        if (appMode === 'chat') {
          next.chat += 1;
        } else {
          next.learn += 1;
        }
        
        localStorage.setItem('kokoro_daily_usage', JSON.stringify(next));
        return next;
      });
    }, 60000);
    
    return () => clearInterval(timer);
  }, [appMode, setup.isReady]);

  const handleSpeak = async (overrideText?: string) => {
    const textToSpeak = overrideText || text;
    if (!textToSpeak.trim() || !setup.isReady) return;

    setIsLoading(true);
    setError(null);

    // Cancel any ongoing fetch
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    fetchAbortControllerRef.current = new AbortController();

    // Filter out emojis and stars for the voice output so TTS sounds fully natural
    const cleanTextToSpeak = textToSpeak
      .replace(/⭐/g, '')
      .replace(/[\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    try {
      const response = await fetch('/api/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: cleanTextToSpeak, 
          mode: (appMode === 'chat' && chatLanguage === 'en') ? 'english' : appMode, 
          level: englishLevel 
        }),
        signal: fetchAbortControllerRef.current.signal
      });

      if (!response.ok) throw new Error('Kokoro no puede hablar en este momento...');

      const blob = await response.blob();
      if (fetchAbortControllerRef.current.signal.aborted) {
        setIsLoading(false);
        return;
      }
      const audioUrl = URL.createObjectURL(blob);
      setHistory(prev => [{ text: textToSpeak, audioUrl }, ...prev].slice(0, 5));
      
      if (appMode === 'english' && (textToSpeak.length > 60 || (spokenStory && textToSpeak.toLowerCase().includes(spokenStory.slice(0, 20).toLowerCase())))) {
        setStoryAudioUrl(audioUrl);
      }
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.volume = volume;
        audioRef.current.playbackRate = 1.20;
        audioRef.current.preservesPitch = false;
        (audioRef.current as any).mozPreservesPitch = false;
        (audioRef.current as any).webkitPreservesPitch = false;
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
             console.error("Playback failed", error);
             setError("Toca la pantalla de nuevo o el botón enviar para permitir el audio.");
             setTimeout(() => setError(null), 5000);
             if (hideMicForEnglishIntro) setHideMicForEnglishIntro(false);
          });
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Error desconocido');
        if (hideMicForEnglishIntro) setHideMicForEnglishIntro(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentModeIntroduced = appMode === 'chat' ? hasIntroducedChat : hasIntroducedEnglish;

  useEffect(() => {
    if (setup.isReady && hasInteracted && !hasGreetedThisSession && !isAsking && !isLoading && !isPlaying && !showSponsor && !showSponsor5Min && !showEnglishWelcomeModal) {
      const timer = setTimeout(async () => {
        setHasGreetedThisSession(true);
        if (!currentModeIntroduced) {
          if (appMode === 'chat') {
            setHasIntroducedChat(true);
            localStorage.setItem('kokoro_has_introduced_chat', 'true');
          } else {
            setHasIntroducedEnglish(true);
            localStorage.setItem('kokoro_has_introduced_english', 'true');
          }
          
          let introMessage = "Hola, soy Kokoro. Estoy aquí contigo. Si te sientes triste, molesto o con muchas cosas en la cabeza, puedes decírmelo. No tienes que guardártelo ni resolverlo solo. Puedes hablar conmigo como si fuera alguien que te escucha sin juzgar. ¿de qué quieres platicar hoy?";
          
          if (appMode === 'chat' && chatLanguage === 'en') {
            introMessage = "Hi, I am Kokoro. I am here with you. If you feel sad, upset or have a lot on your mind, you can tell me. You don't have to keep it to yourself or solve it alone. You can talk to me like someone who listens without judging. What would you like to talk about today?";
          } else if (appMode === 'english') {
            introMessage = "Hello! I am Kokoro, your English tutor. From now on, we will start your lessons. To help you learn, I use the 'story loop' method—short interactive stories. Let's begin!";
          }

          setText(introMessage);
          if (appMode === 'english') {
            setSpokenStory(introMessage);
          }
          
          if (appMode === 'chat') {
            autoResumeMicRef.current = true;
          }
          await handleSpeak(introMessage);
          
          if (appMode === 'chat') {
            setEmotionState({ name: 'alegría', level: 1 });
            setTimeout(() => setEmotionState({ name: 'tristeza', level: 1 }), 2500); // "Si te sientes triste..."
            setTimeout(() => setEmotionState({ name: 'alegría', level: 1 }), 6000); // "puedes decírmelo..."
            setTimeout(() => setEmotionState({ name: 'alegría', level: 2 }), 11000); // "¿de que quieres platicar..."
          } else {
            setEmotionState({ name: 'alegría', level: 2 });
            setTimeout(() => setEmotionState({ name: 'alegría', level: 1 }), 6000);
          }
        } else {
          // Short intro for returning sessions
          let shortIntro = appMode === 'english' ? "Let's begin our English lesson!" : (chatLanguage === 'en' ? "Hi! What would you like to talk about today?" : "¡Hola! ¿De qué quieres hablar hoy?");
          setText(shortIntro);
          if (appMode === 'english') {
            setSpokenStory(shortIntro);
          }
          if (appMode === 'chat') {
            autoResumeMicRef.current = true;
          }
          await handleSpeak(shortIntro);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [setup.isReady, hasInteracted, hasGreetedThisSession, hasIntroducedChat, hasIntroducedEnglish, isAsking, isLoading, isPlaying, showSponsor, showSponsor5Min, appMode, currentModeIntroduced, showEnglishWelcomeModal]);

  const handleAsk = async (overrideQuestion?: string) => {
    const questionToAsk = overrideQuestion || question;
    if (!questionToAsk.trim()) return;

    // Unlock audio element synchronously on user gesture
    if (audioRef.current && !audioRef.current.src) {
      audioRef.current.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      audioRef.current.play().catch(() => {});
    }

    if (!overrideQuestion) {
      // It was triggered manually (typing)
      autoResumeMicRef.current = false;
    }

    setIsAsking(true);
    setError(null);

    // Cancel any ongoing fetch
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    fetchAbortControllerRef.current = new AbortController();

    try {
      const chatResponse = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionToAsk,
          dailyContext: appMode === 'chat' ? dailyContext : '',
          longTermMemory: appMode === 'chat' ? longTermMemory : '',
          appMode,
          chatLanguage: appMode === 'chat' ? chatLanguage : undefined,
          targetEnglishEmotion: appMode === 'english' ? englishEmotionsList[englishEmotionIndex % englishEmotionsList.length] : undefined,
          englishLevel: appMode === 'english' ? englishLevel : undefined,
          selectedTopic: selectedTopic ? selectedTopic.id : null,
          isFirstTurn: history.length === 0,
          currentEmotion: appMode === 'chat' ? emotionState : undefined
        }),
        signal: fetchAbortControllerRef.current.signal
      });

      if (!chatResponse.ok) {
        let errDesc = "Failed to generate AI response";
        try {
          const errData = await chatResponse.json();
          if (errData.details) {
            errDesc = errData.details;
          } else if (errData.error) {
            errDesc = errData.error;
          }
        } catch (e) {}
        throw new Error(errDesc);
      }

      const response = await chatResponse.json();
      
      if (fetchAbortControllerRef.current.signal.aborted) {
        setIsAsking(false);
        return;
      }
      
      let answer = "Mmm, perdón, no sé qué decir...";
      try {
        let cleanText = response.text || "{}";
        if (cleanText.includes("```")) {
           cleanText = cleanText.replace(/```json\n?/g, "").replace(/```/g, "").trim();
        }
        const data = JSON.parse(cleanText);
        if (data.respuesta) {
          answer = data.respuesta;
          setEmotionState({ name: data.emocion || 'alegría', level: data.nivel || 1 });
          if (appMode === 'english') {
            const nextIdx = (englishEmotionIndex + 1) % englishEmotionsList.length;
            setEnglishEmotionIndex(nextIdx);
            localStorage.setItem('kokoro_english_emotion_index', nextIdx.toString());
          }
          
          if (data.secuenciaEmociones && Array.isArray(data.secuenciaEmociones) && data.secuenciaEmociones.length > 0) {
            setEnglishEmotionSequence(data.secuenciaEmociones);
          } else {
            setEnglishEmotionSequence([]);
          }
          if (data.nuevoContexto && data.nuevoContexto.length > 5) {
            setDailyContext(data.nuevoContexto);
          }
          if (data.actualizarLP && data.actualizarLP.length > 5) {
            setLongTermMemory(data.actualizarLP);
          }
        }
      } catch (e) {
        console.error("Failed to parse JSON response:", e);
        console.log("Raw response was:", response.text);
      }
      
      setText(answer);
      if (appMode === 'english') {
        setSpokenStory(answer);
      }
      setQuestion('');
      
      // Auto-speak the answer
      handleSpeak(answer);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message || "Ups, parece que Kokoro está distraída (Error de IA)");
        console.error("Chat API Error:", err);
      }
    } finally {
      setIsAsking(false);
    }
  };

  async function startConversationRound(currentRound: number, historySoFar: { role: 'kokoro' | 'user', text: string }[], userAnswerText?: string) {
    setIsConversationLoading(true);
    setConversationStatusMessage(currentRound === 1 ? 'Kokoro is initiating conversation...' : 'Kokoro is listening and replying...');
    setError(null);
    try {
      const response = await fetch('/api/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: selectedTopic ? selectedTopic.label : 'General Chat',
          level: englishLevel,
          history: historySoFar,
          userAnswer: userAnswerText || '',
          isFirstTurn: currentRound === 1,
          round: currentRound,
          priorStory: currentRound === 1 ? spokenStory : '',
          priorEmotion: currentRound === 1 ? emotionState.name : ''
        })
      });

      if (!response.ok) {
        throw new Error('Connection error. Could not contact Kokoro.');
      }

      const raw = await response.json();
      let cleanText = raw.text || "{}";
      if (cleanText.includes("```")) {
         cleanText = cleanText.replace(/```json\n?/g, "").replace(/```/g, "").trim();
      }
      const data = JSON.parse(cleanText);

      // Save user answer score if not first turn
      if (currentRound > 1 && data.evaluationScore !== undefined) {
        const score = data.evaluationScore;
        setConversationScores(prev => [...prev, score]);
        setConversationFeedbackNotification({
          score: score,
          feedback: data.feedback || "Good job!"
        });
        setTimeout(() => {
          setConversationFeedbackNotification(null);
        }, 4500);
      }

      const kokoroText = data.respuesta || "Let's keep practicing!";
      setEmotionState({ name: data.emocion || 'alegría', level: data.nivel || 1 });
      if (data.secuenciaEmociones) {
        setEnglishEmotionSequence(data.secuenciaEmociones);
      } else {
        setEnglishEmotionSequence([]);
      }

      const updatedHistory = [...historySoFar];
      if (userAnswerText) {
        updatedHistory.push({ role: 'user', text: userAnswerText });
      }
      updatedHistory.push({ role: 'kokoro', text: kokoroText });
      setConversationHistory(updatedHistory);

      setIsMyTurnToSpeak(false);
      handleSpeak(kokoroText);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Apologies, we had a connection error. Please try again.');
    } finally {
      setIsConversationLoading(false);
    }
  }

  async function finishConversationPractice(historySoFar: { role: 'kokoro' | 'user', text: string }[], finalAnswer: string) {
    setIsConversationLoading(true);
    setConversationStatusMessage('Evaluating final answer and compiling score...');
    try {
      const response = await fetch('/api/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: selectedTopic ? selectedTopic.label : 'General Chat',
          level: englishLevel,
          history: historySoFar,
          userAnswer: finalAnswer,
          isFirstTurn: false,
          round: 5
        })
      });

      if (!response.ok) {
        throw new Error('Connection error. Failed to analyze final answer.');
      }

      const raw = await response.json();
      let cleanText = raw.text || "{}";
      if (cleanText.includes("```")) {
         cleanText = cleanText.replace(/```json\n?/g, "").replace(/```/g, "").trim();
      }
      const data = JSON.parse(cleanText);

      let latestScores: number[] = [];
      if (data.evaluationScore !== undefined) {
        setConversationScores(prev => {
          latestScores = [...prev, data.evaluationScore];
          return latestScores;
        });
        setConversationFeedbackNotification({
          score: data.evaluationScore,
          feedback: data.feedback || "Splendid effort!"
        });
        setTimeout(() => {
          setConversationFeedbackNotification(null);
        }, 4500);
      }

      // Compute total stars synchronously so Kokoro can speak the verdict
      const sumS = latestScores.reduce((sum, s) => sum + s, 0);
      const scoreWithLast = sumS;
      
      const kokoroText = data.respuesta || "Excellent job completing your practice!";
      setEmotionState({ name: data.emocion || 'alegría', level: data.nivel || 1 });
      if (data.secuenciaEmociones) {
        setEnglishEmotionSequence(data.secuenciaEmociones);
      }

      let verdictStrArr = [
        "⭐ Needs Practice",
        "⭐⭐ Getting Better",
        "⭐⭐⭐ Good",
        "⭐⭐⭐⭐ Great",
        "⭐⭐⭐⭐⭐ Excellent!"
      ];
      let finalVerdictLabel = "Needs Practice";
      if (scoreWithLast <= 1.5) finalVerdictLabel = verdictStrArr[0];
      else if (scoreWithLast <= 2.5) finalVerdictLabel = verdictStrArr[1];
      else if (scoreWithLast <= 3.5) finalVerdictLabel = verdictStrArr[2];
      else if (scoreWithLast <= 4.5) finalVerdictLabel = verdictStrArr[3];
      else finalVerdictLabel = verdictStrArr[4];

      const fullClosingText = `${kokoroText} You got ${scoreWithLast} stars. ${finalVerdictLabel}!`;

      const updatedHistory: { role: 'user' | 'kokoro'; text: string; }[] = [...historySoFar, { role: 'user', text: finalAnswer }, { role: 'kokoro', text: fullClosingText }];
      setConversationHistory(updatedHistory);

      setIsMyTurnToSpeak(false);
      handleSpeak(fullClosingText);
      setConversationRound(6); // indicator of done
    } catch (err: any) {
      console.error(err);
      setError('Connection error evaluating final response.');
    } finally {
      setIsConversationLoading(false);
    }
  }

  // EXAM PROGRESSION METHODS
  const quitExamSession = () => {
    if (examIntervalRef.current) {
      clearInterval(examIntervalRef.current);
      examIntervalRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
      } catch (e) {}
    }
    if (fetchAbortControllerRef.current) {
      try {
        fetchAbortControllerRef.current.abort();
      } catch (e) {}
    }
    setIsListening(false);
    setIsPlaying(false);
    setIsLoading(false);
    setIsExamLoading(false);
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch (e) {}
    autoResumeMicRef.current = false;
    
    // Reset Exam States
    setIsExamActive(false);
    setIsExamFinished(false);
    setExamCountdown(null);
    setShowExamExitConfirm(false);
    setIsSpeakingExamRules(false);
    isSpeakingExamRulesRef.current = false;
    setEmotionState({ name: 'alegría', level: 1 });
  };

  function startExamCountdownTicking() {
    setIsSpeakingExamRules(false);
    isSpeakingExamRulesRef.current = false;
    
    // Apply countdown of 10, 9, 8, 7...
    let count = 10;
    setExamCountdown(count);
    
    if (examIntervalRef.current) {
      clearInterval(examIntervalRef.current);
    }
    examIntervalRef.current = setInterval(() => {
      count -= 1;
      if (count > 0) {
        setExamCountdown(count);
      } else {
        if (examIntervalRef.current) {
          clearInterval(examIntervalRef.current);
          examIntervalRef.current = null;
        }
        setExamCountdown(null);
        // Start the actual first round
        startExamRound(1, []);
      }
    }, 1000);
  }

  async function startExamSession(levelTarget: number) {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsListening(false);
    setIsPlaying(false);
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch(e) {}
    autoResumeMicRef.current = false;
    
    // Reset Exam States
    setIsExamActive(true);
    setShowExamExitConfirm(false);
    setExamTargetLevel(levelTarget);
    setExamCurrentRound(1);
    setExamHistory([]);
    setExamScores([]);
    setExamRoundWordCounts([]);
    setExamRoundValidity([]);
    setIsExamFinished(false);
    setExamCompletedSuccessfully(null);
    setExamStatusMessage('Iniciando examen oral con Kokoro...');
    setIsProgressionMenuOpen(false);
    setIsPracticeMenuOpen(false);
    setIsTopicsMenuOpen(false);
    setActivePracticePanel(null);
    setIsSpeakingExamRules(true);
    isSpeakingExamRulesRef.current = true;

    const spec = levelsSpecs.find(s => s.level === levelTarget) || levelsSpecs[1];

    // Generate a random sequence of topics from the 6 predefined ones offline with random subtopics
    const examSubtopics: Record<string, string[]> = {
      'Family': [
        'siblings and your relationship with them',
        'your parents or guardians, their occupations and personalities',
        'favorite family gatherings or holiday memories',
        'how you stay in touch with extended family members',
        'family pets and their role in your household',
        'important family traditions or customs you practice',
        'childhood memories with your family',
        'someone in your family you admire the most and why',
        'family dinners and what you usually discuss',
        'advice or values your family has passed down to you',
        'how your family supports your dreams or education',
        'family trips or weekend getaways you enjoyed',
        'similarities and differences in personality among family members',
        'how you share household chores or responsibilities',
        'your favorite relative and why you get along so well with them',
        'activities or hobbies you enjoy doing together as a family',
        'family rules you had growing up',
        'how your family celebrates birthdays or special milestones',
        'funny family stories or inside jokes you all share',
        'what family means to you personally'
      ],
      'Hobbies': [
        'your primary hobby and when you started doing it',
        'how much time you spend on your hobbies each week',
        'how your hobbies help you relax after a busy day',
        'creative hobbies like painting, writing, or playing music',
        'outdoor hobbies like hiking, cycling, or gardening',
        'indoor hobbies like reading, gaming, or crafting',
        'a hobby you would love to learn or try in the future',
        'hobbies you used to have as a child but stopped',
        'whether you prefer hobbies you do alone or with others',
        'how expensive or difficult your hobbies are',
        'showing or sharing your hobbies with friends or family',
        'learning a skill related to a hobby you love',
        'collecting items as a hobby (like books, coins, stamps, etc.)',
        'sports or physical exercises you do for fun',
        'your favorite book, movie, or video game and why it inspires you',
        'how you find new ideas or motivation for your hobbies',
        'if you could turn one of your hobbies into a full-time job',
        'useful skills you have gained through self-taught hobbies',
        'local clubs, groups, or online communities related to your hobbies',
        'how your taste in hobbies has changed over the years'
      ],
      'Travel': [
        'your absolute favorite place you have ever visited',
        'your dream travel destination and what you want to do there',
        'whether you prefer beach holidays, city trips, or nature adventures',
        'memorable food or dishes you tried while traveling',
        'important travel essentials/items you always pack in your suitcase',
        'how you plan and prepare for a trip',
        'traveling alone versus traveling with family or friends',
        'a funny or unexpected situation you experienced while traveling',
        'beautiful tourist attractions or landmarks in your own country',
        'your preferred mode of transportation during travel (plane, train, car)',
        'learning about different cultures and customs when visiting new places',
        'souvenirs or gifts you like to buy for your friends back home',
        'staying in luxury hotels versus camping or hostels',
        'what you dislike most about traveling (long lines, packing, delays)',
        'your favorite season of the year to go on a trip',
        'a short weekend getaway you took recently and what you did',
        'visiting historical sites, museums, or art galleries during a trip',
        'getting lost in a new city and how you resolved the situation',
        'the longest journey or flight you have ever taken',
        'how traveling changes a person’s mindset or perspective on life'
      ],
      'Food & Cooking': [
        'your favorite daily meal (breakfast, lunch, or dinner) and why',
        'dishes or foods you absolutely dislike and avoid',
        'traditional food from your country that you love to recommend',
        'whether you enjoy cooking at home or eating out in restaurants',
        'your specialty dish to cook if you have guests over',
        'sweet desserts versus savory or spicy snacks',
        'memorable restaurants or dining experiences you have had',
        'healthy eating habits or vegetables you try to include on your plate',
        'fast food and how often you eat it',
        'baking cakes, cookies, or bread at home',
        'your favorite drinks (coffee, tea, juices, smoothies) to start the day',
        'trying exotic, unusual, or new international foods for the first time',
        'how you choose what to buy when shopping for groceries',
        'street food and if you enjoy eating it on the go',
        'family secret recipes or dishes passed down from relatives',
        'the role of food in holiday celebrations and family get-togethers',
        'breakfast routines and what you typically eat in the morning',
        'learning how to cook from recipes, videos, or cooking shows',
        'spices, herbs, or ingredients you cannot live without in your kitchen',
        'food delivery apps versus going to a local supermarket'
      ],
      'Work': [
        'your current job or what career path you are currently pursuing',
        'your educational background or your favorite subjects in school',
        'your dream job when you were a little child versus now',
        'how you manage stress or high workloads during a typical workday',
        'your favorite tasks or parts of your job/studies',
        'the skills or qualifications needed to succeed in your field',
        'whether you prefer working as part of a team or independently',
        'working from home (remote work) versus commuting to an office',
        'the importance of a good work-life balance for productivity and health',
        'a project or achievement you are exceptionally proud of',
        'your professional goals or where you see yourself in five years',
        'ideal work environments (quiet spaces, creative designs, lively offices)',
        'intercultural communication or working with people from different countries',
        'what a typical day at your office or school looks like',
        'languages or tools you use daily in your work/studies',
        'how you handle difficult coworkers, clients, or classmates',
        'professional workshops, courses, or certifications you want to take',
        'qualities of a great manager, supervisor, or mentor',
        'job interviews and your experiences or tips for them',
        'how technologies or AI are changing your type of work'
      ],
      'Daily Routine': [
        'what time you usually wake up and your morning habits',
        'your favorite part of the day (morning, afternoon, night) and why',
        'how you wind down and prepare for sleep at night',
        'exercise routines, gym sessions, or morning stretching habits',
        'how you organize your tasks (planners, digital calendars, to-do lists)',
        'your typical weekend routine compared to your weekday routine',
        'how you commute to work or school and what you do on the way',
        'habits you want to build or improve in your daily schedule',
        'how much sleep you get on average and why sleep is important to you',
        'your favorite weekday lunch routine',
        'how you handle sudden changes or disruptions to your planned schedule',
        'making time for socializing and relaxation in your daily routine',
        'chores like cleaning, washing dishes, and laundry in your schedule',
        'bad habits you are trying to break (like scrolling on your phone)',
        'having a productive study or focus session during the day',
        'special treats or activities you do to reward yourself during the week',
        'how you spend your Sunday afternoons',
        'your typical evening routine and dinner preparation',
        'if you consider yourself a morning person (early bird) or a night owl',
        'how your daily routine changes during seasons or vacations'
      ]
    };

    const shuffledSubtopics: Record<string, string[]> = {};
    Object.keys(examSubtopics).forEach(key => {
      shuffledSubtopics[key] = [...examSubtopics[key]].sort(() => Math.random() - 0.5);
    });

    const baseTopics = ['Family', 'Hobbies', 'Travel', 'Food & Cooking', 'Work', 'Daily Routine'];
    const totalCycles = spec.cycles;
    let topicsQueue: string[] = [];
    while (topicsQueue.length < totalCycles) {
      const shuffled = [...baseTopics].sort(() => Math.random() - 0.5);
      // to avoid duplicates on boundaries
      if (topicsQueue.length > 0 && shuffled[0] === topicsQueue[topicsQueue.length - 1]) {
        if (shuffled.length > 1) {
          const tmp = shuffled[0];
          shuffled[0] = shuffled[1];
          shuffled[1] = tmp;
        }
      }
      topicsQueue = [...topicsQueue, ...shuffled];
    }
    const finalQueue = topicsQueue.slice(0, totalCycles).map(mainTopic => {
      const sub = shuffledSubtopics[mainTopic]?.pop() || 'general discussion';
      return `${mainTopic} (focusing on: ${sub})`;
    });

    setExamTopicsList(finalQueue);
    examTopicsListRef.current = finalQueue;
    
    // Rules spoken out loud by Kokoro
    const rulesSpeech = `Let's start your Level ${levelTarget} certification exam. You have to complete ${spec.cycles} conversation cycles with me. Each answer must have at least ${spec.minWords} words, and be completely coherent. Prepare yourself, we will begin in ten seconds!`;
    handleSpeak(rulesSpeech);
  }

  async function startExamRound(currentRound: number, historySoFar: { role: 'kokoro' | 'user', text: string }[], userAnswerText?: string) {
    if (!isExamActiveRef.current) return;
    setIsExamLoading(true);
    setExamStatusMessage(currentRound === 1 ? 'Kokoro is beginning the examination...' : 'Evaluating response and loading next cycle...');
    setError(null);
    try {
      const response = await fetch('/api/exam', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: examTopicsListRef.current[currentRound - 1] || 'General Topics',
          level: examTargetLevel,
          history: historySoFar,
          userAnswer: userAnswerText || '',
          isFirstTurn: currentRound === 1,
          round: currentRound
        })
      });

      if (!isExamActiveRef.current) return;

      if (!response.ok) {
        throw new Error('Connection error with Kokoro. Please verify network.');
      }

      const raw = await response.json();
      if (!isExamActiveRef.current) return;

      let cleanText = raw.text || "{}";
      if (cleanText.includes("```")) {
         cleanText = cleanText.replace(/```json\n?/g, "").replace(/```/g, "").trim();
      }
      const data = JSON.parse(cleanText);

      if (currentRound > 1) {
        // En el modo fluido, no agregamos calificaciones aquí. Solo guardamos la lógica en el historial y esperamos al final.
        // Opcional: contar las palabras rápido en el frontend solo para UI visual si quisieras, pero mejor lo vemos al final
      }

      const kokoroText = data.respuesta || "Ready for the next question?";
      setEmotionState({ name: data.emocion || 'alegría', level: data.nivel || 1 });
      if (data.secuenciaEmociones) {
        setEnglishEmotionSequence(data.secuenciaEmociones);
      }

      const updatedHistory = [...historySoFar];
      if (userAnswerText) {
        updatedHistory.push({ role: 'user', text: userAnswerText });
      }
      updatedHistory.push({ role: 'kokoro', text: kokoroText });
      setExamHistory(updatedHistory);

      handleSpeak(kokoroText);
    } catch (err: any) {
      if (!isExamActiveRef.current) return;
      console.error(err);
      setError(err.message || 'Error executing exam round. Try again.');
    } finally {
      if (isExamActiveRef.current) {
        setIsExamLoading(false);
      }
    }
  }

  async function finishExamSession(historySoFar: { role: 'kokoro' | 'user', text: string }[], finalAnswer: string, totalRounds: number) {
    if (!isExamActiveRef.current) return;
    setIsExamLoading(true);
    setExamStatusMessage('Evaluating all your responses to compile the certification grade...');
    try {
      const response = await fetch('/api/exam', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: selectedTopic ? selectedTopic.label : 'General Topics',
          level: examTargetLevel,
          history: historySoFar,
          userAnswer: finalAnswer,
          isFirstTurn: false,
          round: totalRounds,
          isFinalAnalyze: true
        })
      });

      if (!isExamActiveRef.current) return;

      if (!response.ok) {
        throw new Error('Connection error. Failed to complete assessment.');
      }

      const raw = await response.json();
      if (!isExamActiveRef.current) return;

      let cleanText = raw.text || "{}";
      if (cleanText.includes("```")) {
         cleanText = cleanText.replace(/```json\n?/g, "").replace(/```/g, "").trim();
      }
      const data = JSON.parse(cleanText);
      const evals = data.evaluations || [];

      // Bulk evaluate the rounds
      let newScores: number[] = [];
      let newValidities: boolean[] = [];
      let newWordCounts: number[] = [];

      evals.forEach((ev: any) => {
        newScores.push(ev.evaluationScore !== undefined ? ev.evaluationScore : 0);
        newValidities.push((ev.passedWordCheck !== false) && (ev.isCoherent !== false));
        newWordCounts.push(ev.wordsCount || 0);
      });

      setExamScores(newScores);
      setExamRoundWordCounts(newWordCounts);
      setExamRoundValidity(newValidities);

      const averageScore = newScores.length > 0 ? newScores.reduce((sum, s) => sum + s, 0) / newScores.length : 0;
      const scoreOutOf100 = Math.round((averageScore / 5.0) * 100);
      const allCyclesValid = newValidities.length > 0 && newValidities.every(v => v === true);
      const passed = allCyclesValid && scoreOutOf100 >= 80;

      setExamCompletedSuccessfully(passed);
      setIsExamFinished(true);

      // Dynamically improve speaking and listening metrics based on exam performance
      const nextS = Math.max(speakingGrade, Math.min(speakingGrade + Math.round((scoreOutOf100 - speakingGrade) * 0.15), 100));
      const nextL = Math.max(listeningGrade, Math.min(listeningGrade + Math.round((scoreOutOf100 - listeningGrade) * 0.15), 100));
      setSpeakingGrade(nextS);
      setListeningGrade(nextL);
      localStorage.setItem('kokoro_stat_speaking', nextS.toString());
      localStorage.setItem('kokoro_stat_listening', nextL.toString());

      let closingSpeech = "";
      if (passed) {
        closingSpeech = `Congratulations! You scored ${scoreOutOf100} out of 100 points, and met all language constraints. You have successfully unlocked and certified level ${examTargetLevel}!`;
        const updatedunlocked = Array.from(new Set([...unlockedLevels, examTargetLevel!]));
        setUnlockedLevels(updatedunlocked);
        localStorage.setItem('kokoro_unlocked_levels', JSON.stringify(updatedunlocked));
        syncProgressToCloud(updatedunlocked, masteryTokens, englishLevel, { speaking: nextS, listening: nextL });
        setUnlockedSuccessAnim({ level: examTargetLevel!, method: 'exam' });
      } else {
        let reasons = [];
        if (!allCyclesValid) {
          reasons.push("some answers lacked coherence, had grammar issues or were too short");
        }
        if (scoreOutOf100 < 80) {
          reasons.push(`your score was ${scoreOutOf100} out of 100 points, which is below the minimum of 80 points required`);
        }
        closingSpeech = `You didn't pass the Level ${examTargetLevel} exam because ${reasons.join(' and ')}. Keep practicing!`;
      }

      const updatedHistory: { role: 'user' | 'kokoro'; text: string; }[] = [...historySoFar, { role: 'user', text: finalAnswer }, { role: 'kokoro', text: closingSpeech }];
      setExamHistory(updatedHistory);
      setIsMyTurnToSpeakExam(false);
      handleSpeak(closingSpeech);
    } catch (err: any) {
      if (!isExamActiveRef.current) return;
      console.error(err);
      setError('Connection error finalizing assessment.');
    } finally {
      if (isExamActiveRef.current) {
        setIsExamLoading(false);
      }
    }
  }

  function handleExamUserAnswer(transcript: string) {
    setIsListening(false);
    setExamMicCountdown(null);
    autoResumeMicRef.current = false;
    setIsMyTurnToSpeakExam(false);

    const finalTranscript = transcript.trim() || "... (Silences/No response)";

    const spec = levelsSpecs.find(s => s.level === examTargetLevel) || levelsSpecs[1];
    const totalRounds = spec.cycles;

    if (examCurrentRound < totalRounds) {
      startExamRound(examCurrentRound + 1, examHistory, finalTranscript);
      setExamCurrentRound(prev => prev + 1);
    } else if (examCurrentRound === totalRounds) {
      finishExamSession(examHistory, finalTranscript, totalRounds);
    }
  }

  function handleConversationUserAnswer(transcript: string) {
    if (!transcript.trim()) return;
    setIsListening(false);
    autoResumeMicRef.current = false;

    // Proceed to process this response
    if (conversationRound < 5) {
      startConversationRound(conversationRound + 1, conversationHistory, transcript);
      setConversationRound(prev => prev + 1);
    } else if (conversationRound === 5) {
      finishConversationPractice(conversationHistory, transcript);
    }
  }

  useEffect(() => {
    handleAskRef.current = handleAsk;
  }, [handleAsk]);

  const startListening = () => {
    // Interrupt any ongoing AI response generation
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    setIsAsking(false);

    // Stop currently playing audio immediately
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        // Do not remove attribute or load, just pause so the state resets cleanly,
        // unless we need to clear it. Removing src could break the "unlock" trick below.
        audioRef.current.currentTime = 0;
      } catch (e) {}
    }
    setIsPlaying(false);

    if (audioRef.current && !audioRef.current.src) {
      // Unlock audio element with a tiny silent data URI
      audioRef.current.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      audioRef.current.play().catch(() => {});
    }

    if (!isListening) {
      autoResumeMicRef.current = true; // They manually started it, we should keep it on until manual stop or success
      setIsListening(true);
      setError(null);
      try {
        recognitionRef.current?.start();
      } catch (err: any) {
        if (err.name === 'InvalidStateError') {
          console.warn('Speech recognition is already listening.');
        } else {
          console.error("Failed to start recognition", err);
          setIsListening(false);
        }
      }
    }
  };

  const stopListening = () => {
    autoResumeMicRef.current = false;
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleRepeatListening = () => {
    setIsListeningPlaybackFinished(false);
    if (audioRef.current) {
      let validSrc = audioRef.current.src;
      const isWebpHref = !validSrc || validSrc === "" || (validSrc.startsWith('http') && !validSrc.includes('blob:') && !validSrc.endsWith('.wav') && !validSrc.endsWith('.mp3'));
      if (isWebpHref) {
        const foundItem = history.find(h => h.text && h.text.trim() === spokenStory.trim());
        if (foundItem && foundItem.audioUrl) {
          validSrc = foundItem.audioUrl;
        } else if (storyAudioUrl) {
          validSrc = storyAudioUrl;
        } else if (history.length > 0 && history[0].audioUrl) {
          validSrc = history[0].audioUrl;
        }
        if (validSrc) {
          audioRef.current.src = validSrc;
        }
      }
    }

    if (audioRef.current && audioRef.current.src && !(audioRef.current.src.startsWith('http') && !audioRef.current.src.includes('blob:') && !audioRef.current.src.endsWith('.wav') && !audioRef.current.src.endsWith('.mp3'))) {
      setIsPlaying(false);
      const originalVolume = audioRef.current.volume;
      audioRef.current.volume = 0;
      const unlockPromise = audioRef.current.play();
      if (unlockPromise !== undefined) {
        unlockPromise.then(() => {
          audioRef.current!.pause();
          audioRef.current!.currentTime = 0;
          audioRef.current!.volume = originalVolume;
          
          setListeningCountdown(3);
          setTimeout(() => {
            setListeningCountdown(2);
            setTimeout(() => {
              setListeningCountdown(1);
              setTimeout(() => {
                setListeningCountdown(null);
                if (audioRef.current && audioRef.current.src) {
                  audioRef.current.preservesPitch = false;
                  (audioRef.current as any).mozPreservesPitch = false;
                  (audioRef.current as any).webkitPreservesPitch = false;
                  audioRef.current.playbackRate = 1.20;
                  const playPromise = audioRef.current.play();
                  if (playPromise !== undefined) {
                    playPromise.then(() => {
                      setIsPlaying(true);
                      setIsListeningPracticeActive(true);
                      setIsListeningPlaybackFinished(false);
                    }).catch((e) => {
                      console.error("Listening replay failed:", e);
                      setIsListeningPracticeActive(false);
                      setIsPracticeMenuOpen(true);
                    });
                  } else {
                    setIsPlaying(true);
                    setIsListeningPracticeActive(true);
                    setIsListeningPlaybackFinished(false);
                  }
                }
              }, 1000);
            }, 1000);
          }, 1000);
        }).catch((e) => {
          console.error("Listening replay unlock failed:", e);
          setIsListeningPracticeActive(false);
          setIsPracticeMenuOpen(true);
        });
      }
    }
  };

  const handleCloseListening = () => {
    setIsListeningPracticeActive(false);
    setIsListeningPlaybackFinished(false);
    setIsPracticeMenuOpen(true);
  };

  const handleAbortListening = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsListeningPracticeActive(false);
    setIsListeningPlaybackFinished(false);
    setIsPracticeMenuOpen(true);
  };

  const handleReset = async () => {
    try {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
      
      // Clear ALL kokoro_ prefixes in LocalStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('kokoro_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      await fetch('/api/setup/reset', { method: 'POST' });
      
      stopListening();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
      }
      
      setText('');
      setQuestion('');
      setIsLoading(false);
      setIsAsking(false);
      setIsPlaying(false);
      setError(null);
      setHistory([]);
      setEmotionState({ name: 'alegría', level: 1 });
      
      // Reset all progress state
      setUnlockedLevels([1]);
      setUnlockedBackgrounds([0]);
      setMasteryTokens(0);
      setStarsCount(0);
      setEnglishLevel(1);
      setListeningGrade(0);
      setVocabularyGrade(0);
      setSpeakingGrade(0);
      setReadingGrade(0);
      setWritingGrade(0);
      setStudyMinutes(0);
      
      // Reset timers and practices
      setActivePracticePanel(null);
      setSessionSeconds(0);
      setListeningCompletedCount(0);
      setSpeakingCompletedCount(0);
      setReadingStarEarned(false);
      setVocabularyStarEarned(false);
      setListeningStarEarned(false);
      setSpeakingStarEarned(false);
      setListeningXpAwarded(false);
      setSpeakingXpAwarded(false);
      setSessionXpAwarded(false);
      
      setEnglishEmotionSequence([]);
      setDailyContext('Ninguno aún. Estamos empezando a conocernos.');
      setLongTermMemory('Aún no sé mucho sobre ti. ¿Cómo te llamas?');
      setShowSponsor(false);
      setHasShownSponsor(false);
      setShowSponsor5Min(false);
      setHasShownSponsor5Min(false);
      setHasIntroducedChat(false);
      setHasIntroducedEnglish(false);
      
      setHasInteracted(false);
      setHasStartedStory(false);
      setHasChosenTopic(false);
      setSelectedTopic(null);
      setHasEverSelectedTopic(false);
      
      // Sync complete wipe to Cloud if logged in
      if (auth.currentUser) {
        try {
          await syncProgressToCloud([1], 0, 1, { listening: 0, vocabulary: 0, speaking: 0, reading: 0, writing: 0 }, 0, 0, [0]);
        } catch (e) {
          console.error("Failed to wipe cloud data", e);
        }
      }

      // Always shut down any active Google sign-in session and state
      try {
        await signOut(auth);
      } catch (e) {
        console.error("Failed to sign out from Google on factory reset", e);
      }

      setIsLoggedIn(false);
      setUserName('');
      setUserEmail('');
      setUserAvatar('');
      setIsPremiumUser(false);
      
    } catch (err) {
      alert("Error al reiniciar");
    }
  };

  if (!setup.isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pink-50 p-6 relative" id="initial-setup-screen">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 border border-pink-100 text-center"
        >
          <div className="mb-10 relative flex justify-center items-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute inset-x-0 mx-auto w-[210px] h-[210px] border-[6px] border-indigo-200 border-t-indigo-500 rounded-full"
            />
            {/* Logo Image */}
            <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center relative overflow-hidden shadow-xl border-[6px] border-white z-10">
              <img 
                src="/logo.png" 
                alt="Story Loop Logo"
                className="w-full h-full object-cover scale-[1.02]" 
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.classList.add('fallback-heart');
                }}
              />
              <Heart className="w-16 h-16 text-pink-500 animate-pulse hidden absolute logo-fallback" />
            </div>
            
            <style>{`
              .fallback-heart .logo-fallback {
                display: block;
              }
            `}</style>
          </div>

          <>
            <h2 className="text-3xl font-bold mb-2">Preparando todo</h2>
            <p className="text-gray-500 mb-8 font-sans">
              Espera un momento, Kokoro se está alistando...
            </p>

            <div className="relative h-4 bg-pink-50 rounded-full overflow-hidden mb-4 border border-pink-100">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${setup.progress}%` }}
                className="absolute top-0 left-0 h-full bg-pink-500"
              />
            </div>
            
            <div className="flex justify-between text-xs font-mono text-gray-400 uppercase tracking-widest">
              <span>{setup.status === 'error' ? 'ERROR' : 'CARGANDO'}</span>
              <span>{setup.progress}%</span>
            </div>

            {setup.error && (
              <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-3">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p className="text-left">{setup.error}</p>
              </div>
            )}

            {setup.status === 'error' && (
              <button
                onClick={() => fetch('/api/setup/start', { method: 'POST' })}
                className="mt-6 w-full py-4 bg-sensei-wood text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-sensei-wood/90 transition-colors cursor-pointer"
                id="retry-download-btn"
              >
                <RefreshCw className="w-5 h-5" />
                Reintentar Descarga
              </button>
            )}
          </>
        </motion.div>
      </div>
    );
  }

  const handleHomeClick = async () => {
    setIsLoading(true);

    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    
    // Attempt stopping recognition safely
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch(e) {}
    autoResumeMicRef.current = false;
    setIsListening(false);
    
    setIsAsking(false);
    setIsPlaying(false);
    setIsTopicsMenuOpen(true);
    setIsPracticeMenuOpen(false);
    setError(null);
    setText('');
    setQuestion('');
    setHistory([]);
    setEmotionState({ name: 'alegría', level: 1 });
    
    setHasInteracted(false);
    setSelectedAppMode(null);
    setIsLive2DEngineLoaded(false);
    setSecondaryLoadingProgress(0);
    setMicPermissionStatus('checking');
    setHasGreetedThisSession(false);
    setHasChosenTopic(false);
    setSelectedTopic(null);
    setHasEverSelectedTopic(false);
    
    try {
      await fetch('/api/setup/reset', { method: 'POST' });
    } catch (e) {}

    setTimeout(() => {
      setIsLoading(false);
    }, 600);
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-slate-950 flex items-center justify-center">
      {/* Ambient background glow for desktop view */}
      <div className="absolute inset-0 bg-gradient-to-tr from-[#121026] via-[#090d16] to-[#1d0b1a] hidden md:block" />
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-pink-500/5 rounded-full blur-[100px] hidden md:block animate-pulse" style={{ animationDuration: '8000ms' }} />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[100px] hidden md:block animate-pulse" style={{ animationDuration: '10000ms' }} />

      <div 
        className="relative w-full h-full md:w-[412px] md:h-[90vh] md:max-h-[850px] md:min-h-[640px] md:rounded-[40px] md:shadow-[0_20px_50px_rgba(0,0,0,0.5)] md:border-[8px] md:border-[#1e293b] overflow-hidden bg-slate-50"
        style={{ transform: 'translate3d(0, 0, 0)' }}
      >
        {/* Absolute Landing/Setup/Permissions Overlay */}
      {!hasInteracted && (
        <div className="fixed inset-0 bg-pink-50/95 backdrop-blur-md z-[260] flex items-center justify-center p-6 pointer-events-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 border border-pink-100 text-center animate-fade-in"
          >
            {/* App Brand Header */}
            <div className="mb-6 -mt-2">
              <h1 className="text-4xl font-black tracking-tight bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-500 bg-clip-text text-transparent drop-shadow-sm select-none font-sans">
                Kokoro Sensei
              </h1>
              <div className="text-[10px] font-black uppercase tracking-widest text-pink-400 mt-1">Conecta. Conversa. Descubre.</div>
            </div>

            <div className="mb-6 relative flex justify-center items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className={`absolute inset-x-0 mx-auto w-[210px] h-[210px] border-[6px] rounded-full ${selectedAppMode === 'english' ? 'border-indigo-100 border-t-indigo-500' : 'border-pink-100 border-t-pink-500'}`}
              />
              {/* Logo Image */}
              <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center relative overflow-hidden shadow-xl border-[6px] border-white z-10">
                <img 
                  src="/logo3.png" 
                  alt="Story Loop Logo 1"
                  className={`w-full h-full object-cover scale-[1.02] absolute inset-0 transition-opacity duration-500 ease-in-out ${currentLogoIndex === 0 ? 'opacity-100' : 'opacity-0'}`} 
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement?.classList.add('fallback-heart');
                  }}
                />
                <img 
                  src="/logo.png" 
                  alt="Story Loop Logo 3"
                  className={`w-full h-full object-cover scale-[1.02] absolute inset-0 transition-opacity duration-500 ease-in-out ${currentLogoIndex === 1 ? 'opacity-100' : 'opacity-0'}`} 
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <img 
                  src="/logo4.png" 
                  alt="Story Loop Logo 4"
                  className={`w-full h-full object-cover scale-[1.02] absolute inset-0 transition-opacity duration-500 ease-in-out ${currentLogoIndex === 2 ? 'opacity-100' : 'opacity-0'}`} 
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <Heart className="w-16 h-16 text-pink-500 animate-pulse hidden absolute logo-fallback" />
              </div>
              
              <style>{`
                .fallback-heart .logo-fallback {
                  display: block;
                }
              `}</style>
            </div>

            {selectedAppMode === null ? (
              // Mode selection Screen
              <>
                <h2 className="text-3xl font-black mb-2 text-slate-850 tracking-tight">¡Bienvenido! 🌸</h2>
                <p className="text-gray-500 mb-8 font-sans text-sm px-2">
                  Kokoro está lista. Elige cómo quieres interactuar hoy:
                </p>
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex flex-col w-full relative group">
                    <button
                      onClick={() => handleSelectMode('chat')}
                      className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-2xl font-bold hover:transform hover:scale-[1.01] active:scale-[0.99] transition-all shadow-lg shadow-pink-200 flex items-center justify-center gap-2 cursor-pointer border-none text-center"
                      id="solo-platicar-mode-btn"
                    >
                      <MessageCircleQuestion className="w-5 h-5" />
                      Solo platicar (Chat)
                    </button>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <span className="text-xs text-slate-400 font-medium">Voz de Kokoro:</span>
                      <div className="flex bg-slate-100 p-1 rounded-full border border-slate-200 shadow-inner">
                        <button 
                          className={`text-[10px] font-bold px-3 py-1 rounded-full transition-all ${chatLanguage === 'es' ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                          onClick={(e) => { e.stopPropagation(); setChatLanguage('es'); }}
                        >
                          Español
                        </button>
                        <button 
                          className={`text-[10px] font-bold px-3 py-1 rounded-full transition-all ${chatLanguage === 'en' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                          onClick={(e) => { e.stopPropagation(); setChatLanguage('en'); }}
                        >
                          Inglés
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSelectMode('english')}
                    className="w-full py-4 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-2xl font-bold hover:transform hover:scale-[1.01] active:scale-[0.99] transition-all shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 cursor-pointer border-none text-center"
                    id="aprender-ingles-mode-btn"
                  >
                    <BookOpen className="w-5 h-5" />
                    Aprender inglés
                  </button>
                </div>
              </>
            ) : (
              // Secondary loader
              <>
                <h2 className="text-2xl font-black mb-2 text-slate-850 tracking-tight">Cargando experiencia...</h2>
                <p className="text-gray-550 mb-6 font-sans text-xs px-2 leading-relaxed">
                  Configurando permisos de micrófono e inicializando el modelo virtual de Kokoro...
                </p>

                {/* Checklist status */}
                <div className="text-left bg-slate-50 border border-slate-100 rounded-2xl p-4 mb-6 text-xs flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    {micPermissionStatus === 'granted' ? (
                      <div className="bg-emerald-100 text-emerald-600 p-1 rounded-full"><CheckCircle2 className="w-4 h-4" /></div>
                    ) : micPermissionStatus === 'denied' ? (
                      <div className="bg-rose-100 text-rose-600 p-1 rounded-full"><X className="w-4 h-4" /></div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    <div>
                      <p className="font-bold text-slate-700">Permiso de Micrófono</p>
                      <p className="text-[10px] text-gray-500">
                        {micPermissionStatus === 'granted' ? 'Acceso concedido exitosamente.' : 
                         micPermissionStatus === 'denied' ? '¡Permiso denegado por el navegador!' : 
                         'Comprobando/Solicitando permiso...'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {isLive2DEngineLoaded ? (
                      <div className="bg-emerald-100 text-emerald-600 p-1 rounded-full"><CheckCircle2 className="w-4 h-4" /></div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    <div>
                      <p className="font-bold text-slate-700">Modelo Virtual de Kokoro</p>
                      <p className="text-[10px] text-gray-500">
                        {isLive2DEngineLoaded ? 'Kokoro se ha cargado de forma segura.' : 'Cargando texturas y apariencia virtual...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative h-4 bg-pink-50 rounded-full overflow-hidden mb-4 border border-pink-100">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${secondaryLoadingProgress}%` }}
                    className={`absolute top-0 left-0 h-full ${selectedAppMode === 'english' ? 'bg-indigo-500' : 'bg-pink-500'}`}
                  />
                </div>
                
                <div className="flex justify-between text-xs font-mono text-gray-400 uppercase tracking-widest mb-4">
                  <span>{secondaryLoadingProgress === 100 ? '¡LISTO!' : 'CONFIGURANDO'}</span>
                  <span>{secondaryLoadingProgress}%</span>
                </div>

                {micPermissionStatus === 'denied' && (
                  <div className="mt-4 p-4 bg-rose-50 text-rose-700 rounded-2xl text-xs text-left leading-relaxed">
                    <p className="font-bold mb-1 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Permitir Micrófono</p>
                    El micrófono es esencial para poder conversar con Kokoro. Por favor, haz clic en el icono del candado en la barra de direcciones del navegador y permite el uso de micrófono. Luego, haz clic en reintentar.
                    
                    <button
                      onClick={() => checkAndRequestMicrophone()}
                      disabled={micRetryCountdown > 0}
                      className={`mt-3 w-full py-2.5 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 border-none text-center ${micRetryCountdown > 0 ? 'bg-rose-300 text-rose-100 cursor-not-allowed' : 'bg-rose-600 hover:bg-rose-700 text-white cursor-pointer'}`}
                    >
                      <RefreshCw className={`w-4 h-4 ${micRetryCountdown > 0 ? 'animate-spin' : ''}`} />
                      {micRetryCountdown > 0 ? `Esperando ${micRetryCountdown}s...` : 'Reintentar Permiso'}
                    </button>
                  </div>
                )}

                {/* Cancel button */}
                <button
                  onClick={() => {
                    setSelectedAppMode(null);
                    setMicPermissionStatus('checking');
                    setIsLive2DEngineLoaded(false);
                  }}
                  className="mt-3 text-slate-400 hover:text-slate-600 text-xs font-bold uppercase tracking-wider cursor-pointer border-none bg-transparent"
                >
                  Regresar
                </button>
              </>
            )}
          </motion.div>
        </div>
      )}
      {/* English Welcome & Plan Options Modal */}
      <AnimatePresence>
        {(appMode === 'english' || appMode === 'chat') && showEnglishWelcomeModal && (
          <motion.div
            key="english-welcome-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/85 backdrop-blur-md z-[250] flex items-center justify-center p-4 pointer-events-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              className="w-full max-w-2xl bg-gradient-to-br from-slate-900 to-indigo-950/90 border-2 border-indigo-500/30 rounded-3xl p-5 md:p-6 text-center text-white shadow-2xl relative max-h-[94vh] flex flex-col justify-between overflow-y-auto"
            >
              <button
                onClick={() => setShowEnglishWelcomeModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700 p-2 rounded-full transition-colors cursor-pointer z-10"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div>
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-400/20 flex items-center justify-center mx-auto shadow-lg mb-3">
                  <Sparkles className="w-6 h-6 animate-pulse text-indigo-400" />
                </div>

                <h2 className="text-lg md:text-xl font-black text-white tracking-tight mb-2">
                  ¡Bienvenido a <span className="bg-gradient-to-r from-pink-400 to-indigo-400 bg-clip-text text-transparent">Story Loop</span>! 🌸
                </h2>

                <p className="text-xs text-slate-300 leading-relaxed font-sans max-w-xl mx-auto mb-4">
                  {isPremiumUser ? (
                    <>
                      Aprende inglés junto a Kokoro con historias interactivas. ¡Ya tienes activada tu versión <span className="text-indigo-400 font-bold">Premium</span>! Disfruta de la mejor experiencia sin límites.🌟
                    </>
                  ) : (
                    <>
                      Aprende inglés junto a Kokoro con historias interactivas. Estás en la <span className="text-emerald-400 font-bold">versión gratis</span>, pero puedes mejorar tu experiencia o saber más sobre cada plan a continuación.
                    </>
                  )}
                </p>

                <div className="border-t border-indigo-500/10 pt-4">
                  <h3 className="text-[10px] font-black tracking-widest text-indigo-300 uppercase mb-3 select-none">PLANES DE APRENDIZAJE</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                    {/* Gratis */}
                    <button
                      onClick={() => setSelectedWelcomePlan('gratis')}
                      className={`text-left p-3.5 rounded-2xl flex flex-col justify-between transition-all duration-300 pointer-events-auto border-2 relative overflow-hidden ${
                        selectedWelcomePlan === 'gratis'
                          ? 'bg-emerald-950/40 border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.4)] scale-[1.02]'
                          : isPremiumUser
                            ? 'bg-slate-900/50 hover:bg-slate-900/80 border-slate-700/40 hover:border-slate-500/30'
                            : 'bg-emerald-950/15 border-emerald-500/30 hover:border-emerald-500/60 shadow-[0_0_10px_rgba(16,185,129,0.15)] ring-1 ring-emerald-500/10'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1 mb-2">
                          <div className="text-[9px] font-black bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/30 w-fit uppercase tracking-widest leading-none">Gratis</div>
                          {!isPremiumUser && <span className="text-[7.5px] font-black bg-emerald-400 text-slate-950 px-1.5 py-0.5 rounded-full uppercase tracking-wider leading-none">Plan Actual</span>}
                        </div>
                        <p className="text-[10px] text-slate-350 font-normal leading-normal">
                          2 historias al día y 3 minutos de espera entre historias.
                        </p>
                      </div>
                      <div className="mt-4 flex items-center justify-between w-full">
                        <span className="text-xs font-black text-emerald-400">0 MXN <span className="text-[9px] text-slate-500 font-normal">/ mes</span></span>
                        <span className="text-[9px] font-bold text-slate-400 hover:text-white flex items-center gap-0.5">
                          Saber más <span className="text-emerald-400 text-[10px]">→</span>
                        </span>
                      </div>
                    </button>
                    
                    {/* Premium */}
                    <button
                      onClick={() => setSelectedWelcomePlan('premium')}
                      className={`text-left p-3.5 rounded-2xl flex flex-col justify-between transition-all duration-300 pointer-events-auto border-2 ${
                        selectedWelcomePlan === 'premium'
                          ? 'bg-indigo-950/40 border-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.3)] scale-[1.02]'
                          : isPremiumUser
                            ? 'bg-indigo-950/15 border-indigo-500/30 hover:border-indigo-500/60 shadow-[0_0_10px_rgba(99,102,241,0.15)] ring-1 ring-indigo-500/10'
                            : 'bg-slate-900/50 hover:bg-slate-900/80 border-slate-700/40 hover:border-slate-500/30'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1 mb-2">
                          <div className="text-[9px] font-black bg-indigo-500/20 text-indigo-300 px-2.5 py-0.5 rounded-full border border-indigo-500/30 w-fit uppercase tracking-widest leading-none">Premium</div>
                          {isPremiumUser && <span className="text-[7.5px] font-black bg-indigo-400 text-slate-950 px-1.5 py-0.5 rounded-full uppercase tracking-wider leading-none">Plan Actual</span>}
                        </div>
                        <p className="text-[10px] text-slate-350 font-normal leading-normal">
                          Historias ilimitadas, 1 min. de espera, más XP, sin anuncios al hablar con Kokoro.
                        </p>
                      </div>
                      <div className="mt-4 flex items-center justify-between w-full">
                        <span className="text-xs font-black text-indigo-400">{isPremiumUser ? (subscriptionStatus === 'canceled_at_period_end' ? "Cancelación Pendiente ⏳" : "Activo ✨") : "99 MXN"} <span className="text-[9px] text-slate-500 font-normal">{!isPremiumUser && "/ mes"}</span></span>
                        <span className="text-[9px] font-bold text-indigo-400 hover:text-white flex items-center gap-0.5">
                          Saber más <span className="text-indigo-400 text-[10px]">→</span>
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-2">
                <button
                  onClick={() => {
                    setShowEnglishWelcomeModal(false);
                    setHasInteracted(true);
                  }}
                  className="w-full py-3 bg-gradient-to-r from-indigo-500 to-indigo-650 hover:from-indigo-600 hover:to-indigo-750 text-white font-black rounded-xl text-xs sm:text-sm tracking-widest transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer shadow-lg uppercase"
                >
                  ¡Comenzar a Aprender!
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grand Fullscreen Plan Detail Takeover Overlay */}
      <AnimatePresence>
        {(appMode === 'english' || appMode === 'chat') && showEnglishWelcomeModal && selectedWelcomePlan && (
          <motion.div
            key="plan-details-takeover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/95 backdrop-blur-xl z-[260] flex items-center justify-center p-4 overflow-y-auto pointer-events-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 180 }}
              className="w-full max-w-lg bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-indigo-500/20 rounded-3xl p-6 md:p-8 text-center text-white shadow-2xl relative"
            >
              {/* Close Button ('botoncillo de cerrar') */}
              <button
                onClick={() => setSelectedWelcomePlan(null)}
                className="absolute top-4 right-4 bg-slate-800/80 hover:bg-slate-700 hover:scale-110 p-2 rounded-full cursor-pointer transition-all border border-slate-700/50 shadow-md flex items-center justify-center"
                title="Cerrar detalles"
              >
                <X className="w-5 h-5 text-slate-300 hover:text-white" />
              </button>

              {/* Icon/Emoji */}
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-lg border border-slate-700 bg-slate-900/80">
                {selectedWelcomePlan === 'gratis' ? '🆓' : '💎'}
              </div>

              {/* Heading */}
              <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight uppercase mb-1">
                {selectedWelcomePlan === 'gratis' && "FREE EXPERIENCE"}
                {selectedWelcomePlan === 'premium' && "PREMIUM EXPERIENCE"}
              </h3>
              
              <div className="inline-block px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold mb-6 tracking-wide bg-slate-800 text-slate-300 border border-slate-700">
                {selectedWelcomePlan === 'gratis' && "🆓 GRATIS"}
                {selectedWelcomePlan === 'premium' && "💎 PREMIUM ($99 MXN)"}
              </div>

              {/* Plan Benefits List exactly as requested by user */}
              <div className="space-y-3.5 text-left bg-slate-950/70 border border-slate-800 p-5 rounded-2xl mb-8 max-w-sm mx-auto">
                {selectedWelcomePlan === 'gratis' && (
                  <>
                    <h4 className="text-[10px] sm:text-xs font-black tracking-widest text-emerald-400 mb-2 uppercase">🆓 GRATIS</h4>
                    <div className="flex items-center gap-3 text-slate-200">
                      <span className="text-emerald-400 text-base">✔️</span>
                      <span className="font-semibold text-xs sm:text-sm">2 historias por día</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-200">
                      <span className="text-emerald-400 text-base">✔️</span>
                      <span className="font-semibold text-xs sm:text-sm">Espera de 3 minutos entre historias</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-200">
                      <span className="text-emerald-400 text-base">✔️</span>
                      <span className="font-semibold text-xs sm:text-sm">Anuncios</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-200">
                      <span className="text-emerald-400 text-base">✔️</span>
                      <span className="font-semibold text-xs sm:text-sm">XP normal (100%)</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-400">
                      <span className="text-slate-500 text-base">🔒</span>
                      <span className="font-semibold text-xs sm:text-sm text-slate-500">Acceso limitado a la tienda</span>
                    </div>
                  </>
                )}

                {selectedWelcomePlan === 'premium' && (
                  <>
                    <h4 className="text-[10px] sm:text-xs font-black tracking-widest text-indigo-400 mb-2 uppercase">💎 PREMIUM ($99 MXN)</h4>
                    <div className="flex items-center gap-3 text-indigo-200">
                      <span className="text-indigo-400 text-base">⭐</span>
                      <span className="font-bold text-xs sm:text-sm text-white">Historias ilimitadas</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-200">
                      <span className="text-indigo-400 text-base">⭐</span>
                      <span className="font-bold text-xs sm:text-sm text-white">Espera de 1 minuto entre historias</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-200">
                      <span className="text-indigo-400 text-base">⭐</span>
                      <span className="font-bold text-xs sm:text-sm text-white">Modo charla sin anuncios. (hasta 1 hora)</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-200">
                      <span className="text-indigo-400 text-base">⭐</span>
                      <span className="font-medium text-xs sm:text-sm text-white">Sesiones diarias de práctica (hasta 1 hora)</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-200">
                      <span className="text-indigo-400 text-base">⭐</span>
                      <span className="font-bold text-xs sm:text-sm text-indigo-300">XP +20%</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-200">
                      <span className="text-indigo-400 text-base">⭐</span>
                      <span className="font-medium text-xs sm:text-sm text-white">Acceso completo a la tienda</span>
                    </div>
                  </>
                )}
              </div>

              {/* Perron Activation Button */}
              {subscriptionError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-xs text-center flex flex-col items-center gap-2">
                  <span>{subscriptionError}</span>
                  {checkoutUrl && (
                    <a 
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 mt-2 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 transition-colors"
                      onClick={() => {
                        setSubscriptionError(null);
                        setCheckoutUrl(null);
                      }}
                    >
                      Abrir Stripe (Pestaña Segura)
                    </a>
                  )}
                </div>
              )}
              {selectedWelcomePlan === 'premium' && isPremiumUser ? (
                 <button
                   onClick={() => {
                     setShowEnglishWelcomeModal(false);
                     setHasInteracted(true);
                   }}
                   className="w-full py-3.5 rounded-2xl font-black text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 shadow-xl flex items-center justify-center gap-2 select-none cursor-pointer bg-gradient-to-r from-emerald-500 to-teal-500 hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] text-white hover:brightness-110 border-none hover:scale-[1.03] active:scale-[0.97]"
                 >
                   Plan Premium Activo ¡Empieza a aprender! 🎉
                 </button>
              ) : selectedWelcomePlan === 'premium' && checkoutUrl ? (
                 <a 
                   href={checkoutUrl} 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className={`w-full py-3.5 rounded-2xl font-black text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 shadow-xl flex items-center justify-center gap-2 select-none cursor-pointer bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:shadow-[0_0_25px_rgba(99,102,241,0.5)] text-white hover:brightness-110 border-none hover:scale-[1.03] active:scale-[0.97]`}
                   onClick={() => setIsProcessingSubscription(false)}
                 >
                   Completar pago en Stripe 💳
                 </a>
              ) : (
                <button
                  disabled={selectedWelcomePlan === 'premium' && isProcessingSubscription}
                  onClick={async () => {
                    if (selectedWelcomePlan === 'premium') {
                      let currentUser = auth.currentUser;
                      if (!currentUser) {
                        setIsProcessingSubscription(false);
                        setShowLoginModal({isOpen: true, message: "Debes iniciar sesión para suscribirte al plan Premium."});
                        return;
                      }
                      
                      setIsProcessingSubscription(true);
                      setSubscriptionError(null);
                      setCheckoutUrl(null);
                      
                      try {
                        const response = await fetch('/api/create-checkout-session', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            userId: currentUser.uid,
                            email: currentUser.email || '',
                          }),
                        });
                        
                        const data = await response.json();
                        
                        if (!response.ok) {
                          throw new Error(data.error || data.details || 'No se pudo crear la sesión de pago.');
                        }
                        
                        if (data.url) {
                          const width = 600;
                          const height = 800;
                          const left = window.screenX + (window.outerWidth - width) / 2;
                          const top = window.screenY + (window.outerHeight - height) / 2;
                          const stripeWindow = window.open(data.url, 'StripeCheckout', `width=${width},height=${height},left=${left},top=${top}`);
                          
                          if (stripeWindow) {
                            const popupTick = setInterval(() => {
                              if (stripeWindow.closed) {
                                clearInterval(popupTick);
                                setIsProcessingSubscription(false);
                              }
                            }, 1000);
                          } else {
                            setCheckoutUrl(data.url);
                            setSubscriptionError('Tu navegador bloqueó la ventana emergente de pago. Por favor usa el botón de abajo para continuar.');
                            setIsProcessingSubscription(false);
                          }
                        } else {
                           throw new Error('La respuesta del servidor no contiene una URL válida.');
                        }
                      } catch (err: any) {
                        console.error("Error al redirigir a Stripe:", err);
                        setSubscriptionError(err.message || 'Ocurrió un error al procesar tu solicitud. Inténtalo de nuevo.');
                        setIsProcessingSubscription(false);
                      }
                    } else {
                      setShowEnglishWelcomeModal(false);
                      setHasInteracted(true);
                      if (appMode === 'chat') {
                        setDailyContext('Ninguno aún. Estamos empezando a conocernos.');
                        setLongTermMemory('Aún no sé mucho sobre ti. ¿Cómo te llamas?');
                      }
                    }
                  }}
                  className={`w-full py-3.5 rounded-2xl font-black text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 shadow-xl flex items-center justify-center gap-2 select-none ${(selectedWelcomePlan === 'premium' && isProcessingSubscription) ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.03] active:scale-[0.97]'} ${
                    selectedWelcomePlan === 'gratis'
                      ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700/60 shadow-slate-900/50'
                      : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:shadow-[0_0_25px_rgba(99,102,241,0.5)] text-white hover:brightness-110 border-none'
                  }`}
                >
                  {selectedWelcomePlan === 'gratis' && "Comenzar Experiencia Gratis"}
                  {selectedWelcomePlan === 'premium' && (isProcessingSubscription ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Procesando...
                    </span>
                  ) : "Comenzar Experiencia Premium 💎")}
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Persistent Plan Status Button in the Bottom Right */}
      {(appMode === 'english' || appMode === 'chat') && (
        <div className="fixed bottom-3 right-3 z-[100] pointer-events-auto">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setShowEnglishWelcomeModal(true);
            }}
            className={`bg-slate-950/85 backdrop-blur-md border border-slate-700/50 shadow-lg shadow-black/10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 select-none ring-1 ${
              isPremiumUser 
                ? 'hover:border-indigo-500/50 ring-indigo-500/10 hover:shadow-[0_0_15px_rgba(99,102,241,0.25)]' 
                : 'hover:border-emerald-500/50 ring-emerald-500/10 hover:shadow-[0_0_15px_rgba(16,185,129,0.25)]'
            } ${showPremiumText ? 'px-4 h-12 gap-2 w-auto' : 'w-12 h-12'}`}
          >
            <Crown className={`w-5 h-5 shrink-0 animate-pulse ${isPremiumUser ? 'text-indigo-400 fill-indigo-400/20' : 'text-emerald-400 fill-emerald-400/20'}`} />
            <AnimatePresence mode="popLayout">
              {showPremiumText && (
                <motion.span 
                  key="premium-text"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`text-[10px] sm:text-[11px] font-black tracking-wider uppercase whitespace-nowrap overflow-hidden ${isPremiumUser ? 'text-indigo-300' : 'text-emerald-250'}`}
                >
                  {isPremiumUser ? 'Premium' : 'Free Version'}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      )}

      {/* Persistent Minimalist Stars Counter in the Top Right */}
      <div className={`fixed right-3 sm:right-4 z-[180] pointer-events-auto transition-all duration-300 ${appMode === 'chat' ? 'top-12 sm:top-[52px]' : 'top-3 sm:top-4'}`}>
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          onClick={() => {
            setIsProgressionMenuOpen(true);
            setActiveProgressionTab('rewards');
          }}
          className="bg-slate-950/85 backdrop-blur-md border border-slate-700/50 hover:border-amber-500/50 py-1 px-2 sm:py-1.5 sm:px-3 rounded-full shadow-lg flex items-center gap-1 sm:gap-1.5 cursor-pointer transition-all duration-300 select-none"
        >
          <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400 fill-amber-400 animate-pulse" />
          <span className="text-[10px] sm:text-[11px] font-black font-mono tracking-wider text-amber-200">{starsCount}</span>
        </motion.div>
      </div>

      {/* Payment Success Toast */}
      <AnimatePresence>
        {showPaymentSuccess && (
          <motion.div
            key="payment-success-modal"
            initial={{ opacity: 0, scale: 0.8, y: -20, x: '-50%' }}
            animate={{ opacity: 1, scale: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, scale: 0.8, y: -20, x: '-50%' }}
            className="fixed top-10 left-1/2 z-[300] bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-2xl shadow-[0_0_20px_rgba(16,185,129,0.4)] border border-emerald-300 flex items-center gap-2 sm:gap-3 pointer-events-auto"
          >
            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white/90 drop-shadow-sm" />
            <span className="text-xs sm:text-sm font-black tracking-wide">¡Premium activado con éxito! 🎉</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-in-out"
        style={{ backgroundImage: `url(${appMode === 'english' ? englishBackgrounds[englishBgIndex].url : backgrounds[bgIndex]})` }}
      />
      <div className="absolute inset-0 bg-white/10" />

      {/* Dark background overlay during exam, placed BEHIND Kokoro but in front of classroom bg */}
      <AnimatePresence>
        {isExamActive && (
          <motion.div 
            key="exam-active-bg-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 bg-slate-950/98 z-[5]"
          />
        )}
      </AnimatePresence>

      {/* Target area for Live2D Model */}
      <div className={`absolute inset-0 transition-all duration-300 ${isExamActive ? 'z-[10]' : 'z-0'}`}>
        {selectedAppMode !== null && micPermissionStatus === 'granted' && (
          <Live2DViewer 
            modelUrl={setup.modelPath ? `/live2d/${setup.modelPath}` : '/live2d/model/model.model3.json'} 
            isSpeaking={isPlaying} 
            emotion={emotionState.name}
            emotionLevel={emotionState.level}
            isPaused={isProgressionMenuOpen}
            onLoad={() => {
              console.log("Live2D model fully loaded inside browser.");
              setIsLive2DEngineLoaded(true);
            }}
          />
        )}
      </div>

      {isConfirmingReset && (
        <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center border border-pink-100">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Factory Reset Everything? ⚠️🔄</h3>
            <p className="text-gray-600 mb-6 text-sm">
              This will permanently clear all conversation history, learned memory elements, and cache content. 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setIsConfirmingReset(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-colors"
              >
                Cancel ❌
              </button>
              <button 
                onClick={() => {
                  setIsConfirmingReset(false);
                  handleReset();
                }}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold transition-colors shadow-lg shadow-red-200"
              >
                Confirm Reset 🔄
              </button>
            </div>
          </div>
        </div>
      )}

      {showSignOutWarning && (
        <div id="signout-warning-modal" className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center border border-pink-100 animate-[fade-in_0.2s_ease-out]">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-bounce" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Sign Out / Switch Account? 🚪👤</h3>
            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
              When signing out or switching accounts, your local stars, mastery tokens (XP), and progress cached temporarily on this device will be cleared to protect your privacy. 🛡️
            </p>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-2.5 mb-6 font-semibold leading-normal">
              ⚠️ Your lifetime progress is securely synced to your Google Account in the cloud and will be restored immediately when you sign in again.
            </p>
            <div className="flex gap-3">
              <button 
                id="btn-cancel-signout"
                onClick={() => setShowSignOutWarning(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-colors cursor-pointer"
              >
                No, cancel ❌
              </button>
              <button 
                id="btn-confirm-signout"
                onClick={confirmSignOut}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold transition-colors shadow-lg shadow-red-200 cursor-pointer"
              >
                Yes, Sign Out 🚪
              </button>
            </div>
          </div>
        </div>
      )}

      {activePracticePanel !== 'conversation' && !isExamActive && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-50 pointer-events-auto">
          {/* Volver al menú principal (casa) */}
          <button 
            onClick={handleHomeClick}
            title="Volver al menú principal"
            className="p-2.5 bg-white/80 hover:bg-white text-gray-500 hover:text-indigo-600 rounded-full shadow-sm border border-gray-100 transition-all group cursor-pointer flex items-center justify-center"
          >
            <Home className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
          </button>

          {/* Kokoro Volume Control */}
          <motion.div 
            initial={false}
            animate={{ width: isVolumeExpanded ? 140 : 38 }}
            className="flex items-center bg-white/80 hover:bg-white border border-gray-100 rounded-full shadow-sm transition-colors pointer-events-auto overflow-hidden h-[38px] cursor-pointer"
            onClick={() => { setIsVolumeExpanded(!isVolumeExpanded); }}
          >
            <div className="flex items-center justify-center w-[38px] h-[38px] shrink-0" title={isVolumeExpanded ? "Haz clic para colapsar" : "Volumen de Voz"}>
              {volume === 0 ? <VolumeX className="w-4 h-4 text-gray-500" /> : <Volume2 className="w-4 h-4 text-indigo-400" />}
            </div>
            <AnimatePresence>
              {isVolumeExpanded && (
                <motion.div 
                  key="volume-expanded-slider"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="pr-2.5 flex items-center pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.01" 
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    title="Volumen de Voz"
                    className="w-20 accent-indigo-500 cursor-pointer h-1"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Reiniciar */}
          <button 
            onClick={() => setIsConfirmingReset(true)}
            title="Reiniciar"
            className="hidden flex p-2.5 bg-white/80 hover:bg-red-50 text-gray-500 hover:text-red-500 rounded-full shadow-sm border border-gray-100 transition-all group cursor-pointer flex items-center justify-center"
          >
            <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
          </button>
        </div>
      )}



      {appMode === 'english' && !hideMicForEnglishIntro && activePracticePanel === null && !isExamActive && (
         <div className="fixed top-0 left-1/2 -translate-x-1/2 flex items-start gap-[8px] sm:gap-[16px] md:gap-[24px] z-[160] transition-all duration-500 ease-in-out pointer-events-none w-max max-w-full px-4">
            {/* Background Column */}
            <div className="flex flex-col items-center justify-start pointer-events-auto shrink-0">
               <motion.button
                  initial={{ scale: 0, y: -20 }}
                  animate={{ scale: 1, y: 0 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => { setShowBgSelector(!showBgSelector); }}
                  className="bg-slate-900/80 backdrop-blur-md rounded-b-2xl border-x-2 border-b-2 border-slate-700 shadow-md text-white px-2 py-1.5 sm:px-3 sm:py-2 flex items-center justify-center gap-1 cursor-pointer group w-24 sm:w-28"
               >
                  <ImageIcon className="w-3.5 h-3.5 shrink-0 text-cyan-400 group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-[9px] sm:text-[10px] w-full font-extrabold tracking-widest text-center uppercase leading-tight overflow-hidden whitespace-nowrap text-ellipsis">{englishBackgrounds[englishBgIndex].label}</span>
                  <div className="w-1.5 h-1.5 shrink-0 rounded-full bg-cyan-400 ml-0.5 animate-pulse" />
               </motion.button>
               <AnimatePresence>
                  {showBgSelector && (
                    <motion.div
                      key="bg-selector-dropdown"
                      initial={{ opacity: 0, scale: 0.9, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -10 }}
                      className="fixed top-14 left-1/2 -translate-x-1/2 w-[280px] max-w-[90vw] bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-xl border-2 border-cyan-500/30 p-2.5 z-[160] flex flex-col gap-1.5 pointer-events-auto"
                    >
                      <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-white/10">
                         <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">Select Location 📍</span>
                         <button onClick={(e) => { e.stopPropagation(); setShowBgSelector(false); }} className="p-1 hover:bg-white/10 rounded-full transition-colors"><X className="w-3.5 h-3.5 text-slate-400 hover:text-white" /></button>
                      </div>
                      {englishBackgrounds.map((bg, idx) => {
                         const isUnlocked = unlockedBackgrounds.includes(idx);
                         
                         return (
                          <button
                            key={'bg-' + idx}
                            onClick={() => { 
                              if (isUnlocked) {
                                setEnglishBgIndex(idx); 
                                setShowBgSelector(false); 
                              } else {
                                setShowBgSelector(false);
                                setIsProgressionMenuOpen(true);
                                setActiveProgressionTab('rewards');
                              }
                            }}
                            className={String(`flex items-center gap-3 w-full text-left p-2 rounded-xl text-xs font-bold transition-all ${idx === englishBgIndex ? "bg-cyan-500/20 text-cyan-300 shadow-sm border border-cyan-500/50" : (isUnlocked ? "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent" : "text-slate-500 opacity-70 hover:bg-slate-800/80 border border-transparent")}`)}
                          >
                             <div className={`w-10 h-10 rounded-lg bg-cover bg-center shrink-0 shadow-sm border border-white/10 ${!isUnlocked && 'grayscale brightness-50'}`} style={{ backgroundImage: `url(${bg.url})` }} />
                             <div className="flex-1 flex flex-col justify-center min-w-0">
                                <span className="text-[11px] uppercase tracking-wider flex items-center gap-2 truncate">
                                  {bg.label}
                                  {!isUnlocked && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                                </span>
                                {!isUnlocked && <span className="text-[9px] font-medium text-slate-400">Unlock in Store</span>}
                             </div>
                             {idx === englishBgIndex && <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />}
                          </button>
                         );
                      })}
                    </motion.div>
                  )}
               </AnimatePresence>
            </div>

            {/* Store Column */}
            <div className="flex flex-col items-center justify-start pointer-events-auto shrink-0">
               <motion.button
                  initial={{ scale: 0, y: -20 }}
                  animate={{ scale: 1, y: 0 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => { 
                     setShowBgSelector(false); 
                     setIsProgressionMenuOpen(true);
                     setActiveProgressionTab('rewards');
                  }}
                  className="bg-slate-900/80 backdrop-blur-md rounded-b-2xl border-x-2 border-b-2 border-slate-700 shadow-md text-white px-2 py-1.5 sm:px-3 sm:py-2 flex items-center justify-center gap-1.5 cursor-pointer group w-24 sm:w-28"
               >
                  <Store className="w-4 h-4 shrink-0 text-amber-400 group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-[9px] sm:text-[10px] w-full font-extrabold tracking-widest text-center uppercase leading-tight overflow-hidden whitespace-nowrap text-ellipsis">Store</span>
               </motion.button>
            </div>
         </div>
      )}

      {(appMode === 'chat' && activePracticePanel !== 'conversation' && !isExamActive) && (
         <div className="fixed top-0 left-1/2 -translate-x-1/2 flex items-start gap-[8px] sm:gap-[16px] md:gap-[24px] z-[160] transition-all duration-500 ease-in-out pointer-events-none w-max max-w-full px-4">
           <div className="flex flex-col items-center justify-start pointer-events-auto shrink-0">
              <motion.button
                 initial={{ scale: 0, y: -20 }}
                 animate={{ scale: 1, y: 0 }}
                 whileHover={{ scale: 1.05 }}
                 onClick={() => { setShowBgSelector(!showBgSelector); }}
                 className="bg-slate-900/80 backdrop-blur-md rounded-b-2xl border-x-2 border-b-2 border-slate-700 shadow-md text-white px-2 py-1.5 sm:px-3 sm:py-2 flex items-center justify-center gap-1 cursor-pointer group w-24 sm:w-28"
              >
                 <ImageIcon className="w-3.5 h-3.5 shrink-0 text-cyan-400 group-hover:scale-110 transition-transform duration-200" />
                 <span className="text-[9px] sm:text-[10px] w-full font-extrabold tracking-widest text-center uppercase leading-tight overflow-hidden whitespace-nowrap text-ellipsis">{chatBackgrounds[bgIndex].label}</span>
                 <div className="w-1.5 h-1.5 shrink-0 rounded-full bg-cyan-400 ml-0.5 animate-pulse" />
              </motion.button>
              <AnimatePresence>
                 {showBgSelector && (
                   <motion.div
                     key="chat-bg-selector-dropdown"
                     initial={{ opacity: 0, scale: 0.9, y: -10 }}
                     animate={{ opacity: 1, scale: 1, y: 0 }}
                     exit={{ opacity: 0, scale: 0.9, y: -10 }}
                     className="fixed top-14 left-1/2 -translate-x-1/2 w-[280px] max-w-[90vw] bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-xl border-2 border-cyan-500/30 p-2.5 z-[160] flex flex-col gap-1.5 pointer-events-auto"
                   >
                     <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-white/10">
                        <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">Select Location 📍</span>
                        <button onClick={(e) => { e.stopPropagation(); setShowBgSelector(false); }} className="p-1 hover:bg-white/10 rounded-full transition-colors"><X className="w-3.5 h-3.5 text-slate-400 hover:text-white" /></button>
                     </div>
                     {chatBackgrounds.map((bg, idx) => {
                        const isUnlocked = unlockedChatBackgrounds.includes(idx);
                        
                        return (
                         <button
                           key={'chat-bg-' + idx}
                           onClick={() => { 
                             if (isUnlocked) {
                               setBgIndex(idx); 
                               setShowBgSelector(false); 
                             } else {
                               setShowBgSelector(false);
                               setIsProgressionMenuOpen(true);
                               setActiveProgressionTab('rewards');
                             }
                           }}
                           className={String(`flex items-center gap-3 w-full text-left p-2 rounded-xl text-xs font-bold transition-all ${idx === bgIndex ? "bg-cyan-500/20 text-cyan-300 shadow-sm border border-cyan-500/50" : (isUnlocked ? "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent" : "text-slate-500 opacity-70 hover:bg-slate-800/80 border border-transparent")}`)}
                         >
                            <div className={`w-10 h-10 rounded-lg bg-cover bg-center shrink-0 shadow-sm border border-white/10 ${!isUnlocked && 'grayscale brightness-50'}`} style={{ backgroundImage: `url(${bg.url})` }} />
                            <div className="flex-1 flex flex-col justify-center min-w-0">
                               <span className="text-[11px] uppercase tracking-wider flex items-center gap-2 truncate">
                                 {bg.label}
                                 {!isUnlocked && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                               </span>
                               {!isUnlocked && <span className="text-[9px] font-medium text-slate-400">Unlock in Store</span>}
                            </div>
                            {idx === bgIndex && <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />}
                         </button>
                        );
                     })}
                   </motion.div>
                 )}
              </AnimatePresence>
           </div>
           
           <div className="flex flex-col items-center justify-start pointer-events-auto shrink-0">
              <motion.button
                 initial={{ scale: 0, y: -20 }}
                 animate={{ scale: 1, y: 0 }}
                 whileHover={{ scale: 1.05 }}
                 onClick={() => { 
                   setShowBgSelector(false); 
                   setShowMusicSelector(false);
                   setIsProgressionMenuOpen(true);
                   setActiveProgressionTab('rewards');
                 }}
                 className="bg-slate-900/80 backdrop-blur-md rounded-b-2xl border-x-2 border-b-2 border-slate-700 shadow-md text-white px-2 py-1.5 sm:px-3 sm:py-2 flex items-center justify-center gap-1.5 cursor-pointer group w-24 sm:w-28"
              >
                 <Store className="w-4 h-4 shrink-0 text-amber-400 group-hover:scale-110 transition-transform duration-200" />
                 <span className="text-[9px] sm:text-[10px] w-full font-extrabold tracking-widest text-center uppercase leading-tight overflow-hidden whitespace-nowrap text-ellipsis">Store</span>
              </motion.button>
           </div>
           
           <div className="flex flex-col items-center justify-start pointer-events-auto shrink-0">
              <motion.button
                 initial={{ scale: 0, y: -20 }}
                 animate={{ scale: 1, y: 0 }}
                 whileHover={{ scale: 1.05 }}
                 onClick={() => { setShowMusicSelector(!showMusicSelector); setShowBgSelector(false); }} style={{ display: 'none' }}
                 className="bg-slate-900/80 backdrop-blur-md rounded-b-2xl border-x-2 border-b-2 border-slate-700 shadow-md text-white px-2 py-1.5 sm:px-3 sm:py-2 flex items-center justify-center gap-1 cursor-pointer group w-24 sm:w-28"
              >
                 <Music className="w-3.5 h-3.5 shrink-0 text-pink-400 group-hover:scale-110 transition-transform duration-200" />
                 <span className="text-[9px] sm:text-[10px] w-full font-extrabold tracking-widest text-center uppercase leading-tight overflow-hidden whitespace-nowrap text-ellipsis">{musicIndex >= 0 ? musicTracks[musicIndex].name : 'No Music'}</span>
                 {isMusicLoading ? <Loader2 className="w-2.5 h-2.5 text-pink-400 animate-spin shrink-0 ml-0.5" /> : (musicIndex >= 0 && <div className="w-1.5 h-1.5 shrink-0 rounded-full bg-pink-400 ml-0.5 animate-pulse" />)}
              </motion.button>
              <AnimatePresence>
                 {showMusicSelector && (
                 <motion.div
                   key="music-selector-dropdown"
                   initial={{ opacity: 0, scale: 0.9, y: -10 }}
                   animate={{ opacity: 1, scale: 1, y: 0 }}
                   exit={{ opacity: 0, scale: 0.9, y: -10 }}
                   className="fixed top-14 left-1/2 -translate-x-1/2 w-[280px] max-w-[90vw] bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-xl border-2 border-pink-500/30 p-2.5 z-[160] flex flex-col gap-1.5 pointer-events-auto"
                 >
                   <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-white/10">
                      <span className="text-[10px] font-black text-pink-400 uppercase tracking-widest">Select Music 🎵</span>
                      <button onClick={(e) => { e.stopPropagation(); setShowMusicSelector(false); }} className="p-1 hover:bg-white/10 rounded-full transition-colors"><X className="w-3.5 h-3.5 text-slate-400 hover:text-white" /></button>
                   </div>
                   <button
                     key="no-music-option"
                     onClick={() => { 
                       setMusicIndex(-1); 
                       setShowMusicSelector(false); 
                       if (bgMusicRef.current) {
                         bgMusicRef.current.pause();
                         bgMusicRef.current.removeAttribute('src');
                         bgMusicRef.current.load();
                       }
                     }}
                     className={`flex items-center gap-3 w-full text-left p-2 rounded-xl text-xs font-bold transition-all ${-1 === musicIndex ? "bg-pink-500/20 text-pink-300 shadow-sm border border-pink-500/50" : "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent"}`}
                   >
                     <div className="flex-1 flex flex-col justify-center min-w-0 py-1 border-white/10">
                       <span className="text-[11px] uppercase tracking-wider flex items-center gap-2 truncate">
                         No Music
                       </span>
                     </div>
                     {-1 === musicIndex && <div className="w-2 h-2 rounded-full bg-pink-400 shadow-[0_0_8px_rgba(236,72,153,0.8)]" />}
                   </button>
                   {musicTracks.map((track, idx) => {
                       const isUnlocked = track.unlocked || track.free || unlockedMusic.includes(idx);
                       return (
                        <div
                          key={"music-" + idx}
                          className={`flex items-center justify-between gap-2 w-full p-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${idx === musicIndex ? "bg-pink-500/20 text-pink-300 shadow-sm border border-pink-500/50" : (isUnlocked ? "text-slate-300 border border-transparent hover:bg-slate-800" : "text-slate-500 opacity-70 border border-transparent hover:bg-slate-800/80")}`}
                        >
                           <div 
                             onClick={() => { 
                               if (isUnlocked) {
                                 setMusicIndex(idx); 
                                 setShowMusicSelector(false); 
                                 if (bgMusicRef.current) {
                                   const trackUrl = track.url;
                                   if (!bgMusicRef.current.src.endsWith(trackUrl)) {
                                     bgMusicRef.current.src = trackUrl;
                                     bgMusicRef.current.loop = true;
                                     bgMusicRef.current.load();
                                   }
                                   const isDucked = isListening || isPlaying;
                                   const targetVolume = isDucked ? Math.max(0, musicVolume * 0.2) : musicVolume;
                                   bgMusicRef.current.volume = targetVolume;
                                   bgMusicRef.current.play().catch(err => {
                                     console.warn("Direct play trigger failed:", err);
                                   });
                                 }
                               } else {
                                 setShowMusicSelector(false);
                                 setIsProgressionMenuOpen(true);
                                 setActiveProgressionTab('rewards');
                               }
                             }}
                             className="flex items-center gap-3 flex-1 min-w-0"
                           >
                             <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 shadow-sm border border-white/10 ${isUnlocked ? 'bg-slate-800 text-white' : 'bg-slate-800/50 text-slate-500 grayscale'}`}>
                               <Music className={`w-4 h-4 ${isUnlocked ? 'text-pink-400' : 'text-slate-600'}`} />
                             </div>
                             <div className="flex-1 flex flex-col justify-center min-w-0 py-1">
                                <span className="text-[11px] uppercase tracking-wider flex items-center gap-2 truncate">
                                  {track.name}
                                  {!isUnlocked && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                                </span>
                                {isUnlocked ? (
                                  <div className="flex flex-col gap-0.5">
                                    {musicDownloadProgress[track.id]?.status === 'downloading' ? (
                                      <span className="text-[9px] text-pink-400 animate-pulse font-extrabold uppercase tracking-widest">Descargando... {musicDownloadProgress[track.id]?.percent || 0}%</span>
                                    ) : musicDownloadProgress[track.id]?.status === 'error' ? (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await reDownloadTrack(track.id);
                                        }}
                                        className="text-[9px] bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded border border-rose-500/50 hover:bg-rose-500/40 font-black uppercase tracking-widest text-left cursor-pointer flex items-center gap-1 mt-0.5"
                                      >
                                        <span>⚠️ No se oye? Re-descargar</span>
                                      </button>
                                    ) : (
                                      <span className="text-[9px] text-emerald-400/90 font-medium uppercase tracking-widest">✓ Listo</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[9px] font-medium text-slate-400">Unlock in Store</span>
                                )}
                             </div>
                           </div>

                           <div className="flex items-center gap-1.5 shrink-0">
                             {isUnlocked && (
                               <button
                                 title="Redescargar canción si no se escucha"
                                 onClick={async (e) => {
                                   e.stopPropagation();
                                   await reDownloadTrack(track.id);
                                 }}
                                 className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-pink-500 hover:text-white text-slate-400 transition-all cursor-pointer flex items-center justify-center"
                               >
                                 <RefreshCw className={`w-3.5 h-3.5 ${musicDownloadProgress[track.id]?.status === 'downloading' ? 'animate-spin text-pink-400' : ''}`} />
                               </button>
                             )}
                             {idx === musicIndex && <div className="w-2 h-2 rounded-full bg-pink-400 shadow-[0_0_8px_rgba(236,72,153,0.8)]" />}
                           </div>
                        </div>
                       );
                    })}
                 </motion.div>
               )}
            </AnimatePresence>
         </div>
         </div>
      )}



      {/* English Topics Picker */}
      {appMode === 'english' && !hideMicForEnglishIntro && !isPracticeMenuOpen && activePracticePanel !== 'conversation' && !isExamActive && storyCooldown === 0 && (
         <div className={`fixed flex items-center justify-center z-[100] transition-all duration-500 ease-in-out ${isTopicsMenuOpen ? 'top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24' : 'top-[100px] right-3 translate-x-0 translate-y-0 w-14 h-14'}`}>
           {isTopicsMenuOpen && (
             <>
               {isListeningForOtherTopic && (
                  <p className="absolute -bottom-16 text-xs text-pink-500 font-bold bg-pink-50 border border-pink-100 px-3 py-1 rounded-full shadow-md animate-pulse whitespace-nowrap z-50">
                     ¡Di tu tema en INGLÉS! (estoy en modo inglés) 🎙️
                  </p>
               )}
               {/* Radial buttons */}
               {learningTopics.map((topic, originalIndex) => {
                  const positionIndex = topic.id === 'family' ? 6 : originalIndex;
                  const angle = (positionIndex * (360 / 7)) - 90; // Start at top
                  const radius = 150;
                  const x = Math.cos((angle * Math.PI) / 180) * radius;
                  const y = Math.sin((angle * Math.PI) / 180) * radius;
                  return (
                     <motion.button
                        key={topic.id}
                        initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                        animate={{ opacity: 1, x, y, scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20, delay: positionIndex * 0.05 }}
                        onClick={() => {
                           setSelectedTopic(topic);
                           setHasChosenTopic(true);
                           setHasStartedStory(false);
                           setHasEverSelectedTopic(true);
                           setIsTopicsMenuOpen(false);
                           setIsLevelsMenuOpen(true);
                           if (hasChosenLevel) {
                               localStorage.setItem('kokoro_english_level', englishLevel.toString());
                           }
                        }}
                        className="absolute top-1/2 left-1/2 -ml-10 -mt-10 w-20 h-20 bg-white hover:bg-indigo-50 rounded-full flex flex-col items-center justify-center shadow-xl border-2 border-indigo-100 hover:border-indigo-400 transition-colors z-10"
                     >
                        <span className="text-3xl mb-0.5">{topic.icon}</span>
                        <span className="text-[10px] font-bold text-indigo-900 leading-none px-1 text-center">{topic.label}</span>
                     </motion.button>
                  );
               })}
               
               {/* 7th item (Other / Mic) */}
               <motion.button
                  initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                  animate={{ 
                      opacity: 1, 
                      x: Math.cos((0 * (360 / 7) - 90) * Math.PI / 180) * 150, 
                      y: Math.sin((0 * (360 / 7) - 90) * Math.PI / 180) * 150, 
                      scale: 1 
                  }}
                  transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0 * 0.05 }}
                  onClick={() => {
                     isListeningForOtherTopicRef.current = true;
                     setIsListeningForOtherTopic(true);
                     startListening();
                  }}
                  className="absolute top-1/2 left-1/2 -ml-10 -mt-10 w-20 h-20 bg-white hover:bg-pink-50 rounded-full flex flex-col items-center justify-center shadow-xl border-2 border-pink-100 hover:border-pink-400 transition-colors z-10 p-1"
               >
                  <Mic className="w-8 h-8 text-pink-500 mb-0.5 shrink-0" />
                  <span className="text-[10px] font-bold text-pink-700 leading-tight px-1 text-center break-words line-clamp-2 w-full">
                    {selectedTopic?.id === 'custom_other' ? selectedTopic.label : 'Other'}
                  </span>
               </motion.button>
             </>
           )}

           {/* Central Button */}
           <button
             onClick={() => {
                if (!isTopicsMenuOpen) {
                  setEmotionState({ name: 'alegría', level: 1 });
                  setIsTopicsMenuOpen(true);
                  setIsPracticeMenuOpen(false);
                  setIsLevelsMenuOpen(false);
                  if (fetchAbortControllerRef.current) fetchAbortControllerRef.current.abort();
                  if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute('src'); audioRef.current.load(); }
                  try { if (recognitionRef.current) recognitionRef.current.stop(); } catch(e) {}
                  autoResumeMicRef.current = false;
                  setIsListening(false);
                  setIsAsking(false);
                  setIsPlaying(false);
                  setText('');
                  setQuestion('');
                } else if (isListeningForOtherTopic) {
                    isListeningForOtherTopicRef.current = false;
                    setIsListeningForOtherTopic(false);
                    stopListening();
                 } else {
                   setIsTopicsMenuOpen(false);
                 }
             }}
             className={`bg-gradient-to-br ${isListeningForOtherTopic ? 'from-pink-500 to-rose-500 border-pink-200 shadow-[0_0_40px_rgba(244,63,94,0.7)] animate-pulse' : selectedTopic ? 'from-indigo-400 to-indigo-600 border-indigo-300 shadow-[0_0_40px_rgba(99,102,241,0.6)]' : 'from-indigo-500 to-blue-500 border-indigo-200 shadow-[0_0_40px_rgba(99,102,241,0.6)]'} rounded-full flex flex-col items-center justify-center border-4 text-white z-20 hover:scale-105 transition-all duration-300 ${isTopicsMenuOpen ? 'w-24 h-24' : 'w-14 h-14'}`}
           >
              {isListeningForOtherTopic ? (
                 <>
                   <Mic className={`${isTopicsMenuOpen ? 'w-8 h-8 mb-1' : 'w-4 h-4 mb-0.5'} text-white animate-bounce`} />
                   <span className={`${isTopicsMenuOpen ? 'text-[9px]' : 'text-[7px]'} font-extrabold tracking-widest`}>SAY IT</span>
                 </>
              ) : selectedTopic ? (
                <>
                  <span className={`${isTopicsMenuOpen ? 'text-[8px]' : 'text-[5px]'} font-extrabold tracking-widest text-indigo-100 uppercase mt-0.5 truncate w-full px-1 text-center leading-tight break-words max-h-8 line-clamp-2`}>
                    {selectedTopic.id === 'custom_other' ? selectedTopic.label : 'TOPIC'}
                  </span>
                  <span className={`${isTopicsMenuOpen ? 'text-2xl' : 'text-sm'} select-none shrink-0`}>{selectedTopic.icon}</span>
                </>
              ) : (
                <>
                  <BookOpen className={`${isTopicsMenuOpen ? 'w-7 h-7 mb-0.5' : 'w-5 h-5 mb-0.5'} relative transition-all`} />
                  <span className={`${isTopicsMenuOpen ? 'text-[9px]' : 'text-[7px]'} font-extrabold tracking-widest text-center leading-tight transition-all`}>TOPICS</span>
                  {isTopicsMenuOpen && (
                    <span className="text-[8px] font-bold text-indigo-200 uppercase tracking-widest text-center leading-tight">CLOSE</span>
                  )}
                </>
              )}
           </button>
         </div>
      )}

      {/* English Levels Picker */}
      {appMode === 'english' && !hideMicForEnglishIntro && !isPracticeMenuOpen && !isTopicsMenuOpen && activePracticePanel !== 'conversation' && !isExamActive && storyCooldown === 0 && (
         <div className={`fixed flex items-center justify-center z-[100] transition-all duration-500 ease-in-out ${isLevelsMenuOpen ? 'top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24' : 'top-[164px] right-3 translate-x-0 translate-y-0 w-14 h-14'}`}>
           {isLevelsMenuOpen && (
             <>
               {/* Difficulty Levels mapped radially (1 to 10) */}
               {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level, i) => {
                  const angle = (i * (360 / 10)) - 90; // Start at top
                  const radius = 130; // Closer radius for 10 circles
                  const x = Math.cos((angle * Math.PI) / 180) * radius;
                  const y = Math.sin((angle * Math.PI) / 180) * radius;
                  return (
                     <motion.button
                        key={`level-${level}`}
                        initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                        animate={{ opacity: 1, x, y, scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20, delay: i * 0.04 }}
                        onClick={() => {
                           if (!unlockedLevels.includes(level)) {
                              setShowLevelLockedModal(level);
                              return;
                           }
                           setEnglishLevel(level);
                           setHasStartedStory(false);
                           setHasChosenLevel(true);
                           setIsLevelsMenuOpen(false);
                           localStorage.setItem('kokoro_english_level', level.toString());
                           syncProgressToCloud(unlockedLevels, masteryTokens, level);
                        }}
                        className={`absolute top-1/2 left-1/2 -ml-8 -mt-8 w-16 h-16 rounded-full flex flex-col items-center justify-center shadow-xl border-2 transition-all duration-300 z-10 ${
                            !unlockedLevels.includes(level)
                            ? 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-400 cursor-pointer' 
                            : 'bg-white hover:bg-orange-50 border-orange-100 hover:border-orange-400 text-orange-500'
                         }`}
                     >
                        {unlockedLevels.includes(level) && (
                            <span className="text-xl font-black text-orange-500 mb-0.5 leading-none">{level}</span>
                        )}
                        {!unlockedLevels.includes(level) ? (
                            <>
                              <Lock className={`w-4 h-4 ${(level === 1 || unlockedLevels.includes(level - 1)) ? 'text-amber-500 animate-pulse' : 'text-gray-400'} mb-0.5`} />
                              <span className="text-[8px] font-extrabold text-gray-400 leading-none">L{level}</span>
                            </>
                         ) : (
                            <span className="text-[8px] font-bold text-orange-800 leading-none px-1 text-center">Level</span>
                         )}
                     </motion.button>
                  );
               })}
             </>
           )}

           {/* Central Button */}
           <button
             onClick={() => {
                if (!isLevelsMenuOpen) {
                  setEmotionState({ name: 'alegría', level: 1 });
                  setIsLevelsMenuOpen(true);
                  setIsPracticeMenuOpen(false);
                  setIsTopicsMenuOpen(false);
                  if (fetchAbortControllerRef.current) fetchAbortControllerRef.current.abort();
                  if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute('src'); audioRef.current.load(); }
                  try { if (recognitionRef.current) recognitionRef.current.stop(); } catch(e) {}
                  autoResumeMicRef.current = false;
                  setIsListening(false);
                  setIsAsking(false);
                  setIsPlaying(false);
                  setText('');
                  setQuestion('');
                } else {
                  setIsLevelsMenuOpen(false);
                }
             }}
             className={`bg-gradient-to-br ${hasChosenLevel ? 'from-orange-500 to-red-500 border-orange-200 shadow-[0_0_40px_rgba(249,115,22,0.6)]' : 'from-indigo-500 to-blue-500 border-indigo-200 shadow-[0_0_40px_rgba(99,102,241,0.6)]'} rounded-full flex flex-col items-center justify-center border-4 text-white z-20 hover:scale-105 transition-all duration-300 ${isLevelsMenuOpen ? 'w-24 h-24' : 'w-14 h-14'}`}
           >
              {hasChosenLevel ? (
                <>
                  <span className={`${isLevelsMenuOpen ? 'text-[8px]' : 'text-[6px]'} font-extrabold tracking-widest text-orange-100 uppercase mt-0.5`}>LEVEL</span>
                  <span className={`${isLevelsMenuOpen ? 'text-2xl' : 'text-lg'} font-black leading-none select-none`}>{englishLevel}</span>
                </>
              ) : (
                <>
                  <span className={`${isLevelsMenuOpen ? 'text-2xl' : 'text-lg'} font-black mb-0.5 relative transition-all`}>{englishLevel}</span>
                  <span className={`${isLevelsMenuOpen ? 'text-[9px]' : 'text-[7px]'} font-extrabold tracking-widest mt-0.5 text-center leading-tight transition-all`}>LEVELS</span>
                  {isLevelsMenuOpen && (
                    <span className="text-[8px] font-bold text-indigo-200 uppercase tracking-widest mt-0.5 text-center leading-tight">CLOSE</span>
                  )}
                </>
              )}
           </button>
         </div>
      )}

      {/* Create Story Button */}
      {appMode === 'english' && !hideMicForEnglishIntro && !isTopicsMenuOpen && !isLevelsMenuOpen && !isPracticeMenuOpen && !isExamActive && activePracticePanel === null && (
         <motion.div 
            layout
            className={`fixed z-[90] flex flex-col items-center justify-center pointer-events-none transition-all duration-500 ease-in-out ${
               storyCooldown > 0 ? 'top-[136px] left-3' : 'bottom-[295px] left-1/2 -translate-x-1/2'
            }`}
         >
            <motion.button
               layout
               onClick={() => {
                  if (!isPremiumUser && dailyStoryAttempts <= 0) {
                     handleSpeak("No more story attempts left for today! Please try again tomorrow.");
                     setError("No attempts left today!");
                     setTimeout(() => setError(null), 5000);
                     return;
                  }
                  if (hasChosenTopic && hasChosenLevel && selectedTopic && storyCooldown === 0) {
                     setHasStartedStory(true);
                     setStoryCooldown(isPremiumUser ? 60 : 180);
                     if (!isPremiumUser) {
                        setDailyStoryAttempts(prev => Math.max(0, prev - 1));
                     }
                     handleAsk(`Let's talk about ${selectedTopic.label} at difficulty level ${englishLevel} out of 10`);
                  }
               }}
               disabled={!hasChosenTopic || !hasChosenLevel || storyCooldown > 0 || (!isPremiumUser && dailyStoryAttempts <= 0)}
               className={`rounded-full font-black text-center tracking-wide shadow-2xl transition-all duration-500 flex flex-col items-center justify-center border-4 pointer-events-auto ${
                  storyCooldown > 0
                     ? 'w-14 h-14 text-[9px] gap-0.5 bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed opacity-90'
                     : (!isPremiumUser && dailyStoryAttempts <= 0)
                        ? 'w-24 h-24 text-[10px] gap-1 bg-slate-900 border-rose-500 text-rose-400 cursor-not-allowed opacity-90'
                        : 'w-24 h-24 text-[10px] gap-1 ' + (hasChosenTopic && hasChosenLevel 
                           ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-emerald-300 hover:scale-[1.05] hover:shadow-[0_0_40px_rgba(52,211,153,0.7)] cursor-pointer active:scale-95' 
                           : 'bg-slate-200 text-slate-400 border-slate-100 cursor-not-allowed opacity-80')
               }`}
            >
               {storyCooldown > 0 ? (
                  <>
                     <span className="text-sm font-black leading-none mt-0.5">
                        {Math.floor(storyCooldown / 60)}:{(storyCooldown % 60).toString().padStart(2, '0')}
                     </span>
                     <span className="text-[7px] uppercase tracking-widest leading-none">Wait</span>
                  </>
               ) : (!isPremiumUser && dailyStoryAttempts <= 0) ? (
                  <>
                     <Lock className="w-5 h-5 mb-0.5 text-rose-500 animate-pulse" />
                     <span className="leading-tight px-1 text-[9px] uppercase tracking-wider font-extrabold text-rose-400">0 left</span>
                  </>
               ) : (
                  <>
                     <BookOpen className={`w-5 h-5 mb-0.5 ${hasChosenTopic && hasChosenLevel ? 'animate-bounce' : ''}`} />
                     <span className="leading-tight px-1 text-[9px] uppercase tracking-wider font-extrabold">Story</span>
                  </>
               )}
            </motion.button>

            {/* Daily Attempt indicator circles */}
            {storyCooldown === 0 && (
               <div className="mt-2.5 flex items-center gap-1.5 pointer-events-auto bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-full border border-slate-700/60 shadow-lg">
                  <span className="text-[7px] font-black tracking-widest text-[#a5b4fc] uppercase mr-0.5 leading-none">CREDITS</span>
                  <div className="flex gap-1.5 font-black text-indigo-400 text-xs">
                     {isPremiumUser ? "∞" : (
                        <>
                           <div className={`w-2 h-2 rounded-full border border-[#cbd5e1] transition-all duration-300 ${dailyStoryAttempts >= 1 ? 'bg-emerald-400 border-emerald-450 shadow-[0_0_8px_rgba(52,211,153,0.9)] scale-110' : 'bg-transparent border-slate-600'}`} />
                           <div className={`w-2 h-2 rounded-full border border-[#cbd5e1] transition-all duration-300 ${dailyStoryAttempts >= 2 ? 'bg-emerald-400 border-emerald-450 shadow-[0_0_8px_rgba(52,211,153,0.9)] scale-110' : 'bg-transparent border-slate-600'}`} />
                        </>
                     )}
                  </div>
               </div>
            )}

            {(!hasChosenTopic || !hasChosenLevel) && storyCooldown === 0 && (isPremiumUser || dailyStoryAttempts > 0) && (
               <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 absolute top-full text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-white/90 backdrop-blur shadow-xl px-4 py-2 rounded-full border border-slate-200 flex items-center gap-1 pointer-events-auto whitespace-nowrap"
               >
                  <span>⚠️ Select Topic & Level</span>
               </motion.p>
            )}

            {!isPremiumUser && dailyStoryAttempts === 0 && storyCooldown === 0 && (
               <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 absolute top-full text-[10px] uppercase tracking-widest font-black text-rose-500 bg-rose-50/95 backdrop-blur shadow-xl px-4 py-2 rounded-full border border-rose-200 flex items-center gap-1 pointer-events-auto whitespace-nowrap"
               >
                  <span>⚠️ Sin intentos (0/2 hoy)</span>
               </motion.p>
            )}
         </motion.div>
      )}

      {/* English Skills Menu after Story Ends */}
       {/* Progress Button (on the left side) */}
       {(appMode === 'english' || appMode === 'chat') && (
          <div className="fixed top-[36px] left-3 flex items-center justify-center z-[100] transition-all duration-500 ease-in-out w-14 h-14">
             <motion.button
                id="progress-btn"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsProgressionMenuOpen(true);
                }}
                className="bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 border-purple-300 shadow-[0_0_40px_rgba(147,51,234,0.6)] rounded-full flex flex-col items-center justify-center border-4 text-white z-20 w-14 h-14 transition-all duration-300 cursor-pointer group"
             >
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 mb-0.5 animate-pulse group-hover:scale-110 transition-transform duration-200" />
                <span className="text-[7px] font-extrabold tracking-widest text-center leading-tight">PROFILE</span>
             </motion.button>
          </div>
       )}

      {appMode === 'english' && !hideMicForEnglishIntro && !isTopicsMenuOpen && !isLevelsMenuOpen && !isPlaying && !isLoading && !isAsking && activePracticePanel !== 'conversation' && !isExamActive && hasStartedStory && (
         <div className={`fixed flex items-center justify-center z-[100] transition-all duration-500 ease-in-out ${isPracticeMenuOpen ? 'top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24' : 'top-[228px] right-3 translate-x-0 translate-y-0 w-14 h-14'}`}>
            {/* Center button showing "PRACTICE" */}
            <motion.button
               initial={{ scale: 0 }}
               animate={{ scale: 1 }}
               onClick={() => {
                 if (!isPracticeMenuOpen) {
                   if (!selectedTopic || !hasChosenLevel || !hasStartedStory) {
                     pendingTopicPromptOnAudioEndRef.current = true;
                     setEnglishEmotionSequence([
                       { emocion: 'alegría', nivel: 1 },
                       { emocion: 'alegría', nivel: 2 },
                       { emocion: 'alegría', nivel: 1 },
                       { emocion: 'alegría', nivel: 2 },
                       { emocion: 'alegría', nivel: 1 },
                       { emocion: 'alegría', nivel: 2 },
                       { emocion: 'alegría', nivel: 1 },
                       { emocion: 'alegría', nivel: 2 }
                     ]);
                     if (!hasStartedStory && hasChosenLevel && selectedTopic) {
                        handleSpeak("Please click Create Story to generate a story first!");
                        setError("Tap Create Story first!");
                     } else {
                        handleSpeak("You cannot start practicing yet. Please choose an English conversation topic and a level first!");
                        setError("Please choose a topic and level first!");
                     }
                     setTimeout(() => setError(null), 5000);
                     return;
                   }
                   setIsPracticeMenuOpen(true);
                   setIsTopicsMenuOpen(false);
                   if (fetchAbortControllerRef.current) {
                     fetchAbortControllerRef.current.abort();
                   }
                   if (audioRef.current) {
                     audioRef.current.pause();
                     audioRef.current.removeAttribute('src');
                     audioRef.current.load();
                   }
                   try {
                     if (recognitionRef.current) {
                       recognitionRef.current.stop();
                     }
                   } catch(e) {}
                   autoResumeMicRef.current = false;
                   setIsListening(false);
                   setIsAsking(false);
                   setIsPlaying(false);
                   setText('');
                   setQuestion('');
                 } else {
                   setIsPracticeMenuOpen(false);
                 }
               }}
               className={`bg-gradient-to-br from-indigo-500 to-purple-600 border-indigo-200 shadow-[0_0_45px_rgba(99,102,241,0.5)] rounded-full flex flex-col items-center justify-center border-4 text-white z-20 hover:scale-105 transition-all duration-300 ${isPracticeMenuOpen ? 'w-24 h-24' : 'w-14 h-14'}`}
            >
               {selectedTopic ? (
                 <div className="relative leading-none">
                   <span className={`${isPracticeMenuOpen ? 'text-2xl' : 'text-base'} select-none transition-all`}>{selectedTopic.icon}</span>
                   <span className={`absolute -top-1 -right-2 bg-amber-400 text-slate-950 font-black px-0.5 rounded shadow-sm border border-white leading-none ${isPracticeMenuOpen ? 'text-[7px]' : 'text-[6px]'}`}>
                     L{englishLevel}
                   </span>
                 </div>
               ) : (
                 <Sparkles className={`${isPracticeMenuOpen ? 'w-6 h-6 mb-0.5' : 'w-4 h-4 mb-0.5'} text-white animate-pulse transition-all`} />
               )}
               <span className={`${isPracticeMenuOpen ? 'text-[9px]' : 'text-[7px]'} font-extrabold tracking-widest text-center leading-tight transition-all`}>PRACTICE</span>
               {isPracticeMenuOpen && (
                 <span className="text-[8px] font-bold text-indigo-200 uppercase tracking-widest text-center leading-tight">CLOSE</span>
               )}
            </motion.button>

            {/* Radial practice buttons */}
            {isPracticeMenuOpen && practiceSkills.map((skill, index) => {
               const angle = (index * (360 / 6)) - 90; // Start at top
               const radius = 150;
               const x = Math.cos((angle * Math.PI) / 180) * radius;
               const y = Math.sin((angle * Math.PI) / 180) * radius;
               const IconComponent = skill.icon;

               // Compute score stars for this story
               const currentHash = getStoryHash(spokenStory);
               let maxStars = 1;
               let earnedStars = 0;
               if (skill.id === 'reading') {
                 maxStars = 1;
                 earnedStars = localStorage.getItem(`kokoro_star_${englishLevel}_reading_${currentHash}`) === 'true' ? 1 : 0;
               } else if (skill.id === 'listening') {
                 maxStars = 1;
                 earnedStars = localStorage.getItem(`kokoro_star_${englishLevel}_listening_${currentHash}`) === 'true' ? 1 : 0;
               } else if (skill.id === 'vocabulary') {
                 maxStars = 1;
                 earnedStars = localStorage.getItem(`kokoro_star_${englishLevel}_vocabulary_${currentHash}`) === 'true' ? 1 : 0;
               } else if (skill.id === 'speaking') {
                 maxStars = 5;
                 earnedStars = parseInt(localStorage.getItem(`kokoro_speaking_best_stars_${englishLevel}_${currentHash}`) || '0', 10);
               } else if (skill.id === 'writing') {
                 maxStars = 5;
                 const writeStarCheck = localStorage.getItem(`kokoro_star_${englishLevel}_writing_check_${currentHash}`) === 'true';
                 const writeScoreVal = localStorage.getItem(`kokoro_writing_stars_${englishLevel}_${currentHash}`);
                 earnedStars = writeScoreVal !== null ? parseInt(writeScoreVal, 10) : (writeStarCheck ? 5 : 0);
               } else if (skill.id === 'verbs') {
                 maxStars = 5;
                 const convStarCheck = localStorage.getItem(`kokoro_star_${englishLevel}_conversation_check_${currentHash}`) === 'true';
                 const convScoreVal = localStorage.getItem(`kokoro_conversation_stars_${englishLevel}_${currentHash}`);
                 earnedStars = convScoreVal !== null ? parseInt(convScoreVal, 10) : (convStarCheck ? 5 : 0);
               }
               earnedStars = Math.max(0, Math.min(maxStars, earnedStars));

               return (
                  <motion.button
                     key={skill.id}
                     id={`skill-btn-${skill.id}`}
                     initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                     animate={{ opacity: 1, x, y, scale: 1 }}
                     transition={{ type: "spring", stiffness: 200, damping: 20, delay: index * 0.05 }}
                     onClick={() => {
                        console.log(`Clicked skill practice: ${skill.id}`);
                        if (skill.id === 'reading') {
                          incrementReadingCount();
                          setActivePracticePanel('reading');
                        } else if (skill.id === 'speaking') {
                          // Stop any current chat or main listening
                          setIsListening(false);
                          try {
                            if (recognitionRef.current) {
                              recognitionRef.current.stop();
                            }
                          } catch (e) {}
                          if (audioRef.current) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          }
                          setSpeakingTranscript('');
                          incrementSpeakingCount();
                          setActivePracticePanel('speaking');
                        } else if (skill.id === 'listening') {
                          // Stop any ongoing action or panels
                          setActivePracticePanel(null);
                          setIsListening(false);
                          try {
                            if (recognitionRef.current) {
                              recognitionRef.current.stop();
                            }
                          } catch (e) {}

                          if (audioRef.current) {
                            let validSrc = audioRef.current.src;
                            const isWebpHref = !validSrc || validSrc === "" || (validSrc.startsWith('http') && !validSrc.includes('blob:') && !validSrc.endsWith('.wav') && !validSrc.endsWith('.mp3'));
                            if (isWebpHref) {
                              const foundItem = history.find(h => h.text && h.text.trim() === spokenStory.trim());
                              if (foundItem && foundItem.audioUrl) {
                                validSrc = foundItem.audioUrl;
                              } else if (storyAudioUrl) {
                                validSrc = storyAudioUrl;
                              } else if (history.length > 0 && history[0].audioUrl) {
                                validSrc = history[0].audioUrl;
                              }
                              if (validSrc) {
                                audioRef.current.src = validSrc;
                              }
                            }
                          }

                          const hasValidAudio = audioRef.current && audioRef.current.src && !(audioRef.current.src.startsWith('http') && !audioRef.current.src.includes('blob:') && !audioRef.current.src.endsWith('.wav') && !audioRef.current.src.endsWith('.mp3'));

                          // If audio exists, start playback synchronously to prevent browser block
                           if (hasValidAudio) {
                             incrementListeningCount();
                             setIsPracticeMenuOpen(false); // Close the dial
                             setIsListeningPlaybackFinished(false);
                             setIsPlaying(false);
                             const originalVolume = audioRef.current.volume;
                             audioRef.current.volume = 0;
                             const unlockPromise = audioRef.current.play();
                             if (unlockPromise !== undefined) {
                               unlockPromise.then(() => {
                                 audioRef.current!.pause();
                                 audioRef.current!.currentTime = 0;
                                 audioRef.current!.volume = originalVolume;
                                 
                                 setListeningCountdown(3);
                                 setTimeout(() => {
                                   setListeningCountdown(2);
                                   setTimeout(() => {
                                     setListeningCountdown(1);
                                     setTimeout(() => {
                                       setListeningCountdown(null);
                                       if (audioRef.current && audioRef.current.src) {
                                         audioRef.current.preservesPitch = false;
                                         (audioRef.current as any).mozPreservesPitch = false;
                                         (audioRef.current as any).webkitPreservesPitch = false;
                                         audioRef.current.playbackRate = 1.20;
                                         const playPromise = audioRef.current.play();
                                         if (playPromise !== undefined) {
                                           playPromise.then(() => {
                                             setIsPlaying(true);
                                             setIsListeningPracticeActive(true);
                                           }).catch((e) => {
                                             console.error("Listening practice start failed:", e);
                                             setIsListeningPracticeActive(false);
                                             setIsPracticeMenuOpen(true);
                                           });
                                         } else {
                                           setIsPlaying(true);
                                           setIsListeningPracticeActive(true);
                                         }
                                       }
                                     }, 1000);
                                   }, 1000);
                                 }, 1000);
                               }).catch((e) => {
                                 console.error("Listening practice unlock failed:", e);
                                 setIsListeningPracticeActive(false);
                                 setIsPracticeMenuOpen(true);
                               });
                             }
                           } else {
                             handleSpeak("Please click Create Story to generate and listen to the story first!");
                             setError("Please generate a story first!");
                             setTimeout(() => setError(null), 5000);
                           }
                        } else if (skill.id === 'writing') {
                          // Stop any current play or listening
                          setIsListening(false);
                          try {
                            if (recognitionRef.current) {
                              recognitionRef.current.stop();
                            }
                          } catch (e) {}
                          if (audioRef.current) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          }
                          // Initialize writing exercise with current spokenStory
                          if (spokenStory) {
                            // Find words and build exercise representation
                            const regex = /([a-zA-Z']+)|([^a-zA-Z']+)/g;
                            const matches = [...spokenStory.matchAll(regex)];
                            const tokens = matches.map((m, id) => {
                              const text = m[0];
                              const isWord = m[1] !== undefined;
                              return {
                                id,
                                text,
                                isWord,
                                blankIndex: null as number | null,
                                correctWord: isWord ? text : ''
                              };
                            });

                            const wordTokens = tokens.filter(t => t.isWord && t.text.length >= 3);
                            let finalWordTokens = wordTokens;
                            if (finalWordTokens.length < 5) {
                              finalWordTokens = tokens.filter(t => t.isWord);
                            }

                            const indices = finalWordTokens.map(t => t.id);
                            // To make it fun and random, shuffle indices to pick 5
                            const shuffled = [...indices].sort(() => 0.5 - Math.random());
                            const selectedIDs = shuffled.slice(0, 5);
                            const sortedSelectedIDs = [...selectedIDs].sort((a, b) => a - b);

                            const updatedTokens = tokens.map(t => {
                              const idx = sortedSelectedIDs.indexOf(t.id);
                              if (idx !== -1) {
                                return { ...t, blankIndex: idx };
                              }
                              return t;
                            });

                            const initialAnswers: { [key: number]: string } = {};
                            for (let i = 0; i < sortedSelectedIDs.length; i++) {
                              initialAnswers[i] = '';
                            }

                            setWritingExercise({
                              originalText: spokenStory,
                              tokens: updatedTokens,
                              userAnswers: initialAnswers,
                              checked: false,
                              score: 0
                            });
                            incrementWritingCount();
                            setActivePracticePanel('writing');
                          } else {
                            // Fallback if no narrative is spoken yet
                            handleSpeak("Please choose a topic and let me speak first!");
                          }
                        } else if (skill.id === 'vocabulary') {
                          // Stop any current play or listening
                          setIsListening(false);
                          try {
                            if (recognitionRef.current) {
                              recognitionRef.current.stop();
                            }
                          } catch (e) {}
                          if (audioRef.current) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          }
                          if (spokenStory) {
                            setSelectedVocabWord(null);
                            setVocabTranslation('');
                            setVocabDetails(null);
                            incrementVocabularyCount();
                            setActivePracticePanel('vocabulary');
                          } else {
                            // Fallback if no narrative is spoken yet
                            handleSpeak("Please choose a topic and let me speak first!");
                          }
                        } else if (skill.id === 'verbs') {
                          if (!hasChosenTopic || !hasChosenLevel) {
                            handleSpeak("Please choose a conversation topic and level first!");
                            setError("Please choose a topic and level first!");
                            return;
                          }
                          // Stop any current play or listening
                          setIsListening(false);
                          try {
                            if (recognitionRef.current) {
                              recognitionRef.current.stop();
                            }
                          } catch (e) {}
                          if (audioRef.current) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          }
                          
                          setIsPracticeMenuOpen(false); // Close the dial
                          setConversationCountdown(3);
                          
                          setTimeout(() => {
                            setConversationCountdown(2);
                            setTimeout(() => {
                              setConversationCountdown(1);
                              setTimeout(() => {
                                setConversationCountdown(null);
                                
                                // Initialize conversation practice
                                setConversationRound(1);
                                setConversationHistory([]);
                                setConversationScores([]);
                                setIsMyTurnToSpeak(false);
                                incrementSpeakingCount();
                                incrementListeningCount();
                                setActivePracticePanel('conversation');
                                startConversationRound(1, []);
                              }, 1000);
                            }, 1000);
                          }, 1000);
                        }
                     }}
                     className={`absolute top-1/2 left-1/2 -ml-12 -mt-12 w-24 h-24 rounded-full flex flex-col items-center justify-center shadow-xl border-2 transition-all z-10 ${skill.bg} ${skill.color} cursor-pointer group`}
                  >
                     <IconComponent className="w-6 h-6 mb-0.5 transition-transform group-hover:scale-110 duration-200" />
                     <span className={`${skill.id === 'verbs' ? 'text-[7.5px]' : 'text-[8.5px]'} font-extrabold uppercase tracking-wider leading-none px-1 text-center mt-0.5`}>{skill.label}</span>
                     
                     {/* Compact earned stars list */}
                     <div className="flex gap-0.5 mt-1 items-center justify-center pointer-events-none select-none">
                        {Array.from({ length: maxStars }).map((_, starIndex) => {
                          const isEarned = starIndex < earnedStars;
                          return (
                            <Star
                               key={starIndex}
                               className={`w-3 h-3 transition-all duration-300 ${
                                 isEarned
                                   ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.7)] scale-110'
                                   : 'text-amber-400/60'
                               }`}
                               strokeWidth={isEarned ? 1.5 : 1.2}
                            />
                          );
                        })}
                     </div>
                  </motion.button>
               );
            })}
         </div>
      )}

      {/* Floating Controls */}
      <div className={`absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 z-50 pointer-events-auto ${appMode === 'chat' ? 'bottom-64' : 'bottom-32'}`}>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/90 backdrop-blur px-6 py-3 rounded-2xl text-red-500 text-sm shadow-xl font-medium border border-red-100 text-center max-w-xs"
          >
            {error}
          </motion.div>
        )}
        
        {!hideMicForEnglishIntro && (
          <>
            <div className="flex flex-col items-center relative z-50">
              {appMode === 'chat' && !isExamActive && (
                <div className="flex flex-col items-center">
                  {isAsking || isLoading ? (
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl border-[6px] border-indigo-50">
                      <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                    </div>
                  ) : (
                    <button
                      onClick={toggleListening}
                      className={`w-20 h-20 rounded-full transition-all flex items-center justify-center border-[6px] outline-none ${
                        isListening 
                        ? 'bg-pink-500 border-pink-200 text-white shadow-[0_0_50px_rgba(236,72,153,0.6)] animate-[pulse_2s_ease-in-out_infinite]' 
                        : 'bg-white border-white text-gray-400 hover:border-pink-50 hover:text-pink-500 shadow-2xl hover:scale-105 active:scale-95'
                      }`}
                      title={isListening ? 'Escuchando...' : 'Hablar con Kokoro'}
                      disabled={showSponsor || showSponsor5Min}
                    >
                      {isListening ? <Mic className="w-8 h-8" /> : <MicOff className="w-8 h-8" />}
                    </button>
                  )}
                  <span className="text-xs font-mono tracking-widest text-gray-400 uppercase bg-white/50 px-3 py-1 rounded-full backdrop-blur mt-4">
                    {isListening ? 'Habla ahora...' : 'Toca el micro'}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {showSponsor && (
          <motion.div 
            key="sponsor-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-[340px] w-full shadow-2xl border border-pink-100 text-center relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400" />
              
              <div className="mx-auto w-14 h-14 bg-pink-50 rounded-full flex items-center justify-center mb-4">
                <Heart className="w-7 h-7 text-pink-500 animate-pulse" />
              </div>

              <h3 className="text-xl font-bold text-gray-800 mb-3">¡Bienvenido a Kokoro! 🌸</h3>
              
              <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                🤖 ¡Ya estoy lista! Esta app es 100% <b>gratis</b> gracias a patrocinadores 💖<br/><br/>
                👇 Por favor, abre y <b>explora un momento</b> la propuesta de abajo. ¡Nos ayudas muchísimo a seguir online! 🚀<br/><br/>
                ⚡ <i>¡Tu chat se activará de inmediato!</i> ✨<br/><br/>
                <span className="text-[11px] text-gray-500 bg-gray-50 p-2 rounded-lg block border border-gray-100 leading-tight">
                  Contamos con modalidad <b>Gratis</b> (con anuncios) y <b>Premium</b> (sin interrupciones y con alta capacidad para uso prolongado).
                </span>
              </p>

              <button 
                onClick={() => {
                  setShowSponsor(false);
                  setActiveAdUrl("https://omg10.com/4/11030204");
                }}
                className="block w-full py-3.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-bold hover:opacity-90 transition-opacity shadow-lg shadow-pink-200 flex items-center justify-center gap-2 mb-3 text-sm cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                Continuar en versión Gratis
              </button>
              <button
                onClick={() => {
                  setShowEnglishWelcomeModal(true);
                  setSelectedWelcomePlan('premium');
                }}
                className="block w-full py-3.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 text-white rounded-xl font-bold transition-all shadow-[0_0_20px_rgba(99,102,241,0.5)] flex items-center justify-center gap-2 border border-indigo-300/50 hover:scale-[1.02] active:scale-[0.98] animate-[pulse_2s_ease-in-out_infinite] text-sm"
              >
                👑 Hazte Premium
              </button>
            </motion.div>
          </motion.div>
        )}

        {showSponsor5Min && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-[340px] w-full shadow-2xl border border-pink-100 text-center relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400" />
              
              <div className="mx-auto w-14 h-14 bg-pink-50 rounded-full flex items-center justify-center mb-4">
                <Heart className="w-7 h-7 text-pink-500 animate-pulse" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">✨ ¡Pausa rápida! ✨</h3>
              
              <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                🌸 Para mantener a Kokoro <b>{isPremiumUser ? "online" : "gratis"}</b>, necesitamos tu apoyo 🙏<br/><br/>
                👇 Haz clic abajo y <b>explora la propuesta un momento</b>. ¡Tu ayuda es vital para nosotros! 🎁🚀<br/><br/>
                💬 <i>El chat se reanudará al instante</i> 💖<br/><br/>
                <span className="text-[11px] text-gray-500 bg-gray-50 p-2 rounded-lg block border border-gray-100 leading-tight">
                  {isPremiumUser 
                    ? "Has alcanzado el límite diario de tu plan Premium (60 minutos). Para seguir chateando hoy, veremos anuncios." 
                    : <>Estás usando la versión <b>Gratis</b>. Puedes actualizar a la versión <b>Premium</b> para eliminar estas pausas.</>}
                </span>
              </p>

              <button 
                onClick={() => {
                  setShowSponsor5Min(false);
                  setActiveAdUrl("https://omg10.com/4/11030204");
                }}
                className="block w-full py-3.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-bold hover:opacity-90 transition-opacity shadow-lg shadow-pink-200 flex items-center justify-center gap-2 mb-3 text-sm cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                Continuar
              </button>

              {!isPremiumUser && (
                <button
                  onClick={() => {
                    setShowEnglishWelcomeModal(true);
                    setSelectedWelcomePlan('premium');
                  }}
                  className="block w-full py-3.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 text-white rounded-xl font-bold transition-all shadow-[0_0_20px_rgba(99,102,241,0.5)] flex items-center justify-center gap-2 border border-indigo-300/50 hover:scale-[1.02] active:scale-[0.98] animate-[pulse_2s_ease-in-out_infinite] text-sm"
                >
                  👑 Hazte Premium
                </button>
              )}
            </motion.div>
          </motion.div>
        )}

        {activePracticePanel === 'reading' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-sm pointer-events-auto"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full shadow-2xl border border-indigo-100 relative max-h-[90vh] flex flex-col pt-24"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 via-teal-400 to-indigo-500" />
              
              {/* Stars Indicator in Top-Left Corner */}
              <div className="absolute top-4 left-4 z-20">
                {renderStarsIndicator(
                  readingStarEarned ? 1 : 0,
                  1,
                  "bg-amber-50/20 border border-amber-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs",
                  readingStarEarned ? "Star earned! 🏆" : "Focus 1 min to win star"
                )}
              </div>

              {/* XP Timer in Top-Right Corner */}
              <div className="absolute top-4 right-4 z-20">
                {renderXpTimer("bg-indigo-50/20 border border-indigo-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs")}
              </div>

              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-4 mb-4 gap-4 sm:gap-2 pr-0 sm:pr-40">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shrink-0">
                    <BookOpen className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-left font-sans">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                       <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">Story Reading Practice</h3>
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-400 font-medium leading-tight">Explore the lesson calmly. 🧘‍♂️ Keep your focus for 1 minute to earn a star ⭐, and complete 3 minutes to gain XP ⚡. You can stay as long as you want!</p>
                  </div>
                </div>
                
                {/* Replay and Translation buttons */}
                <div className="flex flex-col gap-2 shrink-0 items-end">
                  {spokenStory && (
                    <div className="flex flex-col gap-1.5 w-full">
                      <button
                        onClick={() => {
                          if (audioRef.current) {
                            let validSrc = audioRef.current.src;
                            const isWebpHref = !validSrc || validSrc === "" || (validSrc.startsWith('http') && !validSrc.includes('blob:') && !validSrc.endsWith('.wav') && !validSrc.endsWith('.mp3'));
                            if (isWebpHref) {
                              const foundItem = history.find(h => h.text && h.text.trim() === spokenStory.trim());
                              if (foundItem && foundItem.audioUrl) {
                                validSrc = foundItem.audioUrl;
                              } else if (storyAudioUrl) {
                                validSrc = storyAudioUrl;
                              } else if (history.length > 0 && history[0].audioUrl) {
                                validSrc = history[0].audioUrl;
                              }
                              if (validSrc) {
                                audioRef.current.src = validSrc;
                              }
                            }
                            
                            if (audioRef.current.src && !(audioRef.current.src.startsWith('http') && !audioRef.current.src.includes('blob:') && !audioRef.current.src.endsWith('.wav') && !audioRef.current.src.endsWith('.mp3'))) {
                               audioRef.current.preservesPitch = false;
                               (audioRef.current as any).mozPreservesPitch = false;
                               (audioRef.current as any).webkitPreservesPitch = false;
                               audioRef.current.playbackRate = 1.20;
                               audioRef.current.currentTime = 0;
                               const playPromise = audioRef.current.play();
                               if (playPromise !== undefined) {
                                   playPromise.then(() => setIsPlaying(true)).catch(e => { console.error("Play error:", e); setIsPlaying(false); });
                               } else {
                                   setIsPlaying(true);
                               }
                            }
                          }
                        }}
                        title="Escuchar de nuevo"
                        className="px-3 py-1.5 bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-600 rounded-xl border border-slate-100 transition-all flex items-center gap-1.5 text-xs font-bold shrink-0 shadow-sm cursor-pointer w-full justify-center"
                      >
                        <Headphones className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                        <span>Listen again</span>
                      </button>

                      <button
                        onClick={translateStoryToSpanish}
                        disabled={isTranslating}
                        title="Translate to Spanish"
                        className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 hover:text-indigo-800 rounded-xl border border-indigo-100 transition-all flex items-center gap-1.5 text-xs font-bold shrink-0 shadow-sm cursor-pointer disabled:opacity-50 w-full justify-center"
                      >
                        <Languages className="w-3.5 h-3.5 text-indigo-600" />
                        <span>{isTranslating ? 'Translating... 🔄' : (showSpanishTranslation ? 'English Original 🇬🇧' : 'Spanish Translation 🇪🇸')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto pr-2 py-2">
                {spokenStory ? (
                  <div className="space-y-6">
                    {/* The highlighted text block */}
                    {!showSpanishTranslation ? (
                    <div className="p-5 md:p-6 bg-slate-50/50 rounded-2xl border border-slate-100 leading-relaxed text-sm md:text-base text-slate-700 font-medium whitespace-pre-wrap text-left">
                      <div className="flex flex-wrap gap-x-1.5 gap-y-2.5">
                        {analyzeText(spokenStory).map((token, idx) => {
                          const isTranslatingThis = activeWordTranslation && activeWordTranslation.index === idx;
                          if (token.type === 'verb') {
                            return (
                              <span 
                                key={`verb-${idx}`} 
                                onPointerDown={(e) => { e.preventDefault(); handleWordPress(idx, token.text, e); }}
                                onPointerUp={handleWordRelease}
                                onPointerLeave={handleWordRelease}
                                onTouchCancel={handleWordRelease}
                                onContextMenu={(e) => e.preventDefault()}
                                className={`font-bold px-1.5 py-0.5 rounded-lg border shadow-sm transition-all duration-200 cursor-pointer select-none ${
                                  isTranslatingThis 
                                    ? 'bg-emerald-300 border-emerald-500 text-emerald-950 scale-110 -translate-y-0.5 ring-4 ring-emerald-300/30' 
                                    : 'text-emerald-700 bg-emerald-50/80 border-emerald-200/60 hover:scale-105 hover:-translate-y-0.5'
                                }`}
                                title="Verbo (Verb) - Press to translate"
                              >
                                {token.text}
                              </span>
                            );
                          } else if (token.type === 'noun') {
                            return (
                              <span 
                                key={`noun-${idx}`} 
                                onPointerDown={(e) => { e.preventDefault(); handleWordPress(idx, token.text, e); }}
                                onPointerUp={handleWordRelease}
                                onPointerLeave={handleWordRelease}
                                onTouchCancel={handleWordRelease}
                                onContextMenu={(e) => e.preventDefault()}
                                className={`font-bold px-1.5 py-0.5 rounded-lg border shadow-sm transition-all duration-200 cursor-pointer select-none ${
                                  isTranslatingThis 
                                    ? 'bg-blue-300 border-blue-500 text-blue-950 scale-110 -translate-y-0.5 ring-4 ring-blue-300/30' 
                                    : 'text-blue-700 bg-blue-50/80 border-blue-200/60 hover:scale-105 hover:-translate-y-0.5'
                                }`}
                                title="Sustantivo (Noun) - Press to translate"
                              >
                                {token.text}
                              </span>
                            );
                          } else {
                            return (
                              <span key={`text-${idx}`} className="text-slate-700 self-center">
                                {token.text}
                              </span>
                            );
                          }
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Spanish Translation Block */
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-5 md:p-6 bg-indigo-50/35 rounded-2xl border border-indigo-100/60 leading-relaxed text-sm md:text-base text-slate-800 text-left relative overflow-hidden"
                      >
                        <div className="absolute top-0 left-0 bg-indigo-200 text-indigo-800 font-extrabold px-3 py-0.5 rounded-br-xl text-[9px] uppercase tracking-wider font-mono">
                          Traducción al Español
                        </div>
                        <p className="mt-4 text-slate-700 font-medium leading-relaxed italic">
                          "{spanishTranslation}"
                        </p>
                      </motion.div>
                    )}

                    {/* Color Legend */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-left">
                      <div className="text-xs font-extrabold tracking-wider uppercase text-slate-400 mb-2.5">Grammar Key / Leyenda</div>
                      <div className="flex flex-wrap gap-4 text-xs font-semibold">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-emerald-500 border border-emerald-600 block shadow-sm" />
                          <span className="text-slate-600">Verbs (Verbos - Verde)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-blue-500 border border-blue-600 block shadow-sm" />
                          <span className="text-slate-600">Nouns (Sustantivos - Azul)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-slate-300 border border-slate-400 block shadow-sm" />
                          <span className="text-slate-400 font-medium">Other Words</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-8 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 py-12">
                    <Sparkles className="w-10 h-10 text-slate-300 mb-3 animate-pulse" />
                    <h4 className="text-sm font-bold text-slate-700 mb-1">No story told yet</h4>
                    <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                      Choose a conversation topic and a level in the TOPICS menu to listen to Kokoro's story first!
                    </p>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="border-t border-slate-100 pt-4 mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setActivePracticePanel(null);
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-800 hover:to-slate-900 rounded-xl font-bold text-sm shadow-md transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer font-sans"
                >
                  Close Practice Panel
                </button>
              </div>

              {/* Floating Word Translation Tooltip directly above the pressed word, clearing the finger */}
              <AnimatePresence>
                {activeWordTranslation && (
                  <motion.div
                    key="active-word-translation-tooltip"
                    initial={{ opacity: 0, y: 12, scale: 0.9, x: "-50%" }}
                    animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
                    exit={{ opacity: 0, y: 8, scale: 0.9, x: "-50%" }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    className="absolute z-[130] bg-slate-900 border border-slate-800 text-white px-3.5 py-2.5 rounded-2xl shadow-[0_12px_28px_rgba(15,23,42,0.35)] flex flex-col items-center justify-center whitespace-nowrap min-w-[140px] pointer-events-none select-none text-center"
                    style={{
                      left: activeWordTranslation.x !== undefined ? `${activeWordTranslation.x}px` : '50%',
                      top: activeWordTranslation.y !== undefined ? `${activeWordTranslation.y - 75}px` : 'auto', // Elevated 75px above the word, safe from finger clicks/touches!
                    }}
                  >
                    <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 font-mono mb-1 leading-none">
                      "{activeWordTranslation.word}"
                    </div>
                    <div className="text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-indigo-300 tracking-wide leading-none">
                      {activeWordTranslation.loading ? (
                        <span className="inline-flex items-center gap-1.5 justify-center py-0.5">
                          <span className="inline-block w-3 h-3 rounded-full border-2 border-white/20 border-t-white animate-spin shrink-0" />
                          <span className="text-slate-300 text-xs font-semibold">Translating... 🕵️‍♂️📚</span>
                        </span>
                      ) : (
                        activeWordTranslation.translation || 'Not found 🔍'
                      )}
                    </div>
                    {/* Tooltip caret pointing downwards to the word */}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-slate-900" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}

        {activePracticePanel === 'writing' && writingExercise && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-3 md:p-4 backdrop-blur-sm pointer-events-auto"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              className="bg-white rounded-3xl p-4 md:p-6 max-w-xl w-full shadow-2xl border border-indigo-100 relative max-h-[92vh] flex flex-col text-slate-700 pt-24"
            >
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-500 rounded-t-3xl" />
              
              {/* Stars Indicator in Top-Left Corner */}
              <div className="absolute top-2.5 left-4 z-20">
                {renderStarsIndicator(
                  writingExercise.checked ? writingExercise.score : 0,
                  5,
                  "bg-amber-50/20 border border-amber-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs",
                  writingExercise.checked ? `${writingExercise.score}/5 correct!` : "Complete gaps to win stars"
                )}
              </div>

              {/* XP Timer in Top-Right Corner next to Close Button */}
              <div className="absolute top-2.5 right-14 z-20">
                {renderXpTimer("bg-indigo-50/20 border border-indigo-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs")}
              </div>

              {/* Reset/Close button */}
              <button
                onClick={() => setActivePracticePanel(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-all cursor-pointer z-20 flex items-center justify-center"
                aria-label="Cerrar práctica"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 mb-3 select-none pr-8 gap-3 sm:gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center border border-indigo-100">
                    <Pencil className="w-4 h-4 text-indigo-500 animate-pulse" />
                  </div>
                  <div className="text-left font-sans">
                    <h3 className="text-base font-extrabold text-slate-800 tracking-tight">Writing Practice</h3>
                    <p className="text-[10px] text-slate-400 font-medium font-sans leading-none mt-0.5">Solve the spelling & recall gaps</p>
                  </div>
                </div>
              </div>

              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto pr-1 py-1">
                <div className="space-y-4">
                  {/* Listen helper */}
                  <div className="flex justify-between items-center bg-slate-50/50 p-2 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">Complete the sentence: ✍️📝</span>
                    <button
                      onClick={() => {
                        if (audioRef.current) {
                          let validSrc = audioRef.current.src;
                          const isWebpHref = !validSrc || validSrc === "" || (validSrc.startsWith('http') && !validSrc.includes('blob:') && !validSrc.endsWith('.wav') && !validSrc.endsWith('.mp3'));
                          if (isWebpHref) {
                            const foundItem = history.find(h => h.text && h.text.trim() === spokenStory.trim());
                            if (foundItem && foundItem.audioUrl) {
                              validSrc = foundItem.audioUrl;
                            } else if (storyAudioUrl) {
                              validSrc = storyAudioUrl;
                            } else if (history.length > 0 && history[0].audioUrl) {
                              validSrc = history[0].audioUrl;
                            }
                            if (validSrc) {
                              audioRef.current.src = validSrc;
                            }
                          }
                          
                          if (audioRef.current.src && !(audioRef.current.src.startsWith('http') && !audioRef.current.src.includes('blob:') && !audioRef.current.src.endsWith('.wav') && !audioRef.current.src.endsWith('.mp3'))) {
                             audioRef.current.preservesPitch = false;
                             (audioRef.current as any).mozPreservesPitch = false;
                             (audioRef.current as any).webkitPreservesPitch = false;
                             audioRef.current.playbackRate = 1.20;
                             audioRef.current.currentTime = 0;
                             const playPromise = audioRef.current.play();
                             if (playPromise !== undefined) {
                                 playPromise.then(() => setIsPlaying(true)).catch(e => { console.error("Play error:", e); setIsPlaying(false); });
                             } else {
                                 setIsPlaying(true);
                             }
                          }
                        }
                      }}
                      className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-100 transition-all flex items-center gap-1 text-[10px] font-bold leading-none select-none cursor-pointer"
                    >
                      <Headphones className="w-3" />
                      <span>Listen again</span>
                    </button>
                  </div>

                  {/* Dynamic gap fill phrase rendering */}
                  <div className="p-5 md:p-6 bg-slate-50/60 rounded-2xl border border-slate-100 leading-relaxed text-sm md:text-base text-slate-700 font-medium whitespace-pre-wrap text-left shadow-inner">
                    <div className="flex flex-wrap items-center gap-x-1 gap-y-3.5 animate-fade-in">
                      {writingExercise.tokens.map((token) => {
                        if (token.blankIndex !== null) {
                          const blankIdx = token.blankIndex;
                          const userVal = writingExercise.userAnswers[blankIdx] || '';
                          const isCorrect = writingExercise.checked && 
                            userVal.toLowerCase().trim().replace(/[^a-z']/g, '') === token.correctWord.toLowerCase().trim().replace(/[^a-z']/g, '');
                          
                          return (
                            <span key={`writing-token-${token.id}`} className="relative inline-block mx-0.5">
                              <input
                                type="text"
                                value={userVal}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setWritingExercise(prev => {
                                    if (!prev) return null;
                                    return {
                                      ...prev,
                                      userAnswers: { ...prev.userAnswers, [blankIdx]: val }
                                    };
                                  });
                                }}
                                disabled={writingExercise.checked}
                                placeholder={`(${blankIdx + 1})`}
                                className={`px-2 py-0.5 text-center font-bold text-xs md:text-sm rounded-lg border-2 w-24 md:w-28 transition-all focus:outline-none focus:ring-2 max-w-[130px] ${
                                  writingExercise.checked
                                    ? (isCorrect
                                        ? 'bg-emerald-50 border-emerald-400 text-emerald-800 ring-2 ring-emerald-200'
                                        : 'bg-rose-50 border-rose-400 text-rose-800 ring-2 ring-rose-200')
                                    : (userVal.trim().length > 0
                                        ? 'bg-indigo-50/50 border-indigo-400 text-indigo-900 focus:bg-white focus:border-indigo-500'
                                        : 'bg-slate-100/80 border-slate-200 text-slate-700 focus:bg-white focus:border-indigo-500')
                                }`}
                              />
                            </span>
                          );
                        } else {
                          return (
                            <span key={`text-token-${token.id}`} className="text-slate-800">
                              {token.text}
                            </span>
                          );
                        }
                      })}
                    </div>
                  </div>

                  {/* Submission done button / check answers or retry */}
                  {!writingExercise.checked ? (
                    <div className="flex gap-2.5">
                      <button
                        onClick={() => {
                          let correctCount = 0;
                          let blankTokensCount = 0;
                          writingExercise.tokens.forEach(token => {
                            if (token.blankIndex !== null) {
                              blankTokensCount++;
                              const userAns = writingExercise.userAnswers[token.blankIndex] || '';
                              const realWord = token.correctWord;
                              const isCorrect = userAns.toLowerCase().trim().replace(/[^a-z']/g, '') === realWord.toLowerCase().trim().replace(/[^a-z']/g, '');
                              if (isCorrect) correctCount++;
                            }
                          });

                          const roundedScore = blankTokensCount > 0 ? Math.round((correctCount / blankTokensCount) * 100) : 100;
                          
                          const hash = getStoryHash(spokenStory);
                          const writeStarKey = `kokoro_star_${englishLevel}_writing_check_${hash}`;
                          const writeScoreKey = `kokoro_writing_stars_${englishLevel}_${hash}`;
                          const previousWritingBest = parseInt(localStorage.getItem(writeScoreKey) || '0', 10);
                          
                          localStorage.setItem(writeStarKey, 'true');
                          if (correctCount > previousWritingBest) {
                            const delta = correctCount - previousWritingBest;
                            localStorage.setItem(writeScoreKey, correctCount.toString());
                            playSuccessShinChime();
                            setStarsCount(prev => {
                              const nextStars = prev + delta;
                              localStorage.setItem('kokoro_stars_count', nextStars.toString());
                              syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
                              return nextStars;
                            });
                          }

                          setWritingGrade(prev => {
                            const next = Math.max(prev, Math.min(prev + Math.ceil((roundedScore - prev) * 0.15), 100));
                            localStorage.setItem('kokoro_stat_writing', next.toString());
                            syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { writing: next });
                            return next;
                          });

                          setWritingExercise(prev => {
                            if (!prev) return null;
                            return {
                              ...prev,
                              checked: true,
                              score: correctCount
                            };
                          });
                        }}
                        className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-md active:scale-95 cursor-pointer flex items-center justify-center gap-1.5 text-xs md:text-sm"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Check Answers / Done
                      </button>
                      <button
                        onClick={() => {
                          setActivePracticePanel(null);
                        }}
                        className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all shadow-none cursor-pointer flex items-center justify-center gap-1 text-xs md:text-sm"
                      >
                        Close Practice Panel
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2.5">
                      <button
                        onClick={() => {
                          setWritingExercise(prev => {
                            if (!prev) return null;
                            const resetAnswers: { [key: number]: string } = {};
                            Object.keys(prev.userAnswers).forEach(key => {
                              resetAnswers[Number(key)] = '';
                            });
                            return {
                              ...prev,
                              checked: false,
                              score: 0,
                              userAnswers: resetAnswers
                            };
                          });
                        }}
                        className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all shadow-md cursor-pointer flex items-center justify-center gap-1 text-xs md:text-sm"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Try Again
                      </button>
                      <button
                        onClick={() => {
                          setActivePracticePanel(null);
                        }}
                        className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all shadow-none cursor-pointer flex items-center justify-center gap-1 text-xs md:text-sm"
                      >
                        Close Practice
                      </button>
                    </div>
                  )}

                  {/* Corrections details area */}
                  {writingExercise.checked && (
                    <div className="mt-2 p-3 bg-slate-100/60 border border-slate-200/50 rounded-2xl text-left max-h-[260px] overflow-y-auto space-y-3">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1 leading-none select-none">
                        <span>📝 Feedback de Escritura</span>
                      </h4>
                      <div className="space-y-2.5">
                        {writingExercise.tokens
                          .filter(t => t.blankIndex !== null)
                          .map(t => {
                            const blankIndex = t.blankIndex!;
                            const userVal = (writingExercise.userAnswers[blankIndex] || '').trim();
                            const isCorrect = userVal.toLowerCase().trim().replace(/[^a-z']/g, '') === t.correctWord.toLowerCase().trim().replace(/[^a-z']/g, '');

                            if (isCorrect) {
                              return (
                                <div key={`blank-pass-${blankIndex}`} className="bg-emerald-50/80 border-2 border-emerald-300 rounded-2xl p-3 flex flex-col gap-1.5 shadow-sm animate-fade-in text-slate-800 transition-all hover:border-emerald-400">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black tracking-widest font-sans uppercase px-2.5 py-0.5 rounded-full bg-emerald-200 text-emerald-800 border border-emerald-300/50 shadow-sm">
                                      📍 GAP {blankIndex + 1}
                                    </span>
                                    <span className="text-xs font-black text-emerald-600 flex items-center gap-1 select-none">
                                      🎯 ¡Excelente!
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs md:text-sm font-semibold bg-white p-2 rounded-xl border border-emerald-100 mt-0.5">
                                    <div className="flex items-center gap-2 text-emerald-900">
                                      <span className="text-[10px] uppercase text-emerald-600 font-extrabold tracking-wider block leading-none select-none bg-emerald-50 px-1.5 py-1 rounded">Escribiste</span>
                                      <strong className="text-xs md:text-sm font-extrabold font-mono text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md border border-emerald-200/60">{t.correctWord}</strong>
                                      <span className="text-sm select-none">🎉</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div key={`blank-fail-${blankIndex}`} className="bg-rose-50/80 border-2 border-rose-300 rounded-2xl p-3 flex flex-col gap-1.5 shadow-sm animate-fade-in text-slate-800 transition-all hover:border-rose-400">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black tracking-widest font-sans uppercase px-2.5 py-0.5 rounded-full bg-rose-200 text-rose-800 border border-rose-300/50 shadow-sm mx-0">
                                      📍 GAP {blankIndex + 1}
                                    </span>
                                    <span className="text-xs font-black text-rose-600 flex items-center gap-1 select-none">
                                      ❌ Incorrecto
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-semibold bg-white p-2 rounded-xl border border-rose-100 mt-0.5">
                                    <div className="flex items-center gap-1.5 text-rose-700 bg-rose-50/50 border border-rose-100/80 px-2 py-1 rounded-lg">
                                      <span className="text-[10px] uppercase text-rose-500 font-extrabold tracking-wider block leading-none select-none">Tu respuesta</span>
                                      <strong className="text-xs md:text-sm font-bold font-mono line-through text-rose-600">{userVal || "(vacío)"}</strong>
                                    </div>
                                    
                                    <div className="text-rose-400 font-black animate-pulse px-0.5 text-base leading-none select-none">➔</div>
                                    
                                    <div className="flex items-center gap-1.5 text-indigo-950 bg-indigo-50 border-2 border-indigo-200/60 px-2 py-1 rounded-lg">
                                      <span className="text-[10px] uppercase text-indigo-500 font-extrabold tracking-wider block leading-none select-none">Debió ser</span>
                                      <strong className="text-xs md:text-sm font-black font-mono text-indigo-700 bg-white border border-indigo-100 px-1.5 py-0.5 rounded">{t.correctWord}</strong>
                                      <span className="text-sm select-none">✨</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {activePracticePanel === 'vocabulary' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-3 md:p-4 backdrop-blur-sm pointer-events-auto"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              className="bg-white rounded-3xl p-4 md:p-6 max-w-xl w-full shadow-2xl border border-orange-100 relative max-h-[92vh] flex flex-col text-slate-700 font-sans pt-24"
            >
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-500 rounded-t-3xl" />
              
              {/* Stars Indicator in Top-Left Corner */}
              <div className="absolute top-2.5 left-4 z-20">
                {renderStarsIndicator(
                  vocabularyStarEarned ? 1 : 0,
                  1,
                  "bg-amber-50/20 border border-amber-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs",
                  vocabularyStarEarned ? "Star earned! 🏆" : "Star Timer: 1 min"
                )}
              </div>

              {/* XP Timer in Top-Right Corner next to Close Button */}
              <div className="absolute top-2.5 right-14 z-20">
                {renderXpTimer("bg-indigo-50/20 border border-indigo-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs")}
              </div>

              {/* Close Button */}
              <button
                onClick={() => setActivePracticePanel(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-full transition-all cursor-pointer z-20 flex items-center justify-center"
                aria-label="Cerrar vocabulario"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 mb-3 select-none pr-8 gap-3 sm:gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-orange-50 rounded-lg flex items-center justify-center border border-orange-100 shrink-0">
                    <Languages className="w-4 h-4 text-orange-500 animate-pulse" />
                  </div>
                  <div className="text-left font-sans">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                       <h3 className="text-base font-extrabold text-slate-800 tracking-tight">Vocabulary Builder</h3>
                    </div>
                    <p className="text-[10px] text-slate-400 font-medium font-sans leading-tight mt-0.5">Explore new words at your own pace! 🧭 Stay focused for 1 minute to earn a star ⭐, and reach 3 minutes to gain XP ⚡. Keep practicing freely!</p>
                  </div>
                </div>
              </div>

              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto pr-1 py-1">
                <div className="space-y-4">
                  
                  {/* Title Phrase area */}
                  <div className="flex justify-between items-center bg-slate-50/50 p-2 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">📚 Tap a word to learn:</span>
                    <button
                      onClick={() => {
                        if (audioRef.current) {
                          let validSrc = audioRef.current.src;
                          const isWebpHref = !validSrc || validSrc === "" || (validSrc.startsWith('http') && !validSrc.includes('blob:') && !validSrc.endsWith('.wav') && !validSrc.endsWith('.mp3'));
                          if (isWebpHref) {
                            const foundItem = history.find(h => h.text && h.text.trim() === spokenStory.trim());
                            if (foundItem && foundItem.audioUrl) {
                              validSrc = foundItem.audioUrl;
                            } else if (storyAudioUrl) {
                              validSrc = storyAudioUrl;
                            } else if (history.length > 0 && history[0].audioUrl) {
                              validSrc = history[0].audioUrl;
                            }
                            if (validSrc) {
                              audioRef.current.src = validSrc;
                            }
                          }
                          
                          if (audioRef.current.src && !(audioRef.current.src.startsWith('http') && !audioRef.current.src.includes('blob:') && !audioRef.current.src.endsWith('.wav') && !audioRef.current.src.endsWith('.mp3'))) {
                            audioRef.current.preservesPitch = false;
                            (audioRef.current as any).mozPreservesPitch = false;
                            (audioRef.current as any).webkitPreservesPitch = false;
                            audioRef.current.playbackRate = 1.20;
                            audioRef.current.currentTime = 0;
                            const playPromise = audioRef.current.play();
                            if (playPromise !== undefined) {
                                playPromise.then(() => setIsPlaying(true)).catch(e => { console.error("Play error:", e); setIsPlaying(false); });
                            } else {
                                setIsPlaying(true);
                            }
                          }
                        }
                      }}
                      className="px-2.5 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg border border-orange-100 transition-all flex items-center gap-1 text-[10px] font-bold leading-none select-none cursor-pointer"
                    >
                      <Headphones className="w-3" />
                      <span>Listen again</span>
                    </button>
                  </div>

                  {/* Words rendering layout */}
                  <div className="p-4 md:p-5 bg-slate-50/60 rounded-2xl border border-slate-100 leading-relaxed text-slate-755 font-medium text-left shadow-inner flex flex-wrap items-center gap-x-1.5 gap-y-3.5 animate-fade-in select-none">
                    {spokenStory.split(/(\s+)/).map((segment, idx) => {
                      if (/^\s+$/.test(segment)) {
                        return <span key={`space-${idx}`}>{segment}</span>;
                      }
                      
                      const wordMatch = segment.match(/[a-zA-Z']+/);
                      if (!wordMatch) {
                        return <span key={`punct-${idx}`} className="text-slate-800 font-medium">{segment}</span>;
                      }
                      
                      const word = wordMatch[0];
                      const firstIdx = segment.indexOf(word);
                      const before = segment.substring(0, firstIdx);
                      const after = segment.substring(firstIdx + word.length);
                      
                      const isSelected = selectedVocabWord === word.toLowerCase();
                      
                      return (
                        <span key={`word-${idx}`} className="inline-flex items-center">
                          {before && <span className="text-slate-800 font-medium">{before}</span>}
                          <button
                            onClick={() => handleVocabWordClick(word)}
                            className={`px-1.5 py-0.5 rounded-lg font-bold transition-all duration-200 cursor-pointer select-none text-xs md:text-sm border ${
                              isSelected
                                ? 'bg-orange-500 text-white border-orange-600 font-extrabold shadow-[0_4px_10px_rgba(249,115,22,0.35)] scale-110 -translate-y-0.5 z-10'
                                : 'text-orange-700 bg-orange-50/70 border-orange-200/60 hover:scale-105 hover:-translate-y-0.5 hover:bg-orange-100 hover:text-orange-850'
                            }`}
                          >
                            {word}
                          </button>
                          {after && <span className="text-slate-800 font-medium">{after}</span>}
                        </span>
                      );
                    })}
                  </div>

                  {/* Detail Panel */}
                  <AnimatePresence mode="wait">
                    {selectedVocabWord ? (
                      isVocabLoading ? (
                        <motion.div 
                          key="vocab-loading"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="p-8 flex flex-col items-center justify-center gap-3 bg-orange-100/10 border-2 border-dashed border-orange-200 rounded-2xl animate-pulse"
                        >
                          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                          <span className="text-xs font-bold text-orange-500/80 uppercase tracking-widest">Looking up in the dictionary... 🕵️‍♂️📖</span>
                        </motion.div>
                      ) : (
                        <motion.div 
                          key="vocab-loaded"
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -15 }}
                          className="space-y-3"
                        >
                          {/* Word main pronunciation & phonetic badge */}
                          <div className="flex items-center justify-between bg-orange-50/50 p-2.5 rounded-xl border border-orange-100">
                            <div className="flex items-center gap-2.5 text-left font-sans">
                              <h4 className="text-xl md:text-2xl font-black text-orange-955 capitalize">
                                {selectedVocabWord}
                              </h4>
                              {vocabDetails?.phonetic && (
                                <span className="text-[10px] font-mono lg:text-xs font-bold text-amber-700 bg-amber-55 px-2 py-0.5 border border-amber-200 rounded-lg leading-none">
                                  {vocabDetails.phonetic}
                                </span>
                              )}
                            </div>

                            <button
                              onClick={() => handleSpeakWord(selectedVocabWord)}
                              disabled={isVocabSpeaking}
                              className={`px-3 py-1.5 bg-orange-500 hover:bg-orange-600 border border-orange-600 hover:border-orange-700 text-white rounded-xl transition-all flex items-center gap-1 text-[11px] font-bold shadow-md cursor-pointer ${
                                isVocabSpeaking ? 'animate-pulse' : 'active:scale-95'
                              }`}
                              title="Listen to pronunciation"
                            >
                              <Volume2 className="w-3.5 h-3.5" />
                              <span>{isVocabSpeaking ? 'Speaking... 🗣️' : 'Pronounce'}</span>
                            </button>
                          </div>

                          {/* Definition & Translation block */}
                          <div className="bg-gradient-to-br from-orange-50/80 to-amber-50/80 border-2 border-orange-300 rounded-2xl p-4 flex flex-col gap-3 shadow-sm">
                            <div className="flex items-center justify-between">
                              <div className="text-xl md:text-2xl font-black text-slate-800 text-left tracking-tight flex items-center gap-1.5">
                                <span className="text-orange-400 select-none font-black">➔</span> {vocabTranslation}
                              </div>
                              <span className="text-[9px] font-black tracking-wider bg-white text-orange-950 rounded px-1.5 py-0.5 border border-orange-200 uppercase">
                                {vocabDetails?.partOfSpeech || 'word'}
                              </span>
                            </div>
                            
                            <div className="pt-3 border-t border-orange-200/60 flex flex-col gap-1 mt-1 text-left">
                              <span className="text-[10px] font-black tracking-widest font-sans uppercase text-orange-850 leading-none mb-1">
                                Definition in Spanish 🇪🇸
                              </span>
                              <p className="text-xs md:text-sm font-semibold text-slate-700 leading-normal">
                                {vocabDetails?.definition || "A vocabulary word from Kokoro's narrative."}
                              </p>
                            </div>
                            
                            {vocabDetails?.example && (
                              <div className="pt-1 flex flex-col gap-1 text-left">
                                <span className="text-[9px] uppercase font-extrabold text-orange-800/70 tracking-wider block leading-none select-none font-sans mb-1">
                                  Example Usage 📝
                                </span>
                                <p className="border-l-4 border-orange-400 pl-3 italic text-xs text-slate-600 font-medium py-1">
                                  "{vocabDetails.example}"
                                </p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )
                    ) : (
                      <motion.div 
                        key="vocab-placeholder"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border-2 border-dashed border-orange-200 bg-orange-50/15 rounded-2xl p-6 text-center text-slate-500 animate-fade-in my-2"
                      >
                        <div className="w-12 h-12 bg-orange-100/40 rounded-full flex items-center justify-center mx-auto mb-2 text-orange-400">
                          <Languages className="w-6 h-6 animate-pulse" />
                        </div>
                        <p className="text-xs md:text-sm font-bold text-orange-800/80">👉 Tap any word in the text above to reveal its meaning! 🔍</p>
                        <p className="text-[10px] text-slate-400 font-medium mt-1">You will immediately see its Spanish translation, hear its native English pronunciation, learn its grammar structure, and explore real examples! 🎙️📚</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Actions Close footer */}
                  <div className="pt-2 border-t border-slate-150">
                    <button
                      onClick={() => {
                        setActivePracticePanel(null);
                      }}
                      className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-all shadow-md active:scale-95 cursor-pointer flex items-center justify-center gap-1.5 text-xs md:text-sm font-sans"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Close Practice Panel
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {activePracticePanel === 'conversation' && (() => {
          const latestKokoroMsg = [...conversationHistory].reverse().find(m => m.role === 'kokoro');
          const latestUserMsg = [...conversationHistory].reverse().find(m => m.role === 'user');
          return (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[150] bg-transparent flex flex-col justify-between p-4 md:p-6 pointer-events-none"
            >
              {/* Sleek transparent close button in Top-Right */}
              <button 
                onClick={() => {
                  setActivePracticePanel(null);
                  stopListening();
                  if (audioRef.current) {
                    audioRef.current.pause();
                    setIsPlaying(false);
                  }
                }}
                className="absolute top-16 right-4 p-2 bg-slate-900/80 hover:bg-slate-900 border border-slate-700/50 text-white rounded-full transition-all duration-200 pointer-events-auto shadow-lg backdrop-blur-md cursor-pointer z-[130]"
                title="Exit practice"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Stars Indicator in Top-Left Corner */}
              <div className="absolute top-2.5 left-4 z-[130] pointer-events-auto shrink-0 select-none">
                {renderStarsIndicator(
                  conversationScores.reduce((sum, s) => sum + s, 0),
                  5,
                  "bg-slate-900/80 border border-slate-700/50 rounded-2xl p-1.5 sm:p-2 shadow-lg backdrop-blur-md text-left w-[100px] sm:w-[130px]",
                  `Turn ${conversationRound <= 5 ? conversationRound : 5} out of 5 rounds`
                )}
              </div>

              {/* XP Timer in Top-Right Corner next to Close Button */}
              <div className="absolute top-2.5 right-14 z-[130] pointer-events-auto shrink-0 select-none">
                {renderXpTimer("bg-slate-900/80 border border-slate-700/50 rounded-2xl p-1.5 sm:p-2 shadow-lg backdrop-blur-md text-left w-[100px] sm:w-[130px]")}
              </div>

              {/* Level & Round info at the top center */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-auto z-[130] select-none">
                <div className="flex flex-col items-center bg-slate-900/85 border border-slate-700/50 backdrop-blur-md py-1.5 px-4 rounded-2xl shadow-xl text-center">
                  <div className="text-[10px] text-white font-extrabold tracking-tight font-sans whitespace-nowrap leading-none">
                    LEVEL {englishLevel}/10
                  </div>
                  <div className="text-[8px] text-white/60 font-bold tracking-tight font-sans whitespace-nowrap leading-none mt-1">
                    Round {conversationRound <= 5 ? conversationRound : 5}/5
                  </div>
                </div>
              </div>

              {/* Bouncing notification in middle screen */}
              <AnimatePresence>
                {conversationFeedbackNotification && (
                  <motion.div 
                    key="conversation-feedback-notification"
                    initial={{ scale: 0.7, opacity: 0, y: 15 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.8, opacity: 0, y: -20 }}
                    className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-none z-50 text-center select-none"
                  >
                    {conversationFeedbackNotification.score === 1.0 ? (
                      <motion.div 
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.5, repeat: 1 }}
                        className="w-16 h-16 bg-emerald-500/95 backdrop-blur-md rounded-full flex items-center justify-center border-4 border-white shadow-[0_10px_25px_rgba(16,185,129,0.5)] mb-3"
                      >
                        <CheckCircle2 className="w-9 h-9 text-white" />
                      </motion.div>
                    ) : conversationFeedbackNotification.score === 0.5 ? (
                      <motion.div 
                        animate={{ scale: [1, 1.15, 1] }}
                        transition={{ duration: 0.5 }}
                        className="w-16 h-16 bg-amber-500/95 backdrop-blur-md rounded-full flex items-center justify-center border-4 border-white shadow-[0_10px_25px_rgba(245,158,11,0.5)] mb-3"
                      >
                        <Sparkles className="w-9 h-9 text-white" />
                      </motion.div>
                    ) : (
                      <motion.div 
                        className="w-16 h-16 bg-rose-500/95 backdrop-blur-md rounded-full flex items-center justify-center border-4 border-white shadow-[0_10px_25px_rgba(239,68,68,0.5)] mb-3"
                      >
                        <X className="w-9 h-9 text-white" />
                      </motion.div>
                    )}

                    <div className="bg-slate-950/85 backdrop-blur-md text-white text-xs font-bold px-4 py-2.5 rounded-2xl border border-white/20 shadow-2xl max-w-sm pointer-events-auto">
                      <div className={`text-xs font-black tracking-wide mb-1 ${
                        conversationFeedbackNotification.score === 1.0 ? "text-emerald-400" :
                        conversationFeedbackNotification.score === 0.5 ? "text-amber-400" : "text-rose-450"
                      }`}>
                        {conversationFeedbackNotification.score === 1.0 ? "Excellent! 🎉 (+1.0 ⭐)" :
                         conversationFeedbackNotification.score === 0.5 ? "Good effort! 👍 (+0.5 ⭐)" : "Review & Practice 📚 (+0 ⭐)"}
                      </div>
                      <p className="font-sans leading-normal text-slate-100">{conversationFeedbackNotification.feedback}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Space fillers */}
              <div className="flex-1" />

              {/* Subtitles & Audio/Microphone action at the absolute bottom */}
              <div className="w-full max-w-xl mx-auto px-4 pb-6 pointer-events-auto flex flex-col items-center gap-4">
                {latestKokoroMsg && (
                  <div className="w-full bg-slate-950/65 backdrop-blur-md border border-white/10 rounded-2xl p-4 text-center text-white shadow-2xl flex flex-col gap-1.5 select-text relative">
                    <span className="text-[10px] tracking-widest font-black text-purple-400 uppercase leading-none mb-0.5">Kokoro</span>
                    <p className="text-sm md:text-base font-bold leading-relaxed font-sans max-h-[140px] overflow-y-auto pr-1">
                      {latestKokoroMsg.text}
                    </p>
                    {latestUserMsg && (
                      <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-center gap-1.5 select-none animate-pulse">
                        <span className="text-[9px] uppercase font-bold text-slate-400">Tú:</span>
                        <span className="text-xs font-medium text-slate-300 truncate max-w-xs italic font-sans">
                          "{latestUserMsg.text}"
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Microphone Trigger and Loading Status */}
                {conversationRound <= 5 ? (
                  <div className="flex flex-col items-center gap-2">
                    {isMyTurnToSpeak ? (
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={toggleListening}
                          className={`w-16 h-16 rounded-full flex items-center justify-center cursor-pointer transition-all ${
                            isListening 
                              ? 'bg-purple-500 text-white shadow-[0_0_25px_rgba(168,85,247,0.7)] animate-pulse border-4 border-purple-300' 
                              : 'bg-white/95 hover:bg-white text-purple-600 shadow-xl hover:scale-105 active:scale-95 border border-purple-100'
                          }`}
                        >
                          <Mic className="w-7 h-7" />
                        </button>
                        <p className="text-[10px] font-black text-white text-shadow px-3 py-1 bg-black/45 backdrop-blur-sm rounded-full leading-none select-none tracking-wide uppercase">
                          {isListening ? '🎙️ Listening... Speak now! 📢' : '🎤 Press to respond 🗣️'}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-slate-950/50 backdrop-blur-sm rounded-full py-2 px-5 flex items-center gap-2 border border-white/10 select-none text-white/90">
                        <Loader2 className="w-3.5 h-3.5 text-purple-300 animate-spin" />
                        <span className="text-xs font-extrabold tracking-wide leading-none font-sans">
                          {isConversationLoading ? conversationStatusMessage : 'Kokoro is speaking... 🗣️💬'}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  // Summary Screen (Semi-transparent floating card)
                  <div className="w-full bg-gradient-to-br from-indigo-950/70 to-purple-950/70 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 text-center text-white shadow-2xl flex flex-col items-center gap-2 select-none pointer-events-auto">
                    <Award className="w-12 h-12 text-amber-400 mb-0.5 animate-bounce" />
                    <h4 className="text-base font-black tracking-tight text-white uppercase">Practice Completed! 🎉👏</h4>

                    {(() => {
                      const finalStars = conversationScores.reduce((sum, s) => sum + s, 0);
                      let verdictText = "Needs practice";
                      let badgeColor = "bg-rose-500/20 text-rose-300 border-rose-500/30";

                      if (finalStars <= 1.5) {
                        verdictText = "Needs Practice ⭐";
                        badgeColor = "bg-rose-500/20 text-rose-300 border-rose-500/30";
                      } else if (finalStars <= 2.5) {
                        verdictText = "Getting Better ⭐⭐";
                        badgeColor = "bg-orange-500/20 text-orange-300 border-orange-500/30";
                      } else if (finalStars <= 3.5) {
                        verdictText = "Good Effort ⭐⭐⭐";
                        badgeColor = "bg-yellow-500/20 text-yellow-300 border-yellow-500/30";
                      } else if (finalStars <= 4.5) {
                        verdictText = "Great Job ⭐⭐⭐⭐";
                        badgeColor = "bg-blue-500/20 text-blue-300 border-blue-500/30";
                      } else {
                        verdictText = "Excellent Practice! ⭐⭐⭐⭐⭐";
                        badgeColor = "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
                      }

                      return (
                        <div className="flex flex-col items-center mt-1 w-full gap-2 font-sans">
                          <span className="text-lg font-black tracking-tight">{finalStars.toFixed(1)} / 5.0 Stars ⭐</span>
                          <span className={`px-4 py-1 rounded-full font-black text-xs border tracking-wide uppercase ${badgeColor}`}>
                            {verdictText}
                          </span>
                        </div>
                      );
                    })()}

                    <button
                      onClick={() => {
                        const finalStars = conversationScores.reduce((sum, s) => sum + s, 0);
                        const starsToAward = Math.round(finalStars);
                        
                        const hash = getStoryHash(spokenStory);
                        const conversationStarKey = `kokoro_star_${englishLevel}_conversation_check_${hash}`;
                        const conversationScoreKey = `kokoro_conversation_stars_${englishLevel}_${hash}`;
                        const previousConversationBest = parseInt(localStorage.getItem(conversationScoreKey) || '0', 10);
                        
                        localStorage.setItem(conversationStarKey, 'true');
                        if (starsToAward > previousConversationBest) {
                          const delta = starsToAward - previousConversationBest;
                          localStorage.setItem(conversationScoreKey, starsToAward.toString());
                          playSuccessShinChime();
                          setStarsCount(prev => {
                            const nextStars = prev + delta;
                            localStorage.setItem('kokoro_stars_count', nextStars.toString());
                            syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, nextStars);
                            return nextStars;
                          });
                        }

                        const scorePercent = Math.round((finalStars / 5) * 100);
                        setSpeakingGrade(prev => {
                          const next = Math.max(prev, scorePercent);
                          localStorage.setItem('kokoro_stat_speaking', next.toString());
                          return next;
                        });
                        setListeningGrade(prev => {
                          const next = Math.max(prev, scorePercent);
                          localStorage.setItem('kokoro_stat_listening', next.toString());
                          syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, { speaking: Math.max(speakingGrade, scorePercent), listening: next });
                          return next;
                        });

                        setActivePracticePanel(null);
                        if (audioRef.current) {
                          audioRef.current.pause();
                          setIsPlaying(false);
                        }
                      }}
                      className="mt-4 px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-95 text-white font-extrabold rounded-2xl text-xs shadow-md transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Finish Practice 🏁🏆
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })()}

        {activePracticePanel === 'speaking' && (() => {
          const expectedWords = getCleanWordsArray(spokenStory);
          const spokenWords = getCleanWordsArray(speakingTranscript);
          const { alignedSpoken } = alignSpeech(expectedWords, spokenWords);
          const correctWordsCount = alignedSpoken.filter(w => w.isCorrect).length;
          const extraWordsCount = alignedSpoken.filter(w => !w.isCorrect).length;

          let currentStars = 0;
          if (expectedWords.length > 0 && correctWordsCount > 0) {
            if (correctWordsCount === expectedWords.length && extraWordsCount === 0) {
              currentStars = 5;
            } else {
              const netScore = Math.max(0, correctWordsCount - extraWordsCount);
              const ratio = netScore / expectedWords.length;
              if (ratio >= 0.85) {
                currentStars = 4;
              } else if (ratio >= 0.65) {
                currentStars = 3;
              } else if (ratio >= 0.45) {
                currentStars = 2;
              } else if (ratio >= 0.20) {
                currentStars = 1;
              } else {
                currentStars = 0;
              }
            }
          }

          const isFullStars = currentStars === 5;
          const starDescription = spokenStory 
            ? `${correctWordsCount}/${expectedWords.length} correct 🎯${extraWordsCount > 0 ? ` (-${extraWordsCount} extra ⚠️)` : ''}${isFullStars ? ' ✨' : ''}`
            : "Practice speaking to win stars ⭐";

          return (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-3 md:p-4 backdrop-blur-sm pointer-events-auto"
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 30 }}
                transition={{ type: "spring", damping: 25, stiffness: 350 }}
                className="bg-white rounded-3xl p-4 md:p-6 max-w-xl w-full shadow-2xl border border-pink-100 relative max-h-[92vh] flex flex-col pt-24"
              >
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-pink-400 via-rose-400 to-indigo-500 rounded-t-3xl" />
                
                {/* Stars Indicator in Top-Left Corner */}
                <div className="absolute top-4 left-4 z-20">
                  {renderStarsIndicator(
                    maxStarsAchieved,
                    5,
                    "bg-amber-50/20 border border-amber-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs",
                    starDescription
                  )}
                </div>

                {/* XP Timer in Top-Right Corner */}
                <div className="absolute top-4 right-4 z-20">
                  {renderXpTimer("bg-indigo-50/20 border border-indigo-100/40 rounded-2xl p-2 shadow-xs text-left backdrop-blur-xs")}
                </div>

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 mb-3 select-none gap-3 sm:gap-2">
                  <div className="flex items-center gap-2.5 font-sans">
                    <div className="w-9 h-9 bg-pink-50 rounded-lg flex items-center justify-center border border-pink-100">
                      <Mic className="w-4 h-4 text-pink-500 animate-pulse" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-base font-extrabold text-slate-800 tracking-tight">Speaking Practice</h3>
                      <p className="text-[10px] text-slate-400 font-medium font-sans leading-none mt-0.5">Practice pronunciation offline</p>
                    </div>
                  </div>
                </div>

              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto pr-1 py-1">
                {spokenStory ? (
                  <div className="space-y-4">
                    {/* The Expected block & Mic side by side or compact list */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-stretch">
                      {/* Left: Original Story (3 cols) */}
                      <div className="md:col-span-3 text-left flex flex-col">
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 font-mono">1. Read Out Loud 🗣️📖</div>
                          <button
                            onClick={() => {
                              if (audioRef.current) {
                                let validSrc = audioRef.current.src;
                                const isWebpHref = !validSrc || validSrc === "" || (validSrc.startsWith('http') && !validSrc.includes('blob:') && !validSrc.endsWith('.wav') && !validSrc.endsWith('.mp3'));
                                if (isWebpHref) {
                                  const foundItem = history.find(h => h.text && h.text.trim() === spokenStory.trim());
                                  if (foundItem && foundItem.audioUrl) {
                                    validSrc = foundItem.audioUrl;
                                  } else if (storyAudioUrl) {
                                    validSrc = storyAudioUrl;
                                  } else if (history.length > 0 && history[0].audioUrl) {
                                    validSrc = history[0].audioUrl;
                                  }
                                  if (validSrc) {
                                    audioRef.current.src = validSrc;
                                  }
                                }
                                
                                if (audioRef.current.src && !(audioRef.current.src.startsWith('http') && !audioRef.current.src.includes('blob:') && !audioRef.current.src.endsWith('.wav') && !audioRef.current.src.endsWith('.mp3'))) {
                                   audioRef.current.preservesPitch = false;
                                   (audioRef.current as any).mozPreservesPitch = false;
                                   (audioRef.current as any).webkitPreservesPitch = false;
                                   audioRef.current.playbackRate = 1.20;
                                   audioRef.current.currentTime = 0;
                                   const playPromise = audioRef.current.play();
                                   if (playPromise !== undefined) {
                                       playPromise.then(() => setIsPlaying(true)).catch(e => { console.error("Play error:", e); setIsPlaying(false); });
                                   } else {
                                       setIsPlaying(true);
                                   }
                                }
                              }
                            }}
                            title="Listen again"
                            className="px-2 py-0.5 bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-650 rounded-lg border border-slate-200/60 transition-all flex items-center gap-1 text-[9px] font-bold cursor-pointer select-none"
                          >
                            <Headphones className="w-2.5 h-2.5 text-emerald-600" />
                            <span>Listen again</span>
                          </button>
                        </div>
                        <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100 leading-relaxed text-xs md:text-sm text-slate-700 font-semibold overflow-y-auto max-h-[100px] md:max-h-[130px] whitespace-pre-wrap">
                          {spokenStory}
                        </div>
                      </div>

                      {/* Right: The entire block acts as a button now! */}
                      <button
                        onClick={toggleSpeakingPracticeListening}
                        className={`md:col-span-1 p-2 rounded-xl border transition-all duration-300 flex flex-col items-center justify-center text-center gap-1 cursor-pointer select-none relative focus:outline-none focus:ring-2 focus:ring-pink-500/30 overflow-hidden md:min-h-[80px] ${
                          isSpeakingListening 
                            ? 'bg-gradient-to-br from-pink-500 to-rose-500 text-white border-transparent shadow-[0_4px_12px_rgba(236,72,153,0.35)]' 
                            : 'bg-gradient-to-br from-pink-50 to-pink-100/60 hover:from-pink-100 hover:to-pink-200/50 border-pink-200/60 text-pink-700 hover:scale-[1.03] active:scale-[0.97]'
                        }`}
                        title={isSpeakingListening ? "Click to stop ⏹️" : "Click to start speaking 🗣️"}
                      >
                        {isSpeakingListening && (
                          <span className="absolute inset-0 bg-white/10 animate-pulse" />
                        )}
                        <div className="relative z-10">
                          {isSpeakingListening && (
                            <span className="absolute inset-0 bg-white/25 rounded-full animate-ping scale-110" />
                          )}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-xs ${
                            isSpeakingListening ? 'bg-white/20 text-white' : 'bg-white text-pink-500 border border-pink-100'
                          }`}>
                            <Mic className={`w-3.5 h-3.5 ${isSpeakingListening ? 'animate-pulse' : ''}`} />
                          </div>
                        </div>
                        <div className="relative z-10 flex flex-col items-center">
                          <span className="text-[10px] font-extrabold uppercase tracking-widest leading-none">
                            {isSpeakingListening ? 'Listening 🎙️' : 'Speak 🗣️'}
                          </span>
                          <span className={`text-[8px] mt-0.5 font-bold leading-none ${isSpeakingListening ? 'text-pink-100 animate-pulse' : 'text-pink-500'}`}>
                            {isSpeakingListening ? 'Stop ⏹️' : 'Start ▶️'}
                          </span>
                        </div>
                      </button>
                    </div>

                    {/* The Spoken and Compared block */}
                    <div className="text-left">
                      <div className="flex items-center justify-between mb-1.5 border-t border-slate-100 pt-3">
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 font-mono">2. Tu Pronunciación (Your Speech)</div>
                        {speakingTranscript && (
                          <button
                            onClick={() => {
                              setSpeakingTranscript('');
                              setMaxStarsAchieved(0);
                            }}
                            className="text-[10px] text-rose-500 hover:text-rose-700 font-bold flex items-center gap-1 hover:underline cursor-pointer bg-transparent border-none p-0 outline-none"
                          >
                            <RefreshCw className="w-2.5 h-2.5" /> Retry 🔄
                          </button>
                        )}
                      </div>

                      {speakingTranscript ? (
                        <div className="p-3 bg-slate-950 text-white rounded-xl border border-slate-900 leading-relaxed shadow-inner max-h-[160px] overflow-y-auto">
                          <div className="flex flex-wrap gap-1.5">
                            {(() => {
                              const expectedWordsOnly = getCleanWordsArray(spokenStory);
                              const spokenWordsOnly = getCleanWordsArray(speakingTranscript);
                              const { alignedSpoken } = alignSpeech(expectedWordsOnly, spokenWordsOnly);
                              
                              // Build sequence of correct, incorrect, and omitted items
                              const renderItems: (
                                | { type: 'spoken'; word: string; isCorrect: boolean; spokenIndex: number; isSubstitution?: boolean; expectedWord?: string; }
                                | { type: 'omitted'; word: string; originalIndex: number }
                              )[] = [];
                              
                              let expectedPtr = 0;
                              alignedSpoken.forEach((spokenItem, spokenIdx) => {
                                if (spokenItem.matchedExpectedIdx !== null) {
                                  // Skip gap as omitted
                                  while (expectedPtr < spokenItem.matchedExpectedIdx) {
                                    renderItems.push({
                                      type: 'omitted',
                                      word: expectedWordsOnly[expectedPtr],
                                      originalIndex: expectedPtr,
                                    });
                                    expectedPtr++;
                                  }
                                  renderItems.push({
                                    type: 'spoken',
                                    word: spokenItem.word,
                                    isCorrect: spokenItem.isCorrect,
                                    spokenIndex: spokenIdx,
                                    isSubstitution: !spokenItem.isCorrect,
                                    expectedWord: !spokenItem.isCorrect ? expectedWordsOnly[spokenItem.matchedExpectedIdx] : undefined,
                                  });
                                  expectedPtr = spokenItem.matchedExpectedIdx + 1;
                                } else {
                                  renderItems.push({
                                    type: 'spoken',
                                    word: spokenItem.word,
                                    isCorrect: false,
                                    spokenIndex: spokenIdx,
                                  });
                                }
                              });
                              
                              // If there are words remaining at the end and they at least said some words
                              if (spokenWordsOnly.length > 0) {
                                while (expectedPtr < expectedWordsOnly.length) {
                                  renderItems.push({
                                    type: 'omitted',
                                    word: expectedWordsOnly[expectedPtr],
                                    originalIndex: expectedPtr,
                                  });
                                  expectedPtr++;
                                }
                              }
                              
                              return renderItems.map((item, idx) => {
                                if (item.type === 'omitted') {
                                  return (
                                    <div 
                                      key={`omit-${idx}`}
                                      className="inline-flex flex-col items-center justify-center px-1.5 py-0.5 rounded-lg border font-bold text-xs shadow-xs transition-all duration-150 select-none bg-sky-500/10 border-sky-500/30 text-sky-300"
                                      title="Omitted word"
                                    >
                                      <span className="font-mono text-[7px] text-sky-400 select-none pb-0.5 border-b border-dashed border-sky-800/40 w-full text-center leading-none mb-0.5">
                                        Omitted
                                      </span>
                                      <span className="leading-tight line-through decoration-sky-500/40 text-sky-200/90">{item.word}</span>
                                    </div>
                                  );
                                } else {
                                  if (item.type !== 'spoken') return null; // Should not happen
                                  const isCorrect = item.isCorrect;
                                  const isSubstitution = item.isSubstitution;
                                  return (
                                    <div 
                                      key={`spoken-${idx}`}
                                      className={`inline-flex flex-col items-center justify-center px-1.5 py-0.5 rounded-lg border font-bold text-xs shadow-xs transition-all duration-150 select-none ${
                                        isCorrect 
                                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' 
                                          : isSubstitution
                                            ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                                            : 'bg-rose-500/10 border-rose-500/30 text-rose-300 animate-pulse'
                                      }`}
                                    >
                                      <span className="font-mono text-[7px] text-slate-500 select-none pb-0.5 border-b border-dashed border-slate-800 w-full text-center leading-none mb-0.5">
                                        {isSubstitution && item.expectedWord ? (
                                          <span className="text-amber-500">was: {item.expectedWord}</span>
                                        ) : (
                                          `#${item.spokenIndex + 1}`
                                        )}
                                      </span>
                                      <span className={`leading-tight ${isSubstitution ? 'line-through decoration-amber-500/50' : ''}`}>{item.word}</span>
                                      {!isCorrect && (
                                        <span className={`text-[6.5px] font-semibold mt-0.5 uppercase font-mono tracking-wide italic leading-none max-w-[85px] truncate text-center ${isSubstitution ? 'text-amber-500/90' : 'text-rose-400/90'}`}>
                                          {isSubstitution ? 'Mispronounced' : 'Extra'}
                                        </span>
                                      )}
                                    </div>
                                  );
                                }
                              });
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 text-center bg-slate-50/50 rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center py-5 select-none">
                          <Languages className="w-5 h-5 text-slate-300 mb-1 animate-pulse" />
                          <h4 className="text-[10px] font-bold text-slate-600 mb-0.5">Waiting for your voice</h4>
                          <p className="text-[9px] text-slate-400 max-w-xs leading-relaxed text-center">
                            Press the microphone button on the right and read the story.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 py-8 select-none">
                    <Sparkles className="w-8 h-8 text-slate-300 mb-2 animate-pulse" />
                    <h4 className="text-xs font-bold text-slate-700 mb-1">No story told yet</h4>
                    <p className="text-[10px] text-slate-400 max-w-xs leading-relaxed">
                      Choose a conversation topic and a level in the TOPICS menu to listen to Kokoro's story first!
                    </p>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="border-t border-slate-100 pt-3 mt-3 flex justify-end">
                <button
                  onClick={() => {
                    setActivePracticePanel(null);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-800 hover:to-slate-900 rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer font-sans"
                >
                  Close Practice Panel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )})()}

        {isListeningPracticeActive && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] w-full max-w-[95%] md:max-w-md pointer-events-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-950/90 backdrop-blur-md border border-slate-800 p-5 rounded-3xl shadow-2xl flex flex-col gap-4 text-white relative pt-24"
            >
              {/* Stars Indicator in Top-Left Corner */}
              <div className="absolute top-2.5 left-4 z-20">
                {renderStarsIndicator(
                  listeningCompletedCount,
                  3,
                  "bg-slate-900/80 border border-slate-800 rounded-2xl p-1.5 sm:p-2 text-left w-[100px] sm:w-[130px]",
                  listeningStarEarned ? "Star earned! 🎉" : `${listeningCompletedCount}/3 completed`
                )}
              </div>

              {/* XP Timer in Top-Right Corner */}
              <div className="absolute top-2.5 right-4 z-20">
                {renderXpTimer("bg-slate-900/80 border border-slate-800 rounded-2xl p-1.5 sm:p-2 text-left w-[100px] sm:w-[130px]")}
              </div>

              <div className="flex items-center gap-3">
                 <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center border border-blue-500/30 shrink-0">
                    <Headphones className="w-6 h-6 text-blue-400 animate-pulse" />
                 </div>
                 <div className="text-left flex-1 font-sans">
                    <div>
                       <h3 className="text-sm font-extrabold text-white tracking-tight">Listening Practice 🎧🔊</h3>
                    </div>
                    <p className="text-[11px] text-slate-300 font-medium leading-tight mt-1">
                      {isListeningPlaybackFinished 
                        ? "Great job! 🎉 You have finished listening to the story for this round. 👏🌟"
                        : "Listen carefully 🎧 to the story from Kokoro 🙋‍♀️👧."
                      }
                    </p>
                 </div>
              </div>

              {!isListeningPlaybackFinished ? (
                /* Playing state: Show animation waveform and stop action */
                <div className="flex flex-col gap-3">
                  <div className="bg-slate-900/60 border border-slate-850 rounded-2xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-ping" />
                      <span className="text-xs font-bold text-blue-300">Kokoro reading... 📖👧</span>
                    </div>
                    {/* CSS Music waveform */}
                    <div className="flex items-end gap-1 h-4">
                      <span className="w-1 bg-blue-400 rounded-full animate-[bounce_1.2s_infinite] h-full" style={{ animationDelay: '0.1s' }} />
                      <span className="w-1 bg-blue-400 rounded-full animate-[bounce_1.2s_infinite] h-2/3" style={{ animationDelay: '0.4s' }} />
                      <span className="w-1 bg-blue-400 rounded-full animate-[bounce_1.2s_infinite] h-2" style={{ animationDelay: '0.2s' }} />
                      <span className="w-1 bg-blue-400 rounded-full animate-[bounce_1.2s_infinite] h-3/4" style={{ animationDelay: '0.6s' }} />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleAbortListening}
                    className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 active:scale-95 text-white font-bold rounded-xl text-xs md:text-sm tracking-wide transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer font-sans"
                  >
                    Stop Practice ⏹️
                  </button>
                </div>
              ) : (
                /* Finished playback state: Show Repeat and Close button */
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleRepeatListening}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-black rounded-2xl text-xs md:text-sm tracking-wide transition-all shadow-lg flex items-center justify-center gap-1.5 cursor-pointer font-sans border border-blue-400/40"
                  >
                    <RefreshCw className="w-4 h-4 animate-[spin_3s_linear_infinite]" />
                    Listen again 🔄🎧
                  </button>
                  <button
                    onClick={handleCloseListening}
                    className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 font-extrabold rounded-2xl text-xs md:text-sm tracking-wide transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer font-sans border border-slate-700"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Close ❌
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {listeningCountdown !== null && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] w-full max-w-[90%] md:max-w-xs pointer-events-none"
          >
            <div className="bg-slate-950/95 backdrop-blur-md border border-slate-800 p-4 rounded-3xl shadow-2xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center border border-blue-500/30">
                  <Headphones className="w-5 h-5 text-blue-400 animate-pulse" />
                </div>
                <div className="text-left">
                  <div className="text-[10px] font-bold text-blue-300 uppercase tracking-widest">Listening 🎧🔊</div>
                  <div className="text-xs font-black text-white leading-tight mt-0.5">The story will repeat in... 🕒🔁</div>
                </div>
              </div>
              <motion.div
                key={listeningCountdown}
                initial={{ scale: 0.3, opacity: 0 }}
                animate={{ scale: [0.3, 1.3, 1], opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-3xl font-black text-blue-400 px-4 py-1 bg-blue-950/50 rounded-2xl border border-blue-500/20"
              >
                {listeningCountdown}
              </motion.div>
            </div>
          </motion.div>
        )}

        {conversationCountdown !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-transparent z-[120] flex flex-col items-center justify-center p-4 pointer-events-auto"
          >
            <div className="flex flex-col items-center justify-center text-center max-w-md">
              <motion.div
                key={conversationCountdown}
                initial={{ scale: 0.2, opacity: 0 }}
                animate={{ scale: [0.2, 1.2, 1], opacity: [0, 1, 1], rotate: [ -15, 5, 0 ] }}
                exit={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.85, ease: "easeOut" }}
                className="text-8xl md:text-9xl font-black text-purple-400 font-sans tracking-tight mb-6 flex items-center justify-center [text-shadow:_0_4px_12px_rgba(0,_0,_0,_1),_0_0_30px_rgba(168,_85,_247,_1),_0_0_50px_rgba(168,_85,_247,_0.8)]"
              >
                {conversationCountdown}
              </motion.div>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="bg-purple-600/40 border-2 border-purple-400/80 text-purple-200 font-mono text-[10px] font-bold tracking-widest uppercase px-3.5 py-1.5 rounded-full flex items-center gap-1.5 shadow-[0_4px_12px_rgba(0,0,0,0.8)] backdrop-blur-sm">
                  <MessagesSquare className="w-3.5 h-3.5 animate-bounce text-purple-300" />
                  <span className="[text-shadow:_0_1px_2px_rgba(0,0,0,0.8)]">Conversation Mode</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-white font-sans mt-3 tracking-tight [text-shadow:_0_2px_8px_rgba(0,_0,_0,_1),_0_0_6px_rgba(0,_0,_0,_1),_0_0_15px_rgba(168,_85,_247,_0.6)]">
                  Get ready to <span className="text-purple-300">chat with Kokoro!</span>
                </h2>
                <p className="text-xs md:text-sm text-purple-100/90 leading-relaxed font-bold mt-1.5 max-w-xs [text-shadow:_0_2px_6px_rgba(0,_0,_0,_1),_0_0_4px_rgba(0,_0,_0,_1)]">
                  Speak clearly and practice interactive conversation skills.
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEVEL LOCKED / EXAM REGISTRATION DIALOG */}
      <AnimatePresence>
        {showLevelLockedModal !== null && (() => {
          const targetLevel = showLevelLockedModal;
          const spec = levelsSpecs.find(s => s.level === targetLevel);
          if (!spec) return null;
          const isEligible = targetLevel === 1 || unlockedLevels.includes(targetLevel - 1);
          
          return (
            <motion.div 
              key="level-locked-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/95 backdrop-blur-md z-[200] flex items-center justify-center p-4 pointer-events-auto"
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 30 }}
                className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-slate-950 border-2 border-slate-800 rounded-3xl p-5 md:p-7 shadow-2xl relative text-white"
              >
                {/* Close Button */}
                <button 
                  onClick={() => setShowLevelLockedModal(null)}
                  className="absolute top-4 right-4 p-2 bg-slate-900 hover:bg-slate-800 rounded-full transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>

                {/* Header Icon */}
                <div className="flex items-center gap-3.5 mb-5 border-b border-slate-900 pb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                    isEligible 
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                      : 'bg-slate-900 text-slate-400 border border-slate-800'
                  }`}>
                    <Award className="w-6 h-6 text-amber-450 fill-amber-450/10" />
                  </div>
                  <div>
                    <span className="text-[9px] font-extrabold tracking-widest text-amber-500 uppercase block mb-0.5">
                      PROGRESSION SYSTEM
                    </span>
                    <h3 className="text-lg md:text-xl font-extrabold tracking-tight text-white leading-tight">
                      Level {spec.level}: {spec.label}
                    </h3>
                  </div>
                </div>

                {/* Level Description & Unlock Paths */}
                <div className="space-y-4 mb-5">
                  <div>
                    <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest block mb-1">
                      Level Description
                    </span>
                    <p className="text-xs text-gray-200 bg-slate-900 p-2.5 rounded-xl border border-slate-800 leading-normal font-semibold">
                      {spec.longDesc}
                    </p>
                  </div>

                  <div className="border-t border-slate-900 pt-3.5">
                    <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest block mb-3">
                      HOW TO UNLOCK THIS LEVEL 🔑
                    </span>
                    
                    <div className="space-y-3">
                      {/* Método 1: Examen Oral */}
                      <div className={`p-3.5 rounded-xl border transition-all ${
                        isEligible 
                          ? 'bg-slate-900 border-amber-500/30 shadow-sm shadow-amber-500/5' 
                          : 'bg-slate-900/60 border-slate-900 opacity-90'
                      }`}>
                        <div className="flex items-center gap-2 mb-2.5">
                          <Mic className={`w-4 h-4 ${isEligible ? 'text-amber-400' : 'text-gray-400'}`} />
                          <h4 className="text-xs font-bold uppercase text-white tracking-wide">
                            Path 1: Oral Certification Exam 🗣️
                          </h4>
                          {isEligible ? (
                            <span className="ml-auto text-[8px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded uppercase tracking-wider">
                              Eligible
                            </span>
                          ) : (
                            <span className="ml-auto text-[8px] font-black bg-slate-950 text-rose-400 border border-slate-800 px-2 py-0.5 rounded uppercase tracking-wider">
                              Locked
                            </span>
                          )}
                        </div>
                        
                        {/* Compact Specs list with Icons */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          <div className="bg-slate-950 p-2 rounded-lg border border-slate-850 flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                            <div>
                              <span className="text-[7px] text-gray-400 uppercase block font-bold leading-none">Rounds</span>
                              <span className="text-[10px] font-bold text-white leading-tight block mt-0.5">{spec.cycles} Dialogues</span>
                            </div>
                          </div>
                          <div className="bg-slate-950 p-2 rounded-lg border border-slate-850 flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                            <div>
                              <span className="text-[7px] text-gray-400 uppercase block font-bold leading-none">Min. Sentence</span>
                              <span className="text-[10px] font-bold text-white leading-tight block mt-0.5">{spec.minWords} Words</span>
                            </div>
                          </div>
                          <div className="bg-slate-950 p-2 rounded-lg border border-slate-850 flex items-center gap-1.5">
                            <Award className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                            <div>
                              <span className="text-[7px] text-gray-400 uppercase block font-bold leading-none">Pass</span>
                              <span className="text-[10px] font-bold text-white leading-tight block mt-0.5">80/100 pts</span>
                            </div>
                          </div>
                        </div>

                        {isEligible ? (
                          <div className="space-y-2">
                            <p className="text-[11px] text-amber-200/90 leading-snug font-medium">
                              Maintain a fluent dialogue with Kokoro answering under the minimum required words. 💬
                            </p>
                            <button
                              onClick={() => {
                                setShowLevelLockedModal(null);
                                startExamSession(targetLevel);
                              }}
                              className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold tracking-wide rounded-lg shadow-lg hover:opacity-95 active:scale-98 transition-all flex items-center justify-center gap-1.5 cursor-pointer text-xs"
                            >
                              <Star className="w-3.5 h-3.5 fill-slate-950" />
                              Take Level {spec.level} Exam
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2 p-2.5 bg-rose-950/20 rounded-lg border border-rose-900/40">
                            <Lock className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />
                            <span className="text-[11px] text-rose-200/95 font-medium leading-snug">
                              Requires passing the previous Level {targetLevel - 1} ({levelsSpecs.find(s => s.level === targetLevel - 1)?.code || ''}).
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Método 2: Hito de XP (No recude puntos) */}
                      {(() => {
                        const requiredXP = 100 * (targetLevel - 1);
                        return (
                          <div className={`p-3.5 rounded-xl border transition-all ${
                            masteryTokens >= requiredXP && isEligible
                              ? 'bg-purple-900/30 border-purple-500/40 shadow-sm shadow-purple-500/10' 
                              : 'border-slate-900 bg-slate-900/40'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-4 h-4 text-purple-400" />
                              <h4 className="text-xs font-bold uppercase text-white tracking-wide">
                                Path 2: XP Milestone 📈
                              </h4>
                              {masteryTokens >= requiredXP && isEligible ? (
                                <span className="ml-auto text-[8px] font-black bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded uppercase tracking-wider">
                                  Available
                                </span>
                              ) : (
                                <span className="ml-auto flex items-center gap-1 text-[9px] font-black bg-slate-900 text-slate-400 border border-slate-800 px-2 py-0.5 rounded uppercase tracking-wider">
                                  <Lock className="w-2.5 h-2.5" /> {requiredXP} req.
                                </span>
                              )}
                            </div>
                            
                            <p className="text-[11px] text-gray-300 leading-snug font-medium mb-3">
                              Reach <strong className="text-purple-300">{requiredXP} accumulated XP points</strong>. Your points <strong>will not be deducted</strong>. 🚀
                            </p>

                            {masteryTokens >= requiredXP && isEligible ? (
                              <button
                                onClick={() => {
                                  // Unlock level
                                  const updatedunlocked = Array.from(new Set([...unlockedLevels, targetLevel]));
                                  setUnlockedLevels(updatedunlocked);
                                  localStorage.setItem('kokoro_unlocked_levels', JSON.stringify(updatedunlocked));
                                  // DO NOT deduct points
                                  syncProgressToCloud(updatedunlocked, masteryTokens, englishLevel);
                                  // Close modal
                                  setShowLevelLockedModal(null);
                                  setUnlockedSuccessAnim({ level: targetLevel, method: 'xp' });
                                }}
                                className="w-full py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-extrabold tracking-wide rounded-lg shadow-lg hover:opacity-95 active:scale-98 transition-all flex items-center justify-center gap-1.5 cursor-pointer text-xs"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                                Unlock (Does not spend XP)
                              </button>
                            ) : (
                              <div className="text-[10px] text-purple-300/80 font-bold italic flex items-center gap-1">
                                <span>✨</span> You have {masteryTokens} / {requiredXP} accumulated XP.
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Método 3: Canje de Estrellas (Sí se reducen) */}
                      {(() => {
                        const requiredStars = 100;
                        return (
                          <div className={`p-3.5 rounded-xl border transition-all ${
                            starsCount >= requiredStars && isEligible
                              ? 'bg-yellow-900/30 border-yellow-500/40 shadow-sm shadow-yellow-500/10' 
                              : 'border-slate-900 bg-slate-900/40'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <h4 className="text-xs font-bold uppercase text-white tracking-wide">
                                Path 3: Stars ⭐
                              </h4>
                              {starsCount >= requiredStars && isEligible ? (
                                <span className="ml-auto text-[8px] font-black bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded uppercase tracking-wider">
                                  Available
                                </span>
                              ) : (
                                <span className="ml-auto flex items-center gap-1 text-[9px] font-black bg-slate-900 text-slate-400 border border-slate-800 px-2 py-0.5 rounded uppercase tracking-wider">
                                  <Lock className="w-2.5 h-2.5" /> {requiredStars} req.
                                </span>
                              )}
                            </div>
                            
                            <p className="text-[11px] text-gray-300 leading-snug font-medium mb-3">
                              Exchange <strong className="text-yellow-400">100 stars</strong>. These <strong className="text-rose-400">will be deducted</strong> from your current account. ✨
                            </p>

                            {starsCount >= requiredStars && isEligible ? (
                              <button
                                onClick={() => {
                                  // Unlock level
                                  const updatedunlocked = Array.from(new Set([...unlockedLevels, targetLevel]));
                                  setUnlockedLevels(updatedunlocked);
                                  localStorage.setItem('kokoro_unlocked_levels', JSON.stringify(updatedunlocked));
                                  
                                  // Deduct stars
                                  const newStars = starsCount - requiredStars;
                                  setStarsCount(newStars);
                                  localStorage.setItem('kokoro_stars', newStars.toString());
                                  
                                  syncProgressToCloud(updatedunlocked, masteryTokens, englishLevel, undefined, undefined, newStars);
                                  // Close modal
                                  setShowLevelLockedModal(null);
                                  setUnlockedSuccessAnim({ level: targetLevel, method: 'stars' });
                                }}
                                className="w-full py-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-amber-950 font-extrabold tracking-wide rounded-lg shadow-lg hover:opacity-95 active:scale-98 transition-all flex items-center justify-center gap-1.5 cursor-pointer text-xs"
                              >
                                <Star className="w-3.5 h-3.5 fill-amber-950" />
                                Exchange {requiredStars} Stars
                              </button>
                            ) : (
                              <div className="text-[10px] text-yellow-300/80 font-bold italic flex items-center gap-1">
                                <span>⭐</span> You have {starsCount} / {requiredStars} stars.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* OVERVIEW PROGRESSION MENU MODAL */}
      <AnimatePresence>
        {isProgressionMenuOpen && (
          <motion.div 
            key="progression-menu-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-indigo-900/40 backdrop-blur-[2px] z-[200] flex items-center justify-center p-3 md:p-4 pointer-events-auto"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="w-full max-w-3xl relative mt-6"
            >
              {/* Anime School Notebook Binder Tabs */}
              <div className="absolute -top-[28px] md:-top-[34px] left-6 right-6 md:right-auto md:left-8 flex gap-1 z-0">
                 <button 
                   onClick={() => setActiveProgressionTab('account')}
                   className={`flex-1 md:w-24 h-7 md:h-9 ${activeProgressionTab === 'account' ? 'bg-[#fafbff] border-[#fafbff] opacity-100 z-10 relative' : 'bg-white/60 border-indigo-100 opacity-65 hover:opacity-100 hover:bg-white relative'} border-2 border-b-0 rounded-t-xl flex items-center justify-center shadow-sm transition-all cursor-pointer px-2`}
                 >
                   <span className={`text-[9px] md:text-[11px] font-black tracking-wider uppercase ${activeProgressionTab === 'account' ? 'text-indigo-600' : 'text-slate-500'}`}>Account</span>
                 </button>
                 <button 
                   onClick={() => setActiveProgressionTab('progress')}
                   className={`flex-1 md:w-24 h-7 md:h-9 ${activeProgressionTab === 'progress' ? 'bg-[#fafbff] border-[#fafbff] opacity-100 z-10 relative' : 'bg-white/60 border-indigo-100 opacity-65 hover:opacity-100 hover:bg-white relative'} border-2 border-b-0 rounded-t-xl flex items-center justify-center shadow-sm transition-all cursor-pointer px-2`}
                 >
                   <span className={`text-[9px] md:text-[11px] font-black tracking-wider uppercase ${activeProgressionTab === 'progress' ? 'text-indigo-600' : 'text-slate-500'}`}>Progress</span>
                 </button>
                 <button 
                   onClick={() => setActiveProgressionTab('rewards')}
                   className={`flex-1 md:w-24 h-7 md:h-9 ${activeProgressionTab === 'rewards' ? 'bg-[#fafbff] border-[#fafbff] opacity-100 z-10 relative' : 'bg-white/60 border-indigo-100 opacity-65 hover:opacity-100 hover:bg-white relative'} border-2 border-b-0 rounded-t-xl flex items-center justify-center shadow-sm transition-all cursor-pointer px-2`}
                 >
                   <span className={`text-[9px] md:text-[11px] font-black tracking-wider uppercase flex items-center justify-center gap-1 ${activeProgressionTab === 'rewards' ? 'text-indigo-600' : 'text-slate-500'}`}>
                     <Star className="w-2.5 h-2.5 md:w-3 md:h-3 fill-yellow-400 text-yellow-400" />
                     Rewards
                   </span>
                 </button>
              </div>
              
              {/* Notebook Paper Body */}
              <div className="w-full bg-[#fafbff] border-4 border-white rounded-3xl p-4 shadow-[0_15px_40px_rgba(79,70,229,0.15)] relative z-10 flex flex-col gap-3 overflow-hidden">
                 
                 {/* Cute Background Pattern (Dots) */}
                 <div className="absolute inset-0 opacity-[0.4] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#c7d2fe 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />

                 {/* Top Right Decorative Tape & Close Button */}
                 <div className="absolute top-3 right-3 flex items-center z-30">
                   <div className="absolute -top-2 -right-1 w-12 h-4 bg-indigo-200/50 -rotate-45 shadow-sm mix-blend-multiply" />
                   <button 
                     onClick={() => setIsProgressionMenuOpen(false)}
                     className="w-8 h-8 bg-white text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full flex items-center justify-center shadow-md border border-indigo-100 transition-all cursor-pointer relative z-40 transform hover:scale-105"
                   >
                     <X className="w-4 h-4" />
                   </button>
                 </div>

                 {/* Header & Logo */}
                 <div className="flex flex-col items-center relative z-20 mt-1 mb-2">
                   <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-[0_4px_15px_rgba(99,102,241,0.2)] border-4 border-indigo-50 z-10 mb-2 rotate-[-2deg]">
                     <img 
                       src="/logo.png" 
                       alt="Story Loop Logo"
                       className="w-full h-full object-cover scale-[1.05]" 
                       style={{ clipPath: 'circle(49% at 50% 50%)' }}
                     />
                   </div>
                   <h3 className="text-xl md:text-2xl font-black tracking-tight text-slate-700 leading-none">
                     {activeProgressionTab === 'account' ? 'Google Account 👤' : activeProgressionTab === 'progress' ? 'Progress Dashboard 📈' : 'Rewards Store 🎁'}
                   </h3>
                 </div>

                 {/* Main Content Layout with Uniform Height */}
                 <div className="w-full h-[380px] md:h-[440px] overflow-y-auto pr-1 relative z-20 flex flex-col gap-3">
                   {activeProgressionTab === 'account' ? (
                     <div className="flex flex-col gap-3 md:gap-4 relative z-20 w-full">
                       {isLoggingIn ? (
                         <div className="bg-white rounded-2xl p-5 border-2 border-indigo-50 shadow-sm flex flex-col items-center justify-center min-h-[300px]">
                           <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-4" />
                           <p className="text-xs font-bold text-slate-500 tracking-wider uppercase">Authenticating... 🔑⏳</p>
                         </div>
                       ) : isLoggedIn ? (
                        <div className="bg-white rounded-2xl p-5 border-2 border-indigo-50 shadow-sm relative overflow-hidden flex flex-col items-center justify-center min-h-[300px]">
                           {/* Decorative subtle background waves */}
                           <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />
                           <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />

                           <div className="relative mb-3">
                             <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-full overflow-hidden flex items-center justify-center border-[4px] border-indigo-50 shadow-md">
                               {userAvatar ? (
                                  <img src={userAvatar} alt="Avatar" className="w-full h-full object-cover" />
                               ) : (
                                  <span className="text-3xl font-black text-indigo-500">{userName.charAt(0).toUpperCase()}</span>
                               )}
                             </div>
                             {/* Sleek Google icon badge */}
                             <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-md border border-slate-150 flex items-center justify-center">
                               <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                                 <path
                                   fill="#4285F4"
                                   d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                 />
                                 <path
                                   fill="#34A853"
                                   d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                 />
                                 <path
                                   fill="#FBBC05"
                                   d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22c-.62-.62-1.03-1.63-1.03-2.63z"
                                 />
                                 <path
                                   fill="#EA4335"
                                   d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                                 />
                               </svg>
                             </div>
                           </div>

                           <h4 className="text-xl font-black text-slate-800 mb-0.5 tracking-tight">{userName}</h4>
                           <p className="text-xs font-semibold text-slate-400 mb-4 tracking-wide">{userEmail}</p>
                           
                           {/* Cute Google Connected Badge */}
                           <div className="bg-emerald-50 border border-emerald-100 rounded-full px-3.5 py-1 flex items-center gap-1.5 mb-5 shadow-sm">
                             <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                             <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Signed in with Google 🟢🔗</span>
                           </div>

                           <p className="text-xs text-indigo-400 font-bold mb-5 text-center max-w-xs leading-normal">
                             Your learning progress, lesson levels, and accumulated stars are synced and secured! ☁️🔒
                           </p>

                           <div className="w-full max-w-xs flex flex-col gap-2">
                              <button 
                                onClick={handleGoogleSignOut}
                                className="w-full py-2.5 bg-white hover:bg-slate-50 text-slate-600 border-2 border-slate-200 font-bold rounded-xl shadow-sm transition-all text-xs flex justify-center items-center gap-1.5 cursor-pointer transform active:scale-98"
                              >
                                Cerrar Sesión
                              </button>
                              <button 
                                onClick={handleGoogleSwitchAccount}
                                className="w-full py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border-2 border-indigo-100 font-bold rounded-xl shadow-sm transition-all text-xs flex justify-center items-center gap-1.5 cursor-pointer transform active:scale-98"
                                id="google-switch-account-btn"
                              >
                                Cambiar cuenta 🔄
                              </button>
                           </div>

                            {isPremiumUser && (
                                <div className="w-full mt-4 pt-4 border-t border-slate-100 flex flex-col gap-2 items-center">
                                  <span className="text-[10px] font-black tracking-widest text-indigo-400 uppercase text-center block mb-2">💎 Suscripción Premium</span>
                                  
                                  {subscriptionStatus === 'canceled_at_period_end' || subscriptionStatus === 'canceled' ? (
                                    <div className="bg-amber-50 text-amber-600 border border-amber-200 rounded-xl p-3 text-center w-full">
                                      <p className="text-[10px] font-bold">Suscripción automática cancelada</p>
                                      <p className="text-[9px] mt-1">Seguirás siendo Premium hasta que termine tu ciclo actual (30 días). No se realizarán más cobros.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => setShowCancelConfirm(true)}
                                        className="w-full max-w-xs py-2 px-1 hover:bg-rose-50 border-2 border-slate-100 hover:border-rose-200 text-slate-400 hover:text-rose-500 font-bold rounded-xl text-[10px] tracking-wider uppercase transition-all duration-300 cursor-pointer text-center bg-white flex justify-center items-center gap-1.5"
                                      >
                                        Cancelar Suscripción Automática
                                      </button>
                                      <p className="text-[9px] text-slate-400 text-center px-4 mt-1">
                                        Al cancelar, no se realizarán más cobros automáticos. Podrás seguir usando Kokoro Premium hasta finalizar tu periodo actual.
                                      </p>
                                    </>
                                  )}
                                </div>
                            )}

                            {/* Stripe / Sync Audit Logs Section */}
                            <div className="w-full mt-5 pt-3 border-t border-slate-100 flex flex-col items-center">
                              <button
                                onClick={() => setShowStripeLogsToggle(!showStripeLogsToggle)}
                                className="text-[10px] font-black tracking-widest text-[#6366f1] hover:text-[#4f46e5] uppercase flex items-center gap-1 cursor-pointer bg-transparent border-0 transition-colors"
                              >
                                {showStripeLogsToggle ? "Ocultar Historial ✕" : "Historial de Sincronización Stripe 🔄"}
                              </button>

                              {showStripeLogsToggle && (
                                <div className="w-full max-w-xs mt-3 bg-slate-50 border border-slate-200 rounded-xl p-2.5 max-h-[160px] overflow-y-auto flex flex-col gap-1.5 scrollbar-thin">
                                  <div className="flex justify-between items-center mb-1 pb-1 border-b border-slate-200/60">
                                    <span className="text-[8px] font-black tracking-wider text-slate-400 uppercase">Registros de Transacciones</span>
                                    <button 
                                      onClick={loadStripeLogs} 
                                      className="text-[8px] font-black text-indigo-500 hover:underline cursor-pointer"
                                      disabled={isLoadingStripeLogs}
                                    >
                                      {isLoadingStripeLogs ? "Cargando..." : "Refrescar 🔄"}
                                    </button>
                                  </div>
                                  
                                  {stripeLogs.length === 0 ? (
                                    <p className="text-[9px] text-slate-400 text-center py-2 font-medium">No se han registrado sincronizaciones o webhooks de Stripe aún.</p>
                                  ) : (
                                    stripeLogs.map((log: any, idx: number) => {
                                      const isSuccess = log.status === 'success';
                                      const isError = log.status === 'error';
                                      const isInfo = log.status === 'info';
                                      
                                      let statusColor = "bg-blue-500";
                                      if (isSuccess) statusColor = "bg-emerald-500";
                                      if (isError) statusColor = "bg-rose-500";
                                      if (isInfo) statusColor = "bg-amber-500";

                                      let dateStr = "";
                                      try {
                                        dateStr = new Date(log.createdAt).toLocaleString();
                                      } catch(e) {}

                                      return (
                                        <div key={`log-${idx}-${log.id || "no-id"}`} className="bg-white border border-slate-150 rounded-lg p-2 flex flex-col gap-0.5 shadow-[0_1px_2px_rgba(0,0,0,0.02)] text-left">
                                          <div className="flex items-center gap-1.5 justify-between">
                                            <div className="flex items-center gap-1.5 font-sans">
                                              <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
                                              <span className="text-[8px] font-black uppercase tracking-wider text-slate-700">
                                                {log.type.replace("webhook:", "webhook: ").replace("manual_sync", "sincronización")}
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-[9px] font-bold text-slate-500 leading-normal">{log.message}</p>
                                          <span className="text-[8px] text-slate-400 font-mono mt-0.5">{dateStr}</span>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                            </div>
                         </div>
                      ) : (
                        <div className="bg-white rounded-2xl p-6 border-2 border-indigo-50 shadow-sm relative overflow-hidden flex flex-col items-center justify-center min-h-[300px]">
                           {/* Google Cloud style graphics */}
                           <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mb-4 border-2 border-indigo-100/50 shadow-sm text-indigo-500">
                             <Lock className="w-6 h-6 text-indigo-400 animate-pulse" />
                           </div>
                           
                           <h4 className="text-xl font-black text-slate-800 mb-2 tracking-tight text-center">Conecta tu cuenta de Google</h4>
                           <p className="text-xs font-semibold text-slate-400 max-w-sm text-center mb-6 leading-relaxed">
                             Inicia sesión para guardar de forma permanente tu progreso de lecciones, tus estrellas acumuladas y el nivel de maestría lingüística con Kokoro.
                           </p>

                           <div className="w-full max-w-xs border-0">
                             {authError && (
                               <div className="bg-red-50 border border-red-200 text-red-600 text-[10px] font-bold p-2 rounded-lg w-full mb-3 text-center">
                                 {authError}
                                 {unverifiedUser && (
                                   <button 
                                     onClick={handleResendVerification}
                                     className="mt-2 block w-full bg-red-100 hover:bg-red-200 text-red-700 py-1.5 rounded-md transition-colors"
                                   >
                                     Reenviar correo
                                   </button>
                                 )}
                               </div>
                             )}
                             {authSuccess && (
                               <div className="bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold p-2 rounded-lg w-full mb-3 text-center">
                                 {authSuccess}
                               </div>
                             )}

                             {authMode === 'google' && (
                               <>
                                 <button
                                   onClick={handleGoogleSignIn}
                                   disabled={isLoggingIn}
                                   className={`w-full py-2.5 px-4 ${isLoggingIn ? 'bg-slate-50 border-slate-200' : 'bg-white hover:bg-slate-50 hover:shadow-md border-slate-200'} border-2 rounded-xl text-slate-700 font-bold text-sm flex justify-center items-center relative transition-all shadow-sm transform active:scale-95 disabled:pointer-events-none cursor-pointer mb-3`}
                                 >
                                   {isLoggingIn ? (
                                     <div className="flex items-center gap-2">
                                       <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                                       <span className="font-bold text-slate-500 text-xs">Accediendo con Google...</span>
                                     </div>
                                   ) : (
                                 <div className="flex items-center w-full justify-center">
                                   <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                                     <path
                                       fill="#4285F4"
                                       d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                     />
                                     <path
                                       fill="#34A853"
                                       d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                     />
                                     <path
                                       fill="#FBBC05"
                                       d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22c-.62-.62-1.03-1.63-1.03-2.63z"
                                     />
                                     <path
                                       fill="#EA4335"
                                       d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                                     />
                                   </svg>
                                   <span className="tracking-wide text-xs font-black text-slate-700">Iniciar sesión con Google</span>
                                 </div>
                               )}
                             </button>
                             <div className="flex items-center w-full mb-3">
                               <div className="flex-1 h-px bg-slate-200"></div>
                               <span className="px-3 text-[10px] text-slate-400 font-bold">O</span>
                               <div className="flex-1 h-px bg-slate-200"></div>
                             </div>
                             <button
                               onClick={() => { setAuthMode('email_login'); setAuthError(''); setAuthSuccess(''); }}
                               className="w-full py-2.5 px-4 bg-slate-800 hover:bg-slate-900 border-2 border-slate-800 rounded-xl text-white font-bold text-xs flex justify-center items-center transition-all shadow-sm transform active:scale-95"
                             >
                               Usar Correo Electrónico
                             </button>
                           </>
                         )}

                         {authMode === 'email_login' && (
                           <form onSubmit={handleEmailSignIn} className="w-full">
                             <input
                               type="email"
                               placeholder="Correo Electrónico"
                               value={authEmail}
                               onChange={(e) => setAuthEmail(e.target.value)}
                               className="w-full mb-2 px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 outline-none text-xs font-bold text-slate-700 transition-colors"
                               required
                             />
                             <div className="relative w-full mb-3">
                               <input
                                 type={showPassword ? "text" : "password"}
                                 placeholder="Contraseña"
                                 value={authPassword}
                                 onChange={(e) => setAuthPassword(e.target.value)}
                                 className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 outline-none text-xs font-bold text-slate-700 transition-colors pr-10"
                                 required
                               />
                               <button
                                 type="button"
                                 onClick={() => setShowPassword(!showPassword)}
                                 className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                               >
                                 {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                               </button>
                             </div>
                             <button
                               type="submit"
                               disabled={isLoggingIn}
                               className="w-full py-2.5 px-4 bg-blue-500 hover:bg-blue-600 border-2 border-blue-500 hover:border-blue-600 rounded-xl text-white font-bold text-xs flex justify-center items-center transition-all shadow-sm transform active:scale-95 disabled:opacity-70"
                             >
                               {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Iniciar Sesión"}
                             </button>
                             <div className="mt-3 flex flex-col gap-1.5">
                               <button type="button" onClick={() => { setAuthMode('email_signup'); setAuthError(''); setAuthSuccess(''); }} className="text-[11px] font-bold text-slate-500 hover:text-blue-500">
                                 ¿No tienes cuenta? Regístrate
                               </button>
                               <button type="button" onClick={() => { setAuthMode('google'); setAuthError(''); setAuthSuccess(''); }} className="text-[10px] font-bold text-slate-400 hover:text-slate-600">
                                 Volver
                               </button>
                             </div>
                           </form>
                         )}

                         {authMode === 'email_signup' && (
                           <form onSubmit={handleEmailSignUp} className="w-full">
                             <input
                               type="email"
                               placeholder="Correo Electrónico"
                               value={authEmail}
                               onChange={(e) => setAuthEmail(e.target.value)}
                               className="w-full mb-2 px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-0 outline-none text-xs font-bold text-slate-700 transition-colors"
                               required
                             />
                             <div className="relative w-full mb-3">
                               <input
                                 type={showPassword ? "text" : "password"}
                                 placeholder="Contraseña (mín. 6 caracteres)"
                                 value={authPassword}
                                 onChange={(e) => setAuthPassword(e.target.value)}
                                 className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-0 outline-none text-xs font-bold text-slate-700 transition-colors pr-10"
                                 required
                                 minLength={6}
                               />
                               <button
                                 type="button"
                                 onClick={() => setShowPassword(!showPassword)}
                                 className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                               >
                                 {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                               </button>
                             </div>
                             <button
                               type="submit"
                               disabled={isLoggingIn}
                               className="w-full py-2.5 px-4 bg-indigo-500 hover:bg-indigo-600 border-2 border-indigo-500 hover:border-indigo-600 rounded-xl text-white font-bold text-xs flex justify-center items-center transition-all shadow-sm transform active:scale-95 disabled:opacity-70"
                             >
                               {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear Cuenta"}
                             </button>
                             <div className="mt-3 flex flex-col gap-1.5">
                               <button type="button" onClick={() => { setAuthMode('email_login'); setAuthError(''); setAuthSuccess(''); }} className="text-[11px] font-bold text-slate-500 hover:text-indigo-500">
                                 ¿Ya tienes cuenta? Inicia sesión
                               </button>
                               <button type="button" onClick={() => { setAuthMode('google'); setAuthError(''); setAuthSuccess(''); }} className="text-[10px] font-bold text-slate-400 hover:text-slate-600">
                                 Volver
                               </button>
                             </div>
                           </form>
                         )}

                         {authMode === 'email_verify_code' && (
                           <form onSubmit={handleVerifyCode} className="w-full">
                             <div className="mb-2 text-center text-xs font-semibold text-slate-500">
                               Ingresa el código que enviamos a tu correo
                             </div>
                             {testModeCode && (
                               <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-[11px] font-medium leading-relaxed">
                                 ⚠️ <strong>Modo de prueba (sin dominio propio)</strong><br />
                                 Usa este código para verificar tu cuenta: <span className="font-mono font-bold bg-amber-100 px-1.5 py-0.5 rounded text-amber-900 select-all">{testModeCode}</span>
                               </div>
                             )}
                             <input
                               type="text"
                               placeholder="Código de 6 dígitos"
                               value={authVerificationCode}
                               onChange={(e) => setAuthVerificationCode(e.target.value)}
                               className="w-full mb-3 px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-0 outline-none text-center text-lg font-bold text-slate-700 tracking-widest transition-colors"
                               required
                               maxLength={6}
                             />
                             <button
                               type="submit"
                               disabled={isLoggingIn || authVerificationCode.length < 6}
                               className="w-full py-2.5 px-4 bg-indigo-500 hover:bg-indigo-600 border-2 border-indigo-500 hover:border-indigo-600 rounded-xl text-white font-bold text-xs flex justify-center items-center transition-all shadow-sm transform active:scale-95 disabled:opacity-70"
                             >
                               {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verificar Cuenta"}
                             </button>
                             <div className="mt-3 flex flex-col gap-1.5">
                               <button type="button" onClick={handleResendVerification} disabled={isLoggingIn} className="text-[11px] font-bold text-indigo-500 hover:text-indigo-600 disabled:opacity-50">
                                 Reenviar código
                               </button>
                               <button type="button" onClick={() => { setAuthMode('email_login'); setAuthError(''); setAuthSuccess(''); }} className="text-[11px] font-bold text-slate-500 hover:text-indigo-500">
                                 Volver a inicio de sesión
                               </button>
                             </div>
                           </form>
                         )}
                             <span className="text-[10px] text-slate-400 font-bold block text-center mt-3 font-sans">
                               Tus datos se guardan de forma segura
                             </span>
                           </div>
                        </div>
                      )}
                     </div>
                   ) : activeProgressionTab === 'progress' ? (
                     (() => {
                     const totalCounts = listeningCount + vocabularyCount + speakingCount + readingCount + writingCount;
                     let lPct = 0, vPct = 0, sPct = 0, rPct = 0, wPct = 0;
                     if (totalCounts > 0) {
                       lPct = Math.round((listeningCount / totalCounts) * 100);
                       vPct = Math.round((vocabularyCount / totalCounts) * 100);
                       sPct = Math.round((speakingCount / totalCounts) * 100);
                       rPct = Math.round((readingCount / totalCounts) * 100);
                       wPct = Math.round((writingCount / totalCounts) * 100);
                       
                       const sum = lPct + vPct + sPct + rPct + wPct;
                       if (sum !== 100 && sum > 0) {
                         const diff = 100 - sum;
                         const maxCount = Math.max(listeningCount, vocabularyCount, speakingCount, readingCount, writingCount);
                         if (maxCount === listeningCount) lPct += diff;
                         else if (maxCount === vocabularyCount) vPct += diff;
                         else if (maxCount === speakingCount) sPct += diff;
                         else if (maxCount === readingCount) rPct += diff;
                         else if (maxCount === writingCount) wPct += diff;
                       }
                     }
                     return (
                       <div className="flex flex-col gap-3 md:gap-4 relative z-20 animate-in fade-in duration-200">
                         {/* Panel 1: Stats & Radar */}
                         <div className="bg-white rounded-2xl p-3 border-2 border-indigo-50 shadow-sm relative overflow-hidden flex flex-row items-center transform rotate-[1deg]">
                            <div className="absolute -left-2 top-2 w-8 h-2 bg-indigo-200/60 rotate-45" />
                            
                            {/* Left Side (Stats) */}
                            <div className="flex flex-col justify-center gap-2 w-[45%] pr-2">
                              <div className="bg-indigo-50/50 rounded-xl py-2 px-1 text-center">
                                <div className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">LEVEL 🏅</div>
                                <div className="flex items-baseline justify-center gap-0.5 mt-0.5">
                                  <span className="text-4xl font-black leading-none text-indigo-500">{Math.max(1, ...unlockedLevels)}</span>
                                  <span className="text-[10px] font-bold text-indigo-300">/{levelsSpecs?.length || 10}</span>
                                </div>
                              </div>
                              <div className="bg-indigo-50/50 rounded-xl py-2 px-1 text-center">
                                <div className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">PRACTICE TIME ⏳</div>
                                <div className="flex items-baseline justify-center gap-0.5 mt-0.5">
                                  <span className="text-2xl font-black leading-none text-slate-700">{Math.floor(studyMinutes / 60)}</span>
                                  <span className="text-[9px] font-bold text-slate-400">h</span>
                                  <span className="text-2xl font-black leading-none text-slate-700 ml-0.5">{studyMinutes % 60}</span>
                                  <span className="text-[9px] font-bold text-slate-400">m</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Right Side (Radar Chart) */}
                            <div className="w-[55%] h-[120px] md:h-[130px] relative z-10">
                              <ResponsiveContainer width="100%" height="100%">
                                <RadarChart cx="50%" cy="50%" outerRadius="60%" data={[
                                  { subject: 'Speaking 🗣️', A: sPct, fullMark: 100 },
                                  { subject: 'Reading 📖', A: rPct, fullMark: 100 },
                                  { subject: 'Listening 🎧', A: lPct, fullMark: 100 },
                                  { subject: 'Writing ✍️', A: wPct, fullMark: 100 },
                                  { subject: 'Vocab 📚', A: vPct, fullMark: 100 },
                                ]}>
                                  <PolarGrid stroke="#e0e7ff" />
                                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#818cf8', fontSize: 7, fontWeight: 800 }} />
                                  <Radar name="Count Share" dataKey="A" stroke="#818cf8" strokeWidth={2} fill="#c7d2fe" fillOpacity={0.5} />
                                </RadarChart>
                              </ResponsiveContainer>
                            </div>
                         </div>

                         {/* Panel 2 (Bottom): Skill Bars (Lined Paper Note) */}
                         <div className="bg-amber-50/40 rounded-2xl p-4 border-2 border-amber-100/50 shadow-sm flex flex-col justify-center gap-3 relative transform rotate-[-1deg]">
                            <div className="absolute top-2 right-4 w-6 h-6 rounded-full bg-red-100/50 border-2 border-red-200/50 flex items-center justify-center opacity-70">
                               <span className="text-[8px] text-red-400 font-bold rotate-12">A+</span>
                            </div>
                            
                            <div className="text-[9px] font-extrabold tracking-widest text-[#d97706] uppercase text-center mb-1 flex items-center justify-center gap-1">
                              <span>📊</span> Practice Breakdown
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                              {[
                                { name: 'Listening', val: lPct, count: listeningCount, icon: <Headphones className="w-3 h-3 text-blue-500"/>, color: 'bg-blue-400', txt: 'text-blue-600' },
                                { name: 'Vocabulary', val: vPct, count: vocabularyCount, icon: <BookOpen className="w-3 h-3 text-amber-500"/>, color: 'bg-amber-400', txt: 'text-amber-600' },
                                { name: 'Speaking', val: sPct, count: speakingCount, icon: <Mic className="w-3 h-3 text-emerald-500"/>, color: 'bg-emerald-400', txt: 'text-emerald-600' },
                                { name: 'Reading', val: rPct, count: readingCount, icon: <Languages className="w-3 h-3 text-purple-500"/>, color: 'bg-purple-400', txt: 'text-purple-600' },
                                { name: 'Writing', val: wPct, count: writingCount, icon: <Pencil className="w-3 h-3 text-pink-500"/>, color: 'bg-pink-400', txt: 'text-pink-600' },
                              ].map(skill => (
                                <div key={skill.name} className="flex flex-col gap-1 relative z-10 w-full">
                                  <div className="flex items-center justify-between text-[10px] font-black text-slate-700">
                                    <div className="flex items-center gap-1">
                                      <span className="bg-white p-0.5 rounded-md shadow-sm border border-slate-100">{skill.icon}</span> 
                                      <span className="truncate max-w-[55px] sm:max-w-none">{skill.name}</span>
                                      <span className="text-[7.5px] text-slate-400 font-bold">({skill.count}x)</span>
                                    </div>
                                    <span className={`${skill.txt} text-[10px] font-extrabold`}>{skill.val}%</span>
                                  </div>
                                  <div className="h-2 w-full bg-white border border-slate-100 rounded-full overflow-hidden shadow-inner flex items-center">
                                    <motion.div 
                                      initial={{ width: 0 }}
                                      animate={{ width: `${skill.val}%` }}
                                      transition={{ duration: 1, delay: 0.1 }}
                                      className={`h-full rounded-full ${skill.color}`}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                         </div>
                       </div>
                     );
                   })()
                 ) : (
                   <div className="flex flex-col gap-4 relative z-20 animate-in fade-in zoom-in-95 duration-300">
                     <div className="bg-indigo-50/80 rounded-2xl p-3 border border-indigo-100 shadow-inner flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                           <div className="bg-white p-2 rounded-full shadow-sm border border-indigo-100 hidden sm:block">
                             <Store className="w-5 h-5 text-indigo-500" />
                           </div>
                           <div>
                             <h4 className="text-sm font-black text-slate-800">Rewards Store</h4>
                             <p className="text-[10px] font-bold text-slate-500">Spend XP or Stars to unlock new chapters! 💎⭐️</p>
                           </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5 bg-white py-1 px-2.5 rounded-xl shadow-sm border border-yellow-200">
                            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                            <span className="text-xs font-black text-slate-800">{starsCount}</span>
                          </div>
                          <div className="flex items-center gap-1.5 bg-white py-1 px-2.5 rounded-xl shadow-sm border border-purple-200">
                            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                            <span className="text-xs font-black text-slate-800">{masteryTokens}</span>
                          </div>
                          <button
                            onClick={() => {
                              setStarsCount(1000);
                              setMasteryTokens(1000);
                              localStorage.setItem('kokoro_stars_count', '1000');
                              localStorage.setItem('kokoro_mastery_tokens', '1000');
                              try {
                                syncProgressToCloud(unlockedLevels, 1000, englishLevel, undefined, undefined, 1000);
                              } catch(e) {}
                            }}
                            className="hidden bg-gradient-to-r from-yellow-500 to-indigo-600 text-white font-black hover:opacity-95 text-[9px] uppercase tracking-widest rounded-xl py-1 px-2 shadow-sm transition-all active:scale-95 cursor-pointer text-center"
                            title="Format Grants 1000 Stars and XP for testing"
                          >
                            🎁 (Test)
                          </button>
                        </div>
                     </div>
 
                     {(() => {
                        const nextLevelTarget = Math.max(...unlockedLevels) + 1;
                        if (nextLevelTarget > levelsSpecs.length) {
                          return (
                            <div className="bg-white border-2 border-emerald-100 rounded-xl p-6 flex flex-col items-center justify-center gap-3 shadow-sm text-center">
                              <Award className="w-12 h-12 text-emerald-400" />
                              <div>
                                <h4 className="text-sm font-black text-slate-800">Maximum Level Reached! 🏆✨</h4>
                                <p className="text-[11px] font-medium text-slate-500 mt-1">You have unlocked all available story chapters.</p>
                              </div>
                            </div>
                          );
                        }
 
                        const reqXP = 100 * (nextLevelTarget - 1);
                        const canUnlock = masteryTokens >= reqXP || starsCount >= 100;
 
                        return (
                          <div 
                            onClick={() => {
                              setIsProgressionMenuOpen(false);
                              setShowLevelLockedModal(nextLevelTarget);
                            }}
                            className={`bg-white border hover:shadow-md cursor-pointer hover:scale-[1.01] rounded-2xl p-3 flex sm:flex-row flex-col items-center justify-between gap-3 transition-all max-w-sm mx-auto w-full group ${
                              canUnlock ? 'border-indigo-300 shadow-sm' : 'border-slate-100 opacity-90'
                            }`}
                          >
                            <div className="flex items-center gap-3 self-start sm:self-auto">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border shadow-xs transition-colors ${
                                canUnlock ? 'bg-indigo-5/80 border-indigo-100 text-indigo-500 group-hover:bg-indigo-100' : 'bg-slate-50 border-slate-100 text-slate-400'
                              }`}>
                                <Unlock className="w-5 h-5" />
                              </div>
                              <div className="text-left">
                                <h4 className="text-xs font-black text-slate-800 leading-tight">
                                  Unlock Level {nextLevelTarget} 🔑
                                </h4>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <div className="flex items-center gap-0.5 text-[9px] font-bold text-purple-500">
                                    <Sparkles className="w-2.5 h-2.5" />
                                    <span>{reqXP} XP</span>
                                  </div>
                                  <span className="text-[8px] text-slate-300">•</span>
                                  <div className="flex items-center gap-0.5 text-[9px] font-bold text-yellow-500">
                                    <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />
                                    <span>100 ⭐</span>
                                  </div>
                                </div>
                              </div>
                            </div>
 
                           <button className={`w-full sm:w-auto px-3.5 py-1 rounded-xl text-[10px] h-7 font-black uppercase tracking-wider transition-colors shrink-0 cursor-pointer ${
                              canUnlock ? 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm' : 'bg-slate-150 text-slate-500'
                            }`}>
                              View 👁️
                            </button>
                          </div>
                        );
                     })()}

                    <div className="mt-3 pt-3 border-t border-indigo-100 flex flex-col gap-2 pb-2">
                       <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest px-1">Study Locations 🌄</h4>
                       <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                         {englishBackgrounds.slice(1).map((bg, index) => {
                            const actualIndex = index + 1;
                            const isUnlocked = unlockedBackgrounds.includes(actualIndex);
                            const hasUnlockedAnyPurchase = unlockedBackgrounds.filter(id => id !== 0).length >= 1;
                            const canBuyInFree = !hasUnlockedAnyPurchase;
                            const isPurchaseAllowed = isPremiumUser || canBuyInFree;
                            const canAfford = starsCount >= bg.price;
                            
                            return (
                               <div key={`eng-bg-${bg.id}`} className={`flex flex-col gap-1 p-1.5 rounded-xl border-2 transition-all ${isUnlocked ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-100'}`}>
                                  <div className={`w-full h-10 rounded-lg bg-cover bg-center border border-slate-200 ${!isUnlocked ? 'opacity-60 grayscale' : ''}`} style={{ backgroundImage: `url(${bg.url})` }} />
                                  <div className="flex flex-col min-w-0 flex-1">
                                     <h5 className="text-[9px] font-black text-slate-700 uppercase tracking-widest truncate">{bg.label}</h5>
                                     {!isUnlocked && (
                                       <div className="flex items-center justify-between gap-1 mt-1">
                                         <div className="flex items-center gap-0.5 text-[8px] font-bold text-amber-500">
                                            <Star className="w-2.5 h-2.5 fill-amber-400" />
                                            <span>{bg.price}</span>
                                         </div>
                                         {isPurchaseAllowed ? (
                                           <button 
                                             disabled={!canAfford}
                                             onClick={() => {
                                                if (!canAfford) return;
                                                const newStars = starsCount - bg.price;
                                                setStarsCount(newStars);
                                                localStorage.setItem('kokoro_stars_count', newStars.toString());
                                                const updatedBackgrounds = [...unlockedBackgrounds, actualIndex];
                                                setUnlockedBackgrounds(updatedBackgrounds);
                                                localStorage.setItem('kokoro_unlocked_backgrounds', JSON.stringify(updatedBackgrounds));
                                                
                                                try {
                                                  syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, newStars, updatedBackgrounds);
                                                } catch(e) {}
                                                
                                                setUnlockedBgAnim({ bgId: actualIndex, price: bg.price });
                                             }}
                                             className={`px-2 h-4 rounded shadow-sm text-[8px] transition-all shrink-0 ${canAfford ? 'bg-amber-400 hover:bg-amber-500 text-amber-950 font-black cursor-pointer' : 'bg-slate-100 text-slate-400 font-bold opacity-80 cursor-not-allowed'}`}
                                           >
                                             BUY
                                           </button>
                                         ) : (
                                           <button 
                                             disabled
                                             className="px-2 h-4 rounded shadow-sm text-[7px] font-bold bg-slate-150 text-slate-400 opacity-80 cursor-not-allowed flex items-center gap-0.5"
                                             title="Límite del plan gratis. Desbloquea más con Premium."
                                           >
                                             👑 PREMIUM
                                           </button>
                                         )}
                                       </div>
                                     )}
                                     {isUnlocked && (
                                       <div className="text-[8px] font-bold text-indigo-500 mt-1 uppercase tracking-widest flex items-center justify-center bg-indigo-100/50 rounded py-0.5 w-full">
                                          <Unlock className="w-2.5 h-2.5 mr-0.5"/> Unlocked
                                       </div>
                                     )}
                                  </div>
                               </div>
                            );
                         })}
                       </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-indigo-100 flex flex-col gap-2 pb-2">
                       <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest px-1">Chat Backgrounds 🎑</h4>
                       <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                         {chatBackgrounds.slice(1).map((bg, index) => {
                            const actualIndex = index + 1;
                            const isUnlocked = unlockedChatBackgrounds.includes(actualIndex);
                            const hasUnlockedAnyPurchase = unlockedChatBackgrounds.filter(id => id !== 0).length >= 1;
                            const canBuyInFree = !hasUnlockedAnyPurchase;
                            const isPurchaseAllowed = isPremiumUser || canBuyInFree;
                            const canAfford = starsCount >= bg.price;
                            
                            return (
                               <div key={`chat-bg-${bg.id}`} className={`flex flex-col gap-1 p-1.5 rounded-xl border-2 transition-all ${isUnlocked ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-100'}`}>
                                  <div className={`w-full h-10 rounded-lg bg-cover bg-center border border-slate-200 ${!isUnlocked ? 'opacity-60 grayscale' : ''}`} style={{ backgroundImage: `url(${bg.url})` }} />
                                  <div className="flex flex-col min-w-0 flex-1">
                                     <h5 className="text-[9px] font-black text-slate-700 uppercase tracking-widest truncate">{bg.label}</h5>
                                     {!isUnlocked && (
                                       <div className="flex items-center justify-between gap-1 mt-1">
                                         <div className="flex items-center gap-0.5 text-[8px] font-bold text-amber-500">
                                            <Star className="w-2.5 h-2.5 fill-amber-400" />
                                            <span>{bg.price}</span>
                                         </div>
                                         {isPurchaseAllowed ? (
                                           <button 
                                             disabled={!canAfford}
                                             onClick={() => {
                                                if (!canAfford) return;
                                                const newStars = starsCount - bg.price;
                                                setStarsCount(newStars);
                                                localStorage.setItem('kokoro_stars_count', newStars.toString());
                                                const updated = [...unlockedChatBackgrounds, actualIndex];
                                                setUnlockedChatBackgrounds(updated);
                                                localStorage.setItem('kokoro_unlocked_chat_backgrounds', JSON.stringify(updated));
                                                
                                                try {
                                                  syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, newStars, undefined, updated);
                                                } catch(e) {}
                                             }}
                                             className={`px-2 h-4 rounded shadow-sm text-[8px] transition-all shrink-0 ${canAfford ? 'bg-amber-400 hover:bg-amber-500 text-amber-950 font-black cursor-pointer' : 'bg-slate-100 text-slate-400 font-bold opacity-80 cursor-not-allowed'}`}
                                           >
                                             BUY
                                           </button>
                                         ) : (
                                           <button 
                                             disabled
                                             className="px-2 h-4 rounded shadow-sm text-[7px] font-bold bg-slate-150 text-slate-400 opacity-80 cursor-not-allowed flex items-center gap-0.5 transform scale-90"
                                             title="Límite del plan gratis. Desbloquea más con Premium."
                                           >
                                             👑 PREMIUM
                                           </button>
                                         )}
                                       </div>
                                     )}
                                     {isUnlocked && (
                                       <div className="text-[8px] font-bold text-indigo-500 mt-1 uppercase tracking-widest flex items-center justify-center bg-indigo-100/50 rounded py-0.5 w-full">
                                          <Unlock className="w-2.5 h-2.5 mr-0.5"/> Unlocked
                                       </div>
                                     )}
                                  </div>
                               </div>
                            );
                         })}
                       </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-indigo-100 flex flex-col gap-2 pb-2">
                        <h4 className="hidden">Music Tracks 🎵</h4>
                        <div className="hidden">
                          {musicTracks.map((track, idx) => {
                             const isUnlocked = track.unlocked || track.free || unlockedMusic.includes(idx);
                             const canAfford = starsCount >= (track.price || 50);
                             
                             return (
                                <div key={`music-track-${track.id}`} className={`flex items-center gap-2 p-2 rounded-xl border-2 transition-all ${isUnlocked ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-100'}`}>
                                   <div className={`w-8 h-8 rounded shrink-0 flex items-center justify-center ${isUnlocked ? 'bg-pink-100 text-pink-500' : 'bg-slate-100 text-slate-400'}`}>
                                      <Music className="w-4 h-4" />
                                   </div>
                                   <div className="flex flex-col min-w-0 flex-1">
                                      <h5 className="text-[10px] font-black text-slate-700 uppercase tracking-widest truncate">{track.name}</h5>
                                      {!isUnlocked && (
                                        <div className="flex items-center justify-between gap-1 mt-0.5">
                                          <div className="flex items-center gap-0.5 text-[9px] font-bold text-amber-500">
                                             <Star className="w-3 h-3 fill-amber-400" />
                                             <span>{track.price || 50}</span>
                                          </div>
                                          <button 
                                            disabled={!canAfford}
                                            onClick={() => {
                                               if (!canAfford) return;
                                               const newStars = starsCount - (track.price || 50);
                                               setStarsCount(newStars);
                                               localStorage.setItem('kokoro_stars_count', newStars.toString());
                                               const updated = [...unlockedMusic, idx];
                                               setUnlockedMusic(updated);
                                               localStorage.setItem('kokoro_unlocked_music', JSON.stringify(updated));
                                               
                                               try {
                                                 syncProgressToCloud(unlockedLevels, masteryTokens, englishLevel, undefined, undefined, newStars, undefined, undefined, updated);
                                               } catch(e) {}
                                            }}
                                            className={`px-3 py-1 rounded shadow-sm text-[9px] transition-all shrink-0 ${canAfford ? 'bg-amber-400 hover:bg-amber-500 text-amber-950 font-black cursor-pointer' : 'bg-slate-100 text-slate-400 font-bold opacity-80 cursor-not-allowed'}`}
                                          >
                                            BUY
                                          </button>
                                        </div>
                                      )}
                                     {isUnlocked && (
                                       <>
                                         <div className="text-[9px] font-bold text-indigo-500 mt-0.5 uppercase tracking-widest flex items-center gap-1">
                                            <Unlock className="w-3 h-3"/> Unlocked
                                         </div>
                                         <div className="flex flex-col gap-1 mt-1 w-full border-t border-indigo-100/30 pt-1.5">
                                             <div className="flex items-center justify-between text-[8px] font-bold text-indigo-500 uppercase tracking-widest mb-1">
                                               <span>Estado:</span>
                                               {musicDownloadProgress[track.id]?.status === 'downloading' ? (
                                                 <span className="text-pink-500 animate-pulse font-extrabold">Descargando... {musicDownloadProgress[track.id]?.percent || 0}%</span>
                                               ) : musicDownloadProgress[track.id]?.status === 'error' ? (
                                                 <button
                                                   onClick={async (e) => {
                                                     e.stopPropagation();
                                                     await reDownloadTrack(track.id);
                                                   }}
                                                   className="text-[8px] bg-rose-500/20 text-rose-600 px-1.5 py-0.5 rounded border border-rose-300 hover:bg-rose-500/40 font-black uppercase tracking-widest text-center cursor-pointer flex items-center justify-center gap-1 mt-0.5"
                                                 >
                                                   ⚠️ No se oye? Re-descargar
                                                 </button>
                                               ) : (
                                                 <span className="text-emerald-500">Listo ✓</span>
                                               )}
                                             </div>
                                             <div className="flex items-center justify-between gap-1 w-full">
                                                {musicIndex === idx ? (
                                                  <span className="text-[8px] font-black text-pink-500 uppercase tracking-wider flex items-center justify-center flex-1 gap-0.5 py-1 bg-pink-50 border border-pink-100 rounded-md">Active 🎵</span>
                                                ) : (
                                                  <button
                                                    onClick={() => {
                                                      setMusicIndex(idx);
                                                      if (bgMusicRef.current) {
                                                        const trackUrl = track.url;
                                                        if (!bgMusicRef.current.src.endsWith(trackUrl)) {
                                                          bgMusicRef.current.src = trackUrl;
                                                          bgMusicRef.current.loop = true;
                                                          bgMusicRef.current.load();
                                                        }
                                                        const isDucked = isListening || isPlaying;
                                                        const targetVolume = isDucked ? Math.max(0, musicVolume * 0.2) : musicVolume;
                                                        bgMusicRef.current.volume = targetVolume;
                                                        bgMusicRef.current.play().catch(e => console.warn(e));
                                                      }
                                                    }}
                                                    className="bg-pink-500 hover:bg-pink-600 text-white font-black text-[8px] px-2 py-1 rounded shadow-sm cursor-pointer uppercase transition-all flex-1 text-center"
                                                  >
                                                     Equip
                                                  </button>
                                                )}
                                                <button
                                                  title="Redescargar canción si no se escucha"
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await reDownloadTrack(track.id);
                                                  }}
                                                  className="p-1 rounded-md bg-slate-100 hover:bg-pink-500 hover:text-white text-slate-500 border border-slate-200 transition-all cursor-pointer flex items-center justify-center shrink-0"
                                                >
                                                  <RefreshCw className={`w-3 h-3 ${musicDownloadProgress[track.id]?.status === 'downloading' ? 'animate-spin text-pink-400' : ''}`} />
                                                </button>
                                             </div>
                                          </div>
                                       </>
                                     )}
                                  </div>
                               </div>
                            );
                         })}
                       </div>
                    </div>

                   </div>
                 )}
                 </div>

              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* IMMERSIVE COMPREHENSIVE ORAL EXAM OVERLAY PANEL */}
      <AnimatePresence>
        {isExamActive && (() => {
          const spec = levelsSpecs.find(s => s.level === examTargetLevel) || levelsSpecs[1];
          const latestKokoroMsg = [...examHistory].reverse().find(m => m.role === 'kokoro');
          const latestUserMsg = [...examHistory].reverse().find(m => m.role === 'user');
          const totalScore = examScores.reduce((a, b) => a + b, 0);
          const avgScore = examScores.length > 0 ? (totalScore / examScores.length) : 0;
          
          return (
            <motion.div 
              key="exam-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[180] bg-transparent flex flex-col justify-between p-4 md:p-6 pointer-events-auto overflow-y-auto"
            >
              {/* EXIT CONFIRMATION MODAL OVERLAY */}
              <AnimatePresence>
                {showExamExitConfirm && (
                  <motion.div
                    key="exam-exit-confirm-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/95 backdrop-blur-md z-[210] flex items-center justify-center p-4 pointer-events-auto"
                  >
                    <motion.div
                      initial={{ scale: 0.9, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.9, y: 20 }}
                      className="w-full max-w-sm bg-slate-950 border-2 border-slate-800 rounded-3xl p-6 text-center text-white shadow-2xl space-y-5"
                    >
                      <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center mx-auto shadow-lg shadow-rose-550/10">
                        <AlertCircle className="w-6 h-6 animate-bounce" />
                      </div>
                      
                      <div className="space-y-2">
                        <h3 className="text-base font-black leading-tight text-white uppercase tracking-wider">
                          ¿Abandonar Evaluación?
                        </h3>
                        <p className="text-xs text-gray-305 leading-normal font-medium px-2">
                          Si sales ahora se perderá todo tu avance del examen actual y tendrás que iniciar nuevamente la certificación.
                        </p>
                      </div>

                      <div className="flex flex-col gap-2 pt-1.55">
                        <button
                          onClick={quitExamSession}
                          className="w-full py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer"
                        >
                          Sí, salir de todas formas
                        </button>
                        <button
                          onClick={() => setShowExamExitConfirm(false)}
                          className="w-full py-2.5 bg-slate-900 border border-slate-800 hover:bg-slate-800 text-gray-305 rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer"
                        >
                          No, continuar examen
                        </button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Header Details */}
              <div className="absolute top-0 left-0 w-full flex flex-row flex-nowrap items-center justify-between bg-slate-950/90 backdrop-blur-md border-b border-slate-800 px-4 py-2 shadow-xl z-[190]">
                <div className="flex flex-row flex-nowrap items-center gap-2 flex-1 min-w-0">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400 animate-pulse shrink-0" />
                  <span className="font-bold text-amber-400 uppercase tracking-wider text-xs md:text-sm whitespace-nowrap truncate">
                    Certificación de Inglés · Nivel {examTargetLevel}
                  </span>
                </div>
                
                <div className="flex flex-row flex-nowrap items-center gap-4 shrink-0 pl-4">
                  {/* Score Summary */}
                  <div className="flex flex-row flex-nowrap items-center gap-2 bg-slate-900 border border-slate-850 px-3 py-1 rounded-full text-xs md:text-sm whitespace-nowrap">
                    <span className="text-gray-400 font-bold whitespace-nowrap">Puntaje acumulado:</span>
                    <span className="text-amber-400 font-extrabold whitespace-nowrap">
                      {spec.cycles > 0 ? Math.round((totalScore / (spec.cycles * 5)) * 100) : 0} / 100
                    </span>
                  </div>
                  
                  {/* Quit close button */}
                  <button
                    onClick={() => {
                      if (isExamFinished) {
                        quitExamSession();
                      } else {
                        setShowExamExitConfirm(true);
                      }
                    }}
                    className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 hover:border-rose-500/40 rounded-lg transition-all cursor-pointer flex-shrink-0"
                    title="Salir del examen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Countdown Stage */}
              {(examCountdown !== null || isSpeakingExamRules) ? (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[95%] max-w-sm z-[190] bg-slate-950/92 backdrop-blur-md border border-slate-800 rounded-3xl p-4 shadow-[0_0_50px_-10px_rgba(0,0,0,0.85)]">
                  <div className="flex flex-col items-center justify-center w-full gap-4">
                    
                    {/* The big countdown */}
                    <div className="flex items-center justify-between w-full bg-slate-900/60 p-2.5 rounded-2xl border border-slate-800/80">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-amber-450" />
                        <span className="text-[10px] text-amber-450 font-black tracking-widest uppercase font-mono">
                          {isSpeakingExamRules ? "Kokoro dictando regla..." : "Comenzando examen en..."}
                        </span>
                      </div>
                      {isSpeakingExamRules ? (
                        <div className="flex items-center gap-1.5 bg-slate-900/40 border border-slate-800/60 px-2.5 py-1.5 rounded-xl animate-pulse shrink-0">
                          <motion.div
                            animate={{ scale: [1, 1.25, 1] }}
                            transition={{ duration: 1.2, repeat: Infinity }}
                            className="w-2.5 h-2.5 bg-amber-500 rounded-full"
                          />
                          <span className="text-[9px] font-black text-amber-450 font-mono tracking-wider uppercase">🔊 Hablando</span>
                        </div>
                      ) : (
                        <motion.div 
                          animate={{ scale: [1, 1.15, 1] }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xl rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.4)] shrink-0"
                        >
                          {examCountdown}
                        </motion.div>
                      )}
                    </div>

                    {/* Left & Center Combined: Info & Stats */}
                    <div className="flex flex-col gap-3 items-center w-full">
                      <div className="flex flex-row items-center justify-between w-full">
                        <div className="text-left">
                          <h3 className="text-xs font-black text-white uppercase tracking-wider">Reglas Nivel {examTargetLevel}</h3>
                          <span className="text-[9px] text-amber-450 font-extrabold tracking-wider uppercase bg-amber-500/10 px-1.5 py-0.5 rounded-sm">{spec.code}</span>
                        </div>
                        <div className="text-right text-[10px] text-gray-400 font-bold">
                          Min. frase: <span className="text-white font-extrabold">{spec.minWords} de p.</span>
                        </div>
                      </div>
                      
                      {/* Rules Stats */}
                      <div className="flex flex-row justify-center gap-2 w-full">
                        <div className="bg-slate-900 px-2.5 py-1.5 rounded-xl border border-slate-850 flex items-center gap-2 flex-1 justify-center">
                          <RefreshCw className="w-3.5 h-3.5 text-amber-450 opacity-80" />
                          <div className="flex flex-col">
                            <span className="text-[7px] text-gray-400 uppercase font-black leading-none">Límite</span>
                            <span className="text-[11px] font-bold text-white mt-1 leading-none">{spec.cycles} Rondas</span>
                          </div>
                        </div>
                        <div className="bg-slate-900 px-2.5 py-1.5 rounded-xl border border-slate-850 flex items-center gap-2 flex-1 justify-center">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 opacity-80" />
                          <div className="flex flex-col">
                            <span className="text-[7px] text-gray-400 uppercase font-black leading-none">Mínimo</span>
                            <span className="text-[11px] font-bold text-white mt-1 leading-none">{spec.minWords} Palabras</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-gray-400 text-[10px] leading-tight font-semibold text-center mt-0.5">
                        {examTargetLevel !== null && examTargetLevel <= 3 
                          ? '⚠️ Respuestas cortas son válidas en este nivel dadas las limitaciones.'
                          : '⚠️ Responde con oraciones completas y estructuradas dadas las reglas de nivel.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : !isExamFinished ? (
                <div className="flex-1 max-w-xl mx-auto w-full flex flex-col justify-end gap-6 py-6 font-sans">
                  
                  {/* Micro action toggle button */}
                  <div className="flex flex-col items-center gap-2.5 mt-2">
                    {isMyTurnToSpeakExam ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="relative">
                          <div
                            className={`w-16 h-16 rounded-full flex items-center justify-center transition-all pointer-events-none select-none ${
                              isListening 
                                ? 'bg-amber-500 text-slate-950 shadow-[0_0_25px_rgba(245,158,11,0.7)] animate-pulse border-4 border-amber-300' 
                                : 'bg-white/40 text-slate-400 border border-slate-800'
                            }`}
                          >
                            <Mic className="w-7 h-7 animate-pulse" />
                          </div>
                          
                          {/* Mini countdown badge */}
                          {examMicCountdown !== null && (
                            <div className="absolute -top-2 -right-2 bg-rose-500 text-white text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border-2 border-slate-950 animate-bounce">
                              {examMicCountdown}
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] font-black text-amber-400 tracking-wide uppercase leading-tight">
                            {isListening 
                              ? (examMicCountdown !== null 
                                  ? `⏳ Esperando a que hables... (${examMicCountdown}s)` 
                                  : '🎙️ Escuchando tu respuesta...')
                              : 'Procesando...'}
                          </p>
                          <p className="text-[8px] text-gray-500 font-semibold mt-1 max-w-[150px]">
                            {examMicCountdown !== null 
                              ? 'Si se acaba el tiempo y no has empezado, se calificará mal.' 
                              : 'Toma tu tiempo, puedes hablar tranquilo.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-950 border-2 border-slate-800 rounded-full py-2.5 px-6 flex items-center gap-2 text-gray-450 select-none shadow-lg">
                        <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" />
                        <span className="text-xs font-extrabold tracking-wide uppercase font-sans text-white">
                          {isExamLoading ? examStatusMessage : 'Kokoro evaluando y hablando...'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Cycles Status Bar */}
                  <div className="flex items-center gap-2 mb-2 bg-slate-950 p-2.5 rounded-full border-2 border-slate-850 shadow-lg">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Rondas:</span>
                    <div className="flex gap-1.5 flex-1 p-0.5">
                      {Array.from({ length: spec.cycles }).map((_, rIdx) => {
                        const score = examScores[rIdx];
                        const wordCount = examRoundWordCounts[rIdx] || 0;
                        const isCoherent = examRoundValidity[rIdx] !== false;
                        const isCompletedByScore = score !== undefined;
                        const isCurrent = rIdx + 1 === examCurrentRound;
                        const isPast = rIdx + 1 < examCurrentRound;
                        
                        return (
                          <div 
                            key={rIdx} 
                            className={`h-2.5 rounded-full flex-1 transition-all ${
                              isCompletedByScore 
                                ? score >= 3.0 && wordCount >= spec.minWords && isCoherent
                                  ? 'bg-emerald-500' 
                                  : 'bg-rose-500' 
                                : isPast
                                  ? 'bg-gray-500/50'
                                  : isCurrent 
                                    ? 'bg-amber-500' 
                                    : 'bg-slate-800'
                            }`}
                          />
                        );
                      })}
                    </div>
                    <span className="text-[10px] font-black tracking-widest uppercase text-amber-500 mr-2">
                      {examCurrentRound} / {spec.cycles}
                    </span>
                  </div>

                  {/* Bouncing notification feedback in middle screen */}
                  <AnimatePresence>
                    {examFeedbackNotification && (
                      <motion.div 
                        key="exam-feedback-notification"
                        initial={{ scale: 0.8, opacity: 0, y: 15 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.8, opacity: 0, y: -20 }}
                        className="bg-slate-950 border-2 border-slate-800 rounded-2xl p-5 text-left shadow-2xl relative"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-extrabold tracking-widest uppercase text-amber-400">Puntaje de Ronda:</span>
                            <span className="text-sm font-black text-white">{examFeedbackNotification.score} / 5</span>
                          </div>
                          
                          {/* Criteria indicators */}
                          <div className="flex items-center gap-2 text-[10px] font-extrabold font-mono tracking-wider">
                            <span className={examFeedbackNotification.passedWords ? 'text-emerald-400' : 'text-rose-450'}>
                              {examFeedbackNotification.passedWords ? 'WORDS ✓' : `WORDS ✗ (Min. ${spec.minWords})`}
                            </span>
                            <span className="text-slate-500">|</span>
                            <span className={examFeedbackNotification.isCoherent ? 'text-emerald-400' : 'text-rose-455'}>
                              {examFeedbackNotification.isCoherent ? 'COHERENCE ✓' : 'COHERENCE ✗'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-300 leading-relaxed font-sans font-semibold italic">
                          " {examFeedbackNotification.feedback} "
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Bubble subtitle dialogues */}
                  {latestKokoroMsg && (
                    <div className="bg-slate-950 border-2 border-slate-800 rounded-2xl p-5 text-center shadow-2xl space-y-3.5 select-text relative">
                      <span className="text-[10px] tracking-widest font-black text-amber-400 uppercase block">KOKORO (EXAMINADORA)</span>
                      <p className="text-base md:text-lg font-black leading-relaxed text-white max-h-[160px] overflow-y-auto">
                        {latestKokoroMsg.text}
                      </p>
                      {latestUserMsg && (
                        <div className="mt-2.5 pt-2.5 border-t border-slate-850 flex items-center justify-center gap-2 select-none">
                          <span className="text-[10px] uppercase font-bold text-gray-500">Tu respuesta:</span>
                          <span className="text-sm font-semibold text-slate-300 truncate max-w-xs italic select-text">
                            "{latestUserMsg.text}"
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              ) : (
                // Final Results Panel
                <div className="flex-1 flex items-center justify-center py-8">
                  <motion.div 
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-full max-w-2xl bg-slate-950 border-2 border-slate-800 rounded-3xl p-6 md:p-8 text-center text-white shadow-2xl space-y-6"
                  >
                    {examCompletedSuccessfully ? (
                      <div className="space-y-2">
                        <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-[0_0_15px_rgba(16,185,129,0.2)] animate-bounce">
                          <Award className="w-9 h-9" />
                        </div>
                        <h3 className="text-2xl font-black text-emerald-450 tracking-tight">🏆 ¡EXAMEN APROBADO!</h3>
                        <p className="text-sm text-gray-350 max-w-md mx-auto leading-relaxed">
                          ¡Increíble desempeño! Has demostrado excelentes destrezas orales para certificar la acreditación del <strong>Nivel {examTargetLevel} ({spec.code})</strong>. ¡Has desbloqueado el nivel con éxito!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-16 h-16 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-2xl flex items-center justify-center mx-auto mb-3 animate-pulse">
                          <X className="w-9 h-9" />
                        </div>
                        <h3 className="text-2xl font-black text-rose-455 tracking-tight">❌ EXAMEN NO APROBADO</h3>
                        <p className="text-sm text-gray-350 max-w-md mx-auto leading-relaxed">
                          No te rindas, el aprendizaje paso a paso es la clave de la fluidez idiomática. Sigue practicando de forma libre en el círculo conversacional para pulir tu vocabulario. ¡La constancia trae el éxito!
                        </p>
                      </div>
                    )}

                    {/* Report Stats */}
                    <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800 text-left space-y-3 font-sans text-xs md:text-sm">
                      <h4 className="text-xs font-black uppercase text-gray-400 tracking-wider">Métricas detalladas del Examen:</h4>
                      <div className="grid grid-cols-3 gap-4 pt-1">
                        <div>
                          <span className="text-gray-500 text-[10px] sm:text-xs block leading-none select-none">Puntaje Final</span>
                          <span className={`text-base font-black flex items-center gap-1 mt-1 ${Math.round((avgScore / 5.0) * 100) >= 80 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {Math.round((avgScore / 5.0) * 100)} / 100 
                          </span>
                          <span className="text-[9px] text-gray-500 block leading-tight mt-0.5">(Mínimo 80 pts)</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-[10px] sm:text-xs block leading-none select-none">Calificación Promedio</span>
                          <span className={`text-base font-black flex items-center gap-1.5 mt-1 ${avgScore >= 4.0 ? 'text-emerald-400' : 'text-rose-450'}`}>
                            <span className="text-sm">⭐</span> {avgScore.toFixed(2)} / 5.0
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-[10px] sm:text-xs block leading-none select-none">Rondas certificadas</span>
                          <span className="text-base font-black mt-1 block">
                            {examScores.length} de {spec.cycles} ciclos
                          </span>
                        </div>
                      </div>

                      {/* Score breakdown list */}
                      <div className="border-t border-slate-850 pt-2 space-y-1.5 font-mono text-[11px] leading-tight max-h-[140px] overflow-y-auto pr-1">
                        {examScores.map((score, idx) => {
                          const wCount = examRoundWordCounts[idx] || 0;
                          const coherent = examRoundValidity[idx] !== false;
                          const metWordLimit = wCount >= spec.minWords;
                          return (
                            <div key={`score-${idx}`} className="flex justify-between items-center text-gray-450 p-1.5 rounded bg-slate-950/45">
                              <span>Ciclo {idx + 1}:</span>
                              <div className="flex items-center gap-2">
                                <span className={score >= 3.0 ? 'text-emerald-450 font-bold' : 'text-rose-400'}>
                                  ⭐ {score.toFixed(1)}
                                </span>
                                <span>|</span>
                                <span className={metWordLimit ? 'text-emerald-455 font-bold' : 'text-rose-400'}>
                                  {wCount} palabras {metWordLimit ? '✓' : `✗`}
                                </span>
                                <span>|</span>
                                <span className={coherent ? 'text-emerald-555 font-bold' : 'text-rose-400'}>
                                  coherente: {coherent ? '✓' : '✗'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex gap-4 pt-3">
                      <button
                        onClick={() => {
                          setIsExamActive(false);
                          setIsExamFinished(false);
                          startExamSession(examTargetLevel!);
                        }}
                        className="flex-1 py-3 border border-slate-800 hover:bg-slate-800 text-white rounded-xl text-xs font-black tracking-wider uppercase transition-colors cursor-pointer"
                      >
                        Reintentar Examen
                      </button>
                      <button
                        onClick={() => {
                          setIsExamActive(false);
                          setIsExamFinished(false);
                        }}
                        className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:opacity-95 text-slate-950 rounded-xl text-xs font-black tracking-wider uppercase shadow-lg transition-all active:scale-98 cursor-pointer"
                      >
                        Cerrar Examen
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* LEVEL UNLOCKED SUCCESS BEAUTIFUL ANIMATION OVERLAY */}
      <AnimatePresence>
        {unlockedSuccessAnim && (
          (() => {
            const spec = levelsSpecs.find(s => s.level === unlockedSuccessAnim.level);
            if (!spec) return null;

            return (
              <motion.div
                key="unlock-success-screen"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setUnlockedSuccessAnim(null);
                }}
                className="fixed inset-0 bg-slate-950/98 backdrop-blur-xl z-[260] flex flex-col items-center justify-center p-6 text-white text-center cursor-pointer pointer-events-auto"
              >
                {/* Sparkle background elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
                  {[...Array(12)].map((_, i) => {
                    const size = Math.random() * 80 + 30;
                    return (
                      <motion.div
                        key={`success-sparkle-${i}`}
                        initial={{ 
                          opacity: 0, 
                          x: "50vw", 
                          y: "55vh",
                          scale: 0.1 
                        }}
                        animate={{ 
                          opacity: [0, 0.7, 0], 
                          x: `${Math.random() * 100}vw`, 
                          y: `${Math.random() * 100}vh`,
                          scale: [0.1, 1, 0.2]
                        }}
                        transition={{ 
                          duration: Math.random() * 2 + 2, 
                          repeat: Infinity,
                          repeatType: "loop",
                          delay: Math.random() * 0.5 
                        }}
                        className="absolute rounded-full bg-gradient-to-tr from-indigo-500/10 to-purple-500/20 blur-xl pointer-events-none"
                        style={{ width: size, height: size }}
                      />
                    );
                  })}
                </div>

                {/* Exploding Micro Confetti Particles */}
                <div className="absolute inset-x-0 top-1/2 flex items-center justify-center pointer-events-none">
                  {[...Array(20)].map((_, i) => {
                    const angle = (i * 360) / 20;
                    const distance = Math.random() * 130 + 80;
                    const rad = (angle * Math.PI) / 180;
                    const targetX = Math.cos(rad) * distance;
                    const targetY = Math.sin(rad) * distance;

                    return (
                      <motion.div
                        key={`confetti-${i}`}
                        initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                        animate={{ 
                          x: targetX, 
                          y: targetY, 
                          scale: [0.6, 1.3, 0], 
                          opacity: [1, 1, 0],
                          rotate: angle * i
                        }}
                        transition={{ 
                          duration: 1.3, 
                          ease: "easeOut",
                          delay: 0.15 
                        }}
                        className={`absolute w-3 h-3 rounded-xs shadow-md pointer-events-none ${
                          i % 3 === 0 ? 'bg-amber-400 shadow-amber-400/20' : i % 3 === 1 ? 'bg-purple-500 shadow-purple-500/20' : 'bg-emerald-400 shadow-emerald-400/20'
                        }`}
                      />
                    );
                  })}
                </div>

                {/* Giant locking/unlocking card container */}
                <motion.div
                  initial={{ scale: 0.4, y: 120, rotate: -15, opacity: 0 }}
                  animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
                  exit={{ scale: 0.6, y: -80, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 130, damping: 14, delay: 0.05 }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="relative z-[265] flex flex-col items-center bg-slate-900/90 border-2 border-slate-800 p-8 rounded-3xl max-w-sm w-full shadow-2xl backdrop-blur-md cursor-default select-none pointer-events-auto"
                >
                  {/* Glowing border outline */}
                  <div className={`absolute -inset-1 rounded-[26px] opacity-25 blur-lg bg-gradient-to-r pointer-events-none ${
                    unlockedSuccessAnim.method === 'xp'
                      ? 'from-purple-500 to-indigo-600'
                      : unlockedSuccessAnim.method === 'stars'
                      ? 'from-yellow-400 to-amber-500'
                      : 'from-emerald-400 to-teal-500'
                  }`} />

                  <div className="relative mb-6">
                    {/* Outer circle rings container */}
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                      className="absolute -inset-5 border border-dashed border-white/10 rounded-full pointer-events-none"
                    />

                    {/* Animated Padlock Circle with Glow */}
                    <div
                      className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 shadow-xl relative z-20 ${
                        unlockedSuccessAnim.method === 'xp' 
                          ? 'bg-purple-950/80 border-purple-500/40 text-purple-400' 
                          : unlockedSuccessAnim.method === 'stars'
                          ? 'bg-yellow-950/80 border-yellow-500/40 text-yellow-400'
                          : 'bg-emerald-950/80 border-emerald-500/40 text-emerald-400'
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        {!isLockAnimOpen ? (
                          <motion.div
                            key="locked"
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.6, rotate: -25, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 220, damping: 12 }}
                          >
                            <Lock className="w-8 h-8" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="unlocked"
                            initial={{ scale: 0.5, rotate: 25, opacity: 0 }}
                            animate={{ scale: [1.3, 1], rotate: 0, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 260, damping: 10 }}
                          >
                            <Unlock className="w-8 h-8" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Main Level Unlocked Banner */}
                  <div className="space-y-2 text-center relative z-20">
                    <span className={`inline-block px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                      unlockedSuccessAnim.method === 'xp'
                        ? 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                        : unlockedSuccessAnim.method === 'stars'
                        ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                        : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                    }`}>
                      {unlockedSuccessAnim.method === 'xp' ? 'Hito de XP Alcanzado' : unlockedSuccessAnim.method === 'stars' ? 'Canjeado por Estrellas' : 'Examen Certificado'}
                    </span>

                    <h3 className="text-xl font-black uppercase tracking-tight text-white leading-none mt-2 font-black">
                      ¡Nivel {unlockedSuccessAnim.level} Desbloqueado!
                    </h3>
                    
                    <p className="text-sm font-bold text-slate-450 tracking-wide block pb-2">
                      "{spec.label}"
                    </p>

                    <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 text-xs text-slate-350 leading-relaxed text-left">
                      <span className="font-extrabold text-white block mb-1">Nueva dificultad de conversación:</span>
                      {spec.longDesc}
                    </div>
                  </div>

                  {/* Highlight Reward / Source */}
                  <div className="mt-5 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 flex items-center justify-between gap-2.5 relative z-20">
                    <div className="flex items-center gap-2 text-left">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-slate-800 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4 text-orange-400 animate-pulse" />
                      </div>
                      <div>
                        <span className="text-[8px] text-gray-400 uppercase font-black block leading-none">Desbloqueado con</span>
                        <span className="text-xs font-bold text-white capitalize leading-snug">
                          {unlockedSuccessAnim.method === 'xp' ? 'Hito acumulado' : unlockedSuccessAnim.method === 'stars' ? 'Canje de Estrellas' : 'Examen Certificado'}
                        </span>
                      </div>
                    </div>
                    {unlockedSuccessAnim.method === 'xp' ? (
                      <div className="flex items-center gap-1.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2.5 py-1 rounded-lg text-xs font-black">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{100 * (unlockedSuccessAnim.level - 1)} XP</span>
                      </div>
                    ) : unlockedSuccessAnim.method === 'stars' ? (
                      <div className="flex items-center gap-1.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-2.5 py-1 rounded-lg text-xs font-black">
                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                        <span>-100 ⭐</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1 rounded-lg text-xs font-black">
                        <Mic className="w-3.5 h-3.5" />
                        <span>APROBADO</span>
                      </div>
                    )}
                  </div>

                  {/* Return CTA */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setUnlockedSuccessAnim(null);
                    }}
                    className="w-full mt-6 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 text-slate-950 font-black text-xs uppercase tracking-widest rounded-xl hover:opacity-95 shadow-md shadow-amber-500/10 cursor-pointer active:scale-95 transition-all text-shadow-xs relative z-30"
                  >
                    ¡Excelente! Continuar 🚀
                  </button>
                </motion.div>
              </motion.div>
            );
          })()
        )}
      </AnimatePresence>

      {/* LOCATION UNLOCKED SUCCESS BEAUTIFUL ANIMATION OVERLAY */}
      <AnimatePresence>
        {unlockedBgAnim && (
          (() => {
            const spec = englishBackgrounds.find(bg => bg.id === "bg_" + unlockedBgAnim.bgId) || englishBackgrounds[unlockedBgAnim.bgId];
            if (!spec) return null;

            return (
              <motion.div
                key="unlock-bg-success-screen"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setUnlockedBgAnim(null);
                }}
                className="fixed inset-0 bg-slate-950/98 backdrop-blur-xl z-[260] flex flex-col items-center justify-center p-6 text-white text-center cursor-pointer pointer-events-auto"
              >
                {/* Sparkle background elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
                  {[...Array(12)].map((_, i) => {
                    const size = Math.random() * 80 + 30;
                    return (
                      <motion.div
                        key={'bgsparkle'+i}
                        initial={{ opacity: 0, x: "50vw", y: "55vh", scale: 0.1 }}
                        animate={{ opacity: [0, 0.7, 0], x: `${Math.random() * 100}vw`, y: `${Math.random() * 100}vh`, scale: [0.1, 1, 0.2] }}
                        transition={{ duration: Math.random() * 2 + 2, repeat: Infinity, repeatType: "loop", delay: Math.random() * 0.5 }}
                        className="absolute rounded-full bg-gradient-to-tr from-amber-500/10 to-yellow-500/20 blur-xl pointer-events-none"
                        style={{ width: size, height: size }}
                      />
                    );
                  })}
                </div>

                {/* Exploding Micro Confetti Particles */}
                <div className="absolute inset-x-0 top-1/2 flex items-center justify-center pointer-events-none">
                  {[...Array(20)].map((_, i) => {
                    const angle = (i * 360) / 20;
                    const distance = Math.random() * 130 + 80;
                    const rad = (angle * Math.PI) / 180;
                    const targetX = Math.cos(rad) * distance;
                    const targetY = Math.sin(rad) * distance;
                    return (
                      <motion.div
                        key={`bgconfetti-${i}`}
                        initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                        animate={{ x: targetX, y: targetY, scale: [0.6, 1.3, 0], opacity: [1, 1, 0], rotate: angle * i }}
                        transition={{ duration: 1.3, ease: "easeOut", delay: 0.15 }}
                        className={`absolute w-3 h-3 rounded-xs shadow-md pointer-events-none ${
                          i % 3 === 0 ? 'bg-amber-400 shadow-amber-400/20' : i % 3 === 1 ? 'bg-orange-500 shadow-orange-500/20' : 'bg-yellow-400 shadow-yellow-400/20'
                        }`}
                      />
                    );
                  })}
                </div>

                <motion.div
                  initial={{ scale: 0.4, y: 120, rotate: -15, opacity: 0 }}
                  animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
                  exit={{ scale: 0.6, y: -80, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 130, damping: 14, delay: 0.05 }}
                  onClick={(e) => { e.stopPropagation(); }}
                  className="relative z-[265] flex flex-col items-center bg-slate-900/90 border-2 border-slate-800 p-8 rounded-3xl max-w-sm w-full shadow-2xl backdrop-blur-md cursor-default select-none pointer-events-auto"
                >
                  <div className={`absolute -inset-1 rounded-[26px] opacity-25 blur-lg bg-gradient-to-r pointer-events-none from-yellow-400 to-amber-500`} />

                  <div className="relative mb-6">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }} className="absolute -inset-5 border border-dashed border-white/10 rounded-full pointer-events-none" />
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 shadow-xl relative z-20 bg-yellow-950/80 border-yellow-500/40 text-yellow-400`}>
                      <AnimatePresence mode="wait">
                        {!isLockAnimOpen ? (
                          <motion.div key="locked-bg" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.6, rotate: -25, opacity: 0 }} transition={{ type: "spring", stiffness: 220, damping: 12 }}>
                            <Lock className="w-8 h-8" />
                          </motion.div>
                        ) : (
                          <motion.div key="unlocked-bg" initial={{ scale: 0.5, rotate: 25, opacity: 0 }} animate={{ scale: [1.3, 1], rotate: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 10 }}>
                            <Unlock className="w-8 h-8" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="space-y-2 text-center relative z-20">
                    <span className="inline-block px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border bg-yellow-500/10 border-yellow-500/20 text-yellow-400">
                      Ubicación Desbloqueada
                    </span>

                    <h3 className="text-xl font-black uppercase tracking-tight text-white leading-none mt-2 font-black">
                      ¡{spec.label}!
                    </h3>
                    
                    <div className="mt-4 w-full h-24 rounded-xl bg-cover bg-center border-2 border-slate-700 shadow-inner" style={{ backgroundImage: `url(${spec.url})` }} />
                  </div>

                  <div className="mt-5 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 flex items-center justify-between gap-2.5 relative z-20">
                    <div className="flex items-center gap-2 text-left">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-slate-800 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4 text-orange-400 animate-pulse" />
                      </div>
                      <div>
                        <span className="text-[8px] text-gray-400 uppercase font-black block leading-none">Desbloqueado con</span>
                        <span className="text-xs font-bold text-white capitalize leading-snug">Canje de Estrellas</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-2.5 py-1 rounded-lg text-xs font-black">
                      <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                      <span>-{unlockedBgAnim.price} ⭐</span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setEnglishBgIndex(unlockedBgAnim.bgId);
                      localStorage.setItem('kokoro_english_bg', unlockedBgAnim.bgId.toString());
                      setUnlockedBgAnim(null); 
                    }}
                    className="w-full mt-6 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 text-slate-950 font-black text-xs uppercase tracking-widest rounded-xl hover:opacity-95 shadow-md shadow-amber-500/10 cursor-pointer active:scale-95 transition-all text-shadow-xs relative z-30"
                  >
                    ¡Equipar y Continuar! 🚀
                  </button>
                </motion.div>
              </motion.div>
            );
          })()
        )}
      </AnimatePresence>

      <AnimatePresence>
        {subscriptionAlert && (
          <motion.div 
            key="subscription-alert-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md"
            onClick={() => setSubscriptionAlert(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full border border-slate-200 text-center"
            >
              <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${subscriptionAlert.type === 'success' ? 'bg-emerald-100 text-emerald-500' : subscriptionAlert.type === 'failed' ? 'bg-rose-100 text-rose-500' : 'bg-amber-100 text-amber-500'}`}>
                {subscriptionAlert.type === 'success' ? <CheckCircle2 className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
              </div>
              
              <h3 className="text-xl font-black text-slate-800 mb-2">
                {subscriptionAlert.type === 'success' ? '¡Suscripción Activa!' : subscriptionAlert.type === 'failed' ? 'Problema de Pago' : 'Suscripción Expirada'}
              </h3>
              
              <p className="text-sm text-slate-600 font-medium mb-6">
                {subscriptionAlert.message}
              </p>
              
              <button 
                onClick={() => setSubscriptionAlert(null)}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all cursor-pointer active:scale-95"
              >
                Entendido
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

            <AnimatePresence>
                {showLoginModal.isOpen && (
          <motion.div 
            key="login-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
            onClick={() => !isLoggingIn && setShowLoginModal({isOpen: false, message: ""})}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full border border-slate-200 text-center relative overflow-hidden flex flex-col items-center"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-indigo-500" />
              
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 border-2 border-blue-100 shadow-sm text-blue-500">
                <Lock className="w-8 h-8 text-blue-500" />
              </div>
              
              <h3 className="text-2xl font-black text-slate-800 mb-3 tracking-tight">
                Iniciar Sesión
              </h3>
              
              <p className="text-sm text-slate-500 font-medium mb-6 leading-relaxed">
                {showLoginModal.message || "Inicia sesión para continuar."}
              </p>
              
              {authError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-bold p-3 rounded-xl w-full mb-4 text-center">
                  {authError}
                  {unverifiedUser && (
                    <button 
                      onClick={handleResendVerification}
                      className="mt-2 block w-full bg-red-100 hover:bg-red-200 text-red-700 py-2 rounded-lg transition-colors"
                    >
                      Reenviar correo
                    </button>
                  )}
                </div>
              )}
              {authSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-xs font-bold p-3 rounded-xl w-full mb-4">
                  {authSuccess}
                </div>
              )}

              {authMode === 'google' && (
                <>
                  <button
                    onClick={async () => {
                      try {
                        await handleGoogleSignIn();
                        setShowLoginModal({isOpen: false, message: ""});
                      } catch(e) {
                        // error handled in handleGoogleSignIn
                      }
                    }}
                    disabled={isLoggingIn}
                    className={`w-full py-3.5 px-4 ${isLoggingIn ? 'bg-slate-50 border-slate-200' : 'bg-white hover:bg-slate-50 hover:shadow-md border-slate-200'} border-2 rounded-xl text-slate-700 font-bold text-base flex justify-center items-center relative transition-all shadow-sm transform active:scale-95 disabled:pointer-events-none cursor-pointer mb-4`}
                  >
                    {isLoggingIn ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                        <span className="font-bold text-slate-500">Accediendo...</span>
                      </div>
                    ) : (
                      <div className="flex items-center w-full justify-center">
                        <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span className="tracking-wide text-sm font-black text-slate-700">Continuar con Google</span>
                      </div>
                    )}
                  </button>
                  <div className="flex items-center w-full mb-4">
                    <div className="flex-1 h-px bg-slate-200"></div>
                    <span className="px-3 text-xs text-slate-400 font-bold">O</span>
                    <div className="flex-1 h-px bg-slate-200"></div>
                  </div>
                  <button
                    onClick={() => { setAuthMode('email_login'); setAuthError(''); setAuthSuccess(''); }}
                    className="w-full py-3.5 px-4 bg-slate-800 hover:bg-slate-900 border-2 border-slate-800 rounded-xl text-white font-bold text-sm flex justify-center items-center transition-all shadow-sm transform active:scale-95"
                  >
                    Usar Correo Electrónico
                  </button>
                </>
              )}

              {authMode === 'email_login' && (
                <form onSubmit={handleEmailSignIn} className="w-full">
                  <input
                    type="email"
                    placeholder="Correo Electrónico"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    className="w-full mb-3 px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 outline-none text-sm font-bold text-slate-700 transition-colors"
                    required
                  />
                  <div className="relative w-full mb-4">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 outline-none text-sm font-bold text-slate-700 transition-colors pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="w-full py-3.5 px-4 bg-blue-500 hover:bg-blue-600 border-2 border-blue-500 hover:border-blue-600 rounded-xl text-white font-bold text-base flex justify-center items-center transition-all shadow-sm transform active:scale-95 disabled:opacity-70"
                  >
                    {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : "Iniciar Sesión"}
                  </button>
                  <div className="mt-4 flex flex-col gap-2">
                    <button type="button" onClick={() => { setAuthMode('email_signup'); setAuthError(''); setAuthSuccess(''); }} className="text-sm font-bold text-slate-500 hover:text-blue-500">
                      ¿No tienes cuenta? Regístrate
                    </button>
                    <button type="button" onClick={() => { setAuthMode('google'); setAuthError(''); setAuthSuccess(''); }} className="text-xs font-bold text-slate-400 hover:text-slate-600">
                      Volver
                    </button>
                  </div>
                </form>
              )}

              {authMode === 'email_signup' && (
                <form onSubmit={handleEmailSignUp} className="w-full">
                  <input
                    type="email"
                    placeholder="Correo Electrónico"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    className="w-full mb-3 px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-0 outline-none text-sm font-bold text-slate-700 transition-colors"
                    required
                  />
                  <div className="relative w-full mb-4">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña (mín. 6 caracteres)"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-0 outline-none text-sm font-bold text-slate-700 transition-colors pr-12"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="w-full py-3.5 px-4 bg-indigo-500 hover:bg-indigo-600 border-2 border-indigo-500 hover:border-indigo-600 rounded-xl text-white font-bold text-base flex justify-center items-center transition-all shadow-sm transform active:scale-95 disabled:opacity-70"
                  >
                    {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : "Crear Cuenta"}
                  </button>
                  <div className="mt-4 flex flex-col gap-2">
                    <button type="button" onClick={() => { setAuthMode('email_login'); setAuthError(''); setAuthSuccess(''); }} className="text-sm font-bold text-slate-500 hover:text-indigo-500">
                      ¿Ya tienes cuenta? Inicia sesión
                    </button>
                    <button type="button" onClick={() => { setAuthMode('google'); setAuthError(''); setAuthSuccess(''); }} className="text-xs font-bold text-slate-400 hover:text-slate-600">
                      Volver
                    </button>
                  </div>
                </form>
              )}

              {authMode === 'email_verify_code' && (
                <form onSubmit={handleVerifyCode} className="w-full">
                  <div className="mb-4 text-center text-sm font-semibold text-slate-500">
                    Ingresa el código que enviamos a tu correo
                  </div>
                  {testModeCode && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs font-medium leading-relaxed">
                      ⚠️ <strong>Modo de prueba (sin dominio propio)</strong><br />
                      Usa este código para verificar tu cuenta: <span className="font-mono font-bold bg-amber-100 px-1.5 py-0.5 rounded text-amber-900 select-all">{testModeCode}</span>
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Código de 6 dígitos"
                    value={authVerificationCode}
                    onChange={(e) => setAuthVerificationCode(e.target.value)}
                    className="w-full mb-4 px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-0 outline-none text-center text-xl font-bold text-slate-700 tracking-widest transition-colors"
                    required
                    maxLength={6}
                  />
                  <button
                    type="submit"
                    disabled={isLoggingIn || authVerificationCode.length < 6}
                    className="w-full py-3.5 px-4 bg-indigo-500 hover:bg-indigo-600 border-2 border-indigo-500 hover:border-indigo-600 rounded-xl text-white font-bold text-base flex justify-center items-center transition-all shadow-sm transform active:scale-95 disabled:opacity-70"
                  >
                    {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verificar Cuenta"}
                  </button>
                  <div className="mt-4 flex flex-col gap-2">
                    <button type="button" onClick={handleResendVerification} disabled={isLoggingIn} className="text-sm font-bold text-indigo-500 hover:text-indigo-600 disabled:opacity-50">
                      Reenviar código
                    </button>
                    <button type="button" onClick={() => { setAuthMode('email_login'); setAuthError(''); setAuthSuccess(''); }} className="text-sm font-bold text-slate-500 hover:text-indigo-500">
                      Volver a inicio de sesión
                    </button>
                  </div>
                </form>
              )}
              
              <button 
                onClick={() => setShowLoginModal({isOpen: false, message: ""})}
                disabled={isLoggingIn}
                className="mt-4 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            </motion.div>
          </motion.div>
        )}

{showCancelConfirm && (
          <motion.div 
            key="cancel-confirm-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
            onClick={() => !isCancelling && setShowCancelConfirm(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full border border-slate-200 text-center relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-rose-400 to-orange-400" />
              
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 bg-rose-50 text-rose-500 border-2 border-rose-100">
                <AlertCircle className="w-8 h-8" />
              </div>
              
              <h3 className="text-xl font-black text-slate-800 mb-2">
                ¿Seguro que quieres cancelar?
              </h3>
              
              <p className="text-sm text-slate-600 font-medium mb-6">
                Te extrañaremos en Kokoro Premium. Al cancelar, perderás acceso al terminar tu periodo actual a:
                <br/><br/>
                <span className="block text-left mb-1">✨ Práctica con voz ilimitada</span>
                <span className="block text-left mb-1">✨ Progresión rápida</span>
                <span className="block text-left mb-1">✨ Sin interrupciones</span>
              </p>
              
              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                  className="w-full py-3.5 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-xl transition-all cursor-pointer shadow-md shadow-indigo-500/20 active:scale-95 disabled:opacity-50"
                >
                  Mejor no, mantener Premium
                </button>
                <button 
                  onClick={async () => {
                     setIsCancelling(true);
                     try {
                        const token = await auth.currentUser?.getIdToken();
                        if (!token) throw new Error("No token");
                        const res = await fetch('/api/cancel-subscription', {
                           method: 'POST',
                           headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                           }
                        });
                        const data = await res.json();
                        if (res.ok) {
                           setSubscriptionAlert({ type: 'expired', message: "Suscripción automática cancelada correctamente. Seguirás siendo Premium hasta el fin de tu ciclo." });
                        } else {
                           throw new Error(data.error || "Error al cancelar");
                        }
                     } catch(err) {
                        console.error(err);
                        alert("Hubo un error al cancelar. Intenta más tarde.");
                     } finally {
                        setIsCancelling(false);
                        setShowCancelConfirm(false);
                     }
                  }}
                  disabled={isCancelling}
                  className="w-full py-2.5 bg-white border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 font-bold rounded-xl transition-all cursor-pointer active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCancelling ? <><Loader2 className="w-5 h-5 animate-spin" /> Cancelando...</> : "Sí, cancelar suscripción"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden Background Music Element */}
      <audio 
        ref={bgMusicRef}
        className="hidden"
        loop
        onWaiting={() => setIsMusicLoading(true)}
        onLoadStart={() => setIsMusicLoading(true)}
        onPlaying={() => setIsMusicLoading(false)}
        onError={() => {
          setIsMusicLoading(false);
          const track = musicTracks[musicIndex];
          if (track) {
            console.warn(`Error on bgMusicRef playing ${track.name} (${track.id}). Marking as error so they can re-download.`);
            setMusicDownloadProgress(prev => ({
              ...prev,
              [track.id]: { status: 'error' }
            }));
          }
        }}
        onCanPlay={(e) => {
          setIsMusicLoading(false);
          const isDucked = isListening || isPlaying;
          const targetVolume = isDucked ? Math.max(0, musicVolume * 0.2) : musicVolume;
          e.currentTarget.volume = targetVolume;
        }}
        onLoadedMetadata={(e) => {
          setIsMusicLoading(false);
          const isDucked = isListening || isPlaying;
          const targetVolume = isDucked ? Math.max(0, musicVolume * 0.2) : musicVolume;
          e.currentTarget.volume = targetVolume;
        }}
      />

      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        className="hidden"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={() => {
          if ((appMode === 'english' || appMode === 'chat') && audioRef.current && englishEmotionSequence.length > 0) {
            const audio = audioRef.current;
            if (audio.duration && isFinite(audio.duration)) {
              const progress = audio.currentTime / audio.duration;
              const index = Math.min(Math.floor(progress * englishEmotionSequence.length), englishEmotionSequence.length - 1);
              const current = englishEmotionSequence[index];
              if (current) {
                setEmotionState(prev => {
                  if (prev.name !== current.emocion || prev.level !== current.nivel) {
                    return { name: current.emocion, level: current.nivel };
                  }
                  return prev;
                });
              }
            }
          }
        }}
        onEnded={() => {
          setIsPlaying(false);
          if (appMode !== 'chat') {
            setEmotionState({ name: 'alegría', level: 1 });
          }
          if (isSpeakingExamRulesRef.current) {
            startExamCountdownTicking();
            return;
          }
          if (isExamActiveRef.current && !isExamFinishedRef.current) {
            setIsMyTurnToSpeakExam(true);
            startListening();
            setExamMicCountdown(5);
            return;
          }
          if (activePracticePanel === 'conversation') {
            if (conversationRound <= 5) {
              setIsMyTurnToSpeak(true);
              startListening();
            } else {
              setIsMyTurnToSpeak(false);
              stopListening();
            }
            return;
          }
          if (hideMicForEnglishIntro && audioRef.current && !audioRef.current.src.startsWith('data:audio/wav;base64')) {
            setHideMicForEnglishIntro(false);
          }
          if (isListeningPracticeActive) {
            setListeningCompletedCount(prev => prev + 1);
            setIsListeningPlaybackFinished(true);
          } else if (pendingTopicPromptOnAudioEndRef.current) {
            pendingTopicPromptOnAudioEndRef.current = false;
            setIsPracticeMenuOpen(false);
            setTimeout(() => {
              setIsTopicsMenuOpen(true);
            }, 300);
          } else if (appMode === 'english' && hasChosenTopic && hasStartedStory && !hasAutoOpenedPracticeRef.current) {
            setIsPracticeMenuOpen(true);
            hasAutoOpenedPracticeRef.current = true;
          }
          if (!isPremiumUser && !hasShownSponsor) {
            setHasShownSponsor(true);
            if (autoResumeMicRef.current) {
              startListening();
            }
          } else if (autoResumeMicRef.current) {
            startListening();
          }
        }}
      />

      {/* Feedback Button and Modal */}
      <button
        onClick={() => setIsFeedbackModalOpen(true)}
        className="fixed bottom-3 left-3 z-[100] bg-white shadow-lg shadow-black/10 rounded-full hover:scale-105 transition-transform duration-200 border border-slate-200 flex items-center justify-center w-12 h-12"
      >
        <Mail className="w-5 h-5 text-slate-700" />
      </button>

      <AnimatePresence>
        {isFeedbackModalOpen && (
          <motion.div
            key="feedback-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex flex-col items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-xl relative overflow-hidden"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold tracking-tight text-slate-800">Sugerencias</h3>
                <button
                  onClick={() => setIsFeedbackModalOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {feedbackStatus === 'success' ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <p className="font-bold text-slate-700">Gracias por tus comentarios.</p>
                  <button
                    onClick={() => {
                      setIsFeedbackModalOpen(false);
                      setTimeout(() => setFeedbackStatus('idle'), 300);
                    }}
                    className="mt-6 w-full py-3 bg-slate-100 text-slate-700 font-bold rounded-2xl hover:bg-slate-200 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-2">
                      Envía un mensaje a los desarrolladores de Kokoro. ¡Agradecemos mucho tus comentarios y sugerencias para seguir mejorando!
                    </p>
                  </div>
                  <div>
                    <textarea
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder="Escribe tu sugerencia o queja aquí..."
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20 transition-all resize-none h-32"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setIsFeedbackModalOpen(false)}
                      className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-2xl hover:bg-slate-200 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleFeedbackSubmit}
                      disabled={isSubmittingFeedback || !feedbackMessage.trim()}
                      className="flex-1 py-3 bg-indigo-500 text-white font-bold rounded-2xl hover:bg-indigo-600 active:scale-[0.98] transition-all disabled:opacity-50 disabled:active:scale-100 flex justify-center items-center gap-2"
                    >
                      {isSubmittingFeedback ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar'}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* INTERNAL AD BROWSER MODAL */}
      <AnimatePresence>
        {activeAdUrl && (
          <motion.div
            key="internal-ad-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex flex-col bg-black/90 backdrop-blur-xl"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800">
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4 text-pink-400" />
                <span className="text-xs font-bold uppercase tracking-wider">Auspiciante</span>
              </div>
              <button
                onClick={() => setActiveAdUrl(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 w-full relative">
              {/* Spinner while loading */}
              <div className="absolute inset-0 flex items-center justify-center -z-10">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
              </div>
              <iframe
                src={activeAdUrl}
                className="w-full h-full border-none bg-white"
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                title="Sponsor Ad"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      </div>
    </div>
  );
}
